// cherish3: 배틀 한글화 3차 적용됨
declare var CherishKo: any; // cherish fork: 한글 이름 헬퍼 (battle-dex.ts 정의)
/**
 * Main menu panel
 *
 * @author Guangcong Luo <guangcongluo@gmail.com>
 * @license AGPLv3
 */

import preact from "../js/lib/preact";
import { PSLoginServer } from "./client-connection";
import { PSBackground } from "./client-core";
import { Config, PS, PSRoom, type PSRoomFocusOptions, type RoomID, type RoomOptions, type Team } from "./client-main";
import { CherishMarketView, cwmAskGate, cwmMarketOpen, cwmNestSeen } from "./cherish-market";
import { CherishLoveView, cwlAskGate, cwlLoveIsNew, cwlLoveOpen, cwlLoveOpened, cwlLoveTodo, cwlWantsLove } from "./cherish-love";
import { CherishLendingView, cwbAskGate, cwbLendingOpen } from "./cherish-lending";
import { CherishGymView, cwgAskGate, cwgGymOpen } from "./cherish-gym";
import { cwrlOpen } from "./cherish-raid-live";
import { CherishTourBanner, CherishTourMatchGate } from "./cherish-tournament";
import { PSIcon, PSPanelErrorBoundary, PSPanelWrapper, PSRoomPanel, PSView, ReconnectTimer } from "./panels";
import type { BattlesRoom } from "./panel-battle";
import type { ChatRoom } from "./panel-chat";
import type { LadderFormatRoom } from "./panel-ladder";
import type { RoomsRoom } from "./panel-rooms";
import { TeamBox, type SelectType } from "./panel-teamdropdown";
import { Dex, toID, type ID } from "./battle-dex";
import type { Args } from "./battle-text-parser";
import { BattleLog } from "./battle-log"; // optional

export type RoomInfo = {
	title: string, id?: RoomID, desc?: string, userCount?: number, section?: string, privacy?: 'hidden',
	spotlight?: string, subRooms?: string[],
};

// cherish-login-core:begin (patch-login-flow.py)
/**
 * 로그인 복구 (2026-09-22) — 정본 cherishpro scripts/showdown-fork/patch-login-flow.py
 *
 * 폰에서 탭이 통째로 닫혔다 다시 열리면 게스트로 남아 있었다. 원인이 둘이다.
 *  1) `PSLoginServer.query` 는 **통신 실패도 null** 로 돌려주는데, 옛 코드는 그것을
 *     「세션이 없다」와 같이 다뤘다. 배틀 사이트와 api 는 오리진이 달라 이 요청 하나가
 *     DNS+TCP+TLS 부터 새로 맺는다 — 모바일이 막 깨어난 직후가 곧잘 끊기는 자리다.
 *     한 번 끊기면 재시도가 없어 그대로 게스트로 굳었다.
 *  2) 자동 이동 가드가 sessionStorage 라 **복원된 탭에 그대로 따라왔다.** 그 탭은 영영
 *     다시 시도하지 않는다.
 * 그래서 **실패는 되시도하고, `no_session` 일 때만 디스코드로 보낸다.**
 */
/** 이 페이지에서 디스코드로 보낸 횟수 — sessionStorage 가 막힌 브라우저용 마지막 안전핀. */
let cherishLoginSent = 0;
/** 지금 upkeep 을 보내고 있는 challstr (없으면 null). 같은 연결에 두 벌 보내지 않으려고. */
let cherishUpkeepFor: string | null = null;
let cherishUpkeepAt = 0;
const CHERISH_UPKEEP_RETRY = [1500, 4000, 10000];

/**
 * 디스코드 로그인으로 이동.
 * @param force 사람이 직접 누른 경우 — 가드를 무시한다 (누른 뜻이 분명하므로).
 */
function cherishLoginStart(force?: boolean) {
	if (!force) {
		if (cherishLoginSent >= 2) return;
		let last = 0;
		let tries = 0;
		try {
			// 값은 "시각,횟수". 옛 값('1')도 그대로 읽힌다 — 시각이 1970 이라 오래된 것으로 친다.
			const parts = (sessionStorage.getItem('cherishAutoLogin') || '').split(',');
			last = Number(parts[0]) || 0;
			tries = Number(parts[1]) || 0;
		} catch {}
		// 10분이 지났으면 새 상황으로 본다 (탭 복원·오래 뒤 재방문). 그 안에서는 두 번까지만 —
		// 쿠키가 아예 안 붙는 브라우저에서 디스코드와 무한 왕복하지 않게.
		const fresh = Date.now() - last < 10 * 60 * 1000;
		if (fresh && tries >= 2) return;
		try {
			sessionStorage.setItem('cherishAutoLogin', `${Date.now()},${fresh ? tries + 1 : 1}`);
		} catch {}
	}
	cherishLoginSent++;
	location.href = 'https://cherish-world.com/api/auth/discord/login?next=' + encodeURIComponent(location.href);
}
(window as any).cherishLoginStart = cherishLoginStart;

/**
 * upkeep 한 판 — 웹 세션으로 이름+assertion 을 받아 자동 로그인한다.
 *
 * ⚠️ **재접속 때도 반드시 돌아야 한다.** 소켓이 새로 붙으면 서버는 새 challstr 을 주고
 * 그것으로 다시 인증해야 한다. `PS.user.named` 는 끊겨도 true 로 남아 있으므로 그걸로
 * 걸러내면 재접속 때 게스트가 된다 — 그래서 여기엔 named 검사를 두지 않는다.
 */
function cherishUpkeep(challstr: string, attempt = 0) {
	if (!challstr) return;
	if (attempt === 0 && cherishUpkeepFor === challstr) return;
	cherishUpkeepFor = challstr;
	cherishUpkeepAt = Date.now();
	PSLoginServer.query('upkeep', { challstr }).then(res => {
		// 그 사이 재접속했으면 새 challstr 쪽이 이어받는다 (옛 답으로 덮어쓰지 않는다).
		if (PS.user.challstr !== challstr) return;
		if (!res) {
			// **통신 실패는 「세션 없음」이 아니다** — 디스코드로 보내지 않고 조용히 다시 해본다.
			if (attempt < CHERISH_UPKEEP_RETRY.length) {
				setTimeout(() => cherishUpkeep(challstr, attempt + 1), CHERISH_UPKEEP_RETRY[attempt]);
				return;
			}
			// 포기. 자물쇠를 풀어 다음 계기(화면 복귀)에 다시 해볼 수 있게 둔다.
			cherishUpkeepFor = null;
			PS.user.initializing = false;
			PS.update();
			return;
		}
		cherishUpkeepFor = null;
		if (!res.username) {
			PS.user.initializing = false;
			const reason = (res as any).cherishReason;
			if (reason === 'not_member' || reason === 'no_role') {
				// 서버원 아님 — 로그인 리다이렉트 대신 안내 화면 (patch-member-gate.py)
				cherishShowGate(reason);
				return;
			}
			// 웹 세션 없음 — 디스코드 로그인으로 (가드는 cherishLoginStart 안에).
			cherishLoginStart();
			PS.update();
			return;
		}
		// | , ; are not valid characters in names
		const username = String(res.username).replace(/[|,;]+/g, '');
		if (res.loggedin) {
			PS.user.registered = { name: username, userid: toID(username) };
		}
		PS.user.handleAssertion(username, res.assertion);
	});
}

// 화면이 돌아온 순간에만 한 번 더 확인한다 — 주기 타이머가 아니라 **계기**다(§4-6 「통로 하나」).
// 복귀 직후 첫 요청이 끊겨 게스트로 굳는 자리의 탈출구. 연결 쪽 건강검진(patch-conn-resilience.py)과
// 같은 계기를 쓰지만 서로 독립이다 — 재연결이 되면 새 challstr 이 알아서 이 함수를 부른다.
if (typeof document !== 'undefined') {
	const cherishLoginRecheck = () => {
		if (document.hidden || PS.user.named || !PS.user.challstr) return;
		if (Date.now() - cherishUpkeepAt < 5000) return;
		cherishUpkeep(PS.user.challstr);
	};
	document.addEventListener('visibilitychange', cherishLoginRecheck);
	window.addEventListener('focus', cherishLoginRecheck);
	window.addEventListener('pageshow', cherishLoginRecheck);
}
// cherish-login-core:end

// cherish-world fork: 서버원 자격 없음 안내 오버레이 (정본 cherishpro scripts/showdown-fork/patch-member-gate.py)
function cherishShowGate(reason: string) {
	if ((window as any).cherishGateShown) return;
	(window as any).cherishGateShown = true;
	const noRole = reason === 'no_role';
	const root = document.createElement('div');
	root.setAttribute('style', [
		'position:fixed', 'inset:0', 'z-index:2147483647',
		'background:linear-gradient(160deg,#2b1b2e,#3d2233)',
		'display:flex', 'align-items:center', 'justify-content:center', 'padding:24px',
		"font-family:'Pretendard','Malgun Gothic',sans-serif", 'color:#fff',
	].join(';'));
	const card = document.createElement('div');
	card.setAttribute('style', [
		'max-width:420px', 'width:100%', 'text-align:center',
		'background:rgba(255,255,255,0.08)', 'border:1px solid rgba(255,255,255,0.18)',
		'border-radius:22px', 'padding:32px 26px', 'box-shadow:0 18px 50px rgba(0,0,0,0.35)',
	].join(';'));
	const title = document.createElement('div');
	title.setAttribute('style', 'font-size:20px;font-weight:800;line-height:1.4');
	title.textContent = noRole ? '서버 입장 안내를 마쳐주세요' : '체리쉬 서버원만 이용할 수 있어요';
	const body = document.createElement('div');
	body.setAttribute('style', 'margin-top:14px;font-size:14px;line-height:1.75;opacity:0.9');
	body.textContent = noRole ?
		'입장 안내를 끝내면 포켓몬 배틀을 바로 이용할 수 있어요. 안내 채널에서 이어서 진행해 주세요!' :
		'포켓몬 배틀은 체리쉬 디스코드 서버에 계신 분들을 위한 공간이에요. 서버에 다시 들어오시면 지금까지 모은 포켓몬과 기록이 그대로 남아 있어요.';
	card.appendChild(title);
	card.appendChild(body);
	if (!noRole) {
		const link = document.createElement('a');
		link.href = 'https://discord.gg/cherish-play';
		link.target = '_blank';
		link.rel = 'noreferrer';
		link.textContent = '체리쉬 서버 들어가기';
		link.setAttribute('style', [
			'display:inline-block', 'margin-top:22px', 'padding:12px 22px', 'border-radius:16px',
			'background:linear-gradient(90deg,#f0679a,#f2909f)', 'color:#fff',
			'font-weight:800', 'font-size:14px', 'text-decoration:none',
		].join(';'));
		card.appendChild(link);
	}
	root.appendChild(card);
	document.body.appendChild(root);
}

// cherish-form-art:begin (patch-custom-form.py)
/**
 * 개체 전용 커스텀 폼 — **배틀 밖** 그림 주소 (2026-09-21).
 *
 * 배틀 화면은 방 로그(`|cherishform|`)로 받은 `battle.cherishForms` 를 보지만, 「내 포켓몬」·
 * 인벤토리·이름 지어주기는 그 배관 밖이라 api 목록 응답의 `customFormId` 를 그대로 읽는다.
 *
 * **폼이 이로치를 덮는다** — 폼이 붙은 개체는 이로치 폴더를 보지 않는다 (자산이 한 벌뿐).
 */
function cherishMonArt(m: any): string {
	const form = m && typeof m.customFormId === 'string' ? m.customFormId : '';
	if (form) return `/cherish/form/${form}/${m.spriteId}.png?v=1`;
	return cherishMonArtBase(m);
}
/** 폼 없는 보통(또는 이로치) 일러스트 — 폴백의 도착지. */
function cherishMonArtBase(m: any): string {
	return `/cherish/sprite/${m && m.shiny ? 'shiny/' : ''}${m ? m.spriteId : ''}.png?v=2`;
}
/**
 * 폼 그림이 없으면 보통 일러스트로 **한 번만** 내려간다.
 *
 * 서버는 자산 유무를 보지 않고 `customFormId` 를 그대로 내려주므로(진화해서 폼 자산이 없는 종이
 * 되는 경우), 이 폴백이 없으면 그 아이 칸이 깨진 그림으로 남는다.
 * 클로저를 만들지 않는 **모듈 고정 함수**다 — 카드는 루프 안에서 그려지고 빌드의 babel 이
 * 루프 변수 캡처를 거부한다. 폴백 주소는 `data-cwfb` 속성으로 받는다.
 */
function cherishMonArtFb(e: any) {
	const img = e.currentTarget as HTMLImageElement;
	if (img.dataset.cwFormFb) return;
	img.dataset.cwFormFb = '1';
	const base = img.getAttribute('data-cwfb') || '';
	if (base && base !== img.src) img.src = base;
}
// cherish-form-art:end

export class MainMenuRoom extends PSRoom {
	override readonly classType: string = 'mainmenu';
	listeners: Record<string, ((response: any) => void)[] | null> = {};
	userdetailsCache: {
		[userid: string]: {
			userid: ID,
			name: string,
			avatar?: string | number,
			status?: string,
			group?: string,
			customgroup?: string,
			rooms?: { [roomid: string]: { isPrivate?: true, p1?: string, p2?: string } },
		},
	} = {};
	roomsCache: {
		battleCount?: number,
		userCount?: number,
		chat?: RoomInfo[],
		sectionTitles?: string[],
	} = {};
	// cherish-rooms: 진행 중 배틀 목록 (roomlist 쿼리 tee — 메인 「배틀 방」 섹션 소비)
	cherishBattles: any[] | null = null;
	searchCountdown: { format: string, packedTeam: string, countdown: number, timer: number } | null = null;
	/** used to track the moment between "search sent" and "server acknowledged search sent" */
	teamSent: string | null = null;
	// cherish-world fork: cherishQueue = 4인 포맷 대기 인원 (patch-ffa-server.py 가 실어 보낸다)
	search: {
		searching: string[], games: Record<RoomID, string> | null,
		/** cherish-one-battle: 진행 중인 배틀 방 id (patch-one-battle.py). */
		cherishBattle?: RoomID | null,
		cherishQueue?: { [formatid: string]: { n: number, need: number } },
	} = { searching: [], games: null };
	disallowSpectators: boolean | null = PS.prefs.disallowspectators;
	lastChallenged: number | null = null;
	constructor(options: RoomOptions) {
		super(options);
		if (this.backlog) {
			// these aren't set yet, but a lot of things could go wrong if we don't
			PS.rooms[''] = this;
			PS.mainmenu = this;
			for (const args of this.backlog) {
				this.receiveLine(args);
			}
			this.backlog = null;
		}
	}
	adjustPrivacy() {
		PS.prefs.set('disallowspectators', this.disallowSpectators);
		if (this.disallowSpectators) return '/noreply /hidenext \n';
		return '';
	}
	startSearch = (format: string, team?: Team, parentElem?: HTMLElement | null) => {
		PS.requestNotifications();
		if (this.searchCountdown) {
			PS.alert("Wait for this countdown to finish first...", { parentElem });
			return;
		} else if (this.search.searching.includes(format)) {
			PS.alert(`You're already searching for a ${BattleLog.formatName(format)} battle...`, { parentElem });
			return;
		}
		this.searchCountdown = {
			format,
			packedTeam: team?.packedTeam || '',
			countdown: 3,
			timer: setInterval(this.doSearchCountdown, 1000),
		};
		this.update(null);
	};
	searchingFormat() {
		return this.searchCountdown?.format || this.teamSent ||
			this.search.searching?.[this.search.searching.length - 1] || null;
	}
	cancelSearch = () => {
		if (this.searchCountdown) {
			clearTimeout(this.searchCountdown.timer);
			this.searchCountdown = null;
			this.update(null);
			return true;
		}
		if (this.teamSent || this.search.searching?.length) {
			this.teamSent = null;
			PS.send(`/cancelsearch`);
			this.update(null);
			return true;
		}
		return false;
	};
	doSearchCountdown = () => {
		if (!this.searchCountdown) return; // ??? race???

		this.searchCountdown.countdown--;
		if (this.searchCountdown.countdown <= 0) {
			this.doSearch(this.searchCountdown);
			clearTimeout(this.searchCountdown.timer);
			this.searchCountdown = null;
		}
		this.update(null);
	};
	doSearch = (search: NonNullable<typeof this.searchCountdown>) => {
		this.teamSent = search.format;
		const privacy = this.adjustPrivacy();
		PS.send(`/utm ${search.packedTeam}`);
		PS.send(`${privacy}/search ${search.format}`);
	};
	override receiveLine(args: Args) {
		const [cmd] = args;
		switch (cmd) {
		case 'challstr': {
			const [, challstr] = args;
			PS.user.challstr = challstr;
			// cherish-login-challstr:begin (patch-login-flow.py)
			// 본체는 같은 파일 위쪽 cherishUpkeep — **통신 실패와 「세션 없음」을 가르고**
			// 실패는 되시도한다. 재접속 때도 새 challstr 로 그대로 다시 돈다.
			cherishUpkeep(challstr);
			// cherish-login-challstr:end
			return;
		} case 'updateuser': {
			const [, fullName, namedCode, avatar, settingsJSON] = args;
			const named = namedCode === '1';
			if (named) PS.user.initializing = false;
			if (settingsJSON) {
				PS.prefs.set('serversettings', { ...PS.prefs.serversettings, ...JSON.parse(settingsJSON) });
				Dex.loadTextData();
			}
			PS.user.setName(fullName, named, avatar);
			// cherish-send-hold-named:begin (patch-reconnect-hold.py)
			// 재연결 뒤 게스트였던 동안 붙잡아 둔 배틀 선택을 계정이 돌아온 지금 내보낸다.
			if (named) PS.connection?.cherishReleaseHeld();
			// cherish-send-hold-named:end
			// cherish fork: 로그인 완료 즉시 접속자 보드 재조회 — 첫 조회는 게스트 시점이라 본인 카드가 다음 25초 폴링까지 안 뜨던 지연 해소
			if (named) PS.send('/cmd rooms');
			PS.teams.loadRemoteTeams();
			return;
		} case 'updatechallenges': {
			const [, challengesBuf] = args;
			this.receiveChallenges(challengesBuf);
			return;
		} case 'updatesearch': {
			const [, searchBuf] = args;
			this.receiveSearch(searchBuf);
			return;
		} case 'queryresponse': {
			const [, queryId, responseJSON] = args;
			this.handleQueryResponse(queryId as ID, JSON.parse(responseJSON));
			return;
		} case 'pm': {
			const [, user1, user2, message] = args;
			this.handlePM(user1, user2, message);
			let sideRoom = PS.rightPanel as ChatRoom;
			if (sideRoom?.type === "chat" && PS.prefs.inchatpm) sideRoom?.log?.add(args);
			return;
		} case 'customgroups': {
			const [, groupsList] = args;
			PS.server.parseGroups(groupsList);
			return;
		} case 'formats': {
			this.parseFormats(args);
			return;
		} case 'popup': {
			let [, message] = args;
			for (const roomid in PS.rooms) {
				const room = PS.rooms[roomid] as ChatRoom | MainMenuRoom;
				if (room.teamSent) {
					room.teamSent = null;
					room.update(null);
				}
				if (room.type === 'team') (room as any).cancelUpload();
			}
			let width: number | undefined;
			if (message.startsWith('|wide|')) {
				message = message.slice(6);
				width = 960;
			}
			PS.alert(message.replace(/\|\|/g, '\n'), { width });
			return;
		}
		}
		const lobby = PS.rooms['lobby'];
		if (lobby) lobby.receiveLine(args);
	}
	receiveChallenges(dataBuf: string) {
		let json;
		try {
			json = JSON.parse(dataBuf);
		} catch {}
		for (const userid in json.challengesFrom) {
			PS.getPMRoom(toID(userid));
		}
		if (json.challengeTo) {
			PS.getPMRoom(toID(json.challengeTo.to));
		}
		for (const roomid in PS.rooms) {
			const room = PS.rooms[roomid] as ChatRoom;
			if (!room.pmTarget) continue;
			const targetUserid = toID(room.pmTarget);
			if (!room.challenged && !(targetUserid in json.challengesFrom) &&
				!room.challenging && json.challengeTo?.to !== targetUserid) {
				continue;
			}
			room.challenged = room.parseChallenge(json.challengesFrom[targetUserid]);
			room.challenging = json.challengeTo?.to === targetUserid ? room.parseChallenge(json.challengeTo.format) : null;
			room.update(null);
		}
	}
	receiveSearch(dataBuf: string) {
		let json;
		this.teamSent = null;
		try {
			json = JSON.parse(dataBuf);
		} catch {}
		this.search = json;
		this.update(null);
	}
	parseFormats(formatsList: string[]) {
		let isSection = false;
		let section = '';

		let column = 0;

		window.NonBattleGames = { rps: 'Rock Paper Scissors' };
		for (let i = 3; i <= 9; i += 2) {
			window.NonBattleGames[`bestof${i}`] = `Best-of-${i}`;
		}
		window.BattleFormats = {};
		for (let j = 1; j < formatsList.length; j++) {
			const entry = formatsList[j];
			if (isSection) {
				section = entry;
				isSection = false;
			} else if (entry === ',LL') {
				PS.teams.usesLocalLadder = true;
			} else if (entry === '' || (entry.startsWith(',') && !isNaN(Number(entry.slice(1))))) {
				isSection = true;

				if (entry) {
					column = parseInt(entry.slice(1), 10) || 0;
				}
			} else {
				let name = entry;
				let searchShow = true;
				let challengeShow = true;
				let tournamentShow = true;
				let partner = false;
				let bestOfDefault = false;
				let teraPreviewDefault = false;
				let itemClauseDefault = false;
				let team: 'preset' | null = null;
				let teambuilderLevel: number | null = null;
				let lastCommaIndex = name.lastIndexOf(',');
				let code = lastCommaIndex >= 0 ? parseInt(name.substr(lastCommaIndex + 1), 16) : NaN;
				if (!isNaN(code)) {
					name = name.substr(0, lastCommaIndex);
					if (code & 1) team = 'preset';
					if (!(code & 2)) searchShow = false;
					if (!(code & 4)) challengeShow = false;
					if (!(code & 8)) tournamentShow = false;
					if (code & 16) teambuilderLevel = 50;
					if (code & 32) partner = true;
					if (code & 64) bestOfDefault = true;
					if (code & 128) teraPreviewDefault = true;
					if (code & 256) itemClauseDefault = true;
				} else {
					// Backwards compatibility: late 0.9.0 -> 0.10.0
					if (name.substr(name.length - 2) === ',#') { // preset teams
						team = 'preset';
						name = name.substr(0, name.length - 2);
					}
					if (name.substr(name.length - 2) === ',,') { // search-only
						challengeShow = false;
						name = name.substr(0, name.length - 2);
					} else if (name.substr(name.length - 1) === ',') { // challenge-only
						searchShow = false;
						name = name.substr(0, name.length - 1);
					}
				}
				let id = toID(name);
				let isTeambuilderFormat = !team && !name.endsWith('Custom Game');
				let teambuilderFormat = '' as ID;
				let teambuilderFormatName = '';
				if (isTeambuilderFormat) {
					teambuilderFormatName = name;
					if (!id.startsWith('gen')) {
						teambuilderFormatName = '[Gen 6] ' + name;
					}
					let parenPos = teambuilderFormatName.indexOf('(');
					if (parenPos > 0 && name.endsWith(')')) {
						// variation of existing tier
						teambuilderFormatName = teambuilderFormatName.slice(0, parenPos).trim();
					}
					if (teambuilderFormatName !== name) {
						teambuilderFormat = toID(teambuilderFormatName);
						if (BattleFormats[teambuilderFormat]) {
							BattleFormats[teambuilderFormat].isTeambuilderFormat = true;
						} else {
							BattleFormats[teambuilderFormat] = {
								id: teambuilderFormat,
								name: teambuilderFormatName,
								team,
								section,
								column,
								rated: false,
								isTeambuilderFormat: true,
								effectType: 'Format',
							};
						}
						isTeambuilderFormat = false;
					}
				}
				if (BattleFormats[id]?.isTeambuilderFormat) {
					isTeambuilderFormat = true;
				}
				// make sure formats aren't out-of-order
				if (BattleFormats[id]) delete BattleFormats[id];
				BattleFormats[id] = {
					id,
					name,
					team,
					section,
					column,
					searchShow,
					challengeShow,
					tournamentShow,
					bestOfDefault,
					teraPreviewDefault,
					itemClauseDefault,
					rated: searchShow && id.substr(4, 7) !== 'unrated',
					teambuilderLevel,
					partner,
					teambuilderFormat,
					isTeambuilderFormat,
					effectType: 'Format',
				};
			}
		}

		// Match base formats to their variants, if they are unavailable in the server.
		let multivariantFormats: { [id: string]: 1 } = {};
		for (let id in BattleFormats) {
			let teambuilderFormat = BattleFormats[BattleFormats[id].teambuilderFormat!];
			if (!teambuilderFormat || multivariantFormats[teambuilderFormat.id]) continue;
			if (!teambuilderFormat.searchShow && !teambuilderFormat.challengeShow && !teambuilderFormat.tournamentShow) {
				// The base format is not available.
				if (teambuilderFormat.battleFormat) {
					multivariantFormats[teambuilderFormat.id] = 1;
					teambuilderFormat.battleFormat = '';
				} else {
					teambuilderFormat.battleFormat = id;
				}
			}
		}
		PS.teams.update('format');
	}
	handlePM(user1: string, user2: string, message?: string) {
		const userid1 = toID(user1);
		const userid2 = toID(user2);
		const pmTarget = PS.user.userid === userid1 ? user2 : user1;
		const pmTargetid = PS.user.userid === userid1 ? userid2 : userid1;
		let roomid = `dm-${pmTargetid}` as RoomID;
		if (pmTargetid === PS.user.userid) roomid = 'dm-' as RoomID;
		let room = PS.rooms[roomid] as ChatRoom | undefined;
		if (!room) {
			PS.addRoom({
				id: roomid,
				args: { pmTarget },
				autofocus: false,
			});
			room = PS.rooms[roomid] as ChatRoom;
		} else {
			room.updateTarget(pmTarget);
		}
		if (message) room.receiveLine([`c`, user1, message]);
		PS.update();
	}
	/**
	 * Client-to-server query. Handles `/crq` aka `/cmd`.
	 *
	 * Most queries are still handled hardcoded, so this is only for certain
	 * special queries that need a Promise.
	 */
	makeQuery(id: string, param?: string, excludeParamFromListener?: boolean) {
		let fullid = id;
		if (param && !excludeParamFromListener) fullid += ` ${toID(param)}`;
		return new Promise<any>(resolve => {
			if (!this.listeners[fullid]) {
				this.listeners[fullid] = [];
				PS.send(`/cmd ${id} ${param || ''}`);
			}
			this.listeners[fullid]!.push(resolve);
		});
	}
	handleQueryResponse(id: ID, response: any) {
		let fullid: string = id;
		switch (id) {
		case 'userdetails':
			let userid = response.userid;
			fullid += ` ${userid}`;
			let userdetails = this.userdetailsCache[userid];
			if (!userdetails) {
				this.userdetailsCache[userid] = response;
			} else {
				response.status ||= '';
				Object.assign(userdetails, response);
			}
			if (userid === PS.user.userid) {
				if (response.avatar !== undefined && PS.user.avatar !== `${response.avatar}`) {
					PS.user.avatar = `${response.avatar}`;
					PS.user.update(null);
				}
			}
			PS.rooms[`user-${userid}`]?.update(null);
			PS.rooms[`viewuser-${userid}`]?.update(null);
			PS.rooms[`users`]?.update(null);
			break;
		case 'rooms':
			if (response.pspl) {
				for (const roomInfo of response.pspl) roomInfo.spotlight = "Spotlight";
				response.chat = [...response.pspl, ...response.chat];
				response.pspl = null;
			}
			if (response.official) {
				for (const roomInfo of response.official) roomInfo.section = "Official";
				response.chat = [...response.official, ...response.chat];
				response.official = null;
			}
			this.roomsCache = response;
			this.update(null); // cherish-world: 홈 접속 보드 즉시 갱신
			const roomsRoom = PS.rooms[`rooms`] as RoomsRoom;
			if (roomsRoom) roomsRoom.update(null);
			break;
		case 'roomlist': {
			// cherish-rooms: 메인 섹션용 사본 (battles 패널과 별개로 항상 저장)
			const cherishTable = response.rooms || {};
			this.cherishBattles = Object.keys(cherishTable).map(id => ({ ...cherishTable[id], id }));
			this.update(null);
		}
			const battlesRoom = PS.rooms[`battles`] as BattlesRoom;
			if (battlesRoom) {
				const battleTable = response.rooms;
				const battles = [];
				for (const battleid in battleTable) {
					battleTable[battleid].id = battleid;
					battles.push(battleTable[battleid]);
				}
				battlesRoom.battles = battles;
				battlesRoom.update(null);
			}
			break;
		case 'laddertop':
			for (const [roomid, ladderRoom] of Object.entries(PS.rooms)) {
				if (roomid.startsWith('ladder-')) {
					(ladderRoom as LadderFormatRoom).update(response);
				}
			}
			break;
		case 'teamupload':
			if (PS.teams.uploading) {
				const team = PS.teams.uploading;
				team.teamid = response.teamid;
				team.uploaded = {
					teamid: response.teamid,
					notLoaded: false,
					private: response.private,
				};
				PS.rooms[`team-${team.key}`]?.update(null);
				PS.rooms.teambuilder?.update(null);
				PS.teams.uploading = null;
			}
			break;
		case 'teamupdate':
			for (const team of PS.teams.list) {
				if (team.teamid === response.teamid) {
					team.uploaded = {
						teamid: response.teamid,
						notLoaded: false,
						private: response.private,
					};
					PS.rooms[`team-${team.key}`]?.update(null);
					PS.rooms.teambuilder?.update(null);
					PS.teams.uploading = null;
					break;
				}
			}
			break;
		}
		for (const callback of this.listeners[fullid] || []) callback(response);
		delete this.listeners[fullid];
	}
}

class NewsPanel extends PSRoomPanel {
	static readonly id = 'news';
	static readonly routes = ['news'];
	static readonly title = 'News';
	static readonly location = 'mini-window';
	change = (ev: Event) => {
		const target = ev.currentTarget as HTMLInputElement;
		this.setClient(target.value as '0' | '1' | 'leave');
	};
	setClient(setting: '0' | '1' | 'leave') {
		if (setting === '1') {
			document.cookie = "preactalpha=1; expires=Thu, 1 Sep 2026 12:00:00 UTC; path=/";
		} else if (setting === '0') {
			document.cookie = "preactalpha=0; expires=Thu, 1 Sep 2026 12:00:00 UTC; path=/";
		} else {
			document.cookie = "preactalpha=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
		}
		if (setting === 'leave') {
			document.location.href = `/`;
		}
	}
	override componentDidMount() {
		if (!document.cookie.includes('preactalpha=')) this.setClient('1');
	}
	override render() {
		const cookieSet = !document.cookie.includes('preactalpha=0');
		return <PSPanelWrapper room={this.props.room} fullSize>
			<div class="construction">
				This is the client rewrite beta test.
				<form>
					<label class="checkbox">
						<input type="radio" name="preactalpha" value="1" onChange={this.change} checked={cookieSet} /> {}
						Use Rewrite always
					</label>
					<label class="checkbox">
						<input type="radio" name="preactalpha" value="0" onChange={this.change} checked={!cookieSet} /> {}
						Use Rewrite with URL
					</label>
					<label class="checkbox">
						<input type="radio" name="preactalpha" value="leave" onChange={this.change} /> {}
						Back to the old client
					</label>
				</form>
				Provide feedback in <a href="development" style="color:black">the Dev chatroom</a>.
			</div>
			<div class="readable-bg" dangerouslySetInnerHTML={{ __html: PS.newsHTML }}></div>
		</PSPanelWrapper>;
	}
}

/** cherish-world fork: 배틀홈과 도전과제·랭킹·내 포켓몬 탭이 공유하는 데이터·렌더 계층.
 *  렌더 코드를 한 벌만 두려고 베이스 클래스로 뺐다.
 *  (정본 cherishpro scripts/showdown-fork/patch-cherish-tabs.py) */
class CherishPanelBase<T extends PSRoom = PSRoom> extends PSRoomPanel<T> {
	cherishPokemon: any[] | null = null;
	cherishTeamIds: string[] = [];
	cherishLoadedFor = '';
	cherishRoomsTimer: any = null;
	cherishQuests: any[] | null = null;
	cherishRanking: any = null;
	cherishAchievements: any = null;

	// cherish-nick-offer: 이름 지어주기 제안·부탁 (0325)
	/** { pending: 내가 답할 것, mine: 내가 답을 기다리는 것, results: 결말 알림 } */
	cherishNickInbox: any = null;
	/** 내가 첫 부모인데 다른 집에 있는 아이들. */
	cherishNickSent: any[] | null = null;
	cherishNickSentOpen = false;
	/** 팝업에서 이미 넘긴 건 (id 또는 'r'+id). 새로고침하면 다시 뜬다 — 답할 일이 남아 있으니. */
	cherishNickSeen: any = {};
	cherishNickBusy = false;
	// cherish-auction:begin (patch-auction-panel.py)
	/** 펼쳐 둔 경매 id. null = 전부 접힘. */
	cherishAuctionOpen: number | null = null;
	/** 펼친 경매의 최신 상세 (GET /showdown/auction/:id). 목록 항목보다 새롭다. */
	cherishAuctionDetail: any = null;
	cherishAuctionBid = '';
	cherishAuctionBusy = false;
	cherishAuctionMsg = '';
	cherishAuctionMsgOk = false;
	cherishAuctionPoll: any = null;
	cherishAuctionTick: any = null;
	cherishAuctionNow(): number { return Date.now() + this.cherishAuctionSkew; }
	cherishAuctionLeft(a: any): number {
		const end = a?.scheduled_end_at ? new Date(a.scheduled_end_at).getTime() : 0;
		return Math.max(0, end - this.cherishAuctionNow());
	}
	cherishAuctionLeftText(a: any): string {
		const ms = this.cherishAuctionLeft(a);
		if (ms <= 0) return '신청 마감';
		const s = Math.floor(ms / 1000);
		const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), mi = Math.floor((s % 3600) / 60), se = s % 60;
		if (d > 0) return `${d}일 ${h}시간 남음`;
		if (h > 0) return `${h}시간 ${mi}분 남음`;
		if (mi > 0) return `${mi}분 ${se}초 남음`;
		return `${se}초 남음`;
	}
	cherishAuctionName(e: any): string {
		return e?.display_name || e?.name || '누군가';
	}
	cherishAuctionToggle(id: number) {
		if (this.cherishAuctionOpen === id) { this.cherishAuctionClose(); return; }
		this.cherishAuctionOpen = id;
		this.cherishAuctionDetail = null;
		this.cherishAuctionBid = '';
		this.cherishAuctionMsg = '';
		this.cherishAuctionFetchDetail();
		if (!this.cherishAuctionPoll) this.cherishAuctionPoll = setInterval(() => {
			if (typeof document !== 'undefined' && document.hidden) return;
			this.cherishAuctionFetchDetail();
		}, 10000);
		if (!this.cherishAuctionTick) this.cherishAuctionTick = setInterval(() => this.forceUpdate(), 1000);
		this.forceUpdate();
	}
	cherishAuctionClose() {
		this.cherishAuctionOpen = null;
		this.cherishAuctionDetail = null;
		if (this.cherishAuctionPoll) { clearInterval(this.cherishAuctionPoll); this.cherishAuctionPoll = null; }
		if (this.cherishAuctionTick) { clearInterval(this.cherishAuctionTick); this.cherishAuctionTick = null; }
		this.forceUpdate();
	}
	cherishAuctionFetchDetail() {
		const id = this.cherishAuctionOpen;
		if (id === null) return;
		fetch(`https://cherish-world.com/api/showdown/auction/${id}`, { credentials: 'include' })
			.then(r => r.json())
			.then(json => {
				if (this.cherishAuctionOpen !== id) return;
				if (json?.ok) {
					this.cherishAuctionDetail = json.data;
					if (json.data.now) this.cherishAuctionSkew = new Date(json.data.now).getTime() - Date.now();
					// 목록 쪽 항목도 같이 갱신 — 접었을 때 낡은 현재가가 보이지 않게
					const list = this.cherishAuction || [];
					for (let i = 0; i < list.length; i++) if (list[i].id === id) list[i] = json.data;
				}
				this.forceUpdate();
			})
			.catch(() => {});
	}
	cherishAuctionMinValid(a: any): number {
		const inc = Number(a?.min_increment || 1);
		const top = a?.highest_bid;
		if (top !== null && top !== undefined) return Number(top) + inc;
		return Number(a?.starting_price || 0);
	}
	cherishAuctionSetBid(v: string) {
		this.cherishAuctionBid = v.replace(/[^0-9]/g, '');
		this.forceUpdate();
	}
	cherishAuctionPlaceBid() {
		const id = this.cherishAuctionOpen;
		const amount = parseInt(this.cherishAuctionBid, 10);
		if (id === null || this.cherishAuctionBusy) return;
		if (!Number.isFinite(amount) || amount <= 0) { this.cherishAuctionMsg = '책임비로 낼 체리를 적어 주세요.'; this.cherishAuctionMsgOk = false; this.forceUpdate(); return; }
		this.cherishAuctionBusy = true;
		this.cherishAuctionMsg = '';
		this.forceUpdate();
		fetch(`https://cherish-world.com/api/showdown/auction/${id}/bid`, {
			method: 'POST', credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ bidAmount: amount }),
		})
			.then(r => r.json())
			.then(json => {
				this.cherishAuctionBusy = false;
				if (json?.ok) {
					const d = json.data || {};
					this.cherishAuctionMsgOk = true;
					this.cherishAuctionMsg = `책임비 ${Number(d.bid_amount || amount).toLocaleString('ko-KR')}체리를 맡겼어요.` +
						(d.deadline_extended ? ' 신청 마감이 연장됐어요.' : '');
					this.cherishAuctionBid = '';
					this.cherishAuctionFetchDetail();
				} else {
					this.cherishAuctionMsgOk = false;
					this.cherishAuctionMsg = json?.message || '책임비를 맡기지 못했어요. 잠시 후 다시 해 주세요.';
					// 최소가·쿨다운 같은 상세는 서버 문구에 이미 들어 있다. 현재가가 바뀌었을 수 있으니 다시 받는다.
					this.cherishAuctionFetchDetail();
				}
				this.forceUpdate();
			})
			.catch(() => { this.cherishAuctionBusy = false; this.cherishAuctionMsgOk = false; this.cherishAuctionMsg = '연결이 불안정해요. 잠시 후 다시 해 주세요.'; this.forceUpdate(); });
	}
	renderCherishAuctionBadges(a: any) {
		const badges: string[] = (a?.pokemon?.badges || []);
		if (!badges.length) return null;
		return <span class="cw-auc-badges">{badges.map(b => <span class="cw-auc-badge">{b}</span>)}</span>;
	}
	renderCherishAuctionDetail(a: any) {
		const active = a.status === 'active' && this.cherishAuctionLeft(a) > 0;
		const inc = Number(a.min_increment || 1);
		const minValid = this.cherishAuctionMinValid(a);
		const myLocked = Number(a.my_locked || 0);
		const myTop = a.highest_bidder_id && a.my_highest_bid && Number(a.my_highest_bid) === Number(a.highest_bid);
		const bidders: any[] = a.top_bidders || [];
		const winners: any[] = a.winners || [];
		const pk = a.pokemon;
		return <div class="cw-auc-detail">
			{a.description ? <p class="cw-auc-desc">{a.description}</p> : null}
			{pk ? <p class="cw-auc-spec">
				<b>{pk.koName}</b> <small>{(pk.typesKo || []).join('·')}</small>
				{pk.hiddenAbilityName ? <small>, 특성 {pk.hiddenAbilityName}</small> : null}
			</p> : null}
			<div class="cw-auc-prices">
				<div><small>{a.status === 'ended' ? '확정 책임비' : '현재 책임비'}</small><b>{a.status === 'ended' && a.uniform_price ? Number(a.uniform_price).toLocaleString('ko-KR') : a.highest_bid !== null && a.highest_bid !== undefined ? Number(a.highest_bid).toLocaleString('ko-KR') : '-'}</b></div>
				<div><small>최소 책임비</small><b>{Number(a.starting_price).toLocaleString('ko-KR')}</b></div>
				<div><small>지금 낼 책임비</small><b>{active ? minValid.toLocaleString('ko-KR') : '-'}</b></div>
				<div><small>맡겨 둔 책임비</small><b>{myLocked.toLocaleString('ko-KR')}</b></div>
			</div>
			{active ? <div class="cw-auc-time">{this.cherishAuctionLeftText(a)} <small>· 마감 {inc > 0 ? Math.round(Number(a.extension_seconds || 1800) / 60) : 30}분 전에 책임비가 들어오면 그만큼 연장돼요</small></div>
				: <div class="cw-auc-time is-end">{a.status === 'ended' ? '신청이 끝났어요' : '가족을 정하는 중이에요'}</div>}
			{active && PS.user.named ? <div class="cw-auc-bidbox">
				{myTop ? <p class="cw-auc-mine">지금 가장 큰 책임비를 맡긴 사람이 바로 당신이에요.</p> : null}
				<div class="cw-auc-bidrow">
					<input class="textbox cw-auc-input" type="text" inputMode="numeric" placeholder={`${minValid.toLocaleString('ko-KR')} 이상`}
						value={this.cherishAuctionBid} onInput={(e: any) => this.cherishAuctionSetBid(e.currentTarget.value)}
						onKeyDown={(e: any) => { if (e.key === 'Enter') this.cherishAuctionPlaceBid(); }} disabled={this.cherishAuctionBusy} />
					<button class="button cw-auc-quick" onClick={() => this.cherishAuctionSetBid(String(minValid))} disabled={this.cherishAuctionBusy}>최소</button>
					<button class="button cw-auc-quick" onClick={() => this.cherishAuctionSetBid(String(Math.max(minValid, (parseInt(this.cherishAuctionBid, 10) || minValid) + inc)))} disabled={this.cherishAuctionBusy}>+{inc.toLocaleString('ko-KR')}</button>
					<button class="button cw-auc-go" onClick={() => this.cherishAuctionPlaceBid()} disabled={this.cherishAuctionBusy || !this.cherishAuctionBid}>{this.cherishAuctionBusy ? '맡기는 중…' : '책임비 제출'}</button>
				</div>
				<p class="cw-auc-note">책임비는 바로 맡겨져요. 더 큰 책임비가 들어오면 자동으로 돌려받아요. 내 체리 {a.my_cherry !== undefined ? Number(a.my_cherry).toLocaleString('ko-KR') : '-'}개.</p>
				{this.cherishAuctionMsg ? <p class={`cw-auc-msg${this.cherishAuctionMsgOk ? ' is-ok' : ' is-err'}`}>{this.cherishAuctionMsg}</p> : null}
			</div> : null}
			{a.status === 'ended' && a.show_winner ? <div class="cw-auc-list">
				<h4>새 가족</h4>
				{winners.length ? winners.map(w => <div class="cw-auc-row"><span class="cw-auc-rank">🏆</span><span class="cw-auc-name">{this.cherishAuctionName(w)}</span><span class="cw-auc-amt">{Number(w.charged || 0).toLocaleString('ko-KR')}체리</span></div>)
					: <p class="cw-auc-empty">아직 보호자를 만나지 못했어요.</p>}
			</div> : null}
			{active && a.show_bidders ? <div class="cw-auc-list">
				<h4>책임비를 맡긴 사람들 <small>{Number(a.participant_count || 0)}명</small></h4>
				{bidders.length ? bidders.map(b => <div class="cw-auc-row"><span class="cw-auc-rank">{b.rank}</span><span class="cw-auc-name">{this.cherishAuctionName(b)}</span><span class="cw-auc-amt">{b.bid_amount !== null && b.bid_amount !== undefined ? `${Number(b.bid_amount).toLocaleString('ko-KR')}체리` : ''}</span></div>)
					: <p class="cw-auc-empty">아직 아무도 책임비를 맡기지 않았어요. 첫 번째 가족 후보가 돼 보세요!</p>}
			</div> : null}
			<p class="cw-auc-foot">입양한 아이는 새 가족과 쭉 함께예요. 다른 집으로 보낼 수 없어요.</p>
		</div>;
	}
	renderCherishAuction() {
		if (!PS.user.named) return null;
		const list = this.cherishAuction;
		if (!list || !list.length) return null;
		const openId = this.cherishAuctionOpen;
		return <div class="menugroup cw-auction" style="max-width:none">
			<div class="cw-auc-head">
				<div><span class="cw-eyebrow">Special Home</span><h3 class="cw-auc-title">특별한 보금자리</h3></div>
				<span class="cw-auc-sub">특별한 아이가 보호자를 기다려요</span>
			</div>
			{list.map((row: any) => {
				const isOpen = openId === row.id;
				const a = isOpen && this.cherishAuctionDetail ? this.cherishAuctionDetail : row;
				const active = a.status === 'active' && this.cherishAuctionLeft(a) > 0;
				const img = a.pokemon?.imageUrl || a.image_url;
				const line = active
					? `${a.highest_bid !== null && a.highest_bid !== undefined ? '현재 책임비 ' + Number(a.highest_bid).toLocaleString('ko-KR') : '최소 책임비 ' + Number(a.starting_price).toLocaleString('ko-KR')}체리, ${Number(a.participant_count || 0)}명, ${this.cherishAuctionLeftText(a)}`
					: a.status === 'ended'
						? (a.winners && a.winners.length ? `${this.cherishAuctionName(a.winners[0])}의 가족이 됐어요, 책임비 ${Number(a.uniform_price || 0).toLocaleString('ko-KR')}체리` : '아직 보호자를 만나지 못했어요')
						: '가족을 정하는 중';
				return <div class={`cw-auc-item${isOpen ? ' is-open' : ''}${active ? '' : ' is-end'}`}>
					<button class="cw-auc-summary" onClick={() => this.cherishAuctionToggle(row.id)} aria-expanded={isOpen}>
						<span class="cw-auc-art">{img ? <img src={String(img).startsWith('http') ? img : 'https://cherish-world.com' + img} alt="" onError={(e: any) => { e.currentTarget.style.visibility = 'hidden'; }} /> : null}</span>
						<span class="cw-auc-body">
							<span class="cw-auc-name-line"><b>{a.title}</b>{this.renderCherishAuctionBadges(a)}</span>
							<span class="cw-auc-line">{line}{Number(a.my_locked || 0) > 0 ? `, 내 책임비 ${Number(a.my_highest_bid || a.my_locked).toLocaleString('ko-KR')}` : ''}</span>
						</span>
						<span class="cw-auc-caret">{isOpen ? '접기' : active ? '책임비 제출' : '결과'}</span>
					</button>
					{isOpen ? this.renderCherishAuctionDetail(a) : null}
				</div>;
			})}
		</div>;
	}
	// cherish-auction:end
	// cherish-auction-state:begin (patch-auction-panel.py)
	/** GET /showdown/auction 응답 배열 (진행 중 + 최근 끝난 것). null = 아직. */
	cherishAuction: any[] | null = null;
	/** 서버 시각 - 내 시각 (ms). 남은 시간 계산에 더한다. */
	cherishAuctionSkew = 0;
	// cherish-auction-state:end
	// cherish-itemauction-state:begin (patch-item-auction.py)
	/** GET /showdown/item-auction 응답 배열 (진행 중 + 최근 끝난 것). null = 아직. */
	cherishItemAuc: any[] | null = null;
	/** 서버 시각 - 내 시각 (ms). 보금자리 카드와 따로 둔다 — 그 패치를 다시 돌려도 안 지워지게. */
	cherishItemAucSkew = 0;
	// cherish-itemauction-state:end
	/** cherish tabs: 패널마다 필요한 엔드포인트만 부른다 (patch-cherish-tabs.py) */
	cherishNeeds: string[] = ['me', 'quests', 'ranking', 'achievements', 'school', 'tower', 'maze', 'blossom'];
	/** 마지막 loadCherishData 시각. 패널이 다시 보이게 될 때 이보다 오래됐으면 다시 받는다. */
	cherishLoadedAt = 0;
	static readonly CHERISH_STALE_MS = 10000;
	ensureCherishData() {
		// 로그인 전엔 부르지 않는다 — 화면이 named 로 막혀 있어 받아도 못 보여주고,
		// 로그인되면 userid 가 바뀌어 어차피 다시 받는다 (같은 데이터 2회 호출 방지).
		if (!PS.user.named) return;
		if (this.cherishLoadedFor !== PS.user.userid) this.loadCherishData();
	}
	/** 안 보이던 패널이 다시 보이게 됐을 때. 부화·퀘스트 진행은 디스코드에서 일어나므로
	 *  웹은 돌아올 때 다시 봐야 안다. 너무 잦은 왕복은 STALE_MS 로 거른다. */
	refreshCherishIfStale() {
		if (!PS.user.named) return;
		if (Date.now() - this.cherishLoadedAt < CherishPanelBase.CHERISH_STALE_MS) return;
		this.loadCherishData();
	}
	cherishOnVisibility = () => {
		if (typeof document !== 'undefined' && document.hidden) return;
		if (!PS.isVisible(this.props.room)) return;
		this.refreshCherishIfStale();
	};
	override componentDidMount() {
		super.componentDidMount();
		// 자동 로그인(upkeep)이 첫 렌더 뒤에 끝나면 아무도 다시 그려주지 않았다 — 새로고침 직후
		// 「로그인하면 볼 수 있어요」가 탭을 옮겼다 올 때까지 남던 원인.
		this.subscribeTo(PS.user, () => this.forceUpdate());
		if (typeof document !== 'undefined') document.addEventListener('visibilitychange', this.cherishOnVisibility);
	}
	override componentWillUnmount() {
		if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', this.cherishOnVisibility);
		super.componentWillUnmount();
	}
	override shouldComponentUpdate() {
		const wasVisible = this.wasVisible;
		const result = super.shouldComponentUpdate();
		if (!wasVisible && this.wasVisible) this.refreshCherishIfStale();
		return result;
	}
	loadCherishData() {
		this.cherishLoadedFor = PS.user.userid;
		this.cherishLoadedAt = Date.now();
		if (this.cherishNeeds.includes('me')) fetch('https://cherish-world.com/api/showdown/me', { credentials: 'include' })
			.then(r => r.json())
			.then(json => {
				const mons = json?.ok ? json.data.pokemon || [] : [];
				this.cherishPokemon = mons;
				this.cherishEggs = json?.ok ? json.data.eggs || [] : []; // cherish-egg
				this.cherishBattleLearn = json?.ok ? json.data.battleLearn || null : null; // cherish-mon-detail: 뉴비 배틀 기술 학습
				this.cherishHotTime = json?.ok ? json.data.hotTime || null : null; // cherish-mon-detail: 핫타임
				this.cherishHatchGate = json?.ok ? json.data.hatchGate || null : null; // cherish-egg
				this.cherishTeamIds = mons
					.filter((p: any) => p.teamSlot)
					.sort((a: any, b: any) => a.teamSlot - b.teamSlot)
					.map((p: any) => p.id);
				this.forceUpdate();
			})
			.catch(() => { this.cherishPokemon = []; this.forceUpdate(); });
		if (this.cherishNeeds.includes('shop')) fetch('https://cherish-world.com/api/showdown/shop', { credentials: 'include' })
			.then(r => r.json())
			.then(json => { this.cherishShop = json?.ok ? json.data : null; this.forceUpdate(); })
			.catch(() => {});
		// cherish-nick-offer: 내 포켓몬 탭만 부른다 (팝업 + 보낸 아이 목록)
		if (this.cherishNeeds.includes('nickoffers')) {
			fetch('https://cherish-world.com/api/showdown/nickname/inbox', { credentials: 'include' })
				.then(r => r.json())
				.then(json => { this.cherishNickInbox = json?.ok ? json.data : null; this.forceUpdate(); })
				.catch(() => {});
			fetch('https://cherish-world.com/api/showdown/nickname/sent', { credentials: 'include' })
				.then(r => r.json())
				.then(json => { this.cherishNickSent = json?.ok ? (json.data.mons || []) : []; this.forceUpdate(); })
				.catch(() => { this.cherishNickSent = []; });
		}
		if (this.cherishNeeds.includes('avoids')) fetch('https://cherish-world.com/api/showdown/avoids', { credentials: 'include' })
			.then(r => r.json())
			.then(json => { this.cherishAvoids = json?.ok ? json.data.avoids || [] : []; this.forceUpdate(); })
			.catch(() => { this.cherishAvoids = []; });
		// cherish-school: 학교 카드 데이터 (patch-school-card.py)
		if (this.cherishNeeds.includes('school')) fetch('https://cherish-world.com/api/showdown/school', { credentials: 'include' })
			.then(r => r.json())
			.then(json => { this.cherishSchool = json?.ok ? json.data : null; this.forceUpdate(); })
			.catch(() => {});
		// cherish-auction-fetch:begin (patch-auction-panel.py)
		if (this.cherishNeeds.includes('auction')) fetch('https://cherish-world.com/api/showdown/auction', { credentials: 'include' })
			.then(r => r.json())
			.then(json => {
				this.cherishAuction = json?.ok ? (json.data.auctions || []) : [];
				if (json?.ok && json.data.now) this.cherishAuctionSkew = new Date(json.data.now).getTime() - Date.now();
				this.forceUpdate();
			})
			.catch(() => {});
		// cherish-auction-fetch:end
		// cherish-itemauction-fetch:begin (patch-item-auction.py)
		if (this.cherishNeeds.includes('itemAuction')) fetch('https://cherish-world.com/api/showdown/item-auction', { credentials: 'include' })
			.then(r => r.json())
			.then(json => {
				this.cherishItemAuc = json?.ok ? (json.data.auctions || []) : [];
				if (json?.ok && json.data.now) this.cherishItemAucSkew = new Date(json.data.now).getTime() - Date.now();
				this.forceUpdate();
			})
			.catch(() => {});
		// cherish-itemauction-fetch:end
		// cherish-home-cards:begin (patch-home-card-fetch.py)
		// 체리쉬타워·구름 미궁 홈 카드 데이터. 학교와 같은 자리에서 같은 주기로 받는다.
		// ⚠️ 예전엔 fetchCherishHome 안에 있었는데, 학교 fetch 가 이 로더로 옮겨질 때 같이 지워져
		//    두 카드가 통째로 안 뜬 적이 있다 (2026-09-09). 마커로 묶어 다시 안 새게 한다.
		if (this.cherishNeeds.includes('tower')) fetch('https://cherish-world.com/api/showdown/tower', { credentials: 'include' })
			.then(r => r.json())
			.then(json => { this.cherishTower = json?.ok ? json.data : null; this.forceUpdate(); })
			.catch(() => {});
		if (this.cherishNeeds.includes('maze')) fetch('https://cherish-world.com/api/showdown/maze', { credentials: 'include' })
			.then(r => r.json())
			.then(json => { this.cherishMaze = json?.ok ? json.data : null; this.forceUpdate(); })
			.catch(() => {});
		// cherish-home-cards:end
		// cherish-blossom-fetch:begin (patch-class-blossom.py)
		// 우리 반 꽃밭 카드 데이터. 학기 스위치가 꺼져 있으면 { open: false } 만 온다.
		if (this.cherishNeeds.includes('blossom')) fetch('https://cherish-world.com/api/showdown/daycare/blossom', { credentials: 'include' })
			.then(r => r.json())
			.then(json => { this.cherishBlossom = json?.ok ? json.data : null; this.forceUpdate(); })
			.catch(() => {});
		// cherish-blossom-fetch:end
		// cherish-school: 학교 카드 데이터 (patch-school-card.py)
		if (this.cherishNeeds.includes('school')) fetch('https://cherish-world.com/api/showdown/school', { credentials: 'include' })
			.then(r => r.json())
			.then(json => { this.cherishSchool = json?.ok ? json.data : null; this.forceUpdate(); })
			.catch(() => {});
		if (this.cherishNeeds.includes('quests')) fetch('https://cherish-world.com/api/showdown/quests', { credentials: 'include' })
			.then(r => r.json())
			.then(json => {
				this.cherishQuests = json?.ok ? json.data.quests || [] : [];
				this.forceUpdate();
			})
			.catch(() => {});
		if (this.cherishNeeds.includes('ranking')) this.loadCherishRanking(); // cherish-ffa: 모드별 URL
		if (this.cherishNeeds.includes('achievements')) fetch('https://cherish-world.com/api/showdown/achievements', { credentials: 'include' })
			.then(r => r.json())
			.then(json => {
				this.cherishAchievements = json?.ok ? json.data : null;
				this.forceUpdate();
			})
			.catch(() => {});
	}
	// cherish-rename-cost2: 첫 명명은 무료, 이후 500 -> 1,000 -> 2,000 ... 두 배씩.
	// 값은 서버가 정하고(p.renameCost) 클라는 누르기 전에 알려주기만 한다.
	editCherishNickname = (p: any) => {
		const base = p.koName || p.speciesName;
		const cost = p.renameCost || 0;
		const won = cost.toLocaleString('ko-KR');
		if (cost > 0 && !window.confirm(`이름을 바꾸면 체리 ${won}개를 써요. 계속할까요?\n바꿀 때마다 두 배로 올라가요.`)) return;
		const label = cost > 0
			? `${base}의 새 이름 (최대 12자, 비우면 원래 이름, 체리 ${won})`
			: `${base}의 이름 (최대 12자, 비우면 원래 이름, 처음은 무료)`;
		const input = window.prompt(label, p.nickname || '');
		if (input === null) return;
		fetch('https://cherish-world.com/api/showdown/nickname', {
			method: 'POST', credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ id: p.id, nickname: input.trim() || null }),
		}).then(r => r.json()).then(json => {
			if (!json?.ok) {
				PS.alert(json?.message || '별명을 저장하지 못했어요.');
				return;
			}
			p.nickname = json.data.nickname;
			// 다음 값을 바로 반영해야 버튼 라벨이 옛 가격을 계속 보여주지 않는다
			if (typeof json.data.nextCost === 'number') p.renameCost = json.data.nextCost;
			// 성공 안내 (2026-09-10) — 그동안 이름만 조용히 바뀌어 체리를 쓴 티가 안 났다.
			// 토스트 정의는 patch-item-tab.py 소유라 typeof 가드로 부른다.
			const done = p.nickname ? `이름을 「${p.nickname}」 (으)로 바꿨어요.` : '원래 이름으로 되돌렸어요.';
			const paid = cost > 0 ? ` (체리 ${won} 썼어요)` : '';
			if (typeof (this as any).cherishToastNote === 'function') (this as any).cherishToastNote(done + paid, false);
			this.forceUpdate();
		}).catch(() => PS.alert('별명을 저장하지 못했어요.'));
	};
	cherishSelectedMonId: string | null = null; // cherish-mon-detail: 상세 패널이 열린 개체
	// cherish-items:state (patch-item-tab.py)
	cherishItemShop: any = null;
	cherishItemBag: any = null;
	cherishItemAdvice: any = null;
	cherishItemSel: string | null = null;
	cherishItemTier: string | null = null;
	cherishItemBusy = false;
	cherishItemNotice: string | null = null;
	cherishItemLoadedFor: string | null = null;
	cherishItemInv: any = null;
	/**
	 * 기본은 **베리 칸** (2026-09-10 사용자 결정) — 체리 도구는 등급·전용이 붙어 고르는 화면이고,
	 * 베리 알은 「모았나」만 보면 되는 화면이라 상점을 열었을 때 볼 것이 먼저 나오는 쪽이 낫다.
	 * 한쪽만 열려 있으면 아래 renderCherishShopTab 이 그쪽으로 붙인다.
	 */
	cherishShopSeg: 'cherry' | 'dust' = 'dust';
	/** 체리 상점에서 펼쳐 본 도구 id (null = 목록만) — 목록은 이름·아이콘만 두고 상세는 아래 한 칸에서 (2026-09-10). */
	cherishShopPick: string | null = null;
	/** 인벤토리 탭에서 펼쳐 본 도구 — 'inv:<id>' | 'bag:<id>' | null (2026-11-01). 설명이 title 툴팁뿐이라 모바일에서 못 봤다. */
	cherishInvPick: string | null = null;
	// cherish-items:state:end
	// cherish-egg:state (patch-egg-tab.py)
	cherishEggs: any[] | null = null;
	cherishHatchGate: any = null;
	cherishShop: any = null;
	// 하위탭 키 전부 — 남의 패치 탭(items·shop = patch-item-tab.py, avoid = patch-avoid-tab.py)도 여기서 든다 (2026-10-17, 09-19 규약)
	cherishMonTab: 'mon' | 'egg' | 'items' | 'shop' | 'avoid' = 'mon';
	cherishEggBusy = false;
	cherishEggNotice: string | null = null;
	// 합성 (0397) — 고른 재료 알 id. 모드를 끄면 비운다.
	cherishFuseMode = false;
	cherishFuseSel: string[] = [];
	// cherish-egg:state:end
	cherishSortMode: string = (() => { try { return localStorage.getItem('cw-mon-sort') || 'got'; } catch { return 'got'; } })(); // cherish-mon-detail: 목록 정렬
	cherishEpithetOpen: string | null = null; // cherish-mon-detail: 칭호 목록이 펼쳐진 개체
	cherishEpithetFor: string | null = null; // 지금 받아둔 목록의 주인 (다른 아이를 열면 버린다)
	cherishEpithetList: any[] | null = null; // null = 불러오는 중
	cherishEpithetBusy = false; // 장착 요청 중 (연타 방지)
	cherishSpecOnlyIds: string[] | null = null; // cherish-mon-detail: 「특기 정할 아이만」 필터 (켤 때의 스냅샷)
	cherishSpecBusy = false; // 특기 저장 중 (연타 방지)
	cherishBattleLearn: any = null; // cherish-mon-detail: 뉴비 배틀 기술 학습 {every} (/showdown/me battleLearn, 뉴비가 아니면 null)
	cherishSeenMoves: any = null; // cherish-mon-detail: 본 기술 {개체 id: 기술[]} (localStorage cw-moves-seen-v1)
	cherishFreshShow: any = null; // cherish-mon-detail: 상세를 열 때 잡은 새 기술 {id, moves} (열자마자 「봤음」이 되어도 그 회차엔 보인다)
	cherishHotTime: any = null; // cherish-mon-detail: 핫타임 {active, startAt, endAt, learnAt} (/showdown/me hotTime)
	cherishBattleLearn: any = null; // cherish-mon-detail: 뉴비 배틀 기술 학습 {every, daycare} (/showdown/me battleLearn, 뉴비가 아니면 null)
	cherishSeenMoves: any = null; // cherish-mon-detail: 본 기술 {개체 id: 기술[]} (localStorage cw-moves-seen-v1)
	cherishFreshShow: any = null; // cherish-mon-detail: 상세를 열 때 잡은 새 기술 {id, moves} (열자마자 「봤음」이 되어도 그 회차엔 보인다)
	cherishHotTime: any = null; // cherish-mon-detail: 핫타임 {active, startAt, endAt, learnAt} (/showdown/me hotTime)
	// cherish-moves:begin (patch-daycare-moves.py)
	/** 초안이 붙어 있는 개체. 상세 패널을 연 아이 하나뿐이다. */
	cherishMoveDraftId: string | null = null;
	cherishMoveDraft: string[] = [];
	/** 목록 정렬 스냅샷 — 패널을 열 때 한 번만 잡는다 (클릭마다 다시 정렬하면 줄이 튄다). */
	cherishMoveOrder: string[] = [];
	cherishMoveSaveTimer: any = null;
	cherishMoveStatus = '';
	cherishMoveNote = '';
	/**
	 * 초안 세대 (2026-09-23) — 장착을 건드릴 때마다 1 오른다. 저장 응답은 **자기가 떠날 때의
	 * 세대**와 지금이 같을 때만 초안을 서버 값으로 맞춘다. 다르면 손대지 않고 이어 보낸다.
	 * 예전엔 응답이 무조건 덮어써서, 저장이 날아가 있는 동안 누른 탭이 조용히 되감기고
	 * 뒤따르던 자동 저장은 「바뀐 게 없다」로 묻혔다 (파일 머리말 2026-09-23 항목).
	 */
	cherishMoveGen = 0;
	/** 마지막으로 띄운 저장이 떠날 때의 세대. 늦게 온 옛 응답을 가려내는 표다. */
	cherishMoveSentGen = -1;
	/** 저장 하나가 날아가 있는 중. 겹쳐 보내면 순서가 뒤집혀 옛 초안이 DB 에 굳는다. */
	cherishMoveSending = false;
	/**
	 * 접어둔 기술 (2026-09-10) — 해금이 20개를 넘어가면 「지금 쓸 기술」이 목록에 묻힌다.
	 * **화면 개념일 뿐** 배틀·어린이집 자동 장착과 무관. 장착 중인 기술은 접을 수 없다
	 * (목록에서 사라지면 해제할 길이 없어진다).
	 */
	cherishHiddenDraft: string[] = [];
	/**
	 * 두 칸(보이는 목록 / 「숨긴 기술」)을 가르는 **스냅샷**. 접는 즉시 여기 넣지 않는다.
	 *
	 * 즉시 내리면 아래 줄이 커서 밑으로 올라와 연달아 접을 때 엉뚱한 기술을 접는다.
	 * 그렇다고 패널을 닫을 때까지 안 내리면 **처음 쓰는 사람은 설명만 사라진 걸로 보여**
	 * 이 버튼이 왜 있는지 모른다 (2026-09-11 사용자 지적). 그래서 둘 다 피한다:
	 * 접은 줄은 그 자리에 축약으로 남았다가, **손을 멈추고 1.5초 뒤** 한꺼번에 아래 칸으로
	 * 내려간다(cherishHiddenSettleTimer). 연타 중에는 줄이 안 움직이고, 손을 떼면
	 * 목록이 짧아지는 것과 「숨긴 기술 N개」가 느는 것이 눈앞에서 일어난다.
	 */
	cherishHiddenAtOpen: string[] = [];
	cherishHiddenOpen = false;
	cherishHiddenSaveTimer: any = null;
	cherishHiddenSettleTimer: any = null;
	/**
	 * 기술 세트 프리셋 (0380, 2026-09-16) — 「담기」를 누른 상태. 이때 칸을 누르면 불러오기가
	 * 아니라 **지금 기술을 그 칸에 담는다**. 칸마다 버튼을 셋씩(불러오기·덮어쓰기·비우기) 두면
	 * 이 좁은 패널에서 기술 목록이 밀려난다 — 모드 하나로 칸 버튼의 뜻만 바꾼다.
	 */
	cherishPresetMode = false;
	/** 빨간 안내 (막힌 이유). 초록 문구는 cherishPresetStatus 로 나눈다. */
	cherishPresetNote = '';
	cherishPresetStatus = '';
	cherishPresetBusy = false;
	/** 장착 먼저 → ★ 추천 → 위력 내림차순. */
	cherishMoveSorted(p: any): string[] {
		const equipped: string[] = p?.moves || [];
		const curated: string[] = p?.curatedMoves || [];
		return [...(p?.unlockedMoves || [])].sort((a: string, b: string) => {
			const ea = equipped.includes(a) ? 0 : 1, eb = equipped.includes(b) ? 0 : 1;
			if (ea !== eb) return ea - eb;
			const ca = curated.includes(a) ? 0 : 1, cb = curated.includes(b) ? 0 : 1;
			if (ca !== cb) return ca - cb;
			return (Dex.moves.get(b).basePower || 0) - (Dex.moves.get(a).basePower || 0);
		});
	}
	/** 상세 패널을 열 때 — 초안과 정렬 스냅샷을 잡는다. 부르는 쪽이 먼저 flush 할 것. */
	loadCherishMoveDraft = (p: any) => {
		this.cherishMoveDraftId = p?.id ?? null;
		this.cherishMoveDraft = [...(p?.moves || [])];
		// 다른 아이로 옮겼다 — 아직 날아가 있는 저장의 응답이 이 초안을 건드리면 안 된다.
		this.cherishMoveGen += 1;
		this.hookCherishMoveLeave();
		this.cherishMoveOrder = this.cherishMoveSorted(p);
		const equipped: string[] = p?.moves || [];
		// 장착 중인 기술은 숨김에서 뺀다 — 어린이집 자동 장착이 접어둔 기술을 끼웠을 수 있다.
		this.cherishHiddenDraft = [...(p?.hiddenMoves || [])].filter((m: string) => !equipped.includes(m));
		this.cherishHiddenAtOpen = [...this.cherishHiddenDraft];
		this.cherishHiddenOpen = false;
		if (this.cherishHiddenSettleTimer) clearTimeout(this.cherishHiddenSettleTimer);
		this.cherishHiddenSettleTimer = null;
		this.cherishMoveStatus = '';
		this.cherishMoveNote = '';
		this.cherishPresetMode = false;
		this.cherishPresetNote = '';
		this.cherishPresetStatus = '';
	};
	/** 보낼 수 없는 중간 상태의 이유 (없으면 빈 문자열) — 서버 검증과 같은 규칙. */
	cherishMoveInvalid(): string {
		const d = this.cherishMoveDraft;
		if (!d.length) return '기술을 1개 이상 장착해야 해요.';
		// 공격기 1개 필수는 2026-10-18 폐지 — 변화기만의 세트(더시마사리 등)도 저장된다.
		return '';
	}
	toggleCherishMove = (name: string) => {
		const i = this.cherishMoveDraft.indexOf(name);
		if (i >= 0) {
			this.cherishMoveDraft.splice(i, 1);
		} else if (this.cherishMoveDraft.length >= 4) {
			this.cherishMoveNote = '기술은 4개까지예요. 하나를 먼저 빼주세요.';
			this.forceUpdate();
			return;
		} else {
			this.cherishMoveDraft.push(name);
		}
		this.cherishMoveGen += 1; // 날아가 있는 저장의 응답이 이 탭을 되감지 못하게
		this.queueCherishMoveSave();
		this.forceUpdate();
	};
	/** 자동 저장 예약 — 마지막 클릭에서 0.6초. 무효한 중간 상태는 보내지 않고 안내만 띄운다. */
	queueCherishMoveSave = () => {
		this.cherishMoveNote = this.cherishMoveInvalid();
		this.cherishMoveStatus = this.cherishMoveNote ? '' : '저장 중';
		if (this.cherishMoveSaveTimer) clearTimeout(this.cherishMoveSaveTimer);
		this.cherishMoveSaveTimer = setTimeout(() => {
			this.cherishMoveSaveTimer = null;
			this.saveCherishMoves();
		}, 600);
	};
	/**
	 * 예약된 저장을 지금 보낸다 — 다른 아이를 고르거나 패널을 닫을 때, 그리고 화면을 떠날 때
	 * (hookCherishMoveLeave). 숨김도 같이 보낸다.
	 *
	 * ⚠️ 저장이 날아가 있는데 그 사이 또 누른 상태(세대가 어긋난 상태)면 **겹쳐서라도 보낸다**.
	 * 평소엔 한 번에 하나만 보내지만(순서 뒤집힘 방지), 떠나는 순간엔 이어 보낼 기회가 더 없다.
	 */
	flushCherishMoves = () => {
		if (this.cherishHiddenSaveTimer) {
			clearTimeout(this.cherishHiddenSaveTimer);
			this.cherishHiddenSaveTimer = null;
			this.saveCherishHiddenMoves();
		}
		const pendingInFlight = this.cherishMoveSending && this.cherishMoveSentGen !== this.cherishMoveGen;
		if (!this.cherishMoveSaveTimer && !pendingInFlight) return;
		if (this.cherishMoveSaveTimer) {
			clearTimeout(this.cherishMoveSaveTimer);
			this.cherishMoveSaveTimer = null;
		}
		this.saveCherishMoves(true);
	};
	/**
	 * 화면을 떠나는 순간에도 flush 를 태운다 (2026-09-23). 0.6초 디바운스는 화면이 살아 있을
	 * 때만 터진다 — 기술을 누르고 곧바로 홈 탭으로 나가 배틀을 시작하면 그 탭이 통째로 유실됐다.
	 *
	 * 패널 생명주기(componentDidMount/WillUnmount)는 patch-cherish-tabs.py 가 소유하므로
	 * 건드리지 않는다. 리스너는 **페이지당 한 번만** 달고, 가리키는 패널은 PS 에 얹어 갱신한다
	 * (패널이 다시 만들어져도 최신 초안을 보내게).
	 */
	hookCherishMoveLeave = () => {
		const ps: any = PS;
		ps.cherishMoveFlush = () => { try { this.flushCherishMoves(); } catch {} };
		if (ps.cherishMoveLeaveHooked) return;
		ps.cherishMoveLeaveHooked = true;
		const flush = () => { ps.cherishMoveFlush?.(); };
		if (typeof window !== 'undefined') {
			window.addEventListener('pagehide', flush);
			window.addEventListener('beforeunload', flush);
		}
		if (typeof document !== 'undefined') {
			document.addEventListener('visibilitychange', () => { if (document.hidden) flush(); });
		}
		// 앱 안에서 탭을 옮기는 경우 — 방이 바뀌는 순간이 「이 화면을 떠났다」다.
		let lastRoom: string | undefined = PS.room?.id;
		PS.subscribe(() => {
			const now: string | undefined = PS.room?.id;
			if (now === lastRoom) return;
			lastRoom = now;
			flush();
		});
	};
	saveCherishMoves = (leaving?: boolean) => {
		const p = (this.cherishPokemon || []).find((m: any) => m.id === this.cherishMoveDraftId);
		if (!p) return;
		if (this.cherishMoveInvalid()) return; // 안내는 이미 떠 있다
		// 한 번에 하나만 — 겹쳐 보내면 응답 순서가 뒤집혀 옛 초안이 DB 에 굳는다.
		// 새 초안은 날아가 있는 요청의 응답 핸들러가 이어서 보낸다.
		// 화면을 떠나는 순간(leaving)만 예외다 — 이어 보낼 기회가 더 없다.
		if (this.cherishMoveSending && !leaving) return;
		const draft = [...this.cherishMoveDraft];
		const cur: string[] = p.moves || [];
		// ⚠️ 「바뀐 게 없다」 판정은 **저장이 안 날아가 있을 때만**. 인플라이트 중의 p.moves 는
		//    아직 옛 값이라, 이때 비교하면 방금 누른 탭이 「변화 없음」으로 묻힌다 (2026-09-22 사고).
		if (!this.cherishMoveSending && draft.length === cur.length && draft.every((m, i) => m === cur[i])) {
			this.cherishMoveStatus = '';
			this.forceUpdate();
			return;
		}
		const gen = this.cherishMoveGen;
		const id = p.id;
		this.cherishMoveSending = true;
		this.cherishMoveSentGen = gen;
		this.cherishMoveStatus = '저장 중';
		this.forceUpdate();
		fetch('https://cherish-world.com/api/showdown/moves', {
			// keepalive — 탭을 닫거나 숨기는 순간 보낸 요청도 끝까지 간다 (flush 경로).
			method: 'POST', credentials: 'include', keepalive: true,
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ id, moves: draft }),
		}).then(r => r.json()).then(json => {
			// 늦게 온 옛 응답은 화면도 캐시도 건드리지 않는다 — 더 새 요청의 응답이 맞춰 준다.
			if (gen !== this.cherishMoveSentGen) return;
			this.cherishMoveSending = false;
			if (!json?.ok) {
				// 실패는 **이어 보내지 않는다** — 빨간 안내가 떠 있어 조용히 사라지지 않고,
				// 같은 거절을 되풀이할 이유도 없다. 다음 탭이 다시 예약한다.
				this.cherishMoveStatus = '';
				this.cherishMoveNote = json?.message || '기술을 저장하지 못했어요.';
				this.forceUpdate();
				return;
			}
			p.moves = json.data.moves;
			// 정렬은 다시 하지 않는다 — 패널이 열려 있는 동안 줄이 움직이면 다음 클릭이 어긋난다.
			if (this.cherishMoveDraftId === id && this.cherishMoveGen === gen) {
				// 보내는 사이에 아무도 안 눌렀다 — 초안을 서버 값으로 맞춘다.
				this.cherishMoveDraft = [...p.moves];
				this.cherishMoveStatus = '저장됨';
				this.cherishMoveNote = '';
				this.forceUpdate();
				return;
			}
			// 세대가 어긋났다 = 보내는 사이에 또 눌렀거나 다른 아이로 옮겼다. 초안은 **그대로 두고**
			// 곧바로 이어 보낸다 — 보낼 게 없으면 저쪽 「바뀐 게 없다」에서 조용히 끝난다.
			// ⚠️ 이 이어 보내기를 `draftId === id` 안에 두지 말 것. 저장이 날아가 있는 동안 다른
			//    아이로 옮겨 편집하면 그 아이의 탭이 통째로 안 나간다 (예약 타이머는 이미 소진됐다).
			this.forceUpdate();
			this.saveCherishMoves();
		}).catch(() => {
			if (gen !== this.cherishMoveSentGen) return;
			this.cherishMoveSending = false;
			this.cherishMoveStatus = '';
			this.cherishMoveNote = '기술을 저장하지 못했어요.';
			this.forceUpdate();
		});
	};
	/**
	 * 접기/되돌리기 — 줄은 그 자리에 남고 표시만 바뀐다 (위 cherishHiddenAtOpen 주석).
	 * 장착 중인 기술은 접을 수 없다. 버튼을 안 그리지만 여기서도 막는다.
	 */
	toggleCherishHiddenMove = (name: string) => {
		const p = (this.cherishPokemon || []).find((m: any) => m.id === this.cherishMoveDraftId);
		const equipped: string[] = (this.cherishMoveDraftId === p?.id ? this.cherishMoveDraft : p?.moves) || [];
		if (equipped.includes(name)) return;
		const i = this.cherishHiddenDraft.indexOf(name);
		if (i >= 0) this.cherishHiddenDraft.splice(i, 1);
		else this.cherishHiddenDraft.push(name);
		this.queueCherishHiddenSave();
		this.queueCherishHiddenSettle();
		this.forceUpdate();
	};
	/**
	 * 줄을 실제로 두 칸 사이로 옮기는 시점 — **마지막 클릭에서 1.5초**. 누를 때마다 밀린다.
	 * 이게 이 화면에서 유일하게 레이아웃이 움직이는 순간이고, 그 움직임 자체가
	 * 「숨긴 기술은 아래로 모인다」는 설명이다. 되돌리기도 같은 타이머를 탄다 —
	 * 아래 칸에서 되돌린 줄은 손을 뗀 뒤 위 목록으로 올라간다.
	 */
	queueCherishHiddenSettle = () => {
		if (this.cherishHiddenSettleTimer) clearTimeout(this.cherishHiddenSettleTimer);
		this.cherishHiddenSettleTimer = setTimeout(() => {
			this.cherishHiddenSettleTimer = null;
			// 지금 접혀 있는 것 = 아래 칸. 위에서 접은 건 내려가고, 아래에서 되돌린 건 올라온다.
			this.cherishHiddenAtOpen = [...this.cherishHiddenDraft];
			this.forceUpdate();
		}, 1500);
	};
	/** 「숨긴 기술 N개 보기」 접이식 — 패널을 열 때마다 닫힌 상태로 시작한다. */
	toggleCherishHiddenSection = () => {
		this.cherishHiddenOpen = !this.cherishHiddenOpen;
		this.forceUpdate();
	};
	/** 연달아 접을 때 요청이 쏟아지지 않게 장착 저장과 같은 0.6초 디바운스. */
	queueCherishHiddenSave = () => {
		if (this.cherishHiddenSaveTimer) clearTimeout(this.cherishHiddenSaveTimer);
		this.cherishHiddenSaveTimer = setTimeout(() => {
			this.cherishHiddenSaveTimer = null;
			this.saveCherishHiddenMoves();
		}, 600);
	};
	/**
	 * 접어둔 기술 저장 — 전체 치환. 실패해도 **화면을 되돌리지 않는다**:
	 * 순수 화면 설정이라 다음에 다시 접으면 그만이고, 접는 손을 멈추게 하는 쪽이 더 나쁘다.
	 */
	saveCherishHiddenMoves = () => {
		const p = (this.cherishPokemon || []).find((m: any) => m.id === this.cherishMoveDraftId);
		if (!p) return;
		const hidden = [...this.cherishHiddenDraft];
		const cur: string[] = p.hiddenMoves || [];
		if (hidden.length === cur.length && hidden.every((m, i) => m === cur[i])) return;
		fetch('https://cherish-world.com/api/showdown/moves/hidden', {
			method: 'POST', credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ id: p.id, hidden }),
		}).then(r => r.json()).then(json => {
			if (json?.ok) p.hiddenMoves = json.data.hidden;
		}).catch(() => {});
	};
	/** 프리셋 3칸 — 서버가 늘 3칸을 주지만 옛 응답·조회 실패에도 화면이 안 깨지게 여기서도 맞춘다. */
	cherishPresets(p: any): string[][] {
		const rows: any[] = Array.isArray(p?.movePresets) ? p.movePresets : [];
		return [0, 1, 2].map(i => (Array.isArray(rows[i]) ? rows[i] : []));
	}
	/**
	 * 이 칸을 지금 그대로 불러올 수 있는가. 진화하면 해금 기술이 잘려 담아둔 칸이 무효해진다.
	 * 그때 **칸을 지우지 않고 잠그기만 한다** — 무엇을 담아뒀는지가 남아야 다시 채울 수 있다.
	 */
	cherishPresetPlayable(p: any, moves: string[]): boolean {
		const unlocked: string[] = p?.unlockedMoves || [];
		return moves.length > 0 && moves.every((m: string) => unlocked.includes(m));
	}
	toggleCherishPresetMode = () => {
		this.cherishPresetMode = !this.cherishPresetMode;
		this.cherishPresetNote = '';
		this.cherishPresetStatus = '';
		this.forceUpdate();
	};
	/**
	 * 칸을 눌렀을 때 — 「담기」 모드면 담고, 아니면 불러온다.
	 * 빈 칸은 불러올 게 없으니 모드와 무관하게 담기로 읽는다 (누르면 아무 일도 안 나는 버튼을 두지 않는다).
	 */
	tapCherishPreset = (p: any, slot: number) => {
		const moves = this.cherishPresets(p)[slot] || [];
		if (this.cherishPresetMode || moves.length === 0) this.saveCherishPreset(p, slot);
		else this.loadCherishPreset(p, slot);
	};
	/**
	 * 지금 장착한 기술을 그 칸에 담는다 (덮어쓰기 포함). 장착은 바뀌지 않는다.
	 * 보내는 값은 **화면의 초안** — 방금 누른 기술이 아직 저장 전이어도 보이는 대로 담긴다.
	 */
	saveCherishPreset = (p: any, slot: number) => {
		if (this.cherishPresetBusy) return;
		const draft: string[] = this.cherishMoveDraftId === p.id ? this.cherishMoveDraft : (p.moves || []);
		const invalid = this.cherishMoveDraftId === p.id ? this.cherishMoveInvalid() : '';
		if (invalid) { this.cherishPresetNote = invalid; this.cherishPresetStatus = ''; this.forceUpdate(); return; }
		this.cherishPresetBusy = true;
		this.cherishPresetNote = '';
		this.cherishPresetStatus = '담는 중';
		this.forceUpdate();
		fetch('https://cherish-world.com/api/showdown/moves/presets', {
			method: 'POST', credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ id: p.id, slot, moves: [...draft] }),
		}).then(r => r.json()).then(json => {
			this.cherishPresetBusy = false;
			if (!json?.ok) {
				this.cherishPresetStatus = '';
				this.cherishPresetNote = json?.message || '세트를 담지 못했어요.';
				this.forceUpdate();
				return;
			}
			p.movePresets = json.data.presets;
			this.cherishPresetMode = false;
			this.cherishPresetStatus = `세트${slot + 1}에 담았어요`;
			this.forceUpdate();
		}).catch(() => {
			this.cherishPresetBusy = false;
			this.cherishPresetStatus = '';
			this.cherishPresetNote = '세트를 담지 못했어요.';
			this.forceUpdate();
		});
	};
	/**
	 * 담아둔 칸을 장착으로 되돌린다 — 저장은 기존 saveCherishMoves 를 그대로 탄다
	 * (검증·오류 문구가 한 곳에만 있게).
	 *
	 * ⚠️ 예약돼 있던 자동 저장을 **먼저 취소**한다. 남겨 두면 0.6초 뒤 옛 초안이 뒤따라 날아가
	 * 방금 불러온 세트를 덮어쓴다.
	 */
	loadCherishPreset = (p: any, slot: number) => {
		const moves = this.cherishPresets(p)[slot] || [];
		if (!this.cherishPresetPlayable(p, moves)) {
			this.cherishPresetStatus = '';
			this.cherishPresetNote = '지금은 못 쓰는 기술이 들어 있어요. 다시 담아주세요.';
			this.forceUpdate();
			return;
		}
		if (this.cherishMoveDraftId !== p.id) this.loadCherishMoveDraft(p);
		if (this.cherishMoveSaveTimer) { clearTimeout(this.cherishMoveSaveTimer); this.cherishMoveSaveTimer = null; }
		this.cherishMoveDraft = [...moves];
		// 세대를 올려 둔다 — 날아가 있는 옛 저장의 응답이 방금 불러온 세트를 되감지 못하게.
		// (저장 중이면 아래 saveCherishMoves 는 건너뛰고, 그 응답 핸들러가 이 초안을 이어 보낸다.)
		this.cherishMoveGen += 1;
		this.cherishMoveNote = '';
		this.cherishPresetNote = '';
		this.cherishPresetStatus = '';
		this.saveCherishMoves();
	};
	// cherish-moves:end
	toggleCherishPartner = (p: any) => {
		fetch('https://cherish-world.com/api/showdown/partner', {
			method: 'POST', credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ id: p.id }),
		}).then(r => r.json()).then(json => {
			if (!json?.ok) {
				PS.alert(json?.message || '파트너를 저장하지 못했어요.');
				return;
			}
			for (const mon of this.cherishPokemon || []) mon.isPartner = false;
			if (json.data.isPartner) p.isPartner = true;
			this.forceUpdate();
		}).catch(() => PS.alert('파트너를 저장하지 못했어요.'));
	};
	toggleCherishTeam = (id: string) => {
		const idx = this.cherishTeamIds.indexOf(id);
		if (idx >= 0) {
			this.cherishTeamIds.splice(idx, 1);
		} else {
			// cherish-team-roster: 출전 1~6 + 후보 1~3. 앞에서부터 나갈 수 있는 아이로 정원을 채운다.
			// 3대3 은 앞 세 마리, 6대6 은 앞 여섯 마리. 어린이집·온실에 가 있으면 서버가 뒤 칸에서 당겨 온다.
			if (this.cherishTeamIds.length >= 9) {
				PS.alert('명단은 9마리까지예요. 3대3 은 앞에서 세 마리, 6대6 은 앞에서 여섯 마리가 나가고 나머지는 후보로 기다려요!');
				return;
			}
			this.cherishTeamIds.push(id);
		}
		this.forceUpdate();
		if (this.cherishTeamIds.length >= 1) {
			fetch('https://cherish-world.com/api/showdown/team', {
				method: 'POST', credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ ids: this.cherishTeamIds }),
			}).catch(() => {});
		}
	};
	claimCherishQuest = (q: any) => {
		fetch('https://cherish-world.com/api/showdown/quests/claim', {
			method: 'POST', credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ questId: q.id }),
		}).then(r => r.json()).then(json => {
			if (!json?.ok) { PS.alert(json?.message || '보상을 받지 못했어요.'); return; }
			q.claimed = true;
			if (!json.data.alreadyClaimed) {
				PS.alert(`\uD83C\uDF81 ${Number(json.data.berryReward).toLocaleString()}체리를 받았어요! (가진 체리 ${Number(json.data.balanceAfter).toLocaleString()}개)`);
			}
			this.forceUpdate();
		}).catch(() => PS.alert('보상을 받지 못했어요.'));
	};
	claimAllCherishQuests = () => {
		const list = (this.cherishQuests || []).filter((q: any) => q.completed && !q.claimed);
		if (!list.length) return;
		let got = 0, total = 0, balance = 0;
		// 순차 호출 — 지갑 차감이 겹치지 않게, 그리고 팝업이 퀘스트 수만큼 뜨지 않게.
		const step = (i: number): void => {
			if (i >= list.length) {
				this.forceUpdate();
				PS.alert(got
					? `\uD83C\uDF81 퀘스트 ${got}개 보상 ${total.toLocaleString()}체리를 받았어요! (가진 체리 ${balance.toLocaleString()}개)`
					: '보상을 받지 못했어요.');
				return;
			}
			const q = list[i];
			fetch('https://cherish-world.com/api/showdown/quests/claim', {
				method: 'POST', credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ questId: q.id }),
			}).then(r => r.json()).then(json => {
				if (json?.ok) {
					q.claimed = true;
					if (!json.data.alreadyClaimed) { got++; total += Number(json.data.berryReward) || 0; }
					balance = Number(json.data.balanceAfter) || balance;
				}
				step(i + 1);
			}).catch(() => step(i + 1));
		};
		step(0);
	};
	renderCherishQuestGroup = (title: string, sub: string, list: any[]) => {
		if (!list.length) return null;
		return <div class="cw-quest-group">
			<div class="cw-quest-group-head"><b>{title}</b><small>{sub}</small></div>
			<div class="cw-quest-grid">
				{list.map((q: any) => {
					const target = Number(q.targetCount) || 1;
					const done = Math.min(target, Number(q.progress) || 0);
					const pct = q.claimed || q.completed ? 100 : Math.round(done / target * 100);
					return <div class={`cw-quest${q.claimed ? ' is-claimed' : q.completed ? ' is-done' : ''}`}>
						<div class="cw-quest-top"><b>{q.title}</b><span class="cw-quest-reward"><svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linejoin="round" aria-hidden><rect x="3" y="8" width="18" height="5" rx="1" /><path d="M5 13v8h14v-8M12 8v13M12 8c-2-3-6-3-6 0h6zM12 8c2-3 6-3 6 0h-6z" /></svg>{Number(q.berryReward).toLocaleString()}체리</span></div>
						<div class="cw-quest-desc">{q.description}</div>
						<div class="cw-quest-foot">
							<div class="cw-track"><div class="cw-fill" style={`width:${pct}%`}></div></div>
							{q.claimed ? <span class="cw-quest-state is-claimed"><svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden><path d="M5 12l5 5 9-10" /></svg> 받음</span>
								: q.completed ? <button class="button cw-quest-claim" onClick={() => this.claimCherishQuest(q)}>받기</button>
								: <span class="cw-quest-state">{done}/{target}</span>}
						</div>
					</div>;
				})}
			</div>
		</div>;
	};
	/** 전체 목록 — 도전과제 탭이 부른다 (홈에는 요약 줄만 둔다). v2 (2026-09-05): 섹션 헤더 + 진행 바 카드. 외형 §24 */
	renderCherishQuestList() {
		if (!PS.user.named) return null;
		const quests = this.cherishQuests;
		if (!quests?.length) return null; // 로딩 중이거나 비공개(403)면 섹션 자체를 숨긴다
		const daily = quests.filter((q: any) => q.period !== 'weekly');
		const weekly = quests.filter((q: any) => q.period === 'weekly');
		const doneAll = quests.filter((q: any) => q.completed).length;
		return <div class="cw-sec cw-quests">
			<div class="cw-sec-head">
				<div class="cw-sec-head-text"><span class="cw-eyebrow">Quests</span><h3 class="cw-sec-title">퀘스트</h3></div>
				<span class="cw-sec-count">{doneAll} / {quests.length} 완료</span>
			</div>
			{this.renderCherishQuestGroup('오늘의 퀘스트', '매일 자정에 새로 뽑혀요', daily)}
			{this.renderCherishQuestGroup('이번 주 퀘스트', '월요일 자정에 새로 뽑혀요', weekly)}
		</div>;
	}
	/** 홈 요약 줄 — 항상 한 줄. 누르면 도전과제 탭의 전체 목록으로 간다. */
	renderCherishQuests() {
		if (!PS.user.named) return null;
		const quests = this.cherishQuests;
		if (!quests?.length) return null;
		const isWeekly = (q: any) => q.period === 'weekly';
		const daily = quests.filter((q: any) => !isWeekly(q));
		const weekly = quests.filter(isWeekly);
		const doneOf = (list: any[]) => list.filter((q: any) => q.completed).length;
		const claimable = quests.filter((q: any) => q.completed && !q.claimed).length;
		return <div class="menugroup cw-questbar" style="max-width:none">
			<button class="button cw-questbar-main" onClick={() => PS.join('cherish-achievements' as RoomID)}>
				<strong>&#x1F4CB; 퀘스트</strong>
				<small>오늘 {doneOf(daily)}/{daily.length} &middot; 이번 주 {doneOf(weekly)}/{weekly.length}</small>
			</button>
			{!!claimable &&
				<button class="button cw-questbar-claim" onClick={this.claimAllCherishQuests}>
					<strong>&#x1F381; 보상 {claimable}개 받기</strong>
				</button>}
		</div>;
	}
	// cherish-ffa-rank:begin (patch-ffa-client.py)
	/** 랭킹 모드 — 1대1·2대2·6대6 은 각각 별도 Elo 풀이다 (apps/api 0275, 6대6 은 2026-09-21).
	 *  'ffa' 는 탭에서 내렸지만 타입에는 남는다 — 저장된 옛 값이 들어와도 fetch 가 그대로 돌아야 한다. */
	cherishRankingMode: 'solo' | 'ffa' | 'multi' | 'six' = 'solo';
	setCherishRankingMode = (mode: 'solo' | 'ffa' | 'multi' | 'six') => {
		if (this.cherishRankingMode === mode) return;
		this.cherishRankingMode = mode;
		this.cherishRanking = null; // 로딩 동안 옛 모드 표가 남아 있으면 오해를 산다
		this.forceUpdate();
		this.loadCherishRanking();
	};
	loadCherishRanking() {
		const mode = this.cherishRankingMode;
		fetch(`https://cherish-world.com/api/showdown/ranking?mode=${mode}`, { credentials: 'include' })
			.then(r => r.json())
			.then(json => {
				if (this.cherishRankingMode !== mode) return; // 늦게 온 응답이 새 모드를 덮지 않게
				this.cherishRanking = json?.ok ? json.data : null;
				this.forceUpdate();
			})
			.catch(() => {});
	}
	cherishRankingEmptyNote() {
		if (this.cherishRankingMode === 'ffa') return '아직 이번 시즌 난투전 기록이 없어요. 첫 주인공이 되어보세요!';
		if (this.cherishRankingMode === 'multi') return '아직 이번 시즌 팀배틀 기록이 없어요. 첫 주인공이 되어보세요!';
		if (this.cherishRankingMode === 'six') return '아직 이번 시즌 6대6 기록이 없어요. 첫 주인공이 되어보세요!';
		return '아직 이번 시즌 배틀 기록이 없어요. 첫 주인공이 되어보세요!';
	}
	// cherish-ffa-rank:end
	// cherish-rank-v2:begin (patch-ranking-v2.py)
	/** 티어 아이콘 — 배치중(icon 0)은 그림이 없어 물음표 원으로 (외형 cherish-skin.css §13) */
	renderCherishTierIcon(tier: any, cls: string) {
		if (tier?.icon) return <img class={cls} src={`/cherish/tier/${tier.icon}.png`} alt={tier.name} />;
		return <span class={`${cls} cw-tier-ph`}>?</span>;
	}
	/** 트레이너 아바타 — 전신: api /showdown/avatar (nginx /sprites/trainers-custom/cherish<id>-full.png).
	 *  얼굴: api 랭킹 응답의 faceUrl(체리쉬홈 장착 아바타의 얼굴 아이콘, cherish-world.com 경로). 없으면(옛 api) 상반신 크롭 폴백. */
	cherishAvatarUrl(e: any, full: boolean) {
		if (full) return `/sprites/trainers-custom/cherish${e.discordUserId}-full.png`;
		if (e.faceUrl) return `https://cherish-world.com${e.faceUrl}`;
		return `/sprites/trainers-custom/cherish${e.discordUserId}.png`;
	}
	cherishRankingModeLabel() {
		if (this.cherishRankingMode === 'ffa') return '난투전';
		if (this.cherishRankingMode === 'multi') return '팀배틀';
		if (this.cherishRankingMode === 'six') return '6대6';
		// 'solo' 풀 = **3대3** 이다 (2026-09-21). 6대6 도 1대1 이라 여기서 「1대1」로 적으면
		// 두 풀을 가리키는 말이 돼 버린다 — 랭킹은 풀 단위라 포맷 이름으로 적는다.
		return '3대3';
	}
	cherishRankingRecord(e: any) {
		return `${e.wins}승 ${e.losses}패${e.ties ? ` ${e.ties}무` : ''}`;
	}
	renderCherishRanking() {
		if (!PS.user.named) return null;
		const r = this.cherishRanking;
		const mode = this.cherishRankingMode;
		// 엠블럼 키는 모드 이름과 **따로**다 (2026-09-21) — 6대6 은 전용 그림이 없어 1대1 것을 쓴다.
		// `is-six` 를 그대로 넣으면 CSS 에 없는 클래스라 동그라미 자리가 빈 칸으로 뜬다.
		const seg = (m: 'solo' | 'ffa' | 'multi' | 'six', label: string, mark = m) => <button
			class={`cw-seg-btn${mode === m ? ' on' : ''}`} onClick={() => this.setCherishRankingMode(m)}
		><span class={`cw-modechip-mark is-${mark}`}></span>{label}</button>;
		const hero = <div class="cw-rank-hero">
			<div class="cw-rank-hero-text">
				<span class="cw-eyebrow">Ranking</span>
				<h3 class="cw-rank-title">{r ? `시즌 ${r.season} 랭킹` : '시즌 랭킹'}</h3>
				<div class="cw-rank-meta">
					{r ? <b>{r.totalPlayers}명</b> : null}{r ? ' 참가, ' : ''}{this.cherishRankingModeLabel()}
				</div>
			</div>
			{/* 난투전 세그먼트는 2026-09-21 에 내렸다 — 진입점이 사라져 새 기록이 안 쌓인다.
			    옛 행은 DB 에 남아 있고 cherishRankingMode 도 'ffa' 를 계속 받는다. */}
			<div class="cw-seg on-accent">{seg('solo', '3대3')}{seg('six', '6대6', 'solo')}{seg('multi', '팀배틀')}</div>
		</div>;
		if (!r) return <div class="cw-rank">{hero}{this.renderCherishSkeleton('rank')}</div>;
		const meId = r.me?.discordUserId;
		const games = (e: any) => e.wins + e.losses + e.ties;
		const winRate = (e: any) => games(e) ? Math.round(e.wins / games(e) * 100) : 0;
		const placementGames = r.placementGames || 5;
		const tierLine = (e: any) => e.tier?.icon ? e.tier.name : `배치중 ${Math.min(games(e), placementGames)}/${placementGames}판`;
		const isMe = (e: any) => !!meId && e.discordUserId === meId;
		const podiumSlot = (e: any, no: number) => !e ? <div class="cw-podium-empty"></div> : (
			<div class={`cw-podium-slot is-${no}${isMe(e) ? ' is-me' : ''}`}>
				{no === 1 ? <svg class="cw-crown" viewBox="0 0 40 30" aria-hidden><path d="M4 24L2 7l9 7 9-12 9 12 9-7-2 17z" fill="#f2b705" stroke="#c98d00" stroke-width="1.5" stroke-linejoin="round" /><rect x="4" y="24" width="32" height="4" rx="1.5" fill="#e0a400" /><circle cx="20" cy="9" r="2.2" fill="#fff7d6" /><circle cx="6" cy="10" r="1.6" fill="#fff7d6" /><circle cx="34" cy="10" r="1.6" fill="#fff7d6" /></svg> : null}
				<span class="cw-podium-no">{no}</span>
				<span class="cw-podium-ava"><img class="cw-podium-full" src={this.cherishAvatarUrl(e, true)} alt="" />{this.renderCherishTierIcon(e.tier, 'cw-podium-tier-ic')}</span>
				<div class="cw-podium-name">{e.name}</div>
				<div class="cw-podium-tier">{tierLine(e)}</div>
				<div class="cw-podium-elo">{e.elo.toLocaleString()}<small>점</small></div>
				<div class="cw-podium-rec">{this.cherishRankingRecord(e)}</div>
			</div>
		);
		const me = r.me;
		let prog: any = null;
		if (me && !me.tier?.icon) {
			const g = Math.min(games(me), placementGames);
			prog = <div class="cw-rank-prog">
				<span>배치 판정까지 <b>{placementGames - g}판</b></span>
				<div class="cw-track"><div class="cw-fill" style={`width:${Math.round(g / placementGames * 100)}%`}></div></div>
				<span>{g}/{placementGames}</span>
			</div>;
		} else if (me && me.nextTierElo != null && me.tierFloorElo != null && me.nextTierElo > me.tierFloorElo) {
			const span = me.nextTierElo - me.tierFloorElo;
			const pct = Math.max(0, Math.min(100, Math.round((me.elo - me.tierFloorElo) / span * 100)));
			prog = <div class="cw-rank-prog">
				<span>다음 티어까지 <b>{Math.max(0, me.nextTierElo - me.elo)}점</b></span>
				<div class="cw-track"><div class="cw-fill" style={`width:${pct}%`}></div></div>
				<span>{me.nextTierElo}</span>
			</div>;
		}
		return <div class="cw-rank">
			{hero}
			{me ? <div class="cw-rank-me">
				<span class="cw-rank-face-wrap is-lg"><img class="cw-rank-face" src={this.cherishAvatarUrl(me, false)} alt="" />{this.renderCherishTierIcon(me.tier, 'cw-tier-xs')}</span>
				<div class="cw-rank-who">
					<div class="cw-rank-me-name">{me.name}</div>
					{/* 미플레이 숨김 (2026-10-12, api idleHidden) — 보드에서 빠진 사람은 순위 자리에 안내 한 줄. 점수·티어·전적은 그대로 보인다. */}
					{me.idleHidden
						? <div class="cw-rank-me-sub">{r.idleHideDays || 10}일 동안 배틀이 없어 랭킹판에서 빠져있어요</div>
						: null}
					<div class="cw-rank-me-sub">{me.idleHidden ? null : <><b>{me.rank}위</b>, </>}{tierLine(me)}, {this.cherishRankingRecord(me)}, 승률 <b>{winRate(me)}%</b></div>
				</div>
				<div class="cw-rank-ring" style={`--p:${winRate(me)}`}><b>{winRate(me)}%</b><small>승률</small></div>
				<div class="cw-rank-elo"><span ref={(el: any) => this.cherishCountUp(el, me.elo)}>{me.elo.toLocaleString()}</span><small>점</small></div>
				{prog}
			</div> : (r.top.length ? <p class="cw-rank-note">배틀에 참가하면 랭킹에 올라가요!</p> : null)}
			{!r.top.length ? <div class="cw-rank-empty">{this.cherishRankingEmptyNote()}</div> : <>
				<div class="cw-podium">{podiumSlot(r.top[1], 2)}{podiumSlot(r.top[0], 1)}{podiumSlot(r.top[2], 3)}</div>
				{r.top.length > 3 ? <div class="cw-rank-list">{r.top.slice(3).map((e: any) => (
					<div class={`cw-rank-row${isMe(e) ? ' is-me' : ''}`}>
						<div class="cw-rank-no">{e.rank}</div>
						<span class="cw-rank-face-wrap"><img class="cw-rank-face" src={this.cherishAvatarUrl(e, false)} alt="" />{this.renderCherishTierIcon(e.tier, 'cw-tier-xs')}</span>
						<div class="cw-rank-who">
							<div class="cw-rank-name">{e.name}</div>
							<div class="cw-rank-tier">{tierLine(e)}</div>
						</div>
						<div class="cw-rank-elo">{e.elo.toLocaleString()}<small>점</small></div>
						<div class="cw-rank-rec">
							{this.cherishRankingRecord(e)}, <b>{winRate(e)}%</b>
							<div class="cw-track"><div class="cw-fill" style={`width:${winRate(e)}%`}></div></div>
						</div>
					</div>
				))}</div> : null}
			</>}
			{/* 안내 문구는 2026-09-05 사용자 요청으로 제거. v1 이 쓰던 cherishRankingNote 는 2026-09-19 에 정의까지 걷었다 */}
		</div>;
	}
	// cherish-rank-v2:end
	// cherish-rooms: 배틀 대기방 (patch-room-list.py) — api /showdown/rooms 폴링
	cherishRooms: any[] | 'hidden' | null = null;
	cherishRoomsPollTimer: any = null;
	/** 이 세션에서 통과한 비밀방 비밀번호 (방 id → pw). */
	cherishRoomPw: { [id: string]: string } = {};
	cherishRoomErr = '';
	fetchCherishRooms = () => {
		if (!PS.user.named) return;
		// 숨긴 탭은 폴링 안 함 — 탭 여러 개면 유저당 60/분 한도를 넘겨 429 (2026-09-03)
		if (typeof document !== 'undefined' && document.hidden) return;
		fetch('https://cherish-world.com/api/showdown/rooms', { credentials: 'include' })
			.then(r => r.json())
			.then(json => {
				this.cherishRooms = json?.ok ? json.data.rooms || [] : 'hidden';
				this.forceUpdate();
			})
			.catch(() => {});
	};
	cherishRoomAct = (path: string, body: any, roomId?: string) => {
		fetch(`https://cherish-world.com/api/showdown/rooms${path}`, {
			method: 'POST', credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(body),
		})
			.then(r => r.json())
			.then(json => {
				if (!json?.ok) {
					this.cherishRoomErr = json?.message || '요청이 실패했어요.';
					if (json?.code === 'bad_password' && roomId) delete this.cherishRoomPw[roomId];
				} else {
					this.cherishRoomErr = '';
				}
				this.fetchCherishRooms();
			})
			.catch(() => {});
	};
	cherishAskPw = (room: any): string | null | undefined => {
		if (!room.locked) return null;
		let pw = this.cherishRoomPw[room.id];
		if (!pw) {
			const input = window.prompt('🔒 비밀방이에요. 비밀번호를 입력해주세요.');
			if (input === null || !input.trim()) return undefined;
			pw = input.trim();
			this.cherishRoomPw[room.id] = pw;
		}
		return pw;
	};
	joinCherishSlot = (room: any, slot: number) => {
		const pw = this.cherishAskPw(room);
		if (room.locked && pw === undefined) return;
		this.cherishRoomAct('/join', { id: room.id, slot, password: pw }, room.id);
	};
	spectateCherishRoom = (room: any) => {
		if (room.battleRoomid) {
			PS.join(room.battleRoomid as any);
			return;
		}
		const pw = this.cherishAskPw(room);
		if (pw === undefined) return;
		fetch('https://cherish-world.com/api/showdown/rooms/unlock', {
			method: 'POST', credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ id: room.id, password: pw }),
		})
			.then(r => r.json())
			.then(json => {
				if (json?.ok && json.data.roomid) {
					this.cherishRoomErr = '';
					PS.join(json.data.roomid as any);
				} else {
					this.cherishRoomErr = json?.message || '입장하지 못했어요.';
					if (json?.code === 'bad_password') delete this.cherishRoomPw[room.id];
					this.forceUpdate();
				}
			})
			.catch(() => {});
	};
	createCherishRoom = (locked: boolean) => {
		let password: string | null = null;
		if (locked) {
			const input = window.prompt('비밀방 비밀번호 (1~16자)');
			if (input === null || !input.trim()) return;
			password = input.trim().slice(0, 16);
		}
		this.cherishRoomAct('', { mode: '1v1', password });
	};
	renderCherishRoomSlot(room: any, idx: number) {
		const slot = room.slots[idx];
		const canAct = room.status === 'waiting';
		if (!slot.occupied) {
			return <button
				class="button" disabled={!canAct}
				onClick={() => this.joinCherishSlot(room, idx)}
				style="width:132px;height:34px;font-size:12px;opacity:.85"
			>+ 참가하기</button>;
		}
		const isMe = room.mySlot === idx;
		return <span class="button" style="width:132px;height:34px;display:inline-flex;align-items:center;justify-content:center;gap:4px;font-size:12px;cursor:default;overflow:hidden">
			{slot.tier && slot.tier.icon > 0 &&
				<img src={`/cherish/tier/${slot.tier.icon}.png`} width="18" height="18" style="flex:none" />}
			<span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{slot.name}</span>
			{isMe && canAct && <button
				onClick={() => this.cherishRoomAct('/leave', { id: room.id })}
				title="나가기" style="border:none;background:none;cursor:pointer;padding:0 2px;flex:none"
			>🚪</button>}
			{!isMe && room.isLeader && idx > 0 && canAct && <button
				onClick={() => this.cherishRoomAct('/kick', { id: room.id, slot: idx })}
				title="내보내기" style="border:none;background:none;cursor:pointer;padding:0 2px;flex:none"
			>✕</button>}
		</span>;
	}
	renderCherishRoomCard(room: any) {
		const is2v2 = room.mode === '2v2';
		// 6대6 은 **자리**가 둘이다 (마릿수만 여섯). 좌석 배치는 1대1 과 같다 — 2026-09-21.
		const modeLabel = is2v2 ? '팀배틀' : room.mode === '6v6' ? '6대6' : '3대3';
		const left = is2v2 ? [0, 2] : [0];
		const right = is2v2 ? [1, 3] : [1];
		const full = room.slots.every((s: any) => s.occupied);
		const statusLabel = room.status === 'playing' ? '배틀 중' :
			room.status === 'starting' ? `시작까지 ${room.startsIn ?? 0}초...` : '대기 중';
		return <div key={room.id} style="border:1px solid #d0d5dc;border-radius:10px;padding:8px 10px;min-width:330px">
			<div style="display:flex;align-items:center;gap:6px;margin-bottom:6px;font-size:12px">
				{room.isLeader && room.status === 'waiting' ? (
					<select class="select" style="font-size:12px;padding:1px 4px" value={room.mode}
						onChange={(e: any) => this.cherishRoomAct('/mode', { id: room.id, mode: e.target.value })}
					>
						<option value="1v1">3대3</option>
						<option value="2v2">팀배틀</option>
						<option value="6v6">6대6</option>
					</select>
				) : (
					<strong>{modeLabel}</strong>
				)}
				{room.locked && <span title="비밀방">🔒</span>}
				<small style={room.status === 'starting' ? 'color:#e0525f;font-weight:bold' : 'color:#68707c'}>{statusLabel}</small>
				<span style="flex:1"></span>
				{room.status === 'playing' && <button class="button" style="font-size:12px"
					onClick={() => this.spectateCherishRoom(room)}
				>👀 관전하기</button>}
				{room.isLeader && room.status === 'waiting' && <button
					class="button" disabled={!full} style="font-size:12px"
					onClick={() => this.cherishRoomAct('/start', { id: room.id })}
				>배틀 시작</button>}
				{room.isLeader && room.status === 'starting' && <button class="button" style="font-size:12px"
					onClick={() => this.cherishRoomAct('/cancel-start', { id: room.id })}
				>취소</button>}
			</div>
			<div style="display:flex;align-items:center;gap:8px">
				<div style="display:flex;flex-direction:column;gap:4px">
					{left.map(i => this.renderCherishRoomSlot(room, i))}
				</div>
				<strong style="color:#e0525f">VS</strong>
				<div style="display:flex;flex-direction:column;gap:4px">
					{right.map(i => this.renderCherishRoomSlot(room, i))}
				</div>
			</div>
			{room.error && <p style="color:#c00;font-size:11px;margin:5px 0 0">{room.error}</p>}
		</div>;
	}
	renderCherishRooms() {
		if (!PS.user.named) return null;
		if (this.cherishRooms === 'hidden') return null;
		if (!this.cherishRoomsPollTimer) {
			this.cherishRoomsPollTimer = setInterval(this.fetchCherishRooms, 3500);
			this.fetchCherishRooms();
		}
		const rooms = Array.isArray(this.cherishRooms) ? this.cherishRooms : [];
		const roomBattleIds = new Set(rooms.map((r: any) => r.battleRoomid).filter(Boolean));
		const battles = ((PS.mainmenu as any).cherishBattles || []).filter((b: any) => !roomBattleIds.has(b.id));
		return <div class="menugroup" style="max-width:none">
			<h3>배틀 방 {}
				<small style="font-weight:normal">
					<button class="button" style="font-size:11px" onClick={() => this.createCherishRoom(false)}>+ 방 만들기</button> {}
					<button class="button" style="font-size:11px" onClick={() => this.createCherishRoom(true)}>🔒 비밀방 만들기</button>
				</small>
			</h3>
			{this.cherishRoomErr && <p style="color:#c00;font-size:12px;margin:0 0 6px">{this.cherishRoomErr}</p>}
			{rooms.length > 0 && (
				<div style="display:flex;flex-wrap:wrap;gap:8px;margin-bottom:8px">
					{rooms.map((room: any) => this.renderCherishRoomCard(room))}
				</div>
			)}
			{battles.length > 0 && <div style="max-width:480px">
				<h4 style="margin:4px 0 3px;font-size:12.5px">진행 중인 배틀 관전</h4>
				{battles.map((b: any) => {
					const multi = String(b.id).includes('cherishmulti');
					const label = multi ?
						`${b.p1 || '?'} & ${b.p3 || '?'} vs. ${b.p2 || '?'} & ${b.p4 || '?'}` :
						`${b.p1 || '?'} vs. ${b.p2 || '?'}`;
					return <div key={b.id}><a href={`/${b.id}`} class="blocklink" style="font-size:12px;padding:4px 8px">
						<small>[{multi ? '팀배틀' : '구름 배틀'}]</small> {label}
					</a></div>;
				})}
			</div>}
		</div>;
	}
	// cherish-achv:begin (patch-achievement-panel.py)
	renderCherishAchievements() {
		// achievement-panel v4 (2026-09-05) — 카테고리 칩 · 달성 도장 · 잠김 실루엣. 인라인 스타일 없음, 외형 cherish-skin.css §14·§24.
		if (!PS.user.named) return null;
		const a = this.cherishAchievements;
		if (!a) return this.renderCherishSkeleton('list'); // 로딩 중 — 스켈레톤 (patch-motion.py)
		if (!a.achievements?.length) return null; // 비공개(403)·빈 목록이면 섹션 자체를 숨긴다
		const all = a.achievements;
		const open = all.filter((it: any) => !it.locked);
		const locked = all.filter((it: any) => it.locked);
		const pct = a.totalCount ? Math.round(a.earnedCount / a.totalCount * 100) : 0;
		const fmt = (iso: string) => { const d = new Date(iso); return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`; };
		const card = (it: any) => (
			<div class={`cw-ach-card ${it.earned ? 'is-earned' : it.locked ? 'is-locked' : 'is-open'}`}>
				<div class="cw-ach-ic">{it.earned ? <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linejoin="round" aria-hidden><path d="M7 4h10v5a5 5 0 0 1-10 0z" fill="currentColor" fill-opacity=".35" /><path d="M7 6H4v2a3 3 0 0 0 3 3M17 6h3v2a3 3 0 0 1-3 3M12 14v4M8 20h8" /></svg> : it.locked ? <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden><path d="M9 9a3 3 0 1 1 4.5 2.6c-1 .6-1.5 1.2-1.5 2.4M12 17h.01" /></svg> : <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linejoin="round" aria-hidden><rect x="5" y="11" width="14" height="10" rx="2" fill="currentColor" fill-opacity=".18" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>}</div>
				<div class="cw-ach-body">
					<div class="cw-ach-top">{it.categoryKo ? <span class="cw-ach-cat">{it.categoryKo}</span> : null}<div class="cw-ach-title">{it.title}</div></div>
					<div class="cw-ach-desc">{it.description}</div>
					{it.earned ? <div class="cw-ach-stamp"><svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden><path d="M5 12l5 5 9-10" /></svg><span>달성</span><b>{fmt(it.earnedAt)}</b></div>
						: it.locked ? <div class="cw-ach-foot is-locked">언젠가 만나게 될 거예요</div> : <div class="cw-ach-foot">도전 중</div>}
				</div>
			</div>
		);
		return <div class="cw-sec">
			<div class="cw-sec-head">
				<div class="cw-sec-head-text">
					<span class="cw-eyebrow">Achievements</span>
					<h3 class="cw-sec-title">도전과제</h3>
					<p class="cw-sec-sub">체리쉬에서의 특별한 순간이 여기 기록돼요.</p>
				</div>
				<span class="cw-sec-count">{a.earnedCount} / {a.totalCount} 달성</span>
			</div>
			<div class="cw-ach-prog"><div class="cw-track"><div class="cw-fill" style={`width:${pct}%`}></div></div><span><b>{pct}%</b></span></div>
			<div class="cw-ach-grid">
				{open.map(card)}
			</div>
			{locked.length ? <>
				<div class="cw-sec-head" style="margin-top:22px">
					<div class="cw-sec-head-text">
						<span class="cw-eyebrow">Hidden</span>
						<h3 class="cw-sec-title">아직 만나지 못한 이야기</h3>
						<p class="cw-sec-sub">노리고 얻는 게 아니라, 지내다 보면 문득 찾아오는 순간들이에요.</p>
					</div>
					<span class="cw-sec-count">{locked.length}개</span>
				</div>
				<div class="cw-ach-grid">
					{locked.map(card)}
				</div>
			</> : null}
		</div>;
	}
	// cherish-achv:end
	// cherish-anon:begin (patch-anon-toggle.py)
	toggleCherishAnon = (on: boolean) => {
		if (on && !window.confirm(
			'익명 모드를 켜면 새 가명으로 다시 접속해요.\n' +
			'상대에게는 가명·실루엣 아바타·포켓몬 종족 이름만 보여요.\n' +
			'(종족·특별한 색상·기술 구성은 그대로 보여요. 승패는 랭킹에 반영돼요.)'
		)) return;
		// 2026-09-17: **큐에 선 채로 토글하면 자기 자신과 붙는다.** 가명은 별도 계정이라,
		// 큐에 남은 실명 세션과 새로 접속한 가명 세션이 서로 매칭될 수 있다 (실제 사고 7918).
		// 서버에도 최종 가드가 있지만(patch-self-battle-guard.py), 여기서 먼저 내려야
		// 유령 대기가 안 남는다. 왕복 전에 보낸다 — fetch 중에도 매칭은 잡힐 수 있다.
		try { PS.send('/cancelsearch'); } catch {}
		fetch('https://cherish-world.com/api/showdown/anon', {
			method: 'POST', credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ on }),
		}).then(r => r.json()).then(json => {
			if (!json?.ok) { PS.alert(json?.message || '익명 모드를 바꾸지 못했어요.'); return; }
			location.reload();
		}).catch(() => PS.alert('익명 모드를 바꾸지 못했어요.'));
	};
	// cherish-anon:end
	// cherish-avoid-list:begin (patch-avoid-ui.py)
	cherishAvoids: any[] | null = null;
	cherishRemoveAvoid = (id: string) => {
		fetch('https://cherish-world.com/api/showdown/avoids/remove', {
			method: 'POST', credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ id }),
		}).then(r => r.json()).then(json => {
			if (!json?.ok) { PS.alert(json?.message || '해제에 실패했어요.'); return; }
			this.cherishAvoids = json.data.avoids || [];
			this.forceUpdate();
		}).catch(() => PS.alert('해제에 실패했어요.'));
	};
	// cherish-avoid-list:end
	/** cherish-skin v5: 화면 테마 (체리/구름/랜덤, 이모지 없음, 키 cw-theme3 — 옛 cw-theme 는 무시). 밤 칩은 2026-09-05 제거 — 남아 있던 다크 모드는 첫 렌더에서 밝게 되돌린다 (patch-skin.py) */
	setCherishTheme = (mode: string) => {
		try { localStorage.setItem('cw-theme3', mode); } catch {}
		const pick = mode === 'random' ? (Math.random() < 0.5 ? 'cream' : 'sky') : mode;
		document.documentElement.dataset.cwTheme = pick;
		if (PS.prefs.theme !== 'light') PS.prefs.set('theme', 'light');
		this.forceUpdate();
	};
	renderCherishThemePicker() {
		let cur = 'random';
		try { cur = localStorage.getItem('cw-theme3') || 'random'; } catch {}
		if (cur !== 'cream' && cur !== 'sky') cur = 'random';
		if (document.body.classList.contains('dark') && !(PS as any).cherishNightOff) {
			(PS as any).cherishNightOff = true;
			setTimeout(() => { if (PS.prefs.theme !== 'light') PS.prefs.set('theme', 'light'); }, 0);
		}
		const chip = (mode: string, label: string) => <button
			class={`cw-theme-chip${cur === mode ? ' on' : ''}`} onClick={() => this.setCherishTheme(mode)}
		>{label}</button>;
		return <p class="cw-theme-row">
			{chip('cream', '체리')} {chip('sky', '구름')} {chip('random', '랜덤')}
		</p>;
	}
	// cherish-mon-detail:begin (patch-mon-detail.py)
	selectCherishMon = (p: any) => {
		const closing = this.cherishSelectedMonId === p.id;
		this.flushCherishMoves(); // 편집 중이던 아이의 예약 저장을 먼저 보낸다
		this.cherishSelectedMonId = closing ? null : p.id;
		this.cherishEpithetOpen = null; // 다른 아이로 옮기면 칭호 목록은 접는다
		if (closing) { this.cherishMoveDraftId = null; } else { this.loadCherishMoveDraft(p); }
		// 새 기술 (2026-10-27) — 여는 순간의 새 기술을 잡아 두고 「봤음」으로 적는다. 카드 알약은 바로 꺼지고 상세 칩은 이번에 보인다
		if (!closing) {
			this.cherishFreshShow = { id: p.id, moves: this.cherishFreshMoves(p) };
			this.cherishMarkMovesSeen(p);
		}
		this.forceUpdate();
	};
	/**
	 * 본 기술 기억 (2026-10-27) — 브라우저 저장소 하나. DB 칸을 늘리지 않는 대신 기기를 바꾸면 처음 한 번은 씨앗만 뿌린다.
	 * **처음 보는 아이는 지금 가진 기술을 전부 「봤음」으로 심는다** — 안 그러면 기능을 켠 날 모든 아이가 새 기술 투성이가 된다.
	 * 그래서 「새 기술」은 이 화면에서 한 번이라도 본 아이가 그 뒤에 배운 것만이다 (어린이집·배틀 둘 다).
	 */
	cherishSeenMap(): any {
		if (!this.cherishSeenMoves) {
			try { this.cherishSeenMoves = JSON.parse(localStorage.getItem('cw-moves-seen-v1') || '{}') || {}; } catch { this.cherishSeenMoves = {}; }
		}
		return this.cherishSeenMoves;
	}
	cherishSaveSeen() {
		try { localStorage.setItem('cw-moves-seen-v1', JSON.stringify(this.cherishSeenMoves || {})); } catch {}
	}
	/** 이 아이의 새 기술 — 빌린 아이는 내 아이가 아니라 안 센다. 처음 보는 아이는 씨앗만 뿌리고 []. */
	cherishFreshMoves(p: any): string[] {
		if (!p || p.borrowed) return [];
		const all: string[] = p.unlockedMoves || [];
		const map = this.cherishSeenMap();
		const seen: string[] | undefined = map[p.id];
		if (!seen) { map[p.id] = all.slice(); this.cherishSaveSeen(); return []; }
		return all.filter((m: string) => !seen.includes(m));
	}
	cherishMarkMovesSeen(p: any) {
		if (!p || p.borrowed) return;
		this.cherishSeenMap()[p.id] = (p.unlockedMoves || []).slice();
		this.cherishSaveSeen();
	}
	/** 카드 알약 「새 기술 N」 — 상세를 열면 꺼진다. */
	renderCherishNewMovePill(p: any) {
		const n = this.cherishFreshMoves(p).length;
		if (!n) return null;
		return <span class="cw-newmove-pill" title="새로 배운 기술이 있어요. 눌러서 확인해 보세요">
			<svg viewBox="0 0 24 24" width="10" height="10" aria-hidden><path d="M12 2.5l2.2 6.3 6.3 2.2-6.3 2.2L12 19.5l-2.2-6.3L3.5 11l6.3-2.2z" fill="currentColor" /></svg>
			새 기술 {n}
		</span>;
	}
	/**
	 * 뉴비 진행 점 (2026-10-27) — 「●●○ 1판 더 하면 새 기술」. 뉴비가 아니거나(api null) 배울 게 없는 아이,
	 * 빌린 아이는 안 그린다. N 이 6 을 넘으면 점 대신 글자만.
	 */
	renderCherishLearnDots(p: any) {
		const every = this.cherishBattleLearn?.every;
		if (typeof every !== 'number' || every <= 0 || p.borrowed || !(p.movesLeft > 0)) return null;
		const done = Math.max(0, Math.min(every - 1, p.battleLearnCount || 0));
		const left = every - done;
		return <span class="cw-learn-dots" title={`배틀 ${every}판마다 새 기술을 하나 배워 와요`}>
			{every <= 6 ? Array.from({ length: every }, (_x: any, i: number) => <i class={`cw-learn-dot${i < done ? ' on' : ''}`} />) : null}
			<span>{left === 1 ? '다음 판에 새 기술' : `${left}판 더 하면 새 기술`}</span>
		</span>;
	}
	/**
	 * 핫타임 포인트 막대 (2026-10-27) — 핫타임이 **열려 있는 동안만**. 「0.6 / 1」. 빌린 아이·배울 게 없는 아이는 없다.
	 * 외형 cherish-hot.css (patch-hot-time-ui.py).
	 */
	renderCherishHotBar(p: any) {
		const t = this.cherishHotTime;
		if (!t?.active || p.borrowed || !(p.movesLeft > 0)) return null;
		const at = t.learnAt || 10;
		const pts = Math.max(0, Math.min(at, p.hotLearnPoints || 0));
		return <span class="cw-hot-bar" title={`핫타임 기술 포인트 (이기면 많이, 져도 조금 올라요. ${at / 10}점 모이면 새 기술)`}>
			<svg viewBox="0 0 24 24" width="10" height="10" aria-hidden><path d="M12.6 2.2c.4 3.1-1.3 4.8-2.9 6.4C8.1 10.2 6.5 11.9 6.5 15a5.5 5.5 0 0 0 11 0c0-2.3-1-3.9-2-5.2-.3 1.4-1 2.3-2 2.6.6-3.2-.2-6.6-.9-10.2z" fill="currentColor" /></svg>
			<span><span style={`width:${Math.round((pts / at) * 100)}%`} /></span>
			<b>{(pts / 10).toFixed(1)} / {at / 10}</b>
			{p.hotWatch ? <i class="cw-hot-watch">응원석</i> : null}
		</span>;
	}
	/** 기술 한 줄 설명 — 툴팁과 같은 한글 데이터(Dex.text) 우선, 없으면 영어. */
	cherishMoveDesc(name: string): string {
		const mv = Dex.moves.get(name);
		const t: any = Dex.text.get(mv as any) || {};
		return t.desc || t.shortDesc || mv.shortDesc || mv.desc || '';
	}
	/**
	 * 특성이 무슨 일을 하는지 한 줄 (2026-10-07). 기술과 달리 **ko desc 만** 쓴다 — ko 표의 특성 shortDesc 는
	 * 영어 원문이 그대로 남아 있어(patch-ko-text.py 는 desc 만 채웠다) 폴백하면 영어가 샌다. 한글이 아니면 ''.
	 */
	cherishAbilityDesc(name: string): string {
		const e = (window as any).BattleText?.ko?.Abilities?.[toID(name)];
		const d = String(e?.desc || '');
		return /[가-힣]/.test(d) ? d : '';
	}
	/**
	 * 물리·특수 중 어느 쪽이 센가 — **서버 판정을 그대로 쓴다** (2026-09-09).
	 *
	 * 예전에는 여기서 클라 도감의 종족값으로 직접 계산했다. 그런데 클라 도감을 **원본 종족값**으로
	 * 되돌리면서(콘솔에서 우리 보정을 못 보게) 그 계산이 실제 배틀과 어긋나게 됐다.
	 * api 는 보정된 종족값 + 순수한힘·천하장사 같은 공격 배율까지 넣어 판정한다
	 * (apps/api pokemon-specialty.ts attackLean). 성격은 감춰진 동안 계산에서도 빠진다.
	 */
	cherishAtkLean(p: any): { lean: string } {
		return { lean: p?.atkLean || '' };
	}
	/** 장착·해제 버튼의 체크 아이콘 (2026-09-11 시안 C). */
	cherishIcCheck() {
		return <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:-1px" aria-hidden><path d="M4 12.5l5.5 5.5L20 6" /></svg>;
	}
	/** 숨기기 = 감은 눈, 되돌리기 = 뜬 눈 (2026-09-11 시안 C). 글자만으로는 무슨 버튼인지 늦게 읽힌다. */
	cherishIcEye(off: boolean) {
		return off
			? <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" style="vertical-align:-1px" aria-hidden><path d="M3 3l18 18" /><path d="M10.6 6.2A9.7 9.7 0 0 1 12 6c6.4 0 10 6 10 6a17 17 0 0 1-3.2 3.8" /><path d="M6.3 8.2A17 17 0 0 0 2 12s3.6 6 10 6c1.5 0 2.8-.3 4-.8" /></svg>
			: <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" style="vertical-align:-1px" aria-hidden><path d="M2 12s3.6-6 10-6 10 6 10 6-3.6 6-10 6S2 12 2 12z" /><circle cx="12" cy="12" r="2.6" /></svg>;
	}
	/**
	 * 기술 줄 버튼 세 종의 인라인 스타일 (2026-09-11 시안 C, 사용자 선택).
	 * 세 상태를 **색면으로** 가른다 — 끼운 것(체리 채움) · 끼울 것(체리 테두리) · 치울 것(회색 채움).
	 * 그전에는 장착 중도 테두리 색만 달라 스무 줄이 넘으면 훑어보기 어려웠고, 숨기기는
	 * 혼자 회색 글자(#8a8f98)라 다른 버튼(스킨 기본이 체리색 글자)들 사이에서 죽어 보였다.
	 */
	cherishMoveBtnStyle(kind: 'on' | 'off' | 'hide') {
		const base = 'font-size:12px;min-width:64px;padding:5px 0;box-shadow:none;';
		if (kind === 'on') return base + 'font-weight:800;border-color:#e11d48;background:#e11d48;color:#fff;text-shadow:none';
		if (kind === 'off') return base + 'font-weight:800;border-color:#e11d48;background:#fff;color:#be123c';
		return base + 'font-weight:700;border-color:#c9ced6;background:#eef0f3;color:#4b5563';
	}
	/**
	 * 기술 한 줄 — 이름·타입 칩·분류 칩·위력/명중·설명. onToggle 이 있으면 앞에 장착/해제 버튼.
	 * 분류(물리/특수)는 2026-09-06 추가 — 개체마다 공격·특공이 다른데 「위력 90」만 보고
	 * 고르게 되어 있었다. 배지는 배틀 조작부와 같은 Dex.getCategoryIcon(한글 물리/특수/변화).
	 * 변화 기술의 위력 자리 「변화」 문구는 배지와 중복이라 뺐다 (명중만 남는다).
	 * onHide 가 있으면 「숨기기」가 장착 버튼 아래 붙는다 (2026-09-10) — 장착 중인 기술에는 안 준다.
	 */
	renderCherishMoveLine(name: string, opts: { star?: boolean, egg?: boolean, fresh?: boolean, on?: boolean, onToggle?: () => void, onHide?: () => void }) {
		const mv = Dex.moves.get(name);
		const power = mv.category === 'Status' ? '' : `위력 ${mv.basePower || '-'}`;
		const acc = mv.accuracy === true ? '' : `명중 ${mv.accuracy}`;
		const stat = [power, acc].filter(Boolean).join(', ');
		// 새로 배운 기술 줄은 왼쪽 청록 띠 + 「새 기술」 칩 (2026-10-27, 외형 cherish-learn.css)
		return <div class={opts.fresh ? 'cw-move-fresh' : undefined} style="display:flex;gap:8px;align-items:flex-start;padding:5px 0;border-top:1px solid rgba(0,0,0,.06)">
			{opts.onToggle || opts.onHide ? <div style="display:flex;flex-direction:column;gap:4px;flex:none">
				{opts.onToggle ? <button
					class="button" style={this.cherishMoveBtnStyle(opts.on ? 'on' : 'off')}
					onClick={opts.onToggle}
				>{this.cherishIcCheck()} {opts.on ? '해제' : '장착'}</button> : null}
				{opts.onHide ? <button
					class="button" style={this.cherishMoveBtnStyle('hide')}
					onClick={opts.onHide}
				>{this.cherishIcEye(true)} 숨기기</button> : null}
			</div> : null}
			<div style="flex:1;min-width:0">
				<div style="font-size:12.5px">
					{opts.fresh ? <em class="cw-rec cw-rec-new">새 기술</em> : null}{opts.star ? <em class="cw-rec">추천</em> : null}{opts.egg ?<em class="cw-rec" style="background:#e3f5e6;color:#2f8f46" title="온실에서 태어날 때만 배울 수 있는 기술">온실</em> : null}<strong>{CherishKo.move(mv.name)}</strong> {}
					<span dangerouslySetInnerHTML={{ __html: Dex.getTypeIcon(mv.type) }} /> {}
					<span dangerouslySetInnerHTML={{ __html: Dex.getCategoryIcon(mv.category) }} /> {}
					<small style="color:#68707c">{stat}</small>
				</div>
				<div style="font-size:11.5px;color:#68707c;margin-top:1px;line-height:1.35">{this.cherishMoveDesc(name)}</div>
			</div>
		</div>;
	}
	/** 타입 색 — Dex.getTypeIcon 이 그리는 한글 배지의 배경색을 그대로 읽는다 (타일 원광·상세 아트 배경용). */
	cherishTypeColor(type: string | undefined): string {
		if (!type) return '#fda4af';
		const m = /background:(#[0-9a-fA-F]{6})/.exec(Dex.getTypeIcon(type));
		return m ? m[1] : '#fda4af';
	}
	/** 명단 저장 — 배열 순서가 곧 자리(1~6 출전, 7~9 후보). 서버는 앞에서부터 나갈 수 있는 아이로 정원을 채운다. */
	cherishSaveTeam() {
		fetch('https://cherish-world.com/api/showdown/team', {
			method: 'POST', credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ ids: this.cherishTeamIds }),
		}).catch(() => {});
	}
	/**
	 * 곁에 두기/빼기 (0317) — 목록·드롭다운 맨 앞으로. 개수 제한 없음.
	 * patch-mon-detail.py 가 상세 액션 줄을 다시 만들기 때문에 여기서도 같이 보존한다.
	 */
	toggleCherishPin = (p: any) => {
		fetch('https://cherish-world.com/api/showdown/pin', {
			method: 'POST', credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ id: p.id }),
		}).then(r => r.json()).then(json => {
			if (!json?.ok) {
				PS.alert(json?.message || '저장하지 못했어요.');
				return;
			}
			p.pinned = !!json.data.pinned;
			this.forceUpdate();
		}).catch(() => PS.alert('저장하지 못했어요.'));
	};
	/**
	 * 자리 지정 (2026-09-05, 사용자 확정: 맞바꾸기). slot 0~5 출전, 6~8 후보 (2026-09-22, 종전 6칸).
	 * - 이미 명단에 있는 아이 → 그 칸의 아이와 자리를 **교환** (빈 칸이면 이동)
	 * - 명단 밖 아이 → 빈 칸이면 채우고, 차 있으면 그 아이를 **대신** 들어간다 (기존 아이는 명단 밖으로)
	 * 배열에 구멍은 없다 — 뒤쪽 빈 칸을 골라도 실제로는 다음 빈 순번에 붙는다 (출전 = 앞에서부터라 어쩔 수 없다).
	 */
	cherishSetTeamSlot = (id: string, slot: number) => {
		const ids = this.cherishTeamIds.slice();
		const cur = ids.indexOf(id);
		if (slot > ids.length) slot = ids.length;
		if (cur === slot) return;
		if (cur >= 0) {
			if (slot < ids.length) { ids[cur] = ids[slot]; ids[slot] = id; }
			else { ids.splice(cur, 1); ids.push(id); }
		} else if (slot < ids.length) {
			ids[slot] = id;
		} else {
			if (ids.length >= 9) { PS.alert('명단은 9마리까지예요. 차 있는 칸을 눌러 바꿔 넣어 주세요!'); return; }
			ids.push(id);
		}
		this.cherishTeamIds = ids;
		this.forceUpdate();
		this.cherishSaveTeam();
	};
	cherishRemoveFromTeam = (id: string) => {
		const idx = this.cherishTeamIds.indexOf(id);
		if (idx < 0) return;
		this.cherishTeamIds.splice(idx, 1);
		this.forceUpdate();
		this.cherishSaveTeam();
	};
	/**
	 * 칭호 (2026-09-07) — 목록은 개체마다 다르고 자주 안 바뀌니 「칭호」를 누를 때 한 번만
	 * 받아 온다. /showdown/me 에 얹으면 개체 수만큼 무거워진다.
	 */
	toggleCherishEpithets = (p: any) => {
		if (this.cherishEpithetOpen === p.id) { this.cherishEpithetOpen = null; this.forceUpdate(); return; }
		this.cherishEpithetOpen = p.id;
		if (this.cherishEpithetFor !== p.id) {
			this.cherishEpithetFor = p.id;
			this.cherishEpithetList = null;
			fetch(`https://cherish-world.com/api/showdown/epithets?id=${encodeURIComponent(p.id)}`, { credentials: 'include' })
				.then(r => r.json()).then(json => {
					if (this.cherishEpithetFor !== p.id) return; // 그새 다른 아이를 열었다
					if (!json?.ok) { this.cherishEpithetFor = null; PS.alert(json?.message || '칭호를 불러오지 못했어요.'); this.forceUpdate(); return; }
					this.cherishEpithetList = json.data.epithets || [];
					this.forceUpdate();
				}).catch(() => {
					if (this.cherishEpithetFor !== p.id) return;
					// 실패를 빈 목록으로 두면 「아직 칭호가 없어요」가 거짓말이 된다 — 다시 누르면 재시도
					this.cherishEpithetFor = null;
					PS.alert('칭호를 불러오지 못했어요.');
					this.forceUpdate();
				});
		}
		this.forceUpdate();
	};
	/** 칩을 누르면 장착, 장착된 칩을 다시 누르면 해제 (봇의 「대표 칭호 빼기」와 같다). */
	pickCherishEpithet = (p: any, e: any) => {
		if (this.cherishEpithetBusy) return;
		this.cherishEpithetBusy = true;
		this.forceUpdate();
		fetch('https://cherish-world.com/api/showdown/epithets/equip', {
			method: 'POST', credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ id: p.id, epithetRowId: e.equipped ? null : e.id }),
		}).then(r => r.json()).then(json => {
			this.cherishEpithetBusy = false;
			if (!json?.ok) { PS.alert(json?.message || '칭호를 바꾸지 못했어요.'); this.forceUpdate(); return; }
			const list = json.data.epithets || [];
			if (this.cherishEpithetFor === p.id) this.cherishEpithetList = list;
			// 카드·상세 헤더의 「칭호」도 같은 값으로 — 목록을 다시 받지 않아도 화면이 맞는다
			const on = list.find((x: any) => x.equipped);
			p.epithet = on ? on.label : null;
			this.forceUpdate();
		}).catch(() => { this.cherishEpithetBusy = false; PS.alert('칭호를 바꾸지 못했어요.'); this.forceUpdate(); });
	};
	/**
	 * 배틀에 실제로 나갈 이름 — 서버 battleDisplayName(lib/showdown-pokemon.ts) 과 **같은 규칙**.
	 * 규칙이 갈라지면 미리보기가 거짓말을 하므로, 저쪽을 고치면 여기도 같이 고칠 것.
	 * 첫 부모 라벨(받은 아이·빌린 아이)이 칭호보다 우선하고, 18자를 넘으면 칭호가 빠진다.
	 */
	cherishBattleName(p: any): { name: string, note: string } {
		const base = p.nickname || p.koName || p.speciesName;
		const owner = p.borrowedFromName || (p.originalOwnerId ? (p.originalOwnerName || '떠난 트레이너') : null);
		if (owner) {
			const note = '받은 아이는 첫 부모 이름이 앞에 붙어서 칭호는 안 나와요';
			const labeled = `(${owner})의 ${base}`;
			if (labeled.length <= 18) return { name: labeled, note };
			const room = 18 - base.length - 5;
			return { name: room >= 1 ? `(${owner.slice(0, room)}…)의 ${base}` : base, note };
		}
		if (!p.epithet) return { name: base, note: '' };
		const titled = `「${p.epithet}」 ${base}`;
		if (titled.length > 18) return { name: base, note: '칭호까지 붙이면 18자가 넘어서 배틀에선 이름만 나와요' };
		return { name: titled, note: '' };
	}
	/** 「~로/~으로 나가요」 — 받침 판정 (조사 유틸이 클라엔 없다). */
	cherishRoJosa(s: string): string {
		const c = s.charCodeAt(s.length - 1) - 0xAC00;
		if (c < 0 || c > 11171) return '로';
		const jong = c % 28;
		return (jong === 0 || jong === 8) ? '로' : '으로';
	}
	/** 칭호 고르기 — 「칭호」 버튼으로 펼친다. 대표 1개만 이름 앞에 나간다. */
	renderCherishEpithets(p: any) {
		if (this.cherishEpithetOpen !== p.id) return null;
		const list = this.cherishEpithetFor === p.id ? this.cherishEpithetList : null;
		const bn = this.cherishBattleName(p);
		return <div class="cw-epi">
			<div class="cw-epi-head"><b>칭호</b><small>함께 지낸 행적으로 저절로 붙어요. 대표 하나만 이름 앞에 나가요</small></div>
			{list === null
				? <p class="cw-epi-empty">불러오는 중이에요…</p>
				: !list.length
					? <p class="cw-epi-empty">아직 얻은 칭호가 없어요. 어린이집과 통화방, 배틀에서 함께 지내다 보면 이 아이만의 칭호가 생겨요.</p>
					: <div class="cw-epi-list">
						{list.map((e: any) => <button
							class={`cw-epi-chip${e.equipped ? ' on' : ''}`}
							disabled={this.cherishEpithetBusy}
							title={e.equipped ? '다시 누르면 이름 앞에서 빼요' : '이름 앞에 붙여요'}
							onClick={() => this.pickCherishEpithet(p, e)}
						>「{e.label}」</button>)}
					</div>}
			<p class="cw-epi-prev">배틀에선 <b>{bn.name}</b>{this.cherishRoJosa(bn.name)} 나가요{bn.note ? <small> ({bn.note})</small> : null}</p>
		</div>;
	}
	/**
	 * 특기 (2026-10-16) — 카드 뱃지 · 「특기 정할 아이」 필터 · 상세에서 정하기.
	 * 그전까지 배틀 사이트 어디에도 특기가 안 보였고, 정하는 곳은 디스코드 봇뿐이었다.
	 * 데이터는 /showdown/me 의 `growth`(api growthView) 그대로. 수치는 안 낸다 — 진행은 단계 말(🌱🌿🪴🌳)만.
	 * 「완성」이라는 말은 쓰지 않는다 (2026-09-03 사용자 확정) — 252 에 닿아 굳은 칸은 ✨ 와 금색으로만 가른다.
	 * 카드는 3열 78px(폰)에도 들어가게 특공·특방으로 줄이고, 상세는 원래 이름 그대로 (사용자 확정).
	 */
	cherishSpecShort(k: string): string {
		return ({ hp: '체력', atk: '공격', def: '방어', spa: '특공', spd: '특방', spe: '속도' } as any)[k] || '';
	}
	cherishSpecKo(k: string): string {
		return ({ hp: '체력', atk: '공격', def: '방어', spa: '특수공격', spd: '특수방어', spe: '속도' } as any)[k] || '';
	}
	/** 252 에 닿아 굳었나 — api `frozen[].full` 우선. api 가 아직 옛 응답이면 노력치로 직접 본다 (배포 순서 무관). */
	cherishSpecFull(p: any, f: any): boolean {
		return typeof f?.full === 'boolean' ? f.full : ((p?.evs?.[f?.stat] ?? 0) >= 252);
	}
	/** 필터 대상 — 다 자랐는데 특기 칸이 빈 아이 (api needsSpecialty). 빌린 아이는 정할 수 없어 뺀다. */
	cherishNeedsSpec(p: any): boolean {
		return !p.borrowed && !!p.growth?.needsSpecialty;
	}
	/**
	 * 내가 빌려준 아이(사제·빌려주기) 한 줄 (2026-10-18) — 「🤝 OO 님에게 빌려주는 중 · 10월 20일 14시까지」.
	 * api `rentedOutToName`·`rentedOutUntil`(사제 대여는 기한 없음). 돌려받기 예약이면 기한이 지금으로 당겨져 있어 「곧 돌아와요」.
	 * 24시간 출전이 없으면 기한 전에 돌아오므로 「까지」는 늦어도의 뜻이다.
	 */
	cherishLentNote(p: any): string {
		const who = p.rentedOutToName ? `${p.rentedOutToName} 님에게 ` : '';
		const t = p.rentedOutUntil ? Date.parse(p.rentedOutUntil) : NaN;
		let when = '';
		if (Number.isFinite(t)) {
			if (t <= Date.now() + 60000) when = ' · 곧 돌아와요';
			else { const d = new Date(t); when = ` · ${d.getMonth() + 1}월 ${d.getDate()}일 ${d.getHours()}시까지`; }
		}
		return `🤝 ${who}빌려주는 중${when}`;
	}
	/** 카드 뱃지 줄 — 키우는 중(분홍) · 몸에 밴 ✨(금색) · 바꾸다 굳은 칸(회색) · 특기 미정(점선). 510 전엔 없다. */
	renderCherishSpecBadges(p: any) {
		const g = p.growth;
		if (!g || !g.fullyGrown) return null;
		const pill = 'display:inline-block;padding:0 6px;border-radius:999px;font-size:10px;line-height:15px;font-weight:700;white-space:nowrap;border:1px solid;';
		const out: any[] = [];
		if (g.specialty) {
			out.push(<span title={`특기로 키우는 중이에요${g.specialtyStage?.label ? ` (${g.specialtyStage.label})` : ''}`} style={pill + 'background:#ffe4e9;border-color:#fda4af;color:#be123c'}>{this.cherishSpecShort(g.specialty)} {g.specialtyStage?.emoji || ''}</span>);
		}
		for (const f of (g.frozen || [])) {
			const full = this.cherishSpecFull(p, f);
			out.push(full
				? <span title="특기가 몸에 뱄어요" style={pill + 'background:#fde68a;border-color:#f59e0b;color:#78350f'}>✨ {this.cherishSpecShort(f.stat)}</span>
				: <span title="특기를 바꿀 때 그 자리에서 굳었어요" style={pill + 'background:#eef0f3;border-color:#c9ced6;color:#4b5563'}>{this.cherishSpecShort(f.stat)}</span>);
		}
		if (!g.specialty && this.cherishNeedsSpec(p)) {
			out.push(<span title="다 자랐어요. 특기를 정해줄 수 있어요" style={pill + 'background:#fff;border-style:dashed;border-color:#e11d48;color:#e11d48'}>특기 미정</span>);
		}
		if (!out.length) return null;
		return <span style="display:flex;flex-wrap:wrap;justify-content:center;gap:3px;margin-top:4px;max-width:100%">{out}</span>;
	}
	/** 「특기 정할 아이만」 켜기·끄기 — 켤 때 그 순간의 대상 id 를 잡아 둔다. */
	toggleCherishSpecOnly = () => {
		this.cherishSpecOnlyIds = this.cherishSpecOnlyIds
			? null
			: (this.cherishPokemon || []).filter((p: any) => this.cherishNeedsSpec(p)).map((p: any) => p.id);
		this.forceUpdate();
	};
	/** 은/는 — 받침 판정 (cherishRoJosa 와 같은 방식). */
	cherishTopicJosa(s: string): string {
		const c = s.charCodeAt(s.length - 1) - 0xAC00;
		if (c < 0 || c > 11171) return '는';
		return c % 28 === 0 ? '는' : '은';
	}
	/** 특기 정하기·바꾸기 — 봇과 같은 api(setSpecialtyTx). 바꾸면 이전 특기가 굳으니 확인창을 꼭 거친다. */
	pickCherishSpecialty = (p: any, stat: string) => {
		if (this.cherishSpecBusy) return;
		const g = p.growth || {};
		if (g.specialty === stat) return;
		const name = p.nickname || p.koName || p.speciesName;
		const ko = this.cherishSpecKo(stat);
		const prev = g.specialtyKo || '';
		const ask = g.specialty
			? `${name}의 특기를 「${ko}」${this.cherishRoJosa(ko)} 바꿀까요?\n\n지금 특기인 「${prev}」${this.cherishTopicJosa(prev)} 지금 자란 만큼에서 굳어요. 굳은 특기를 다시 풀려면 체리가 들어요.`
			: `${name}의 특기를 「${ko}」${this.cherishRoJosa(ko)} 정할까요?\n\n특기로 정한 능력은 줄지 않고 자라기만 해요.`;
		if (!window.confirm(ask)) return;
		this.cherishSpecBusy = true;
		this.forceUpdate();
		fetch('https://cherish-world.com/api/showdown/specialty/set', {
			method: 'POST', credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ id: p.id, stat }),
		}).then(r => r.json()).then(json => {
			this.cherishSpecBusy = false;
			if (!json?.ok) { PS.alert(json?.message || '특기를 정하지 못했어요.'); this.forceUpdate(); return; }
			p.growth = json.data; // 카드 뱃지·상세가 같은 값으로 — 목록을 다시 받지 않는다
			this.forceUpdate();
		}).catch(() => { this.cherishSpecBusy = false; PS.alert('특기를 정하지 못했어요.'); this.forceUpdate(); });
	};
	/**
	 * 상세의 특기 칸 — 다 자란 내 아이만. 여섯 칸 중 하나를 누르면 확인창 뒤 특기가 된다.
	 * 굳은 칸은 누를 수 없다(서버도 stat_frozen 으로 막는다). 굳힘 풀기(체리)는 디스코드에만 둔다.
	 */
	renderCherishSpecialty(p: any) {
		const g = p.growth;
		if (!g || !g.fullyGrown || p.borrowed) return null;
		const frozen: any[] = g.frozen || [];
		const head = g.settled
			? '어엿하게 자랐어요. 이제 더 바뀌지 않아요'
			: (g.specialty
				? `지금 특기는 ${g.specialtyKo}, ${g.specialtyStage?.label || ''} ${g.specialtyStage?.emoji || ''}`
				: '아직 특기를 정하지 않았어요');
		const btn = 'font-size:11.5px;padding:4px 10px;box-shadow:none;font-weight:700;opacity:1;';
		return <div style="margin-top:10px">
			<div style="font-size:12px;font-weight:bold">특기 <small style={`font-weight:normal;color:${g.specialty || g.settled ? '#68707c' : '#e11d48'}`}>{head}</small></div>
			{g.settled ? null : <p style="font-size:11px;margin:2px 0 6px;color:#68707c;line-height:1.4">특기로 정한 능력은 줄지 않고 자라기만 해요. 바꾸면 전에 정한 특기는 그 자리에서 굳어요</p>}
			<div style="display:flex;flex-wrap:wrap;gap:4px;margin-top:4px">
				{['hp', 'atk', 'def', 'spa', 'spd', 'spe'].map((k: string) => {
					const f = frozen.find((x: any) => x.stat === k);
					const on = g.specialty === k;
					const full = f ? this.cherishSpecFull(p, f) : false;
					const style = btn + (f
						? (full ? 'border-color:#f59e0b;background:#fde68a;color:#78350f;cursor:default' : 'border-color:#c9ced6;background:#eef0f3;color:#4b5563;cursor:default')
						: (on ? 'border-color:#e11d48;background:#e11d48;color:#fff;text-shadow:none;cursor:default' : 'border-color:#e11d48;background:#fff;color:#be123c'));
					const title = f ? (full ? '특기가 몸에 뱄어요' : '특기를 바꿀 때 그 자리에서 굳었어요') : (on ? '지금 특기예요' : (g.specialty ? '누르면 이 능력으로 바꿔요' : '누르면 이 능력을 특기로 정해요'));
					return <button class="button" style={style} title={title}
						disabled={!!f || on || this.cherishSpecBusy}
						onClick={() => this.pickCherishSpecialty(p, k)}
					>{f && full ? '✨ ' : ''}{on ? `${g.specialtyStage?.emoji || ''} ` : ''}{this.cherishSpecKo(k)}</button>;
				})}
			</div>
			{frozen.length ? <p style="font-size:11px;margin:4px 0 0;color:#8a8f98">굳은 특기는 디스코드 /포켓몬 에서 풀 수 있어요 ({(g.unfreezeCost || 5000).toLocaleString('ko-KR')}체리)</p> : null}
		</div>;
	}
	cherishSlotLabel(i: number) { return i < 6 ? `출전 ${i + 1}` : `후보 ${i - 5}`; }
	cherishMonName(p: any) { return (p.shiny ? '✨ ' : '') + (p.nickname || p.koName || p.speciesName); }
	/**
	 * 상세 패널의 9칸 자리표 — 칸을 누르면 이 아이가 거기 들어간다 (2026-09-22, 종전 6칸).
	 * 1~3 은 어디서나 나가고 4~6 은 6대6 에서 더 나간다. 둘 다 라벨은 「출전」이고 **색으로만**
	 * 가른다(사용자 확정 — 구역 제목은 넣지 않는다). 7~9 는 후보다.
	 */
	renderCherishTeamSlots(p: any) {
		const mons: any[] = this.cherishPokemon || [];
		const ids = this.cherishTeamIds;
		const mine = ids.indexOf(p.id);
		return <div class="cw-slots">
			<div class="cw-slots-head"><b>「{p.nickname || p.koName || p.speciesName}」 자리 정하기</b><small>{mine >= 0 ? `지금 ${this.cherishSlotLabel(mine)}, 다른 칸을 누르면 자리를 바꿔요` : '칸을 누르면 그 자리에 들어가요'}</small></div>
			<div class="cw-slots-row">
				{[0, 1, 2, 3, 4, 5, 6, 7, 8].map(i => {
					const id = ids[i];
					const m = id ? mons.find((x: any) => x.id === id) : null;
					const dead = !id && i > ids.length; // 앞 빈 칸부터 채워지므로 그 뒤는 못 고른다
					const cls = `cw-slot${i < 3 ? ' is-out' : i < 6 ? ' is-out2' : ' is-sub'}${id === p.id ? ' is-me' : ''}${!id ? ' is-empty' : ''}${dead ? ' is-dead' : ''}${m && (m.inDaycare || m.rentedOut) ? ' is-away' : ''}`;
					return <button class={cls} disabled={dead || id === p.id} onClick={() => this.cherishSetTeamSlot(p.id, i)} title={m ? this.cherishMonName(m) + (m.inDaycare ? ' · 어린이집' : m.rentedOut ? ' · 빌려주는 중' : '') : ''}>
						<small>{this.cherishSlotLabel(i)}</small>
						{/* 특별폼 그림은 custom-form 의 모듈 함수를 블록 주인인 여기가 직접 부른다 (2026-10-17, typeof 가드) */}
						{m ? <img src={typeof cherishMonArt === 'function' ? cherishMonArt(m) : `/cherish/sprite/${m.shiny ? 'shiny/' : ''}${m.spriteId}.png?v=2`} data-cwfb={typeof cherishMonArtBase === 'function' ? cherishMonArtBase(m) : ''} onError={typeof cherishMonArtFb === 'function' ? cherishMonArtFb : undefined} width="36" height="36" alt="" /> : <i>{dead ? '·' : '빈 자리'}</i>}
						<b>{m ? (m.nickname || m.koName || m.speciesName) : ''}</b>
					</button>;
				})}
			</div>
			{mine >= 0 ? <button class="button cw-slots-remove" onClick={() => this.cherishRemoveFromTeam(p.id)}>명단에서 빼기</button> : null}
		</div>;
	}
	/** 그리드 위 명단 스트립 + 정렬 세그먼트 (마크업 patch-mon-detail.py, 외형 cherish-skin.css §21) */
	setCherishSort = (mode: string) => {
		this.cherishSortMode = mode;
		try { localStorage.setItem('cw-mon-sort', mode); } catch {}
		this.forceUpdate();
	};
	cherishSortedMons(mons: any[]): any[] {
		const mode = this.cherishSortMode;
		const ids = this.cherishTeamIds;
		const nm = (p: any) => p.nickname || p.koName || p.speciesName || '';
		const dex = (p: any) => { const s: any = Dex.species.get(p.spriteId || p.speciesName); return s?.num || 9999; };
		const arr = mons.slice();
		if (mode === 'name') arr.sort((a, b) => nm(a).localeCompare(nm(b), 'ko'));
		else if (mode === 'dex') arr.sort((a, b) => dex(a) - dex(b));
		else if (mode === 'team') arr.sort((a, b) => { const ia = ids.indexOf(a.id), ib = ids.indexOf(b.id); return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib); });
		else if (mode === 'shiny') arr.sort((a, b) => (b.shiny ? 1 : 0) - (a.shiny ? 1 : 0));
		// cherish-pin: 곁에 두는 아이는 어느 정렬에서도 맨 앞 (0317). 안정 정렬이라
		// 핀 그룹 안 순서는 위에서 고른 모드 그대로다 (획득순이면 서버가 준 최근 지정 순).
		arr.sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0));
		// 빌린 아이(사제·빌려주기)는 핀보다도 앞 — 「내 포켓몬 맨 앞」 (0477, 2026-10-16 사용자 결정). 서버도 맨 앞에 실어 보낸다
		arr.sort((a, b) => (b.borrowed ? 1 : 0) - (a.borrowed ? 1 : 0));
		return arr; // got(획득순) = 서버 순서 그대로. sort 는 안정 정렬이라 동점은 획득순 유지
	}
	/** 그리드 위 도구 줄 — 지금은 정렬뿐 (마크업 patch-mon-detail.py, 외형 cherish-skin.css §21).
	 *
	 * 2026-09-21: 여기 있던 **6칸 명단 스트립을 걷었다.** 상세의 「자리 정하기」와 칸 모양이 같아
	 * 「1~6번 칸이 위에도 아래에도 있다」는 말을 두 번 들었다 — 2026-09-07 에는 제목만 갈라 두었는데
	 * (위 「지금 나가는 순서」/아래 「{이름} 자리 정하기」) 그걸로는 안 풀렸다. 지금 누가 나가는지는
	 * 카드의 「출전 1~3」·「후보 1~3」 배지와 상세의 자리표가 말해 준다.
	 * `mons` 는 이제 안 쓰지만 호출부 시그니처는 그대로 둔다. */
	renderCherishRosterBar(mons: any[]) {
		const seg = (mode: string, label: string) => <button class={`cw-seg-btn${this.cherishSortMode === mode ? ' on' : ''}`} onClick={() => this.setCherishSort(mode)}>{label}</button>;
		// 「특기 정할 아이」 칩 (2026-10-16) — 79마리 사이에서 뱃지만 보고 찾게 두지 않는다. 대상이 없으면 안 그린다
		const specOn = !!this.cherishSpecOnlyIds;
		const specN = (mons || []).filter((p: any) => this.cherishNeedsSpec(p)).length;
		const learnEvery = this.cherishBattleLearn?.every;
		const learnOn = typeof learnEvery === 'number' && learnEvery > 0;
		// 어린이집 새내기 배율 (2026-10-28, api battleLearn.daycare) — 배율 값은 안 쓴다 (수치 비노출)
		const daycareOn = !!this.cherishBattleLearn?.daycare;
		return <div class="cw-roster">
			{/* 새내기 안내 (2026-10-27) — 뉴비에게만 (api battleLearn). 외형 cherish-learn.css. 배틀 문장은 사용자 검수본 */}
			{learnOn || daycareOn ? <p class="cw-learn-banner"><b>새내기 혜택</b><span>{learnOn ? `배틀 ${learnEvery}판마다 명단에 있던 아이들이 새 기술을 하나씩 배워 와요.` : ''}{learnOn && daycareOn ? ' ' : ''}{daycareOn ? '어린이집에서도 평소보다 훨씬 빨리 자라요.' : ''}</span></p> : null}
			<div class="cw-roster-sort"><span class="lab">정렬</span><div class="cw-seg cw-seg-sm">{seg('got', '획득순')}{seg('name', '이름')}{seg('dex', '도감')}{seg('team', '명단 우선')}{seg('shiny', '특별한 색 우선')}</div></div>
			{specOn || specN > 0 ? <div style="margin-top:6px">
				<button class="button" onClick={this.toggleCherishSpecOnly}
					style={`font-size:11.5px;padding:4px 11px;box-shadow:none;font-weight:700;border-radius:999px;${specOn ? 'border-color:#e11d48;background:#e11d48;color:#fff;text-shadow:none' : 'border:1px dashed #e11d48;background:#fff;color:#be123c'}`}
				>{specOn ? `특기 정할 아이만 보는 중 (${(this.cherishSpecOnlyIds || []).length}) · 전부 보기` : `특기를 정할 수 있는 아이 ${specN}`}</button>
			</div> : null}
		</div>;
	}
	/**
	 * 상세가 앉는 자리 (2026-09-21). 넓은 화면에서는 목록 오른쪽에 **붙어서 같이 내려간다**
	 * (cherish-skin.css §34 의 sticky).
	 *
	 * 그전에는 상세가 그리드 **전체 아래**에 그려졌다. 79마리를 가진 사람이 맨 위 카드를 눌러도
	 * 79장을 다 지나야 기술·자리표가 나왔고, 「곁에 두기」로 카드를 앞으로 끌어와도 상세는 여전히
	 * 맨 밑이라 그걸로는 안 풀린다. 폰은 2026-09-05 에 §20 바텀 시트로 이미 풀었고 여기는 PC 몫이다.
	 *
	 * 아무것도 안 골랐을 때도 **빈 자리를 남겨 둔다**. 자리를 접으면 목록이 그 폭까지 퍼졌다가
	 * 카드를 누르는 순간 열 수가 줄어 카드가 통째로 다시 깔린다 (방금 누른 카드가 눈앞에서 움직인다).
	 */
	renderCherishMonDock() {
		const detail = this.renderCherishMonDetail();
		return <div class={`cw-mondock${detail ? ' is-open' : ''}`}>
			{detail || <div class="cw-mondock-hint">
				<b>포켓몬을 눌러 보세요</b>
				<small>고른 아이의 기술과 배틀 자리를 여기에서 정해요.</small>
			</div>}
		</div>;
	}
	/** 선택한 카드 아래 상세 패널 — 배틀 팀·이름·파트너·일지·기술 관리가 전부 여기. */
	renderCherishMonDetail() {
		const p = (this.cherishPokemon || []).find((m: any) => m.id === this.cherishSelectedMonId);
		if (!p) return null;
		const slot = this.cherishTeamIds.indexOf(p.id);
		const base = p.koName || p.speciesName;
		const name = p.nickname || base;
		const noRename = p.canRename === false;
		return <div class="cw-card cw-mon-detail" style="margin-top:10px;max-width:none">
			{/* cw-sheet-close: 모바일 바텀 시트 닫기 (PC 에선 CSS 로 숨김). 같은 아이를 다시 고르면 닫힌다 */}
			<button class="cw-sheet-close" aria-label="닫기" onClick={() => this.selectCherishMon(p)}>&times;</button>
			<div class="cw-md-head">
				<span class="cw-md-art" style={`--glow:${this.cherishTypeColor((p.types || [])[0])}`}><img src={typeof cherishMonArt === 'function' ? cherishMonArt(p) : `/cherish/sprite/${p.shiny ? 'shiny/' : ''}${p.spriteId}.png?v=2`} data-cwfb={typeof cherishMonArtBase === 'function' ? cherishMonArtBase(p) : ''} onError={typeof cherishMonArtFb === 'function' ? cherishMonArtFb : undefined} width="96" height="96" /></span>
				<div class="cw-md-body">
					{p.epithet ? <span class="cw-md-epithet">&#x300C;{p.epithet}&#x300D;</span> : null}
					<div class="cw-md-name">{p.shiny ? <svg class="cw-shiny-ic" viewBox="0 0 24 24" aria-hidden><path d="M12 2l2.4 6.6L21 11l-6.6 2.4L12 20l-2.4-6.6L3 11l6.6-2.4z" /></svg> : null}{name}{p.nickname ? <small>{base}</small> : null}</div>
					<div class="cw-md-chips">
						{(p.types || []).map((t: string) => <span dangerouslySetInnerHTML={{ __html: Dex.getTypeIcon(t) }} />)}
						<span class="cw-chip">특성 <b>{p.abilityKo}</b>{p.abilityHidden ? ' (숨은 특성)' : ''}</span>
						{p.careIvTotal > 0 ? <span class="cw-chip is-love">애정의 힘 +{p.careIvTotal}</span> : null}
						{p.inDaycare ? <span class="cw-chip is-away">어린이집 등원 중</span> : null}
						{p.rentedOut ? <span class="cw-chip is-away" title="돌아올 때까지 배틀, 어린이집, 온실, 교환은 쉬어요">{this.cherishLentNote(p)}</span> : null}
					</div>
					{/* 특성 설명 한 줄 (2026-10-07) — 이름만으로는 무슨 일을 하는지 몰랐다. 스킨 패치를 안 건드리려 인라인 style */}
					{this.cherishAbilityDesc(p.ability || '') ? <p style="margin:4px 0 0;font-size:9pt;line-height:1.45;color:var(--cw-ink-muted, #a8637a);word-break:keep-all">{this.cherishAbilityDesc(p.ability || '')}</p> : null}
					{/* 빌린 아이(0477): 이름·칭호·파트너·곁에 두기·기술은 주인 것 — 자리 정하기와 일지 보기만 */}
					{p.borrowed ? <div class="cw-md-chips"><span class="cw-chip">🤝 {p.borrowedFromName || '빌린'} 님의 아이</span><span class="cw-chip">기술은 주인이 정한 대로 나가요</span><button class="button cw-act" onClick={() => window.open(`https://cherish-world.com/pokemon/album/${p.id}`, '_blank', 'noopener')}>일지 보기</button></div> : <div class="cw-md-actions">
						{/* 배틀 팀 넣기/빼기 버튼은 자리표(renderCherishTeamSlots)로 대체 (2026-09-05) */}
						<button
							class="button cw-act" disabled={noRename}
							title={noRename ? '받은 아이는 처음 지어준 이름 그대로예요' : ''}
							onClick={() => this.editCherishNickname(p)}
						><svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden><path d="M4 20h4l10.5-10.5a2.1 2.1 0 0 0-3-3L5 17z" /><path d="M13.5 7.5l3 3" /></svg> {p.renameCost > 0 ? `이름 바꾸기 (${p.renameCost.toLocaleString('ko-KR')}체리)` : '이름 짓기'}</button>
						{/* cherish-nick-offer: 받은 아이는 위 버튼이 잠겨 있다. 대신 첫 부모에게 이름을 부탁할 수 있다.
						    **호출은 블록 주인인 여기가 들고 있는다** (2026-09-19 규약) — 저쪽 패치가 이 블록 안에
						    끼워 넣었다가 이 패치 재적용에 조용히 지워져 09-10 이후 버튼이 안 보였다.
						    메서드는 patch-nickname-offer.py 것이라 typeof 가드를 끼고 부른다. */}
						{noRename && typeof this.cherishNickOfferFor === 'function' ? (() => {
							const o = this.cherishNickOfferFor(p.id);
							return <button
								class="button cw-act" disabled={!!o}
								title={o ? '답을 기다리는 중이에요' : '처음 만난 트레이너에게 이름을 부탁해요'}
								onClick={() => this.cherishNickAsk(p)}
							>{o ? '이름 이야기 중' : '이름 부탁하기'}</button>;
						})() : null}
						<button class={`button cw-act${this.cherishEpithetOpen === p.id ? ' is-on' : ''}`} onClick={() => this.toggleCherishEpithets(p)}><svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden><circle cx="12" cy="15" r="6" /><path d="M8.5 9.6L6 2.5h12L15.5 9.6" /></svg> 칭호</button>
						<button class={`button cw-act${p.isPartner ? ' is-on' : ''}`} onClick={() => this.toggleCherishPartner(p)}><svg viewBox="0 0 24 24" width="13" height="13" aria-hidden><path d="M12 21s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 11c0 5.6-7 10-7 10z" fill="currentColor" /></svg> {p.isPartner ? '파트너 해제' : '파트너로 지정'}</button>
						{/* cherish-pin: 목록·등원 드롭다운 맨 앞으로. 다시 두면 맨 앞 (0317) */}
						<button class={`button cw-act${p.pinned ? ' is-on' : ''}`} title="목록과 어린이집 드롭다운에서 맨 앞으로 와요" onClick={() => this.toggleCherishPin(p)}>🧡 {p.pinned ? '곁에 두지 않기' : '곁에 두기'}</button>
						<button class="button cw-act" onClick={() => window.open(`https://cherish-world.com/pokemon/album/${p.id}`, '_blank', 'noopener')}><svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden><path d="M4 5a2 2 0 0 1 2-2h13v16H6a2 2 0 0 0-2 2z" /><path d="M4 19a2 2 0 0 1 2-2h13" /></svg> 일지 보기</button>
					</div>}
				</div>
			</div>
			{p.borrowed ? null : this.renderCherishEpithets(p)}
			{this.renderCherishSpecialty(p)}
			{this.renderCherishTeamSlots(p)}
			{p.borrowed ? null : this.renderCherishMoveEditorInline(p)}
		</div>;
	}
	/**
	 * 기술 목록 — **항상 펼쳐** 두고, 누르면 바로 장착/해제된다 (0.6초 뒤 자동 저장).
	 * 접어두면 뉴비는 기술을 골라 끼운다는 것 자체를 모른 채 지나간다 (2026-09-05).
	 * 순서는 패널을 열 때 잡은 스냅샷 그대로 — 클릭할 때마다 다시 정렬하면 방금 누른 줄이 위로
	 * 튀고 아래 줄이 커서 밑으로 올라와 연타 시 엉뚱한 기술을 건드리게 된다.
	 * 상태·저장은 patch-daycare-moves.py 것.
	 *
	 * **숨기기** (2026-09-10): 해금이 20개를 넘어가면 지금 쓸 기술이 목록에 묻힌다.
	 * 안 쓸 기술은 「숨기기」로 아래 「숨긴 기술 N개 보기」 칸에 접어둔다.
	 * 두 칸을 가르는 기준은 **패널을 열 때의 스냅샷**(cherishHiddenAtOpen)이라, 방금 접은 줄은
	 * 사라지지 않고 그 자리에 축약 표시 + 「되돌리기」로 남는다. 즉시 지우면 아래 줄이 커서 밑으로
	 * 올라와 연달아 접을 때 엉뚱한 기술을 접는다 (정렬 스냅샷과 같은 이유).
	 * 장착 중인 기술에는 숨기기 버튼을 주지 않는다 — 목록에서 사라지면 해제할 길이 없어진다.
	 */
	renderCherishMoveEditorInline(p: any) {
		const mine = this.cherishMoveDraftId === p.id;
		const draft: string[] = mine ? this.cherishMoveDraft : (p.moves || []);
		const order: string[] = mine && this.cherishMoveOrder.length ? this.cherishMoveOrder : this.cherishMoveSorted(p);
		const curated: string[] = p.curatedMoves || [];
		// 온실 기술 (2026-09-30) — 온실에서 태어날 때만 배우는 기술. 어디서 왔는지 모르면 뉴비는 못 알아본다.
		const greenhouse: string[] = p.greenhouseMoves || [];
		// 두 칸을 가르는 건 패널을 열 때의 스냅샷 — 접는 즉시 줄이 옮겨가면 다음 클릭이 어긋난다.
		const atOpen: string[] = mine ? this.cherishHiddenAtOpen : (p.hiddenMoves || []);
		const equipped: string[] = p.moves || [];
		const hideable = atOpen.filter((m: string) => !equipped.includes(m));
		const shown = order.filter((m: string) => !hideable.includes(m));
		const folded = order.filter((m: string) => hideable.includes(m));
		const note = mine ? this.cherishMoveNote : '';
		const status = mine ? this.cherishMoveStatus : '';
		const lean = this.cherishAtkLean(p);
		// 상세를 열 때 잡은 새 기술 (selectCherishMon) — 열자마자 「봤음」이 되므로 그 스냅샷으로 칩을 그린다
		const fresh: string[] = this.cherishFreshShow?.id === p.id ? this.cherishFreshShow.moves : [];
		return <div style="margin-top:10px">
			<div style="font-size:12px;font-weight:bold">
				기술 <small style="font-weight:normal;color:#68707c">장착 {draft.length}/4, 해금 {order.length}개, 추천 = 잘 어울리는 기술{greenhouse.length ? ', 온실 = 온실에서 태어날 때만 배우는 기술' : ''}</small>
				{status ? <small style="font-weight:normal;color:#3aa76d">, {status}</small> : null}
			</div>
			<p style="font-size:11px;margin:2px 0 4px;color:#68707c">
				{note
					? <span style="color:#e0525f">{note}</span>
					: (order.length > 8
						? '누르면 바로 장착돼요. 안 쓸 기술은 「숨기기」로 아래에 접어둘 수 있어요'
						: '누르면 바로 장착돼요. 새 기술은 어린이집에서 배울 수 있어요')}
			</p>
			{this.renderCherishMovePresets(p)}
			{lean.lean ? <p style="font-size:11px;margin:0 0 6px;color:#68707c;line-height:1.4">
				{lean.lean === 'even'
					? '이 아이는 물리공격과 특수공격이 비슷한 편이에요'
					: <span>이 아이는 <b style={`color:${lean.lean === 'physical' ? '#C23616' : '#3B5BA5'}`}>{lean.lean === 'physical' ? '물리공격' : '특수공격'}</b>이 더 강한 편이에요</span>}
			</p> : null}
			{shown.map(mvName => this.renderCherishMoveRow(mvName, draft, curated, mine, greenhouse, fresh))}
			{folded.length ? <div style="margin-top:6px">
				<button class="button" style="font-size:11px;padding:3px 10px;color:#68707c" onClick={this.toggleCherishHiddenSection}>
					{this.cherishHiddenOpen ? '▾' : '▸'} 숨긴 기술 {folded.length}개 {this.cherishHiddenOpen ? '접기' : '보기'}
				</button>
				{this.cherishHiddenOpen ? <div>{folded.map(mvName => this.renderCherishMoveRow(mvName, draft, curated, mine, greenhouse, fresh))}</div> : null}
			</div> : null}
		</div>;
	}
	/**
	 * 기술 세트 프리셋 (0380, 2026-09-16) — 기술 목록 **바로 위** 한 줄.
	 *
	 * 1대1 과 2대2 는 쓸 기술이 달라서 방을 옮길 때마다 네 칸을 손으로 갈아 끼워야 했다.
	 * 담아두고 칸을 누르면 한 번에 바뀐다.
	 *
	 * 칸마다 버튼을 셋씩(불러오기·덮어쓰기·비우기) 두면 이 패널에서 기술 목록이 화면 밖으로
	 * 밀린다. 그래서 **「담기」 모드 하나로 칸 버튼의 뜻만 바꾼다** — 평소엔 불러오기,
	 * 담기 중엔 그 칸에 담기(덮어쓰기 포함). 빈 칸만은 모드와 무관하게 담기다.
	 * 상태·저장은 patch-daycare-moves.py 것.
	 *
	 * 진화로 기술이 잘려 무효해진 칸은 회색으로 잠그되 **지우지 않는다** — 무엇을 담아뒀는지가
	 * 남아야 되채운다. 담기 모드에서는 그 칸도 덮어쓸 수 있다.
	 */
	renderCherishMovePresets(p: any) {
		if (this.cherishMoveDraftId !== p.id) return null;
		const presets = this.cherishPresets(p);
		const mode = this.cherishPresetMode;
		const note = this.cherishPresetNote;
		const status = this.cherishPresetStatus;
		return <div style="margin:0 0 6px">
			<div style="display:flex;flex-wrap:wrap;gap:4px;align-items:center">
				{presets.map((moves, i) => {
					const playable = this.cherishPresetPlayable(p, moves);
					const empty = moves.length === 0;
					// 담기 모드에서는 세 칸이 전부 「여기 담기」라 잠긴 칸도 눌러야 한다
					const dim = !mode && !empty && !playable;
					const style = 'font-size:11px;padding:4px 9px;box-shadow:none;font-weight:700;'
						+ (mode
							? 'border-color:#e11d48;background:#fff;color:#be123c'
							: (dim || empty
								? 'border-color:#c9ced6;background:#eef0f3;color:#8a8f98'
								: 'border-color:#c9ced6;background:#fff;color:#374151'));
					const tail = mode ? '여기 담기' : (empty ? '비어 있음' : (playable ? `${moves.length}개` : '못 씀'));
					return <button
						key={i} class="button" style={style}
						title={empty ? '' : moves.map((m: string) => CherishKo.move(Dex.moves.get(m).name)).join(', ')}
						onClick={() => this.tapCherishPreset(p, i)}
					>세트{i + 1} {tail}</button>;
				})}
				<button class="button" style="font-size:11px;padding:4px 9px;box-shadow:none;color:#68707c" onClick={this.toggleCherishPresetMode}>
					{mode ? '취소' : '담기'}
				</button>
			</div>
			<p style="font-size:11px;margin:3px 0 0;color:#68707c">
				{note
					? <span style="color:#e0525f">{note}</span>
					: (status
						? <span style="color:#3aa76d">{status}</span>
						: (mode
							? '지금 기술을 담을 칸을 눌러주세요'
							: '자주 쓰는 기술 조합을 담아두고 눌러서 한 번에 바꿔요'))}
			</p>
		</div>;
	}
	/**
	 * 기술 한 줄 — 접힌 상태면 축약 표시로 바꾼다. 두 칸(보이는 목록 / 숨긴 기술) 모두 같은 규칙이라
	 * 「숨긴 기술」 칸에서 되돌린 줄도 그 자리에서 곧바로 온전한 줄(설명·장착)로 펼쳐진다.
	 * 장착 중인 기술은 숨김 목록에 남아 있어도 접힌 것으로 보지 않는다 — 어린이집 자동 장착이
	 * 접어둔 기술을 끼웠을 수 있고, 그때 회색 축약으로 보이면 해제할 길이 안 보인다.
	 */
	renderCherishMoveRow(name: string, draft: string[], curated: string[], mine: boolean, greenhouse: string[] = [], fresh: string[] = []) {
		const on = draft.includes(name);
		if (mine && !on && this.cherishHiddenDraft.includes(name)) {
			const mv = Dex.moves.get(name);
			// 아직 위 목록에 남아 있는 줄이면 「곧 내려간다」를 말해 준다 — 처음 쓰는 사람에게는
			// 설명이 사라진 것만 보여 이 버튼이 왜 있는지 알 수 없다 (2026-09-11 사용자 지적).
			const settling = !this.cherishHiddenAtOpen.includes(name);
			return <div style="display:flex;gap:8px;align-items:center;padding:4px 0;border-top:1px solid rgba(0,0,0,.06)">
				<button class="button" style={`${this.cherishMoveBtnStyle('hide')};flex:none`} onClick={() => this.toggleCherishHiddenMove(name)}>{this.cherishIcEye(false)} 되돌리기</button>
				<div style="flex:1;min-width:0;font-size:12px;color:#8a8f98;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{CherishKo.move(mv.name)}{settling ? ' · 아래 「숨긴 기술」로 내려가요' : ' · 숨김'}</div>
			</div>;
		}
		return this.renderCherishMoveLine(name, {
			star: curated.includes(name), egg: greenhouse.includes(name), fresh: fresh.includes(name), on,
			onToggle: () => this.toggleCherishMove(name),
			onHide: mine && !on ? () => this.toggleCherishHiddenMove(name) : undefined,
		});
	}
	// cherish-mon-detail:end
	// cherish-special-items:begin (patch-special-items.py)
	/** GET /showdown/special-items 응답. null = 아직 안 받음. */
	cherishSpecial: any = null;
	cherishSpecialFor = '';
	/** 펼쳐 둔 물건 id. '' = 접힘. */
	cherishSpecialSel = '';
	/** 고른 아이 id. '' = 아직. */
	cherishSpecialMon = '';
	cherishSpecialBusy = false;
	cherishSpecialNotice = '';
	cherishBoxStage: any = null;
	cherishBoxJosa(name: string, consonant: string, vowel: string) {
		const last = name.charCodeAt(name.length - 1) - 0xac00;
		return name + (last >= 0 && last <= 11171 && last % 28 !== 0 ? consonant : vowel);
	}
	/** 신비한 상자 (2026-10-26) — 펼쳐 둔 무리 ('' = 전부)와 찾기 글자. */
	cherishSpecialGroup = '';
	cherishSpecialQuery = '';
	loadCherishSpecial(force?: boolean) {
		if (!PS.user.named) return;
		if (!force && this.cherishSpecialFor === PS.user.userid) return;
		this.cherishSpecialFor = PS.user.userid;
		fetch('https://cherish-world.com/api/showdown/special-items', { credentials: 'include' })
			.then(r => r.json())
			.then(json => {
				this.cherishSpecial = json?.ok ? json.data : { items: [], targets: [] };
				this.forceUpdate();
			})
			.catch(() => {});
	}
	cherishSpecialPick(itemId: string) {
		if (this.cherishSpecialBusy) return;
		this.cherishBoxStage = null;
		this.cherishSpecialSel = this.cherishSpecialSel === itemId ? '' : itemId;
		this.cherishSpecialMon = '';
		this.cherishSpecialGroup = '';
		this.cherishSpecialQuery = '';
		this.cherishSpecialNotice = '';
		this.forceUpdate();
	}
	cherishSpecialPickMon(monId: string) {
		this.cherishSpecialMon = this.cherishSpecialMon === monId ? '' : monId;
		this.forceUpdate();
	}
	/**
	 * 특성 설명 한 줄 (2026-10-07) — 이름만 보고 고르던 자리다. patch-mon-detail.py 의 cherishAbilityDesc 와
	 * 같은 규칙(ko desc 만, 한글 아니면 '')이지만 그쪽에 기대지 않으려 따로 둔다 (패치 적용 순서 무관).
	 */
	cherishSpAbilityDesc(name: string): string {
		const e = (window as any).BattleText?.ko?.Abilities?.[toID(name)];
		const d = String(e?.desc || '');
		return /[가-힣]/.test(d) ? d : '';
	}
	/**
	 * 쓰기 — 확인창 한 번. 되돌릴 수 없다는 말과, 숨은 특성을 잃는 경우를 여기서 말한다.
	 * `ability` 는 지목(깨우는 종)일 때만 온다. 무작위(`pick: 'random'`, 지금은 없음)면 null 이고 서버가 굴린다.
	 */
	useCherishSpecial(item: any, target: any, ability: any) {
		if (this.cherishSpecialBusy) return;
		const isShiny = item.effect === 'shiny';
		const cands: any[] = target.candidates || [];
		const to = ability ? `${ability.ko}(으)로`
			: cands.length === 1 ? `${cands[0].ko}(으)로`
			: cands.map((c: any) => c.ko).join(', ') + ' 중 하나로';
		// 색 물건 (2026-10-13): 특성이 아니라 색이다. 고를 것도, 잃을 숨은 특성도 없다.
		let ask = isShiny ? `${target.nameKo}을(를) 색이 다른 아이로 만들까요?` : `${target.nameKo}의 특성을 ${to} 바꿀까요?`;
		if (!isShiny && !ability && cands.length > 1) ask += '\n어느 쪽이 될지는 무작위예요.';
		ask += `\n${item.confirm}`;
		// 숨은 특성은 어느 물건으로든 잃는다 — 물건마다 두지 않고 여기서 한 번 더 묻는다 (2026-09-28)
		if (!isShiny && target.losesHidden) ask += `\n\n⚠️ 지금 가진 ${target.current ? target.current.ko : '특성'}은(는) 숨겨진 특성이에요. 바꾸면 사라지는데, 그래도 바꿀까요?`;
		if (!window.confirm(ask)) return;
		this.cherishSpecialBusy = true;
		this.cherishSpecialNotice = '';
		this.forceUpdate();
		fetch('https://cherish-world.com/api/showdown/special-items/use', {
			method: 'POST', credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ itemId: item.id, pokemonId: target.pokemonId, ability: ability ? ability.name : null }),
		})
			.then(r => r.json())
			.then(json => {
				this.cherishSpecialBusy = false;
				if (json?.ok) {
					const d = json.data || {};
					const res = d.result || {};
					this.cherishSpecial = { items: d.items || [], targets: d.targets || [], targetsByItem: d.targetsByItem || null, itemChoices: d.itemChoices || null };
					this.cherishSpecialMon = '';
					if (!(d.items || []).some((x: any) => x.id === item.id)) this.cherishSpecialSel = '';
					this.cherishSpecialNotice = (isShiny || res.shiny)
						? `${res.nameKo || target.nameKo}이(가) 색이 다른 아이가 되었어요. ✨`
						: `${res.nameKo || target.nameKo}의 특성이 ${res.after ? res.after.ko : ability ? ability.ko : '새 특성'}(으)로 바뀌었어요.`;
					// 내 포켓몬 목록의 특성 표시도 낡는다 — 다음에 열 때 새로 받게 표시만 비운다
					this.cherishLoadedFor = '';
				} else {
					this.cherishSpecialNotice = json?.message || '쓰지 못했어요. 잠시 후 다시 해 주세요.';
				}
				this.forceUpdate();
			})
			.catch(() => { this.cherishSpecialBusy = false; this.cherishSpecialNotice = '연결이 불안정해요. 잠시 후 다시 해 주세요.'; this.forceUpdate(); });
	}
	/**
	 * 신비한 상자 열기 (2026-10-26) — 아이 없이 도구 하나를 골라 인벤토리로. 확인창 한 번.
	 * 전용 도구면 누구 전용인지 한 번 더 말한다 (맞는 아이가 없으면 꺼내도 줄 데가 없다).
	 * `choice` 가 null 이면 두근두근 상자(2026-10-29, pick 'random') — 서버가 뽑는다.
	 */
	useCherishBox(item: any, choice: any) {
		if (this.cherishSpecialBusy) return;
		let ask: string;
		if (choice) {
			const only: string[] = choice.speciesOnlyKo || choice.abilityOnlyKo || [];
			ask = `${item.ko}에서 ${choice.ko}을(를) 꺼낼까요?`;
			if (only.length) ask += `\n${only.join(', ')} 전용 도구예요.`;
		} else {
			ask = `${item.ko}를 열까요?`;
		}
		ask += `\n${item.confirm}`;
		if (!window.confirm(ask)) return;
		this.cherishSpecialBusy = true;
		this.cherishSpecialNotice = '';
		this.cherishBoxStage = choice ? null : { count: 3, icon: item.icon, result: null };
		// 1회 지급 요청과 3초 연출을 함께 시작한다. 자동 재요청하지 않는다.
		const opening = choice ? Promise.resolve() : new Promise<void>(resolve => {
			let count = 3;
			const tick = () => {
				count--;
				if (this.cherishBoxStage) this.cherishBoxStage.count = count;
				this.forceUpdate();
				if (count > 0) window.setTimeout(tick, 1000);
				else resolve();
			};
			window.setTimeout(tick, 1000);
		});
		this.forceUpdate();
		fetch('https://cherish-world.com/api/showdown/special-items/use', {
			method: 'POST', credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ itemId: item.id, heldItemId: choice ? choice.id : null }),
		})
			.then(r => r.json())
			.then(json => opening.then(() => json))
			.then(json => {
				this.cherishSpecialBusy = false;
				if (json?.ok) {
					const d = json.data || {};
					const res = d.result || {};
					this.cherishSpecial = { items: d.items || [], targets: d.targets || [], targetsByItem: d.targetsByItem || null, itemChoices: d.itemChoices || null };
					if (!(d.items || []).some((x: any) => x.id === item.id)) this.cherishSpecialSel = '';
					const ko = res.pickedItem ? res.pickedItem.ko : choice ? choice.ko : '도구';
					this.cherishSpecialNotice = choice ? `${this.cherishBoxJosa(ko, '을', '를')} 꺼냈어요. 인벤토리에 있어요.` : `두근두근! ${this.cherishBoxJosa(ko, '이', '가')} 나왔어요. 인벤토리에 있어요.`;
					if (!choice) this.cherishBoxStage = { count: 0, result: res.pickedItem };
					// 위의 도구 인벤토리도 새로 받는다 (patch-item-tab.py 의 메서드 — 없으면 건너뛴다)
					(this as any).fetchCherishInventory?.();
					// 꺼낸 도구를 인벤토리에서 펼쳐 둔다 — 설명이 바로 보이게 (2026-11-01, patch-item-tab.py 의 상태. 없으면 무해)
					const gotId = res.pickedItem ? res.pickedItem.id : choice ? choice.id : '';
					if (gotId) (this as any).cherishInvPick = 'inv:' + gotId;
				} else {
					this.cherishSpecialNotice = json?.message || '꺼내지 못했어요. 잠시 후 다시 해 주세요.';
					this.cherishBoxStage = null;
				}
				this.forceUpdate();
			})
			.catch(() => opening.then(() => {
				this.cherishSpecialBusy = false;
				this.cherishBoxStage = null;
				this.cherishSpecialNotice = '결과를 확인하지 못했어요. 다시 열기 전에 인벤토리를 확인해 주세요.';
				this.loadCherishSpecial(true);
				(this as any).fetchCherishInventory?.();
				this.forceUpdate();
			}));
	}
	/** 신비한 상자의 도구 고르기 — 무리 버튼 + 찾기 칸 + 도구 목록. */
	renderCherishBoxPicker(item: any) {
		const all: any[] = (this.cherishSpecial && this.cherishSpecial.itemChoices) || [];
		if (!all.length) return <div class="cw-empty">고를 수 있는 도구를 불러오는 중이에요.</div>;
		const groups: string[] = [];
		all.forEach((c: any) => { if (groups.indexOf(c.group) < 0) groups.push(c.group); });
		const g = this.cherishSpecialGroup;
		const q = this.cherishSpecialQuery.trim();
		const shown = all.filter((c: any) => (!g || c.group === g) && (!q || c.ko.indexOf(q) >= 0));
		const busy = this.cherishSpecialBusy;
		return <div class="cw-sp-step">
			<div class="cw-items-label">어떤 도구를 꺼낼까요?</div>
			<div class="cw-sp-groups">
				<button class={`cw-sp-group${!g ? ' on' : ''}`} onClick={() => { this.cherishSpecialGroup = ''; this.forceUpdate(); }}>전부</button>
				{groups.map((name: string) => <button class={`cw-sp-group${g === name ? ' on' : ''}`} onClick={() => { this.cherishSpecialGroup = name; this.forceUpdate(); }}>{name}</button>)}
				<input class="textbox cw-sp-find" type="search" placeholder="도구 이름 찾기" value={this.cherishSpecialQuery}
					onInput={(e: any) => { this.cherishSpecialQuery = String(e.currentTarget.value || ''); this.forceUpdate(); }} />
			</div>
			{!shown.length ? <div class="cw-empty">그런 이름의 도구는 없어요.</div> : <div class="cw-sp-picks">
				{shown.map((c: any) => <button class="button cw-sp-pick" disabled={busy} title={c.desc} onClick={() => this.useCherishBox(item, c)}>
					<i class="cw-item-ic" style={Dex.getItemIcon(c.id)} aria-hidden />
					<b>{c.ko}</b>
					{(c.speciesOnlyKo || c.abilityOnlyKo) ? <em>{(c.speciesOnlyKo || c.abilityOnlyKo).join(', ')} 전용</em> : null}
					{c.desc ? <small>{c.desc}</small> : null}
				</button>)}
			</div>}
		</div>;
	}
	/** 두근두근 상자 (2026-10-29) — 고를 게 없다. 여는 버튼 하나. */
	renderCherishRandomBox(item: any) {
		return <div class="cw-sp-step">
			<div class="cw-sp-abils">
				<button class="button cw-sp-abil" disabled={this.cherishSpecialBusy} onClick={() => this.useCherishBox(item, null)}>
					<b>상자 열기</b>
				</button>
			</div>
		</div>;
	}
	/** 인벤토리 탭 안의 특별 물건 칸. 물건이 없으면 아무것도 안 그린다. */
	renderCherishSpecialItems() {
		if (!PS.user.named) return null;
		this.loadCherishSpecial();
		const data = this.cherishSpecial;
		const items: any[] = (data && data.items) || [];
		if (!items.length && !this.cherishBoxStage && !this.cherishSpecialNotice) return null;
		const selId = this.cherishSpecialSel;
		const item = items.find((i: any) => i.id === selId) || null;
		// 물건마다 쓸 수 있는 아이가 다르다 (2026-09-28 흔드는 방울). targetsByItem 이 없으면 옛 api — 종 기준 targets.
		const byItem = data && data.targetsByItem;
		const targets: any[] = (item && byItem ? byItem[item.id] : data && data.targets) || [];
		const target = targets.find((t: any) => t.pokemonId === this.cherishSpecialMon) || null;
		const busy = this.cherishSpecialBusy;
		// 색 물건 (2026-10-13) — 재주 단계가 없다. 아이 칩엔 특성 대신 종 이름, 숨은 특성 경고 없음.
		const isShiny = !!item && item.effect === 'shiny';
		// 신비한 상자 (2026-10-26) — 아이 단계 없이 도구 고르기만
		const isBox = !!item && item.effect === 'pick_item';
		return <div class="cw-special">
			<div class="cw-items-label">특별한 물건</div>
			{this.cherishBoxStage ? <div class={`cw-box-reveal${this.cherishBoxStage.result ? ' revealed' : ''}`} role="status" aria-live="polite">
				<div class="cw-box-sparks" aria-hidden="true">✦ · ✧ · ✦</div>
				{this.cherishBoxStage.result ? <img class="cw-box-prize" src={`https://cherish-world.com/pokemon/items/${this.cherishBoxStage.result.id}.png`} alt={this.cherishBoxStage.result.ko} width="144" height="144" />
					: <img class="cw-box-shake" src={this.cherishBoxStage.icon} alt="두근두근 상자" width="112" height="112" />}
				<strong>{this.cherishBoxStage.result ? this.cherishBoxStage.result.ko : this.cherishBoxStage.count || '개봉 중…'}</strong>
				<span>{this.cherishBoxStage.result ? '✨ 인벤토리에 넣어 두었어요!' : '두근두근… 어떤 도구가 나올까요?'}</span>
				{/* 나온 도구의 효과 (2026-11-01) — api 결과엔 id·ko 뿐이라 방금 새로 받은 도구 인벤토리에서 찾는다. 받기 전엔 비어 있다. */}
				{(() => {
					const r = this.cherishBoxStage.result;
					const got = r ? (((this as any).cherishItemInv?.items || []) as any[]).find((x: any) => x.id === r.id) : null;
					return got && got.desc ? <p class="cw-box-desc">{got.desc}</p> : null;
				})()}
			</div> : null}
			{this.cherishSpecialNotice ? <p class="cw-note">{this.cherishSpecialNotice}</p> : null}
			<div class="cw-sp-chips">
				{items.map((i: any) => <button class={`cw-sp-chip${i.id === selId ? ' on' : ''}`} onClick={() => this.cherishSpecialPick(i.id)} title={i.desc}>
					<img src={String(i.icon || "").startsWith("http") ? i.icon : "https://cherish-world.com" + i.icon} width="28" height="28" alt="" onError={(e: any) => { e.currentTarget.style.visibility = 'hidden'; }} />
					<b>{i.ko}{i.count > 1 ? ` ×${i.count}` : ''}</b>
				</button>)}
			</div>
			{item ? <div class="cw-sp-open">
				<p class="cw-sp-desc">{item.desc}</p>
				{isBox ? (item.pick === 'random' ? this.renderCherishRandomBox(item) : this.renderCherishBoxPicker(item)) : !targets.length ? <div class="cw-empty">{item.noTarget || '지금은 쓸 수 있는 아이가 없어요. 배울 특성이 더 있는 아이에게만 쓸 수 있어요.'}</div> : <div class="cw-sp-step">
					<div class="cw-items-label">어느 아이에게 쓸까요?</div>
					<div class="cw-sp-mons">
						{targets.map((t: any) => <button class={`cw-sp-mon${t.pokemonId === this.cherishSpecialMon ? ' on' : ''}`} onClick={() => this.cherishSpecialPickMon(t.pokemonId)}>
							<b>{t.nameKo}</b><small>{isShiny ? (t.speciesKo || '') : (t.current ? t.current.ko : '?')}{!isShiny && t.losesHidden ? ' ✨' : ''}</small>
						</button>)}
					</div>
				</div>}
				{isBox ? null : target && isShiny ? <div class="cw-sp-step">
					<div class="cw-items-label">{target.nameKo}을(를) 색이 다른 아이로 만들까요?</div>
					<div class="cw-sp-abils">
						<button class="button cw-sp-abil" disabled={busy} onClick={() => this.useCherishSpecial(item, target, null)}>
							<b>{item.ko} 쓰기</b>
						</button>
					</div>
				</div> : target && item.pick === 'random' ? <div class="cw-sp-step">
					<div class="cw-items-label">{target.nameKo}에게 {item.ko}을 쓸까요?</div>
					{target.losesHidden ? <p class="cw-sp-warn">지금 가진 <b>{target.current ? target.current.ko : '특성'}</b> 는 숨겨진 특성이에요. 바꾸면 사라져요.</p> : null}
					<p class="cw-sp-desc">{(target.candidates || []).length === 1
						? `${target.candidates[0].ko}(으)로 바뀌어요.`
						: `${(target.candidates || []).map((c: any) => c.ko).join(', ')} 중 하나로 바뀌어요.`}</p>
					<div class="cw-sp-abils">
						<button class="button cw-sp-abil" disabled={busy} onClick={() => this.useCherishSpecial(item, target, null)}>
							<b>{item.ko} 쓰기</b>
						</button>
					</div>
				</div> : target ? <div class="cw-sp-step">
					<div class="cw-items-label">{target.nameKo}의 특성을 무엇으로 {item.toHidden === false ? '바꿀까요?' : '깨울까요?'}</div>
					{target.losesHidden ? <p class="cw-sp-warn">지금 가진 <b>{target.current ? target.current.ko : '특성'}</b> 는 숨겨진 특성이에요. 바꾸면 사라져요.</p> : null}
					{target.current && this.cherishSpAbilityDesc(target.current.name) ? <p class="cw-sp-now">지금 <b>{target.current.ko}</b>: {this.cherishSpAbilityDesc(target.current.name)}</p> : null}
					<div class="cw-sp-abils">
						{(target.candidates || []).map((c: any) => <button class="button cw-sp-abil" disabled={busy} onClick={() => this.useCherishSpecial(item, target, c)}>
							<b>{c.ko}</b>{c.hidden ? <em>숨겨진 특성</em> : null}
							{this.cherishSpAbilityDesc(c.name) ? <small>{this.cherishSpAbilityDesc(c.name)}</small> : null}
						</button>)}
					</div>
				</div> : null}
			</div> : null}
		</div>;
	}
	// cherish-special-items:end
	// cherish-items:begin (patch-item-tab.py)
	/** 도구 상점 진열 — 유저당 1회. 닫혀 있으면(open:false) 하위탭 자체가 안 생긴다. */
	fetchCherishItemShop(force = false) {
		if (!PS.user.named) return;
		if (!force && this.cherishItemLoadedFor === PS.user.userid) return;
		this.cherishItemLoadedFor = PS.user.userid;
		this.fetchCherishInventory();
		fetch('https://cherish-world.com/api/showdown/items/shop', { credentials: 'include' })
			.then(r => r.json())
			.then(json => {
				this.cherishItemShop = json?.ok ? json.data : null;
				const tiers: any[] = this.cherishItemShop?.tiers || [];
				if (!this.cherishItemTier || !tiers.some((t: any) => t.tier === this.cherishItemTier)) this.cherishItemTier = tiers[0]?.tier || null;
				this.forceUpdate();
			})
			.catch(() => {});
	}
	/** 인벤토리 — 아직 아무에게도 안 준 도구 (0322). 구매·건네주기 뒤에도 다시 받는다. */
	fetchCherishInventory() {
		if (!PS.user.named) return;
		fetch('https://cherish-world.com/api/showdown/items/inventory', { credentials: 'include' })
			.then(r => r.json())
			.then(json => { this.cherishItemInv = json?.ok ? json.data : null; this.forceUpdate(); })
			.catch(() => {});
	}
	/** 아이 선택 — 가방·추천을 그 아이 기준으로 다시 받는다. */
	selectCherishItemMon(id: string) {
		this.cherishItemSel = id;
		this.cherishItemBag = null;
		this.cherishItemAdvice = null;
		this.forceUpdate();
		const q = encodeURIComponent(id);
		fetch('https://cherish-world.com/api/showdown/items/bag?id=' + q, { credentials: 'include' })
			.then(r => r.json())
			.then(json => { if (this.cherishItemSel === id) { this.cherishItemBag = json?.ok ? json.data : null; this.forceUpdate(); } })
			.catch(() => {});
		fetch('https://cherish-world.com/api/showdown/items/advice?id=' + q, { credentials: 'include' })
			.then(r => r.json())
			.then(json => { if (this.cherishItemSel === id) { this.cherishItemAdvice = json?.ok ? json.data : null; this.forceUpdate(); } })
			.catch(() => {});
	}
	/**
	 * 결과 토스트 — 화면 **고정**이다 (2026-09-10). 인라인 `cw-note` 는 목록 위쪽이라
	 * 아래로 스크롤해 도구를 사면 안내가 화면 밖에서 떴다 사라졌다(사용자 신고).
	 * 배틀의 `cherishToast` 는 `.cw-bshell` 안에 붙어서 메인메뉴에선 못 쓴다 — body 에 직접 단다.
	 *
	 * ⚠️ **메인메뉴 공용이다.** 알 탭(patch-egg-tab.py)·이름 짓기(patch-nickname-*.py)도 이걸 부른다.
	 * 정의는 여기 하나뿐이므로 **바깥 패치는 반드시 `typeof this.cherishToastNote === 'function'`
	 * 가드로** 부를 것 — 이 패치가 안 발린 작업본에서 호출부만 남아 화면이 죽는 사고를 막는다
	 * (2026-09-09 이름 지어주기 전멸과 같은 계열). CSS 는 style/cherish-items.css 소유.
	 */
	cherishToastNote(text: string, bad?: boolean) {
		if (!text) return;
		let wrap = document.getElementById('cw-note-toasts');
		if (!wrap) {
			wrap = document.createElement('div');
			wrap.id = 'cw-note-toasts';
			document.body.appendChild(wrap);
		}
		const el = document.createElement('div');
		el.className = bad ? 'cw-note-toast bad' : 'cw-note-toast';
		el.textContent = text;
		wrap.appendChild(el);
		const hold = bad ? 5200 : 3600;
		setTimeout(() => { el.className += ' out'; }, hold);
		setTimeout(() => { if (el.parentNode) el.parentNode.removeChild(el); }, hold + 800);
	}
	cherishItemPost(path: string, body: any, done: (data: any) => void) {
		if (this.cherishItemBusy) return;
		this.cherishItemBusy = true;
		this.cherishItemNotice = null;
		this.forceUpdate();
		fetch('https://cherish-world.com/api/showdown/items/' + path, {
			method: 'POST', credentials: 'include',
			headers: { 'content-type': 'application/json' },
			body: JSON.stringify(body),
		})
			.then(r => r.json())
			.then(json => {
				this.cherishItemBusy = false;
				if (!json?.ok) {
					this.cherishItemNotice = json?.message || json?.error?.message || '실패했어요.';
					this.cherishToastNote(this.cherishItemNotice, true);
					this.forceUpdate();
					return;
				}
				done(json.data);
				// done 이 채운 안내를 그대로 띄운다 — 구매·건네주기·장착 전부 한 자리에서 (2026-09-10)
				this.cherishToastNote(this.cherishItemNotice || '', false);
				this.forceUpdate();
			})
			.catch(() => { this.cherishItemBusy = false; this.cherishItemNotice = '연결이 끊겼어요.'; this.cherishToastNote(this.cherishItemNotice, true); this.forceUpdate(); });
	}
	/** 상점 구매 — **인벤토리로** 들어간다 (0322). 고른 아이와 무관하다.
	 *  2026-09-10: 09-09 개편 때 호출부만 `buyCherishItem(it)` 로 바뀌고 이 메서드는 mon 을 그대로
	 *  받아 `mon.nickname` 에서 터졌다 — onClick 안이라 화면엔 아무 반응도 안 났다(「사기」 맨발). */
	buyCherishItem(item: any) {
		if (!window.confirm(`${item.ko}을(를) ${(item.price || 0).toLocaleString('ko-KR')}체리에 살까요?\n인벤토리로 들어가요.`)) return;
		this.cherishItemPost('buy', { itemId: item.id }, (data: any) => {
			if (data.inventory) this.cherishItemInv = data.inventory;
			if (data.shop) this.cherishItemShop = data.shop;
			this.cherishItemNotice = `${item.ko}을(를) 인벤토리에 넣었어요. (체리 ${(data.balanceAfter || 0).toLocaleString('ko-KR')} 남음)`;
			this.loadCherishData();
		});
	}
	/** 인벤토리 → 그 아이 가방 (**단방향**, 회수 없음). api POST /items/give.
	 *  2026-09-09: 호출부(「…에게 주기」 버튼 둘)만 있고 이 메서드가 없어서 누르면 그 자리에서 터졌다
	 *  — verify-patch-markers.py 의 DANGLING 검사가 잡았다. */
	giveCherishItem(item: any, mon: any) {
		const name = mon.nickname || mon.koName || mon.speciesName;
		// 신비한 상자에서 꺼낸 것만 남았으면 받는 아이가 교환 불가가 된다 (2026-10-26, api locksOnGive)
		const lock = item.locksOnGive ? `\n\n신비한 상자에서 꺼낸 도구라, 받은 ${name}은(는) 앞으로 누구에게도 보낼 수 없어요.` : '';
		if (!window.confirm(`${item.ko}을(를) ${name}에게 줄까요?\n${name}의 가방에 들어가고 다른 아이에게 옮길 수 없어요.${lock}`)) return;
		this.cherishItemPost('give', { itemId: item.id, pokemonId: mon.id }, (data: any) => {
			if (data.bag) this.cherishItemBag = data.bag;
			if (data.inventory) this.cherishItemInv = data.inventory;
			this.cherishItemNotice = `${item.ko}을(를) ${name}의 가방에 넣었어요.`;
			this.loadCherishData(); // 목록의 장착·가방 표시 갱신
		});
	}
	equipCherishItem(mon: any, itemId: string | null) {
		this.cherishItemPost('equip', { id: mon.id, itemId }, (data: any) => {
			this.cherishItemBag = data;
			this.cherishItemNotice = itemId ? `${data.heldItemKo}을(를) 들려 줬어요.` : '도구를 가방에 넣어 뒀어요.';
			this.loadCherishData(); // 목록의 장착 표시 갱신
		});
	}
	/** 인벤토리 탭 — 받을 아이 → 인벤토리 → 그 아이 가방 → 베리의 추천. **사는 자리는 상점 탭**이다
	 *  (2026-09-09 사용자 확정으로 갈랐다). 추천 줄의 「사기」만 편의로 남긴다. 외형 style/cherish-items.css. */
	renderCherishItemTab() {
		if (this.cherishLoadedFor !== PS.user.userid) this.loadCherishData();
		const shop = this.cherishItemShop;
		if (!shop || !shop.open) return <div class="menugroup cw-items" style="max-width:none"><div class="cw-empty">도구는 아직 준비 중이에요.</div></div>;
		const mons: any[] = (this.cherishPokemon || []).filter((m: any) => !m.borrowed); // 빌린 아이는 도구를 못 만진다 (0477)
		if (mons.length && (!this.cherishItemSel || !mons.some((m: any) => m.id === this.cherishItemSel))) {
			const first = mons.find((m: any) => m.teamSlot === 1) || mons[0];
			this.selectCherishItemMon(first.id);
		}
		const mon = mons.find((m: any) => m.id === this.cherishItemSel) || null;
		const bag = this.cherishItemBag;
		const advice = this.cherishItemAdvice;
		const busy = this.cherishItemBusy;
		const inBag = new Set<string>((bag?.items || []).map((i: any) => i.id));
		const invItems: any[] = this.cherishItemInv?.items || [];
		const monName = (m: any) => m.nickname || m.koName || m.speciesName;
		const invPicked = this.cherishInvPick;
		const pick = (key: string) => { this.cherishInvPick = this.cherishInvPick === key ? null : key; this.forceUpdate(); };
		// 특별폼 그림(patch-custom-form.py 의 모듈 함수 cherishMonArt·Base·Fb)은 **블록 주인인 여기가 직접 부른다** (2026-10-17, 09-19 규약).
		// 예전엔 custom-form 이 이 두 줄을 치환해서, 이 패치를 다시 바르면 원본으로 돌아가 특별폼 아이가 보통 그림으로 보였다
		// (verify REVERTED). 저쪽이 안 발린 작업본에서도 안 죽게 typeof 로 가드한다.
		const sprite = (m: any) => typeof cherishMonArt === 'function' ? cherishMonArt(m) : `/cherish/sprite/${m.shiny ? 'shiny/' : ''}${m.spriteId}.png?v=2`;
		const spriteFb = (m: any) => typeof cherishMonArtBase === 'function' ? cherishMonArtBase(m) : '';
		const spriteOnError = typeof cherishMonArtFb === 'function' ? cherishMonArtFb : undefined;
		const icon = (it: any) => <i class="cw-item-ic" style={Dex.getItemIcon(it.id)} aria-hidden />;
		return <div class="menugroup cw-items" style="max-width:none">
			<div class="cw-sec-head"><div class="cw-sec-head-text"><span class="cw-eyebrow">Inventory</span><h3 class="cw-sec-title">인벤토리</h3>
				<p class="cw-sec-sub">산 도구는 여기 있어요. 아이에게 주면 그 아이 것이 되고 다시 옮길 수 없어요. 새로 사려면 <strong>상점</strong> 탭으로 가요.</p>
			</div><span class="cw-sec-count">내 체리 <strong>{(shop.cherry || 0).toLocaleString('ko-KR')}</strong></span></div>
			{this.cherishItemNotice && <p class="cw-note">{this.cherishItemNotice}</p>}
			{/* cherish-special: 「특별한 물건」 칸 — patch-special-items.py 가 메서드 소유, 호출은 블록 주인인 여기가 든다 (2026-10-17, 09-19 규약).
			    예전엔 저쪽이 이 자리에 한 줄을 끼워 넣어서 이 패치를 다시 바르면 사라졌다(10-16 빌려주기 배포에서 실제로 사라짐). */}
			{(this as any).renderCherishSpecialItems?.()}

			{!mons.length ? <div class="cw-empty">아직 포켓몬이 없어요. 알을 부화해 첫 아이를 만나면 도구를 줄 수 있어요.</div> : <>
				<div class="cw-items-label">아이 고르기</div>
				<div class="cw-item-mons">
					{mons.map((m: any) => <button class={`cw-item-mon${m.id === this.cherishItemSel ? ' on' : ''}`} onClick={() => this.selectCherishItemMon(m.id)} title={monName(m)}>
						<img src={sprite(m)} data-cwfb={spriteFb(m)} onError={spriteOnError} width="40" height="40" alt="" /><small>{monName(m)}</small>{m.heldItemKo ? <em>{m.heldItemKo}</em> : null}
					</button>)}
				</div>

				{mon && !invItems.length && <div class="cw-item-inv">
					<div class="cw-items-label">인벤토리 (아직 아무에게도 안 준 도구)</div>
					<div class="cw-empty">비어 있어요. <button class="button cw-linkish" onClick={() => { this.cherishMonTab = 'shop'; this.forceUpdate(); }}>상점</button> 에서 도구를 데려올 수 있어요.</div>
				</div>}

				{mon && invItems.length > 0 && <div class="cw-item-inv">
					<div class="cw-items-label">인벤토리 (아직 아무에게도 안 준 도구)</div>
					{/* 칩은 아이콘·이름만, 누르면 아래 한 칸에 설명·버튼 (2026-11-01, 상점 탭과 같은 방식).
					    예전엔 설명이 title 툴팁뿐이라 PC 는 호버해야 알았고 모바일은 아예 못 봤다. */}
					<div class="cw-item-chips">
						{invItems.map((i: any) => <button class={`cw-item-chip cw-inv${invPicked === 'inv:' + i.id ? ' is-pick' : ''}`} onClick={() => pick('inv:' + i.id)}>
							{icon(i)}<b>{i.ko}{i.count > 1 ? ` ×${i.count}` : ''}</b>
						</button>)}
					</div>
					{(() => {
						const i = invItems.find((x: any) => invPicked === 'inv:' + x.id);
						if (!i) return <p class="cw-item-hint">도구를 누르면 설명이 아래에 나와요.</p>;
						return <div class="cw-item-card cw-item-detail">
							<div class="cw-item-card-head">{icon(i)}<b>{i.ko}</b>{i.count > 1 ? <small class="cw-item-only">{i.count}개</small> : null}</div>
							{i.desc ? <p>{i.desc}</p> : null}
							{(i.speciesOnlyKo || []).length ? <small class="cw-item-only">{i.speciesOnlyKo.slice(0, 3).join('·')} 전용</small> : null}
							{(i.abilityOnlyKo || []).length ? <small class="cw-item-only">특성 {i.abilityOnlyKo.join('·')} 전용</small> : null}
							{i.kind !== 'held' ? <em class="cw-item-have">진화할 때 써요</em> : inBag.has(i.id) ? <em class="cw-item-have">{monName(mon)}은(는) 이미 있어요</em>
								: shop.heldOpen ? <button class="button cw-buy" disabled={busy} onClick={() => this.giveCherishItem(i, mon)}>{monName(mon)}에게 주기</button>
								: <em class="cw-item-have">아직 줄 수 없어요</em>}
						</div>;
					})()}
				</div>}

				{mon && <div class="cw-item-bag">
					<div class="cw-items-label">{monName(mon)}의 가방{bag ? ` (${bag.items.length}개)` : ''}{bag?.heldItemKo ? <span class="cw-item-held"> 🎒 {bag.heldItemKo} 장착</span> : null}</div>
					{!bag ? <div class="cw-empty">가방 여는 중…</div> : !bag.items.length ? <div class="cw-empty">이 아이에게 준 도구가 아직 없어요.</div> : <>
						<div class="cw-item-chips">
							{bag.items.map((i: any) => <button class={`cw-item-chip${i.equipped ? ' on' : ''}${invPicked === 'bag:' + i.id ? ' is-pick' : ''}`} onClick={() => pick('bag:' + i.id)}>
								{icon(i)}<b>{i.ko}</b>{i.equipped ? <small>장착</small> : null}
							</button>)}
						</div>
						{(() => {
							const i = bag.items.find((x: any) => invPicked === 'bag:' + x.id);
							if (!i) return null;
							return <div class="cw-item-card cw-item-detail">
								<div class="cw-item-card-head">{icon(i)}<b>{i.ko}</b>{i.equipped ? <small class="cw-item-only">들고 있어요</small> : null}</div>
								{i.desc ? <p>{i.desc}</p> : null}
								{i.kind !== 'held' ? <em class="cw-item-have">진화용이에요</em> : shop.heldOpen ? (i.equipped
									? <button class="button cw-buy" disabled={busy} onClick={() => this.equipCherishItem(mon, null)}>빼기</button>
									: <button class="button cw-buy" disabled={busy} onClick={() => this.equipCherishItem(mon, i.id)}>들려 주기</button>) : null}
							</div>;
						})()}
					</>}
				</div>}

				{mon && advice && (advice.items || []).length > 0 && <div class="cw-item-advice">
					<div class="cw-items-label">베리의 추천</div>
					{advice.items.map((a: any) => <div class="cw-item-advice-row">
						<p>{a.line}</p>
						<span>{icon(a)}<b>{a.ko}</b>{a.equipped ? <em>장착 중</em> : a.owned
							? (shop.heldOpen ? <button class="button" disabled={busy} onClick={() => this.equipCherishItem(mon, a.id)}>들려 주기</button> : <em>이 아이가 가지고 있어요</em>)
							: (a.inInventory || 0) > 0
								? (shop.heldOpen ? <button class="button" disabled={busy} onClick={() => this.giveCherishItem(a, mon)}>인벤토리에서 주기</button> : <em>인벤토리에 있어요</em>)
								: a.notForSale ? <em>{a.notForSale}</em> : <button class="button cw-buy" disabled={busy || (shop.cherry || 0) < a.price} onClick={() => this.buyCherishItem(a)}>{(a.price || 0).toLocaleString('ko-KR')}체리</button>}</span>
					</div>)}
				</div>}

			</>}
		</div>;
	}
	/** 상점 탭을 띄울까 — 도구 상점이 열렸거나, 베리로 파는 알이 하나라도 있으면. 둘 다 없으면 탭 자체를 숨긴다. */
	cherishShopTabOpen() {
		if (this.cherishItemShop && this.cherishItemShop.open) return true;
		return ((this.cherishShop || {}).berryEggs || []).length > 0;
	}
	/** 상점 탭 — 🍒 체리(도구) / 🫐 베리(고등급 알) 두 칸 (2026-09-09 사용자 확정).
	 *  체리로 사는 **일반 알**은 옮기지 않았다 — 파는 자리(내 알 목록)와 붙어 있어야 해서 알 탭에 남는다. */
	renderCherishShopTab() {
		if (this.cherishLoadedFor !== PS.user.userid) this.loadCherishData();
		const shop = this.cherishItemShop;
		const eggShop = this.cherishShop;
		const dustEggs: any[] = (eggShop || {}).berryEggs || [];
		const cherryOn = !!(shop && shop.open);
		const dustOn = dustEggs.length > 0;
		// 한쪽만 열려 있으면 그쪽으로 붙인다 — 빈 칸을 기본으로 보여 주면 「상점에 아무것도 없다」로 읽힌다
		let seg = this.cherishShopSeg;
		if (seg === 'cherry' && !cherryOn && dustOn) seg = 'dust';
		if (seg === 'dust' && !dustOn && cherryOn) seg = 'cherry';
		const busy = this.cherishItemBusy;
		const eggBusy = this.cherishEggBusy;
		const mons: any[] = (this.cherishPokemon || []).filter((m: any) => !m.borrowed); // 빌린 아이는 도구를 못 만진다 (0477)
		const mon = mons.find((m: any) => m.id === this.cherishItemSel) || null;
		const bag = this.cherishItemBag;
		const inBag = new Set<string>((bag?.items || []).map((i: any) => i.id));
		const invItems: any[] = this.cherishItemInv?.items || [];
		const invCount = new Map<string, number>(invItems.map((i: any) => [i.id, i.count] as [string, number]));
		const tiers: any[] = (shop || {}).tiers || [];
		const tierNow = this.cherishItemTier || tiers[0]?.tier || null;
		const items: any[] = ((shop || {}).items || []).filter((it: any) => it.tier === tierNow);
		// 등급을 바꾸면 그 등급에 없는 선택은 저절로 풀린다 (되돌아오면 다시 펼쳐져 있다)
		const picked: any = items.find((it: any) => it.id === this.cherishShopPick) || null;
		const usable = (it: any) => {
			if (!mon) return true;
			if (it.speciesOnly && !it.speciesOnly.some((s: string) => s === mon.speciesId || s.startsWith(mon.speciesId) || mon.speciesId.startsWith(s))) return false;
			if (it.abilityOnly && !it.abilityOnly.includes(mon.ability)) return false;
			return true;
		};
		const monName = (m: any) => m.nickname || m.koName || m.speciesName;
		const icon = (it: any) => <i class="cw-item-ic" style={Dex.getItemIcon(it.id)} aria-hidden />;
		const evoRow = (it: any) => {
			const evs: any[] = it.evolves || [];
			if (evs.length) return <div class="cw-item-evos">{evs.slice(0, 4).map((e: any) => <span class="cw-item-evo" title={`${e.fromKo} → ${e.toKo}`}>
				<img src={`/cherish/sprite/${e.fromSprite}.png?v=2`} width="28" height="28" alt={e.fromKo} /><b>→</b><img src={`/cherish/sprite/${e.toSprite}.png?v=2`} width="28" height="28" alt={e.toKo} /><small>{e.toKo}</small>
			</span>)}</div>;
			if ((it.evolvesKo || []).length) return <div class="cw-item-evos"><small>{it.evolvesKo.slice(0, 3).join(', ')}</small></div>;
			return null;
		};
		return <div class="menugroup cw-items cw-shop" style="max-width:none">
			<div class="cw-sec-head"><div class="cw-sec-head-text"><span class="cw-eyebrow">Shop</span><h3 class="cw-sec-title">상점</h3>
				<p class="cw-sec-sub">체리로는 도구를, 베리로는 알을 데려올 수 있어요. 베리는 배틀·미궁·체리쉬타워와 <strong>체리쉬놀이터</strong>에서 모아요.</p>
			</div><span class="cw-sec-count cw-wallet">
				<span>🍒 <strong>{((shop || {}).cherry || (eggShop || {}).cherry || 0).toLocaleString('ko-KR')}</strong></span>
				<span>🫐 <strong>{((eggShop || {}).berry || 0).toLocaleString('ko-KR')}</strong></span>
			</span></div>
			{this.cherishItemNotice && <p class="cw-note">{this.cherishItemNotice}</p>}
			{this.cherishEggNotice && <p class="cw-note">{this.cherishEggNotice}</p>}

			<div class="cw-shop-seg">
				<button class={`cw-seg-btn${seg === 'cherry' ? ' on' : ''}`} onClick={() => { this.cherishShopSeg = 'cherry'; this.forceUpdate(); }}>🍒 체리<small>도구</small></button>
				<button class={`cw-seg-btn${seg === 'dust' ? ' on' : ''}`} onClick={() => { this.cherishShopSeg = 'dust'; this.forceUpdate(); }}>🫐 베리<small>알</small></button>
			</div>

			{seg === 'cherry' ? (!cherryOn ? <div class="cw-empty">도구 상점은 아직 준비 중이에요.</div> : <>
				<div class="cw-item-tiers">
					{tiers.map((t: any) => <button class={`cw-item-tier${t.tier === tierNow ? ' on' : ''}`} onClick={() => { this.cherishItemTier = t.tier; this.forceUpdate(); }}>{t.label}<small>{(t.price || 0).toLocaleString('ko-KR')}</small></button>)}
				</div>
				{/* 목록은 아이콘·이름만 (2026-09-10 사용자 결정) — 설명·진화 미리보기까지 다 펼치면 한 등급만
				    스무 개가 넘어 화면을 통째로 넘겨야 했다. 상세는 아래 한 칸에서 한 번에 하나만 펼친다.
				    누른 줄이 움직이지 않도록 상세는 **목록 아래**에 둔다 (목록 사이에 끼우면 아래 알약이 밀린다). */}
				{!items.length ? <div class="cw-empty">이 칸에는 아직 팔 도구가 없어요.</div> : <>
					<div class="cw-item-list">
						{items.map((it: any) => {
							const ok = usable(it);
							const nInv = invCount.get(it.id) || 0;
							return <button
								class={`cw-item-pill${!ok ? ' is-off' : ''}${picked && picked.id === it.id ? ' on' : ''}`}
								onClick={() => { this.cherishShopPick = this.cherishShopPick === it.id ? null : it.id; this.forceUpdate(); }}
							>{icon(it)}<b>{it.ko}</b>{nInv ? <small>{nInv}</small> : null}</button>;
						})}
					</div>
					{picked ? (() => {
						const it = picked;
						const have = inBag.has(it.id);
						const ok = usable(it);
						const poor = ((shop || {}).cherry || 0) < (it.price || 0);
						const nInv = invCount.get(it.id) || 0;
						// 구매는 고른 아이와 무관하다 (인벤토리로 간다) — 전용 표기는 「이 아이한텐 못 준다」는 안내로만 남긴다
						return <div class={`cw-item-card cw-item-detail${!ok ? ' is-off' : ''}`}>
							<div class="cw-item-card-head">{icon(it)}<b>{it.ko}</b><span>{(it.price || 0).toLocaleString('ko-KR')}</span></div>
							<p>{it.desc}</p>
							{evoRow(it)}
							{it.speciesOnlyKo ? <small class="cw-item-only">{it.speciesOnlyKo.slice(0, 3).join('·')} 전용</small> : null}
							{it.abilityOnlyKo ? <small class="cw-item-only">특성 {it.abilityOnlyKo.join('·')} 전용</small> : null}
							{nInv ? <small class="cw-item-only">인벤토리에 {nInv}개</small> : null}
							{/* 이미 가진 아이가 있어도 「사기」는 남긴다 — 인벤토리는 유저 단위라 다른 아이 몫으로 더 살 수 있다 (0322) */}
							{have && mon ? <em class="cw-item-have">{monName(mon)}이(가) 가지고 있어요</em> : null}
							<button class={`button cw-buy${busy || poor ? ' disabled' : ''}`} disabled={busy || poor} onClick={() => this.buyCherishItem(it)}>{poor ? '체리 부족' : '사기'}</button>
						</div>;
					})() : <p class="cw-item-hint">도구를 누르면 설명과 가격이 아래에 나와요.</p>}
				</>}
			</>) : (!dustOn ? <div class="cw-empty">베리로 데려올 알은 아직 준비 중이에요.</div> : <>
				{/* 베리 알 (2026-09-09 알 탭에서 이 자리로 옮김). 체리로는 살 수 없어서 「모아야 닿는」 자리다.
				    파는 자리(알을 베리로 바꾸기)는 내 알 목록 옆이라 알 탭에 그대로 있다. */}
				{dustEggs.map((d: any) => {
					const grade = d.grade;
					const price = d.price || 0;
					const dust = (eggShop || {}).berry || 0;
					const poor = dust < price;
					return <div class="cw-egg-row is-dust">
						<i class={`cw-egg-ic g${grade}`} aria-hidden />
						<div class="cw-egg-row-body">
							<b>{this.cherishEggName(grade)}</b>
							<span>🫐 <strong>{price.toLocaleString('ko-KR')}</strong>, 품기 {d.warmMinutes}분</span>
							{/* 「(포근한 알 N개어치)」 환산은 2026-09-09 사용자 요청으로 뺐다 — 모자란 양만 말한다. */}
							<small>{poor
								? `베리가 ${(price - dust).toLocaleString('ko-KR')} 더 필요해요`
								: '모은 베리로 데려올 수 있어요'}</small>
							{/* 주간 누진 (2026-09-15) — 같은 주에 더 살수록 오른다. 산 적이 없으면 굳이 안 알린다. */}
							{d.boughtThisPeriod > 0 && <small class="cw-egg-ladder">{d.weekly ? '이번 주' : '오늘'} {d.boughtThisPeriod}개째 데려왔어요, 다음은 🫐{(d.nextPrice || 0).toLocaleString('ko-KR')} ({d.weekly ? '월요일에' : '자정에'} 처음 값으로 돌아와요)</small>}
						</div>
						<button class={`button cw-buy${eggBusy || poor ? ' disabled' : ''}`} disabled={eggBusy || poor} onClick={() => this.buyCherishEggBerry(grade, price)}>{poor ? '베리 부족' : '데려오기'}</button>
					</div>;
				})}
				<p class="cw-shop-foot">데려온 알은 <strong>알</strong> 탭에서 품어요. 알을 팔아 베리로 바꾸는 것도 그쪽이에요.</p>
			</>)}
		</div>;
	}
	// cherish-items:end
	// cherish-avoid-tab:begin (patch-avoid-tab.py)
	/**
	 * 「거리두기」 하위탭 — 세 목록을 한 화면에 (2026-09-09, 09-17 2대2 추가).
	 * 담는 자리는 흩어져 있다(배틀 종료 화면 · 미궁 런). **푸는 자리는 여기 하나**다.
	 */
	renderCherishAvoidTab() {
		if (!PS.user.named) return null;
		const all: any[] = this.cherishAvoids || [];
		// ⚠️ rank 는 **목록을 남기고 거르지 않는다** — 종류가 늘 때마다 여기가 조용히 삼켜버린다.
		const rank = all.filter((a: any) => a.kind !== 'maze' && a.kind !== 'multi');
		const multi = all.filter((a: any) => a.kind === 'multi');
		const maze = all.filter((a: any) => a.kind === 'maze');
		const row = (a: any) => <li class="cw-avoid-row">
			<span class="cw-avoid-name">{a.label}</span>
			<button class="cw-avoid-x" onClick={() => this.cherishRemoveAvoid(a.id)}>해제</button>
		</li>;
		// ⚠️ 상한은 인자로 받는다 — 제목 문자열로 갈라내면 문구를 고치는 순간 숫자가 틀린다.
		const group = (title: string, cap: number, note: string, list: any[], empty: string) => <div class="cw-avoid-grp">
			<h4>{title} <small>{list.length}/{cap}</small></h4>
			<p class="cw-avoid-note">{note}</p>
			{list.length ? <ul class="cw-avoid-list">{list.map(row)}</ul> : <p class="cw-avoid-empty">{empty}</p>}
		</div>;
		return <div class="cw-avoid-tab">
			{group('자동매칭에서 안 만날 사람', 2, '3대3·6대6 자동매칭에서만 걸러. 대기방·팀배틀에는 적용되지 않아. 배틀이 끝난 화면에서 담을 수 있어.',
				rank, '아직 없어.')}
			{group('팀배틀에서 같은 팀이 안 될 사람', 2, '팀배틀 자동매칭에서 같은 편으로 안 붙여. 그 쌍은 짝이 아예 안 만들어져. 팀배틀이 끝난 화면에서 담을 수 있어.',
				multi, '아직 없어.')}
			{group('미궁에서 팀이 안 될 사람', 20, '구름 미궁 동행자 찾기에서 짝으로 안 붙여. 미궁을 도는 중에 「팀 그만」으로 담을 수 있어.',
				maze, '아직 없어.')}
			<p class="cw-avoid-foot">상대에게는 아무 알림도 가지 않아.</p>
		</div>;
	}
	// cherish-avoid-tab:end
	// cherish-music-tab:begin (patch-music-tab.py)
	/*
	 * 「내 포켓몬 › 💿 음악 CD」 (2026-09-23) — 가진 CD · 장착 · 미리듣기 · 여분 선물.
	 * 탭 버튼과 본문 분기 호출은 블록 주인 patch-egg-tab.py 가 옵셔널로 들고 있다.
	 * 서버는 선반(유저당 1회) · 장착·선물(누를 때) · 사람 찾기(칠 때 디바운스)만 부른다 — 주기 타이머 없음.
	 */
	cherishMusic: any = null;
	cherishMusicFor: string | null = null;
	cherishMusicBusy = false;
	cherishMusicPlaying: any = null;
	cherishMusicGift: any = null;
	cherishMusicGiftTimer: any = null;
	/** 탭을 그릴지 — `music_cd_open` 이 켜져 있을 때만. 탭 줄이 그려질 때 선반을 1회 받아 둔다. */
	cherishMusicTabOpen() {
		if (!PS.user.named) return false;
		this.fetchCherishMusic();
		return !!(this.cherishMusic && this.cherishMusic.open);
	}
	cherishMusicTabCount() {
		const n = (this.cherishMusic?.cds || []).length;
		return n ? `${n}곡` : '';
	}
	fetchCherishMusic(force = false) {
		if (!PS.user.named) return;
		if (!force && this.cherishMusicFor === PS.user.userid) return;
		this.cherishMusicFor = PS.user.userid;
		fetch('https://cherish-world.com/api/showdown/music', { credentials: 'include' })
			.then(r => r.json())
			.then(json => { this.cherishMusic = json?.ok ? json.data : null; this.forceUpdate(); })
			.catch(() => {});
	}
	/** 원반 색 — 곡마다 고정 (id 해시). */
	cherishMusicHue(id: string) {
		let h = 0;
		for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) % 360;
		return h;
	}
	/** 결과 토스트는 patch-item-tab.py 의 공용 토스트를 빌린다 — 없는 작업본에서도 안 죽게 가드. */
	cherishMusicToast(text: string, bad?: boolean) {
		if (typeof (this as any).cherishToastNote === 'function') (this as any).cherishToastNote(text, bad);
	}
	/** 서버 문구는 한글일 때만 보인다 — 영어 오류 원문을 유저에게 내보내지 않는다 (09-21 지시). */
	cherishMusicErr(json: any) {
		const m = String(json?.message || json?.error?.message || '');
		return /[가-힣]/.test(m) ? m : '잠깐 문제가 생겼어요. 다시 해 볼래요?';
	}
	cherishMusicPost(path: string, body: any, done: (data: any) => void, fail?: (msg: string) => void) {
		if (this.cherishMusicBusy) return;
		this.cherishMusicBusy = true;
		this.forceUpdate();
		fetch('https://cherish-world.com/api/showdown/music/' + path, {
			method: 'POST', credentials: 'include',
			headers: { 'content-type': 'application/json' },
			body: JSON.stringify(body),
		})
			.then(r => r.json())
			.then(json => {
				this.cherishMusicBusy = false;
				if (!json?.ok) {
					const msg = this.cherishMusicErr(json);
					if (fail) fail(msg); else this.cherishMusicToast(msg, true);
					this.forceUpdate();
					return;
				}
				done(json.data);
				this.forceUpdate();
			})
			.catch(() => { this.cherishMusicBusy = false; this.cherishMusicToast('연결이 끊겼어요.', true); this.forceUpdate(); });
	}
	toggleCherishMusicEquip(cd: any) {
		const on = !cd.equipped;
		this.cherishMusicPost('equip', { trackId: cd.trackId, on }, (data: any) => {
			this.cherishMusic = data;
			this.cherishMusicToast(on ? `「${cd.ko}」 장착했어요. 다음 배틀부터 나올 수 있어요.` : `「${cd.ko}」 뺐어요.`);
		});
	}
	/**
	 * 미리듣기 곡 정보 — 배틀 화면이 떠 있으면 그 표(CHERISH_BGM)를, 아니면 패치할 때 구워 둔 사본을 쓴다.
	 * ⚠️ 사본은 patch-music-tab.py 를 바를 때의 표다. 곡을 더했으면 이 패치를 다시 바를 것.
	 */
	cherishMusicTrack(id: string): any {
		const live = (window as any).CHERISH_BGM;
		if (Array.isArray(live)) {
			const t = live.find((x: any) => x && x.id === id);
			if (t) return t;
		}
		const baked: any = {"black-sun":{"loopend":175637,"loopstart":3413,"url":"audio/cherish/black-sun.mp3"},"catch-my-breath":{"loopend":162352,"loopstart":6532,"url":"audio/cherish/catch-my-breath-v2.mp3"},"dreaming-ribbon":{"loopend":177302,"loopstart":2345,"url":"audio/cherish/dreaming-ribbon.mp3"},"hearts-regathered":{"loopend":292792,"loopstart":134790,"url":"audio/cherish/hearts-regathered.mp3"},"mirror-queen":{"loopend":173968,"loopstart":1904,"url":"audio/cherish/mirror-queen.mp3"},"never-show-my-back":{"loopend":155887,"loopstart":2368,"url":"audio/cherish/never-show-my-back.mp3"},"no-turning-back":{"loopend":190236,"loopstart":3738,"url":"audio/cherish/no-turning-back.mp3"},"onslaught":{"loopend":230431,"loopstart":3785,"url":"audio/cherish/onslaught.mp3"},"power-of-love":{"loopend":173013,"loopstart":96722,"url":"audio/cherish/power-of-love.mp3"},"silent-sanctuary":{"loopend":185795,"loopstart":4621,"url":"audio/cherish/silent-sanctuary.mp3"},"still-going":{"loopend":149539,"loopstart":74645,"url":"audio/cherish/still-going-v2.mp3"},"tournament-final":{"loopend":135000,"loopstart":0,"url":"audio/cherish/tournament-final-v1.mp3"},"tournament-quarterfinal":{"loopend":135000,"loopstart":0,"url":"audio/cherish/tournament-quarterfinal-v1.mp3"},"tournament-semifinal":{"loopend":135000,"loopstart":0,"url":"audio/cherish/tournament-semifinal-v1.mp3"},"unbroken-resolve":{"loopend":246106,"loopstart":1602,"url":"audio/cherish/unbroken-resolve.mp3"},"unending":{"loopend":297686,"loopstart":234600,"url":"audio/cherish/unending.mp3"},"what-we-kept":{"loopend":295114,"loopstart":199114,"url":"audio/cherish/what-we-kept.mp3"},"world-end":{"loopend":172521,"loopstart":105482,"url":"audio/cherish/world-end.mp3"},"wrapped-in-sparkle":{"loopend":173872,"loopstart":47175,"url":"audio/cherish/wrapped-in-sparkle.mp3"}};
		return baked[id] || null;
	}
	/** 배틀 음악과 같은 음량 — 설정의 음악 소리 × 배틀 기준선(CHERISH_BGM_SCALE, battle-sound.ts). 음소거면 0. */
	cherishMusicVolume() {
		const w = window as any;
		const scale = typeof w.CHERISH_BGM_SCALE === 'number' ? w.CHERISH_BGM_SCALE : 0.25;
		const bs = w.BattleSound;
		const muted = bs ? !!bs.muted : !!PS.prefs.mute;
		const vol = bs ? bs.bgmVolume : PS.prefs.musicvolume;
		if (muted) return 0;
		return Math.max(0, Math.min(1, (Number(vol ?? 50) || 0) / 100 * scale));
	}
	/**
	 * 미리듣기 — 한 번에 한 곡, 루프 구간 시작점에서 25초. 앞뒤로 살짝 페이드.
	 * 배틀 곡이 돌고 있으면 잠깐 멈췄다가 끝나면 되돌린다 (두 곡이 겹치면 안 들린다).
	 * 탭을 떠나거나 창을 가리면 멈춘다 — 화면 밖에서 소리만 남지 않게.
	 */
	playCherishMusicPreview(cd: any) {
		const was = this.cherishMusicPlaying && this.cherishMusicPlaying.id;
		this.stopCherishMusicPreview();
		if (was === cd.trackId) return;
		const track = this.cherishMusicTrack(cd.trackId);
		if (!track) { this.cherishMusicToast('이 곡은 아직 미리 들을 수 없어요.', true); return; }
		if (!this.cherishMusicVolume()) { this.cherishMusicToast('배틀 음악 소리가 꺼져 있어요. 설정에서 음악 소리를 올리면 들을 수 있어요.', true); return; }
		let audio: HTMLAudioElement;
		try { audio = new Audio('/' + track.url); } catch { return; }
		audio.preload = 'auto';
		audio.volume = 0;
		const bs = (window as any).BattleSound;
		const bgm = bs && typeof bs.currentBgm === 'function' ? bs.currentBgm() : null;
		if (bgm && typeof bgm.actuallyPause === 'function') bgm.actuallyPause();
		const LEN = 25000, FADE_IN = 700, FADE_OUT = 1800;
		const play: any = { id: cd.trackId, audio, t0: 0, live: false, tick: null, resume: !!bgm };
		const begin = () => {
			if (this.cherishMusicPlaying !== play) return;
			try { audio.currentTime = (Number(track.loopstart) || 0) / 1000; } catch {}
			audio.play().then(() => {
				if (this.cherishMusicPlaying !== play) return;
				play.t0 = Date.now();
				play.live = true;
				this.forceUpdate();
			}).catch(() => {
				if (this.cherishMusicPlaying !== play) return;
				this.stopCherishMusicPreview();
				this.cherishMusicToast('소리를 틀지 못했어요. 한 번 더 눌러 볼래요?', true);
			});
		};
		play.tick = setInterval(() => {
			if (this.cherishMusicPlaying !== play) return;
			if ((this.cherishMonTab as string) !== 'music' || document.hidden) { this.stopCherishMusicPreview(); return; }
			if (!play.t0) return;
			const t = Date.now() - play.t0;
			let g = Math.min(1, t / FADE_IN);
			if (t > LEN - FADE_OUT) g = Math.max(0, (LEN - t) / FADE_OUT);
			try { audio.volume = this.cherishMusicVolume() * g; } catch {}
			if (t >= LEN) this.stopCherishMusicPreview();
		}, 100);
		this.cherishMusicPlaying = play;
		audio.addEventListener('error', () => {
			if (this.cherishMusicPlaying !== play) return;
			this.stopCherishMusicPreview();
			this.cherishMusicToast('곡을 불러오지 못했어요.', true);
		});
		if (audio.readyState >= 1) begin();
		else audio.addEventListener('loadedmetadata', begin);
		this.forceUpdate();
	}
	stopCherishMusicPreview() {
		const play = this.cherishMusicPlaying;
		if (!play) return;
		this.cherishMusicPlaying = null;
		if (play.tick) clearInterval(play.tick);
		try { play.audio.pause(); play.audio.removeAttribute('src'); play.audio.load(); } catch {}
		if (play.resume) {
			const w = window as any;
			if (w.BattleBGM && typeof w.BattleBGM.update === 'function') w.BattleBGM.update();
		}
		this.forceUpdate();
	}
	openCherishMusicGift(cd: any) {
		this.stopCherishMusicPreview();
		this.cherishMusicGift = { trackId: cd.trackId, ko: cd.ko, q: '', results: null, loading: false, picked: null, err: '' };
		this.forceUpdate();
		setTimeout(() => {
			const el = document.getElementById('cw-music-q') as HTMLInputElement | null;
			if (el) el.focus();
		}, 60);
	}
	closeCherishMusicGift() {
		if (this.cherishMusicGiftTimer) clearTimeout(this.cherishMusicGiftTimer);
		this.cherishMusicGift = null;
		this.forceUpdate();
	}
	/** 사람 찾기 — 글자마다 부르지 않는다. 손을 멈추면 한 번. 늦게 온 답은 지금 검색어와 다르면 버린다. */
	onCherishMusicGiftQuery(q: string) {
		const g = this.cherishMusicGift;
		if (!g) return;
		g.q = q;
		g.picked = null;
		g.err = '';
		if (this.cherishMusicGiftTimer) clearTimeout(this.cherishMusicGiftTimer);
		if (!q.trim()) { g.results = null; g.loading = false; this.forceUpdate(); return; }
		g.loading = true;
		this.forceUpdate();
		this.cherishMusicGiftTimer = setTimeout(() => {
			fetch('https://cherish-world.com/api/showdown/music/recipients?q=' + encodeURIComponent(q.trim()), { credentials: 'include' })
				.then(r => r.json())
				.then(json => {
					const cur = this.cherishMusicGift;
					if (!cur || cur.q !== q) return;
					cur.loading = false;
					cur.results = json?.ok ? (json.data?.recipients || []) : [];
					this.forceUpdate();
				})
				.catch(() => {
					const cur = this.cherishMusicGift;
					if (!cur || cur.q !== q) return;
					cur.loading = false;
					cur.results = [];
					this.forceUpdate();
				});
		}, 280);
	}
	sendCherishMusicGift() {
		const g = this.cherishMusicGift;
		if (!g || !g.picked) return;
		const to = g.picked;
		this.cherishMusicPost('gift', { toDiscordUserId: to.discordUserId, trackId: g.trackId }, (data: any) => {
			this.cherishMusic = data;
			this.cherishMusicGift = null;
			this.cherishMusicToast(`「${g.ko}」 여분 한 장을 ${to.name}에게 선물했어요.`);
		}, (msg: string) => {
			if (this.cherishMusicGift) this.cherishMusicGift.err = msg;
		});
	}
	renderCherishMusicTab() {
		if (!PS.user.named) return null;
		this.fetchCherishMusic();
		const shelf = this.cherishMusic;
		if (!shelf) return <div class="menugroup cw-music" style="max-width:none"><div class="cw-empty">CD 선반을 여는 중이에요…</div></div>;
		if (!shelf.open) return <div class="menugroup cw-music" style="max-width:none"><div class="cw-empty">음악 CD는 아직 준비 중이에요.</div></div>;
		const cds: any[] = shelf.cds || [];
		const on = cds.filter((c: any) => c.equipped);
		const defaults: string[] = shelf.defaultKo || [];
		const playing = this.cherishMusicPlaying;
		const busy = this.cherishMusicBusy;
		// 아직 못 만난 CD 는 흐린 원반 자리로 보여 준다 — 이름은 숨긴다 (모으는 재미). 곡이 많아지면 여섯 자리까지만.
		const missing = Math.max(0, Number(shelf.missing) || 0);
		const ghosts = Math.min(missing, 6);
		const PLAY = <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8.5 5.6v12.8a1 1 0 0 0 1.53.85l10.1-6.4a1 1 0 0 0 0-1.7l-10.1-6.4a1 1 0 0 0-1.53.85z" /></svg>;
		const STOP = <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="6.5" y="5.5" width="4" height="13" rx="1.3" /><rect x="13.5" y="5.5" width="4" height="13" rx="1.3" /></svg>;
		// ⚠️ 선물 시트는 .menugroup **밖** 형제로 둔다. .menugroup 의 backdrop-filter 가 position:fixed 의
		//    기준(containing block)이 되어, 안에 두면 시트가 화면이 아니라 카드 안에 갇힌다 (skin §20 과 같은 함정).
		return <div><div class="menugroup cw-music" style="max-width:none">
			<div class="cw-sec-head"><div class="cw-sec-head-text"><span class="cw-eyebrow">Music CD</span><h3 class="cw-sec-title">음악 CD</h3>
				<p class="cw-sec-sub">장착한 CD 중에서 배틀 곡이 나와요. 같은 판에 CD를 낀 사람이 여럿이면 모두의 곡을 합쳐서 뽑아요. 내 곡이 상대에게도 들려요.</p>
			</div>{cds.length ? <span class="cw-sec-count">장착 <strong>{on.length}</strong>곡</span> : null}</div>

			{!cds.length && ghosts ? <p class="cw-music-lead"><b>아직 가진 CD가 없어요.</b> 포켓몬이 통화방에 놀러 오면 가끔 CD를 물어와요.</p> : null}
			{!cds.length && !ghosts ? <div class="cw-music-empty">
				<i class="cw-music-disc is-ghost" aria-hidden="true" />
				<b>아직 가진 CD가 없어요</b>
				<p>포켓몬이 통화방에 놀러 오면 가끔 CD를 물어와요.</p>
			</div> : <ul class="cw-music-list">
				{cds.map((c: any) => {
					const isPlaying = !!playing && playing.id === c.trackId;
					return <li
						class={`cw-music-cd${c.equipped ? ' is-on' : ''}${isPlaying ? ' is-playing' : ''}${isPlaying && playing.live ? ' is-live' : ''}`}
						style={`--cw-disc-hue:${this.cherishMusicHue(c.trackId)}`}
					>
						<button class="cw-music-play" title={isPlaying ? '미리듣기 멈추기' : '미리듣기 (25초)'} aria-label={isPlaying ? `${c.ko} 미리듣기 멈추기` : `${c.ko} 미리듣기`} onClick={() => this.playCherishMusicPreview(c)}>
							<i class="cw-music-disc" aria-hidden="true" />
							<span class="cw-music-play-ic">{isPlaying ? STOP : PLAY}</span>
						</button>
						<div class="cw-music-body">
							<b class="cw-music-title">{c.ko}</b>
							<span class="cw-music-tags">
								{c.equipped ? <em class="cw-music-tag is-on">장착 중</em> : <em class="cw-music-tag">서랍에 있어요</em>}
								{c.spare > 0 ? <em class="cw-music-tag is-spare">여분 {c.spare}장</em> : null}
							</span>
							{isPlaying ? <span class="cw-music-progress" aria-hidden="true"><i /></span> : null}
						</div>
						<div class="cw-music-actions">
							<span class="cw-music-equip">
								<small>장착</small>
								<button class={`cw-music-switch${c.equipped ? ' on' : ''}`} role="switch" aria-checked={c.equipped ? 'true' : 'false'} aria-label={`${c.ko} 장착`} disabled={busy} onClick={() => this.toggleCherishMusicEquip(c)}><i /></button>
							</span>
							{c.spare > 0 ? <button class="cw-music-gift" disabled={busy} onClick={() => this.openCherishMusicGift(c)}>🎁 선물</button> : null}
						</div>
					</li>;
				})}
				{Array.from({ length: ghosts }, (_, i) => <li class="cw-music-cd is-locked" key={`locked-${i}`}>
					<span class="cw-music-play is-locked" aria-hidden="true"><i class="cw-music-disc" /><span class="cw-music-lock">?</span></span>
					<div class="cw-music-body">
						<b class="cw-music-title">아직 못 만난 CD</b>
						<span class="cw-music-tags"><em class="cw-music-tag is-locked">포켓몬이 가끔 물어와요</em></span>
					</div>
				</li>)}
			</ul>}

			<div class="cw-music-foot">
				{!on.length && defaults.length ? <p>장착한 CD가 없으면 기본곡 {defaults.map(k => `「${k}」`).join(' ')} 중에서 나와요.</p> : null}
				{missing > ghosts ? <p>이 밖에도 못 만난 CD가 <strong>{missing - ghosts}장</strong> 더 있어요.</p> : !missing && cds.length ? <p>모든 CD를 모았어요!</p> : null}
			</div>
		</div>{this.cherishMusicGift ? this.renderCherishMusicGiftSheet() : null}</div>;
	}
	/** 여분 선물 시트 — 찾기 → 고르기 → 확인. 보내고 나면 되돌릴 수 없다. */
	renderCherishMusicGiftSheet() {
		const g = this.cherishMusicGift;
		if (!g) return null;
		const busy = this.cherishMusicBusy;
		const close = () => this.closeCherishMusicGift();
		// 아바타가 없거나 못 읽으면 이름 첫 글자가 비쳐 보인다 (그림이 글자를 덮는다)
		const face = (r: any) => <span class="cw-music-face"><b aria-hidden="true">{Array.from(String(r.name || '?'))[0]}</b><img src={`/sprites/trainers-custom/cherish${r.discordUserId}.png`} alt="" loading="lazy" onError={(e: any) => { e.currentTarget.style.visibility = 'hidden'; }} /></span>;
		const results: any[] = g.results || [];
		return <div class="cw-music-sheet-back" onClick={(e: any) => { if (e.target === e.currentTarget) close(); }}>
			<div class="cw-music-sheet" role="dialog" aria-modal="true" aria-label="CD 선물하기" onKeyDown={(e: any) => { if (e.key === 'Escape') close(); }}>
				<div class="cw-music-sheet-head">
					<i class="cw-music-disc is-sm" aria-hidden="true" style={`--cw-disc-hue:${this.cherishMusicHue(g.trackId)}`} />
					<div><b>「{g.ko}」 선물하기</b><small>여분 한 장이 넘어가요. 장착한 한 장은 그대로 남아요.</small></div>
					<button class="cw-music-x" aria-label="닫기" onClick={close}>&times;</button>
				</div>
				{!g.picked ? <>
					<label class="cw-music-search">
						<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="6.5" /><path d="M16 16l4 4" /></svg>
						<input id="cw-music-q" type="text" autocomplete="off" spellcheck={false} placeholder="받을 사람의 서버 닉네임" value={g.q}
							onInput={(e: any) => this.onCherishMusicGiftQuery(e.currentTarget.value)} />
					</label>
					<div class="cw-music-results">
						{!g.q.trim() ? <p class="cw-music-hint">배틀페이지에 한 번이라도 들어온 사람만 찾을 수 있어요.</p>
							: g.loading && !g.results ? <p class="cw-music-hint">찾는 중…</p>
							: !results.length ? <p class="cw-music-hint">그런 이름의 트레이너가 없어요.</p>
							: <ul>{results.map((r: any) => <li><button class="cw-music-who" onClick={() => { g.picked = r; g.err = ''; this.forceUpdate(); }}>{face(r)}<span>{r.name}</span></button></li>)}</ul>}
					</div>
				</> : <div class="cw-music-confirm">
					{face(g.picked)}
					<p><b>{g.picked.name}</b>에게<br />「{g.ko}」 여분 한 장을 줄까요?</p>
					<small>한 번 주면 되돌릴 수 없어요.</small>
					{g.err ? <p class="cw-music-err">{g.err}</p> : null}
					<div class="cw-music-confirm-btns">
						<button class="cw-music-btn" disabled={busy} onClick={() => { g.picked = null; g.err = ''; this.forceUpdate(); }}>다시 고르기</button>
						<button class="cw-music-btn is-go" disabled={busy} onClick={() => this.sendCherishMusicGift()}>{busy ? '보내는 중…' : '선물하기'}</button>
					</div>
				</div>}
			</div>
		</div>;
	}
	// cherish-music-tab:end
	// cherish-nick-offer:begin (patch-nickname-offer.py)
	/**
	 * 성공 안내 토스트 (2026-09-10). 이름 짓기는 그동안 **성공하면 아무 말이 없었다** — 체리를
	 * 쓰는 자리인데 조용히 이름만 바뀌었다. 실패는 예전부터 `PS.alert` 이라 그대로 둔다.
	 *
	 * ⚠️ 토스트 **정의는 patch-item-tab.py 소유**다. 그 패치가 안 발린 작업본에서 호출부만 남으면
	 * 화면이 통째로 죽으므로 `typeof` 가드를 끼고 부른다 — 이 파일 머리의 2026-09-09 사고와 같은 계열.
	 */
	cherishNickToast(text: string) {
		if (text && typeof (this as any).cherishToastNote === 'function') (this as any).cherishToastNote(text, false);
	}
	/** 공통 POST. 성공하면 me·inbox·sent 를 한꺼번에 다시 받는다 (이름·비용·목록이 같이 움직인다).
	 *  `note` 를 주면 성공했을 때 토스트로 띄운다 (2026-09-10). */
	cherishNickPost(path: string, body: any, after?: (data: any) => void, note?: string) {
		if (this.cherishNickBusy) return;
		this.cherishNickBusy = true;
		fetch('https://cherish-world.com/api/showdown/' + path, {
			method: 'POST', credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(body),
		}).then(r => r.json()).then(json => {
			this.cherishNickBusy = false;
			if (!json?.ok) {
				PS.alert(json?.message || '지금은 하지 못했어요.');
				this.forceUpdate();
				return;
			}
			if (after) after(json.data);
			if (note) this.cherishNickToast(note);
			this.loadCherishData();
			this.forceUpdate();
		}).catch(() => {
			this.cherishNickBusy = false;
			PS.alert('지금은 하지 못했어요.');
			this.forceUpdate();
		});
	}
	/** 이름 입력창 — 값을 그대로 넘긴다 (빈 값이면 원래 이름으로 되돌리기). */
	cherishNickPrompt(label: string, cur: string): string | null {
		const input = window.prompt(label, cur || '');
		if (input === null) return null;
		return input.trim();
	}
	/** 첫 부모가 값을 낸다는 안내 — 무료 회차면 굳이 겁주지 않는다. */
	cherishNickCostNote(cost: number): string {
		return cost > 0
			? '체리 ' + cost.toLocaleString('ko-KR') + '개를 써요.'
			: '처음 짓는 이름이라 무료예요.';
	}
	cherishNickOfferFor(pokemonId: string): any {
		const box = this.cherishNickInbox;
		if (!box) return null;
		const all = (box.pending || []).concat(box.mine || []);
		for (let i = 0; i < all.length; i++) {
			if (all[i].pokemonId === pokemonId) return all[i];
		}
		return null;
	}
	/** 데리고 있는 사람 → 첫 부모에게 이름을 부탁한다 (상세 패널 버튼). */
	cherishNickAsk(p: any) {
		const who = p.originalOwnerName || '처음 만난 트레이너';
		if (!window.confirm(who + ' 님에게 이 아이 이름을 지어달라고 부탁할까요?')) return;
		this.cherishNickPost('nickname/requests', { id: p.id }, undefined, who + ' 님에게 부탁을 보냈어요.');
	}
	/** 첫 부모 → 이름 제안 (보낸 아이 목록 버튼). */
	cherishNickOffer(mon: any) {
		const label = mon.currentName + '에게 지어줄 이름 (최대 12자, 비우면 원래 이름) · ' + this.cherishNickCostNote(mon.cost);
		const name = this.cherishNickPrompt(label, mon.currentName);
		if (name === null) return;
		this.cherishNickPost('nickname/offers', { id: mon.pokemonId, nickname: name || null }, undefined, name ? '「' + name + '」 (으)로 이름을 제안했어요.' : '원래 이름으로 되돌리자고 제안했어요.');
	}
	/** 첫 부모 → 부탁받은 아이 이름 지어주기 (승인 없이 바로 적용된다). */
	cherishNickFulfill(offer: any) {
		const label = offer.currentName + '의 새 이름 (최대 12자, 비우면 원래 이름) · ' + this.cherishNickCostNote(offer.cost);
		const name = this.cherishNickPrompt(label, offer.currentName);
		if (name === null) return;
		this.cherishNickPost('nickname/offers/fulfill', { id: offer.id, nickname: name || null }, undefined, name ? '「' + name + '」 (으)로 이름을 지어 줬어요.' : '원래 이름으로 되돌렸어요.');
	}
	cherishNickAccept(offer: any) {
		// 체리 부족은 이미 PS.alert 로 알린다 — 그 위에 「받았어요」 토스트까지 겹치면 거짓말이 된다
		this.cherishNickPost('nickname/offers/accept', { id: offer.id }, (data: any) => {
			if (data && data.notEnoughCherry) PS.alert('상대의 체리가 모자라서 이름을 바꾸지 못했어요.');
			else this.cherishNickToast('새 이름을 받았어요!');
		});
	}
	cherishNickDecline(offer: any) {
		this.cherishNickPost('nickname/offers/decline', { id: offer.id }, undefined, '제안을 거절했어요.');
	}
	cherishNickCancel(offerId: string) {
		this.cherishNickPost('nickname/offers/cancel', { id: offerId });
	}
	/** 결말을 봤다고 표시 — 안 보내면 들어올 때마다 같은 알림이 뜬다. */
	cherishNickSeenResult(id: string) {
		this.cherishNickSeen['r' + id] = true;
		this.forceUpdate();
		fetch('https://cherish-world.com/api/showdown/nickname/offers/seen', {
			method: 'POST', credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ ids: [id] }),
		}).catch(() => {});
	}
	/**
	 * 내 포켓몬 탭에 들어오면 뜨는 팝업. 한 번에 한 건만 보여주고 나머지는 「N건 더」로 센다.
	 * 답할 것(pending)이 먼저고, 없으면 내가 만든 것의 결말(results)을 알린다.
	 */
	renderCherishNickPopup() {
		const box = this.cherishNickInbox;
		if (!box) return null;
		const pending = (box.pending || []).filter((o: any) => !this.cherishNickSeen[o.id]);
		const results = (box.results || []).filter((r: any) => !this.cherishNickSeen['r' + r.id]);
		const offer = pending[0] || null;
		const result = offer ? null : (results[0] || null);
		if (!offer && !result) return null;
		const rest = (offer ? pending.length : results.length) - 1;
		const close = () => {
			if (offer) { this.cherishNickSeen[offer.id] = true; this.forceUpdate(); }
			else if (result) this.cherishNickSeenResult(result.id);
		};
		// 특별폼 그림은 custom-form 의 모듈 함수를 블록 주인인 여기가 직접 부른다 (2026-10-17, typeof 가드 — 09-19 규약)
		const art = (o: any) => <img
			src={typeof cherishMonArt === 'function' ? cherishMonArt(o) : '/cherish/sprite/' + (o.shiny ? 'shiny/' : '') + o.spriteId + '.png?v=2'}
			data-cwfb={typeof cherishMonArtBase === 'function' ? cherishMonArtBase(o) : ''}
			onError={typeof cherishMonArtFb === 'function' ? cherishMonArtFb : undefined}
			width="96" height="96" alt=""
			style="image-rendering:pixelated;display:block;margin:0 auto"
		/>;
		return <div class="cw-intro-backdrop" onClick={close}>
			<div class="cw-intro" role="dialog" aria-label="이름 이야기" onClick={(ev: any) => ev.stopPropagation()} style="max-width:380px">
				{offer ? this.renderCherishNickAsk(offer, art) : this.renderCherishNickResult(result, art)}
				<div class="cw-intro-foot">
					{rest > 0 ? <p class="cw-intro-note">{rest}건 더 있어요.</p> : null}
					{offer ? <>
						<button class="cw-intro-close" onClick={() => this.cherishNickDecline(offer)}>
							{offer.kind === 'offer' ? '괜찮아요' : '지금은 어려워요'}
						</button>
						<button class="cw-intro-go" onClick={() => (offer.kind === 'offer' ? this.cherishNickAccept(offer) : this.cherishNickFulfill(offer))}>
							{offer.kind === 'offer' ? '좋아요' : '이름 짓기'}
						</button>
					</> : <button class="cw-intro-go" onClick={close}>확인</button>}
				</div>
			</div>
		</div>;
	}
	renderCherishNickAsk(offer: any, art: any) {
		const who = offer.partnerName;
		if (offer.kind === 'offer') {
			const name = offer.proposedNickname;
			return <>
				<h3 class="cw-intro-title">이름을 지어주고 싶대요</h3>
				{art(offer)}
				<p class="cw-intro-lead">
					{who} 님이 <b>{offer.currentName}</b>에게<br />
					{name ? <>&#x300C;<b>{name}</b>&#x300D;라는 이름을 지어주고 싶어해요.</> : <>원래 이름으로 되돌리고 싶어해요.</>}
				</p>
				<p class="cw-intro-note" style="margin:0 0 4px">받아들이면 이름이 바로 바뀌어요. 체리는 {who} 님이 내요.</p>
			</>;
		}
		return <>
			<h3 class="cw-intro-title">이름을 지어달래요</h3>
			{art(offer)}
			<p class="cw-intro-lead">
				{who} 님이 <b>{offer.currentName}</b>의<br />
				이름을 지어달라고 부탁했어요.
			</p>
			<p class="cw-intro-note" style="margin:0 0 4px">지어주면 바로 그 이름이 돼요. {this.cherishNickCostNote(offer.cost)}</p>
		</>;
	}
	renderCherishNickResult(r: any, art: any) {
		const who = r.partnerName;
		let line: any = null;
		if (r.status === 'accepted') {
			line = r.kind === 'offer'
				? <>{who} 님이 받아주셨어요.<br />이제 &#x300C;<b>{r.nickname || r.currentName}</b>&#x300D;예요.</>
				: <>{who} 님이 이름을 지어주셨어요.<br />이제 &#x300C;<b>{r.nickname || r.currentName}</b>&#x300D;예요.</>;
		} else if (r.status === 'declined') {
			line = <>{who} 님이 지금은 그대로 두고 싶대요.</>;
		} else if (r.status === 'failed') {
			line = <>체리가 모자라서 이름을 짓지 못했어요.</>;
		} else if (r.status === 'stale') {
			line = <>그새 그 아이의 사정이 바뀌어서<br />없던 일이 됐어요.</>;
		} else {
			line = <>답을 받지 못한 채 기한이 지났어요.</>;
		}
		return <>
			<h3 class="cw-intro-title">이름 이야기</h3>
			{art(r)}
			<p class="cw-intro-lead">{line}</p>
		</>;
	}
	/**
	 * 「내가 보낸 아이들」 — 내 포켓몬 탭 맨 아래 접이식.
	 * 여기 있는 아이는 내가 첫 부모라 이름을 지어줄 수 있지만, 지금 데리고 있는 사람의
	 * 승인이 있어야 바뀐다 (저쪽이 먼저 부탁했으면 그대로 적용된다).
	 */
	renderCherishSentMons() {
		const mons = this.cherishNickSent;
		if (!mons || !mons.length) return null;
		const open = this.cherishNickSentOpen;
		return <div class="cw-card" style="margin-top:12px;max-width:none">
			<button
				style="display:flex;align-items:center;gap:8px;width:100%;background:none;border:0;padding:2px 0;cursor:pointer;font-size:13px;text-align:left"
				onClick={() => { this.cherishNickSentOpen = !open; this.forceUpdate(); }}
			>
				<span aria-hidden style="font-size:11px;color:#68707c">{open ? '\u25BC' : '\u25B6'}</span>
				<b>내가 보낸 아이들 {mons.length}마리</b>
				<small style="color:#68707c;font-weight:normal">이름을 다시 지어줄 수 있어요</small>
			</button>
			{open ? <div style="margin-top:8px;display:flex;flex-direction:column;gap:6px">
				{mons.map((m: any) => this.renderCherishSentRow(m))}
			</div> : null}
		</div>;
	}
	renderCherishSentRow(m: any) {
		const pending = m.pending;
		const cooling = !pending && !!m.cooldownUntil;
		let note: any = null;
		let action: any = null;
		if (pending && pending.kind === 'offer') {
			note = pending.proposedNickname
				? <>&#x300C;{pending.proposedNickname}&#x300D;로 제안했어요. 답을 기다리는 중이에요.</>
				: <>원래 이름으로 되돌리자고 했어요. 답을 기다리는 중이에요.</>;
			action = <button class="button" style="font-size:11px;padding:3px 9px" onClick={() => this.cherishNickCancel(pending.id)}>거두기</button>;
		} else if (pending) {
			note = <>{m.holderName} 님이 이름을 지어달래요.</>;
			action = <button class="button" style="font-size:11px;padding:3px 9px" onClick={() => this.cherishNickFulfill({ id: pending.id, currentName: m.currentName, cost: m.cost })}>이름 짓기</button>;
		} else if (cooling) {
			note = <>조금 전에 이야기가 오갔어요. 하루 뒤에 다시 해볼까요?</>;
		} else {
			action = <button class="button" style="font-size:11px;padding:3px 9px" onClick={() => this.cherishNickOffer(m)}>
				{m.cost > 0 ? '이름 지어주기 (' + m.cost.toLocaleString('ko-KR') + '체리)' : '이름 지어주기'}
			</button>;
		}
		return <div style="display:flex;align-items:center;gap:8px;padding:6px 8px;border:1px solid rgba(0,0,0,.07);border-radius:9px">
			{/* 특별폼 그림 — 블록 주인인 여기가 직접 부른다 (2026-10-17, typeof 가드) */}
			<img
				src={typeof cherishMonArt === 'function' ? cherishMonArt(m) : '/cherish/sprite/' + (m.shiny ? 'shiny/' : '') + m.spriteId + '.png?v=2'}
				data-cwfb={typeof cherishMonArtBase === 'function' ? cherishMonArtBase(m) : ''}
				onError={typeof cherishMonArtFb === 'function' ? cherishMonArtFb : undefined}
				width="40" height="40" alt="" style="image-rendering:pixelated;flex:none"
			/>
			<div style="flex:1;min-width:0">
				<div style="font-size:12px"><b>{m.currentName}</b> <small style="color:#68707c">{m.speciesKo}</small></div>
				<div style="font-size:11px;color:#68707c">{m.holderName} 님과 함께 있어요{note ? <> ({note})</> : null}</div>
			</div>
			{action}
		</div>;
	}
	// cherish-nick-offer:end
	// cherish-lab-tab:begin (patch-lab-card.py)
	/**
	 * 시험장 (2026-10-27) — 출전 횟수가 적은 아이들로만 나가는 비랭크 1대1 3대3 (api lib/pokemon-lab.ts).
	 * 재료는 GET /showdown/lab 하나. access 가 closed 면 탭도 없다. 주기 요청 없음 (§4-6 통로 하나).
	 */
	cherishLabFormat = 'gen9cherishlabbattle';
	cherishLab: { access: string, maxGames?: number, maxLabGames?: number, difficulty?: string, roster?: { id: string, name: string, games: number, labGames: number, ok: boolean }[], eligible?: boolean, note?: string } | null = null;
	cherishLabFor: string | null = null;
	cherishLabTick: any = null;
	cherishLabSeenBattle = false;
	/** 탭을 그릴지 — 탭 줄이 그려질 때 유저당 1회 받아 둔다 (patch-music-tab.py 와 같은 결). */
	cherishLabTabOpen() {
		if (!PS.user.named) return false;
		this.fetchCherishLab();
		return !!(this.cherishLab && this.cherishLab.access !== 'closed');
	}
	fetchCherishLab(force = false) {
		if (!PS.user.named) return;
		if (!force && this.cherishLabFor === PS.user.userid) return;
		this.cherishLabFor = PS.user.userid;
		fetch('https://cherish-world.com/api/showdown/lab', { credentials: 'include' })
			.then(r => r.json())
			.then(json => { this.cherishLab = json?.ok ? json.data : null; this.forceUpdate(); })
			.catch(() => {});
	}
	cherishLabWaiting(): boolean {
		return (PS.mainmenu.search.searching || []).includes(this.cherishLabFormat);
	}
	/**
	 * 대기·배틀 상태는 PS.mainmenu.search 에 서버가 밀어 주는데 이 패널은 그 갱신에 구독되지 않는다.
	 * 대기 중·배틀 중에만 2초마다 다시 그린다 — 서버 요청이 아니라 로컬 상태 재그리기다. 배틀이 끝나 돌아오면 명단 수치를 한 번 다시 받는다.
	 */
	cherishLabEnsureTick(on: boolean) {
		if (on && !this.cherishLabTick) {
			this.cherishLabTick = setInterval(() => this.forceUpdate(), 2000); // poll-ok: 서버 요청 없음, 대기·배틀 중 화면 재그리기
		} else if (!on && this.cherishLabTick) {
			clearInterval(this.cherishLabTick);
			this.cherishLabTick = null;
		}
	}
	cherishLabStart = () => {
		if (!PS.user.named) {
			// cherish-login-flow: 디스코드 로그인 (patch-login-flow.py) — 랭크 CTA 와 같은 경로
			cherishLoginStart(true);
			return;
		}
		const mm = PS.mainmenu;
		if (mm.search.searching?.includes(this.cherishLabFormat)) return;
		PS.send('/utm ');
		PS.send(`${mm.adjustPrivacy()}/search ${this.cherishLabFormat}`);
		mm.update(null);
		this.forceUpdate();
	};
	cherishLabCancel = () => {
		// 랭크 CTA 와 같은 명령 — 전 모드 대기(파티 몫까지)를 끊는다
		PS.send('/cherishparty cancel');
		PS.mainmenu.update(null);
		this.forceUpdate();
	};
	/**
	 * 봇 난이도 (2026-10-27 사용자 지적) — 봇 후보는 주력 팀으로 쌓은 내 Elo 로 뽑혀서, 시험하려는 약한 아이들이 계속 진다.
	 * 살살(가장 약한 봇) · 보통(내 Elo 보다 150 아래, 기본) · 제대로(내 Elo). 내 조작으로만 바뀌는 값이라 누른 자리에서
	 * 바로 바꿔 그리고 서버에 저장한다 — 주기 요청이 아니다.
	 */
	cherishLabDifficulties = [
		{ id: 'easy', title: '살살', sub: '가장 약한 상대' },
		{ id: 'normal', title: '보통', sub: '내 레이팅보다 한 단계 아래' },
		{ id: 'hard', title: '제대로', sub: '내 레이팅 그대로' },
	];
	cherishLabSetDifficulty = (id: string) => {
		const lab = this.cherishLab;
		if (!lab || lab.difficulty === id) return;
		lab.difficulty = id;
		this.forceUpdate();
		fetch('https://cherish-world.com/api/showdown/lab/difficulty', {
			method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ difficulty: id }),
		}).catch(() => {});
	};
	/** 「내 포켓몬 › 시험장」 하위탭 본문. 호출은 블록 주인 patch-egg-tab.py 의 분기가 든다. */
	renderCherishLabTab() {
		const lab = this.cherishLab;
		if (!lab || lab.access === 'closed') return null;
		const battleRoom = PS.mainmenu.search.cherishBattle;
		const waiting = this.cherishLabWaiting();
		this.cherishLabEnsureTick(waiting || !!battleRoom);
		// 배틀을 치르고 돌아왔으면 명단 수치(출전 수)가 바뀌었다 — 한 번 다시 받는다
		if (battleRoom) this.cherishLabSeenBattle = true;
		else if (this.cherishLabSeenBattle) { this.cherishLabSeenBattle = false; this.fetchCherishLab(true); }
		const roster = lab.roster || [];
		const diff = lab.difficulty || 'normal';
		return <div class="cw-lab">
			<div class="cw-lab-head">
				<span class="cw-lab-tag"><i class="fa fa-flask" aria-hidden></i> 훈련소</span>
				<b>새 아이를 시험해 보는 곳</b>
				{lab.access === 'tester' && <span class="cw-lab-tester">관리자만 보여요</span>}
				<button class="button cw-lab-refresh" onClick={() => this.fetchCherishLab(true)} title="명단 수치 다시 받기"><i class="fa fa-refresh" aria-hidden></i></button>
			</div>
			<p class="cw-lab-rule">
				출전 <b>{lab.maxGames}배틀 이하</b>, 훈련소 <b>{lab.maxLabGames}배틀 이하</b>인 아이들로만 나갈 수 있어요.<br />
				레이팅과 전적, 오늘의 배틀 점수와 무관하게 아이들을 배틀에 참여 시켜 볼 수 있어요.
			</p>
			{roster.length > 0 && <ul class="cw-lab-roster">
				{roster.map(m => <li class={m.ok ? 'is-ok' : 'is-over'} key={m.id}>
					<span class="cw-lab-name">{m.name}</span>
					<small>{m.games}배틀<em>훈련소 {m.labGames}</em></small>
					<i class={`fa ${m.ok ? 'fa-check' : 'fa-ban'}`} aria-hidden></i>
				</li>)}
			</ul>}
			<div class="cw-lab-diff" role="group" aria-label="연습 상대 세기">
				<span class="cw-lab-diff-label">연습 상대</span>
				{this.cherishLabDifficulties.map(d => <button
					class={`cw-lab-diff-chip${diff === d.id ? ' on' : ''}`}
					disabled={waiting || !!battleRoom}
					aria-pressed={diff === d.id}
					title={d.sub}
					onClick={() => this.cherishLabSetDifficulty(d.id)}
				>{d.title}</button>)}
				<small class="cw-lab-diff-sub">{(this.cherishLabDifficulties.find(d => d.id === diff) || this.cherishLabDifficulties[1]).sub}</small>
			</div>
			{battleRoom ? <button class="cw-cta-passive" data-href={battleRoom}>
				<span class="cw-cta-icon"><i class="fa fa-gamepad" aria-hidden></i></span>
				<span class="cw-cta-body">
					<span class="cw-cta-title">배틀 진행 중</span>
					<span class="cw-cta-sub">지금 배틀을 끝내면 다시 들어갈 수 있어요. 눌러서 돌아가기</span>
				</span>
				<span class="cw-cta-go"><i class="fa fa-chevron-right" aria-hidden></i></span>
			</button> : waiting ? <button class="cw-cta cw-cta-lab is-waiting" onClick={this.cherishLabCancel}>
				<span class="cw-cta-icon"><i class="fa fa-refresh fa-spin" aria-hidden></i></span>
				<span class="cw-cta-body">
					<span class="cw-cta-title">상대를 찾고 있어요</span>
				</span>
				<span class="cw-cta-go" title="대기 취소"><i class="fa fa-times" aria-hidden></i> 취소</span>
				<span class="cw-cta-wait-bar" />
			</button> : <button class={`cw-cta cw-cta-lab${lab.eligible ? '' : ' is-locked'}`} disabled={!lab.eligible} onClick={this.cherishLabStart}>
				<span class="cw-cta-icon"><i class="fa fa-flask" aria-hidden></i></span>
				<span class="cw-cta-body">
					<span class="cw-cta-title">훈련소 입장</span>
					<span class="cw-cta-sub">{lab.eligible ? '배틀 명단 앞 세 마리가 나가요' : lab.note || '지금은 들어갈 수 없어요'}</span>
				</span>
			</button>}
		</div>;
	}
	// cherish-lab-tab:end
	// cherish-coach-notes-tab:begin (patch-coach-notes-tab.py)
	/**
	 * 담임 노트 (2026-10-30) — 복기 코치가 지난 판들에서 한 말을 다시 읽는 자리 (api lib/showdown-coach-review.ts coachNotes).
	 * 재료는 GET /showdown/coach/notes 하나. 받은 줄이 없으면 탭도 없다. 주기 요청 없음 (§4-6 통로 하나).
	 */
	cherishCoachNotes: { battles: { roomId: string, at: string, persona: string, lines: { rule: string, turn: number, text: string }[] }[] } | null = null;
	cherishCoachNotesFor: string | null = null;
	/** 규칙 라벨 — 정본은 NPC 서비스 review.ts REVIEW_RULES, 글은 어드민 COACH_RULE_LABELS 와 같다. 모르는 규칙은 칩 없이. */
	cherishCoachRuleLabels: { [rule: string]: string } = {
		'escape': '위험한 자리', 'ko-missed': '마무리 놓침', 'resisted': '반감·무효 기술', 'pingpong': '교체 핑퐁', 'praise': '칭찬',
		'needless-switch': '불필요 교체', 'heal-missed': '회복 놓침', 'foe-boost': '랭크업 방치', 'bad-matchup': '바꿀 자리',
		'setup-missed': '랭크업 기회', 'ko-priority': '선공기 마무리', 'ko-gamble': '뺄 곳 없던 자리', 'status-missed': '상태이상 기회',
		'predict': '상대가 바꿔 헛나감', 'weak-hit': '기술이 거의 안 박힘', 'take-hit': '크게 맞음', 'encore-missed': '앙코르 기회',
		'taunt-missed': '도발 기회', 'haze-missed': '랭크 초기화 기회', 'chip-priority': '선공기로 깎을 자리', 'protect-wish': '희망 뒤 방어',
		'lead': '선봉',
	};
	/**
	 * 탭을 그릴지 — 탭 줄이 그려질 때 유저당 1회 받아 둔다 (patch-music-tab.py 와 같은 결).
	 * 종료 카드 「담임 노트 보기」(panel-battle renderCherishCoachNotesEnd)가 전역 표식을 남기면 여기서 받아 탭을 열고 노트를 다시 받는다.
	 */
	cherishCoachNotesTabOpen() {
		if (!PS.user.named) return false;
		if ((window as any).cherishWantMonTab === 'coach') {
			(window as any).cherishWantMonTab = null;
			this.cherishMonTab = 'coach' as any;
			this.fetchCherishCoachNotes(true);
			setTimeout(() => this.forceUpdate(), 0);
		}
		this.fetchCherishCoachNotes();
		return !!(this.cherishCoachNotes && this.cherishCoachNotes.battles.length);
	}
	cherishCoachNotesTabCount() {
		const n = this.cherishCoachNotes?.battles.length || 0;
		return n ? `${n}판` : '';
	}
	fetchCherishCoachNotes(force = false) {
		if (!PS.user.named) return;
		if (!force && this.cherishCoachNotesFor === PS.user.userid) return;
		this.cherishCoachNotesFor = PS.user.userid;
		fetch('https://cherish-world.com/api/showdown/coach/notes', { credentials: 'include' })
			.then(r => r.json())
			.then(json => { this.cherishCoachNotes = json?.ok ? json.data : null; this.forceUpdate(); })
			.catch(() => {});
	}
	cherishCoachNoteDate(iso: string) {
		const d = new Date(iso);
		if (isNaN(d.getTime())) return '';
		const hh = String(d.getHours()).padStart(2, '0');
		const mm = String(d.getMinutes()).padStart(2, '0');
		return `${d.getMonth() + 1}월 ${d.getDate()}일 ${hh}:${mm}`;
	}
	/** 「내 포켓몬 › 담임 노트」 하위탭 본문. 호출은 블록 주인 patch-egg-tab.py 의 분기가 든다. */
	renderCherishCoachNotesTab() {
		const notes = this.cherishCoachNotes;
		if (!notes || !notes.battles.length) return null;
		return <div class="cw-cnotes">
			<div class="cw-cnotes-head">
				<span class="cw-cnotes-tag"><i class="fa fa-book" aria-hidden></i> 담임 노트</span>
				<b>지난 배틀에서 담임이 짚어 준 것</b>
				<button class="button cw-cnotes-refresh" onClick={() => this.fetchCherishCoachNotes(true)} title="다시 받기"><i class="fa fa-refresh" aria-hidden></i></button>
			</div>
			<p class="cw-cnotes-rule">배틀 중에 담임이 한 말을 판마다 모아 뒀어요. 같은 자리가 또 나오면 담임도 다시 짚어 줘요.</p>
			{notes.battles.map(b => {
				const closing = b.lines.filter(l => l.rule === 'closing');
				const body = b.lines.filter(l => l.rule !== 'closing');
				const berry = b.persona === 'berry';
				return <div class={`cw-cnotes-battle${berry ? ' is-berry' : ''}`} key={b.roomId}>
					<div class="cw-cnotes-bh">
						<span class="cw-cnotes-face">
							<img src={`/cherish/ui/coach-${berry ? 'berry' : 'cherry'}.png`} alt="" onError={(e: any) => { e.currentTarget.style.display = 'none'; }} />
						</span>
						<b>담임 {berry ? '베리' : '체리'}</b>
						<small>{this.cherishCoachNoteDate(b.at)}</small>
					</div>
					<ul class="cw-cnotes-lines">
						{body.map((l, i) => <li class={l.rule === 'praise' ? 'is-praise' : ''} key={i}>
							{this.cherishCoachRuleLabels[l.rule] ? <em>{this.cherishCoachRuleLabels[l.rule]}</em> : null}
							{l.turn > 0 ? <small>{l.turn}턴</small> : null}
							<span>{l.text}</span>
						</li>)}
					</ul>
					{closing.length ? <p class="cw-cnotes-closing">{closing[0].text}</p> : null}
				</div>;
			})}
		</div>;
	}
	// cherish-coach-notes-tab:end
	// cherish-egg:begin (patch-egg-tab.py)
	/**
	 * 알 이름 — **정본은 packages/shared/src/pokemon-egg-grade.ts 의 `EGG_GRADE_NAME`** 이다 (2026-10-01 봇에서 이사). 포크라 import 를 못 해서
	 * 손으로 베껴 둔다. 등급이 바뀌면 **두 곳을 같이** 고칠 것.
	 *
	 * ⚠️ 2026-09-09 수정: 여기가 6단계 재편(2026-09-05) 이전의 **3단계 이름표**에 멈춰 있어서
	 * 1등급 빼고 전부 한 칸씩 밀려 있었다 (2등급을 「반짝이는 알」로, 4·5등급을 「전설이 깃든 알」로
	 * 불렀다). 베리로 파는 2등급이 화면에서 「반짝이는 알」로 보인 게 이것 때문 — 파는 물건이
	 * 틀린 게 아니라 이름이 틀렸다.
	 */
	cherishEggName(grade: number) {
		return ({ 1: '포근한 알', 2: '은은한 알', 3: '반짝이는 알', 4: '신비로운 알', 5: '수상한 알', 6: '전설이 깃든 알' } as any)[grade] || `${grade}등급 알`;
	}
	/** 남은 시간 — 통화방에 있어야 1분씩 차므로 "통화방 기준"이라고 읽어야 한다. */
	cherishEggRemain(e: any) {
		const left = Math.max(0, (e.requiredMinutes || 0) - (e.warmMinutes || 0));
		if (left >= 60) return `${Math.floor(left / 60)}시간 ${left % 60}분`;
		return `${left}분`;
	}
	/** 서버가 돌려준 알 목록·상점 시세로 화면을 갱신한다 (액션마다 전체 재조회를 안 하려고). */
	applyCherishEggResult(data: any) {
		if (data?.eggs) this.cherishEggs = data.eggs;
		if (data?.shop) this.cherishShop = data.shop;
		this.cherishEggBusy = false;
		this.forceUpdate();
	}
	/**
	 * 안내를 화면 고정 토스트로도 띄운다 (2026-09-10). 인라인 `cw-note` 는 알 목록 위쪽이라
	 * 아래로 스크롤해 알을 사면 안내가 화면 밖에서 떴다 사라졌다 — 도구 상점과 같은 문제였다.
	 *
	 * ⚠️ 토스트 **정의는 patch-item-tab.py 소유**다. 그 패치가 안 발린 작업본에서 호출부만
	 * 남으면 화면이 통째로 죽으므로 `typeof` 가드를 끼고 부른다 (2026-09-09 사고와 같은 계열).
	 */
	cherishEggToast(bad: boolean) {
		const note = this.cherishEggNotice;
		if (note && typeof (this as any).cherishToastNote === 'function') (this as any).cherishToastNote(note, bad);
	}
	/**
	 * 알 모으기 구간 라벨 — **수치 대신 구간만** 말한다 (2026-09-19 사용자 확정).
	 *
	 * ⚠️ 낱말로 **「온기」·「따뜻함」 계열을 쓰지 말 것.** 뜻은 제일 잘 맞지만 같은 카드에
	 * 품기 진행도가 붙어 있어서 「온기 가득」이 **「다 데워졌다」로 읽힌다**.
	 *
	 * ⚠️ 정본은 api `lib/pokemon-egg-fuse.ts` 의 `fuseBadgeOf` 다. 포크라 shared import 를
	 * 못 해서 손으로 베낀 사본이고, 이미 만든 알의 배지는 서버가 `fusedBadge` 로 내려준다.
	 * 여기 사본은 **고르는 중 미리보기**에만 쓰인다. 구간을 바꾸면 두 곳을 같이 고칠 것
	 * (알 이름표 `cherishEggName` 이 3단계에 멈춰 한 칸씩 밀려 있던 사고와 같은 계열).
	 */
	cherishFuseDegree(n: number) {
		if (n >= 20) return '가득';
		if (n >= 15) return '아주 많이';
		if (n >= 10) return '많이';
		if (n >= 5) return '조금 더';
		return '조금';
	}
	/** 알 이름 옆 배지 — 「소망 가득」. */
	cherishFuseBadge(n: number) {
		return '소망 ' + this.cherishFuseDegree(n);
	}
	/**
	 * 알림 문장 — 「소망이 조금 더 담긴 알이에요」.
	 *
	 * 배지를 문장에 그대로 꽂지 않는다 — 「소망 조금 더 담긴」은 조사가 없어 끊긴다.
	 */
	cherishFuseLine(n: number) {
		return '소망이 ' + this.cherishFuseDegree(n) + ' 담긴 알이에요';
	}
	/** 이 알을 재료로 쓸 수 있나 — 포근한 알이고, 이미 모은 알이 아니어야 한다. */
	cherishFusable(e: any) {
		return Number(e.grade) === 1 && e.fusedCount == null;
	}
	/** 최소·최대 개수는 서버가 준다 (`shop.fuse`). 확률은 안 준다 — 그건 노출이다. */
	cherishFuseMax() {
		return Number((((this.cherishShop || {}).fuse) || {}).max) || 20;
	}
	cherishFuseMin() {
		return Number((((this.cherishShop || {}).fuse) || {}).min) || 2;
	}
	toggleCherishFuseMode() {
		this.cherishFuseMode = !this.cherishFuseMode;
		this.cherishFuseSel = [];
		this.cherishEggNotice = null;
		this.forceUpdate();
	}
	/**
	 * 재료를 고르거나 뺀다. 상한에 닿으면 **개수로** 알려 준다 — 개수는 유저가 직접 고르는
	 * 값이라 노출이 아니지만, 확률은 노출이라 어디에도 수치로 쓰지 않는다.
	 */
	toggleCherishFusePick(id: string) {
		const at = this.cherishFuseSel.indexOf(id);
		if (at >= 0) {
			this.cherishFuseSel.splice(at, 1);
			this.cherishEggNotice = null;
		} else {
			const max = this.cherishFuseMax();
			if (this.cherishFuseSel.length >= max) {
				this.cherishEggNotice = `알은 한 번에 ${max}개까지 넣을 수 있어요.`;
				this.forceUpdate();
				return;
			}
			this.cherishFuseSel.push(id);
			this.cherishEggNotice = null;
		}
		this.forceUpdate();
	}
	/**
	 * 합성 실행. 확인창은 **두 줄이 끝이다** (알 팔기와 같은 규칙) — 되돌릴 수 없는 일이라
	 * 길게 쓰고 싶어지지만 길어지면 안 읽힌다. 「팔 수 없다」가 가장 중요한 한 줄이다.
	 */
	doCherishFuse() {
		const n = this.cherishFuseSel.length;
		const min = this.cherishFuseMin();
		if (n < min) {
			this.cherishEggNotice = `알을 ${min}개 이상 골라주세요.`;
			this.forceUpdate();
			return;
		}
		if (!confirm(`알 ${n}개를 하나로 모을까요?

		모은 알은 되돌릴 수 없고, 팔 수도 없어요.`)) return;
		this.cherishEggPost('eggs/fuse', { eggIds: this.cherishFuseSel.slice() }, data => {
			const used = Number(data.used) || n;
			this.cherishEggNotice = `알 ${used}개가 하나로 모였어요. ${this.cherishFuseLine(used)}!`;
			this.cherishFuseMode = false;
			this.cherishFuseSel = [];
			this.applyCherishEggResult(data);
		});
	}
	cherishEggPost(path: string, body: any, onOk: (data: any) => void) {
		if (this.cherishEggBusy) return;
		this.cherishEggBusy = true;
		this.cherishEggNotice = null;
		this.forceUpdate();
		fetch(`https://cherish-world.com/api/showdown/${path}`, {
			method: 'POST', credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(body),
		})
			.then(r => r.json())
			.then(json => {
				if (!json?.ok) {
					this.cherishEggNotice = json?.message || json?.error?.message || '처리하지 못했어요. 잠시 후 다시 시도해주세요.';
					this.cherishEggBusy = false;
					this.cherishEggToast(true);
					this.forceUpdate();
					return;
				}
				onOk(json.data);
				// onOk 가 채운 안내를 그대로 띄운다 — 구매·팔기·품기·부화 전부 한 자리에서 (2026-09-10)
				this.cherishEggToast(false);
			})
			.catch(() => {
				this.cherishEggNotice = '연결이 끊겼어요. 잠시 후 다시 시도해주세요.';
				this.cherishEggBusy = false;
				this.cherishEggToast(true);
				this.forceUpdate();
			});
	}
	/**
	 * 자정 리셋 뒤 **첫 구매 가격**. 사다리(`ladder`)가 있으면 그 첫 칸이 진짜 첫 값이고,
	 * `basePrice`(기준가)는 사다리 없는 등급에서만 쓰인다. 1등급은 사다리가 켜져 있어
	 * (1,500/3,000/5,000/…) basePrice(1,000)를 그대로 보여주면 실제와 어긋난다 (2026-09-05).
	 */
	cherishEggResetPrice(p: any) {
		if (p && p.ladder && p.ladder.length) return p.ladder[0] || 0;
		return (p && p.basePrice) || 0;
	}
	buyCherishEgg(grade: number, price: number) {
		const name = this.cherishEggName(grade);
		const entry = (((this.cherishShop || {}).prices) || []).find((x: any) => x.grade === grade);
		const reset = this.cherishEggResetPrice(entry);
		// 사다리는 값 목록이라 "두 배씩"이라고 못 쓴다 — 실제 첫 값도 basePrice 가 아니라 ladder[0].
		const resetLine = reset ? ` (자정에 ${reset.toLocaleString('ko-KR')}체리로 돌아와요)` : ' (자정에 처음 가격으로 돌아와요)';
		if (!confirm(`${name} 을(를) ${price.toLocaleString('ko-KR')}체리로 데려올까요?\n\n같은 날 살수록 가격이 올라가요.${resetLine}`)) return;
		this.cherishEggPost('shop/buy', { grade }, data => {
			this.cherishEggNotice = `${name} 을(를) 데려왔어요! 다음 ${name} 은 ${(data.nextPrice || 0).toLocaleString('ko-KR')}체리예요.`;
			this.applyCherishEggResult(data);
		});
	}
	/**
	 * 베리로 알을 산다 (2026-09-15 통합).
	 *
	 * 값은 **그 주기 몇 번째냐**에 따라 오른다. 포근한 알은 자정, 은은한 알은 월요일 자정(KST)에
	 * 첫 값으로 돌아온다 — 등급마다 경계가 달라서 서버가 `weekly` 로 알려 준다.
	 * 값을 정하는 건 서버다 — 여기 price 는 표시·확인창용이라, 화면이 낡았으면 서버가 다른 값을
	 * 매긴다. 그래서 산 뒤에 실제로 낸 값(data.paid)과 다음 값을 알림에 같이 싣는다.
	 */
	buyCherishEggBerry(grade: number, price: number) {
		const name = this.cherishEggName(grade);
		if (!confirm(`${name} 을(를) 베리 ${price.toLocaleString('ko-KR')} 로 데려올까요?`)) return;
		this.cherishEggPost('shop/buy-berry', { grade }, data => {
			const paid = data.paid || price;
			const next = data.nextPrice || 0;
			const when = data.weekly ? '이번 주 다음' : '오늘 다음';
			const nextLine = next > 0 ? ` ${when} 알은 🫐${next.toLocaleString('ko-KR')} 이에요.` : '';
			this.cherishEggNotice = `${name} 을(를) 🫐${paid.toLocaleString('ko-KR')} 로 데려왔어요! 남은 베리는 ${(data.berry || 0).toLocaleString('ko-KR')} 이에요.${nextLine}`;
			this.applyCherishEggResult(data);
		});
	}
	/**
	 * 알을 팔아 베리로 바꾼다 (2026-09-09).
	 *
	 * **확인창은 두 줄이 끝이다** (2026-09-10 사용자 결정). 전에는 「판 알은 돌아오지 않아요」·
	 * 데운 시간·「은은한 알까지 N개 더」까지 붙여 다섯 줄이었다 — 되돌릴 수 없는 일이라 길게
	 * 쓴 건데, 길어지면 오히려 안 읽힌다. 지금 부화시킬 수 있다는 것 하나만 남긴다.
	 * 여기에 줄을 다시 늘리지 말 것.
	 */
	sellCherishEgg(e: any) {
		const shop = this.cherishShop || {};
		const gain = ((shop.berrySell || {})[e.grade]) || 0;
		if (!gain) return;
		const name = this.cherishEggName(e.grade);
		// 다 데운 알에만 붙는다 — 부화 버튼 바로 옆이라 손이 미끄러지면 지금 깔 수 있던 알이 사라진다.
		const readyLine = e.ready ? `

지금 부화시킬 수 있는 알이에요.` : '';
		if (!confirm(`${name} 을(를) 팔아 베리 ${gain.toLocaleString('ko-KR')} 로 바꿀까요?${readyLine}`)) return;
		this.cherishEggPost('shop/sell', { eggId: e.id }, data => {
			this.cherishEggNotice = `베리 ${(data.gained || 0).toLocaleString('ko-KR')} 를 받았어요. 지금 ${(data.berry || 0).toLocaleString('ko-KR')} 모았어요.`;
			this.applyCherishEggResult(data);
		});
	}
	slotCherishEgg(id: string) {
		this.cherishEggPost('eggs/slot', { eggId: id }, data => {
			this.cherishEggNotice = data.displaced
				? '이 알을 품기 시작했어요. 자리를 내준 알은 데운 만큼 그대로 간직해요.'
				: '이 알을 품기 시작했어요!';
			this.applyCherishEggResult(data);
		});
	}
	hatchCherishEgg(id: string) {
		this.cherishEggPost('eggs/hatch', { eggId: id }, data => {
			const p = data.pokemon || {};
			this.cherishEggNotice = `🎉 ${p.shiny ? '✨ ' : ''}${p.koName || p.speciesName || '새 친구'} 가 태어났어요!`;
			this.cherishEggBusy = false;
			this.loadCherishData(); // 포켓몬 목록까지 바뀌므로 전체 재조회
		});
	}
	/** 「내 포켓몬」 하위탭 — 상단 탭바가 이미 꽉 차 여기서 나눈다.
	 *  v2 (2026-09-05): 12.5px 버튼 둘이 「탭」으로 안 읽혀 신규 유저가 알 탭을 몰랐다 → 카드 폭 세그먼트(cw-subtabs) +
	 *  알이 하나도 없고 상점이 열려 있으면 「새로운 알을 구매해보세요 !」 카드(닫으면 localStorage 로 기억). 외형 cherish-skin.css §22. */
	renderCherishTeamTabs() {
		if (!PS.user.named) return null;
		// 이 메서드는 남의 하위탭(인벤토리·상점·거리두기·음악 CD)의 **호출을 전부 직접 든다** (2026-10-17, 09-19 규약).
		// 예전엔 patch-item-tab.py · patch-avoid-tab.py 가 이 블록 안에 줄을 끼워 넣었는데, 이 패치를 다시 바르면
		// 블록이 통째로 갈려 그 줄만 조용히 사라졌다 — 10-16 빌려주기 배포에서 실제로 상점·인벤토리·거리두기 탭이
		// 한꺼번에 증발해 베리 상점(은은한 알)에 아무도 못 들어갔다. 저쪽이 안 발린 작업본에서도 안 죽게 옵셔널로 부른다.
		(this as any).fetchCherishItemShop?.(); // cherish-items: 인벤토리·상점 하위탭 표시 여부 (유저당 1회)
		const mons: any[] = (this.cherishPokemon || []).filter((m: any) => !m.borrowed); // 탭 숫자는 내 아이만 (0477)
		const eggs: any[] = this.cherishEggs || [];
		const ready = eggs.filter((e: any) => e.ready).length;
		const avoidN = ((this as any).cherishAvoids || []).length;
		const tab = (key: 'mon' | 'egg' | 'items' | 'shop' | 'avoid', label: any, count: string, badge: string | null) => <button
			class={`cw-subtab${this.cherishMonTab === key ? ' on' : ''}`}
			onClick={() => { this.cherishMonTab = key; this.forceUpdate(); }}
		><b>{label}</b>{count ? <small>{count}</small> : null}{badge ? <em>{badge}</em> : null}</button>;
		let promoOff = false;
		try { promoOff = localStorage.getItem('cw-egg-promo-off') === '1'; } catch {}
		const shopOpen = !!(this.cherishShop && this.cherishShop.open);
		const promo = this.cherishMonTab === 'mon' && !eggs.length && shopOpen && !promoOff;
		return <div>
			<div class="cw-subtabs">
				{tab('mon', '내 포켓몬', mons.length ? `${mons.length}마리` : '', null)}
				{tab('egg', <span><i class="cw-egg-ic" aria-hidden /> 알</span>, eggs.length ? `${eggs.length}개` : '', ready > 0 ? `부화 가능 ${ready}` : null)}
				{/* cherish-items-tab: 🎒 인벤토리 · 🛍️ 상점 — patch-item-tab.py 가 메서드 소유, 호출은 블록 주인인 여기가 든다. 상점은 도구 상점이 닫혀도 베리 알이 있으면 뜬다 */}
				{(this as any).cherishItemShop?.open ? tab('items', '🎒 인벤토리', '', null) : null}
				{(this as any).cherishShopTabOpen?.() ? tab('shop', '🛍️ 상점', '', null) : null}
				{/* cherish-music-tab: 💿 음악 CD — patch-music-tab.py 가 메서드 소유, 호출은 블록 주인인 여기가 든다. ⚠️ 한 줄로 둘 것(patch-avoid-tab.py 의 탭 줄 정규식이 `{` 로 시작하는 줄만 센다) */}
				{(this as any).cherishMusicTabOpen?.() ? tab('music' as any, '💿 음악 CD', (this as any).cherishMusicTabCount?.() || '', null) : null}
				{/* cherish-lab-tab: 🧪 훈련소 — patch-lab-card.py 가 메서드 소유, 호출은 블록 주인인 여기가 든다 (2026-10-27). 스위치가 꺼져 있으면 탭이 없다. 한 줄로 둘 것 */}
				{(this as any).cherishLabTabOpen?.() ? tab('lab' as any, '🧪 훈련소', '', null) : null}
				{/* cherish-coach-notes-tab: 📓 담임 노트 — patch-coach-notes-tab.py 가 메서드 소유, 호출은 블록 주인인 여기가 든다 (2026-10-30). 받은 줄이 없으면 탭이 없다. 한 줄로 둘 것 */}
				{(this as any).cherishCoachNotesTabOpen?.() ? tab('coach' as any, '📓 담임 노트', (this as any).cherishCoachNotesTabCount?.() || '', null) : null}
				{/* cherish-avoid-tab: 🔕 거리두기 — patch-avoid-tab.py 가 메서드 소유, 호출은 여기. 언제나 맨 끝 */}
				{(this as any).renderCherishAvoidTab ? tab('avoid', '🔕 거리두기', avoidN ? `${avoidN}명` : '', null) : null}
			</div>
			{promo ? <div class="cw-egg-promo">
				<i class="cw-egg-ic" aria-hidden />
				<span>새로운 알을 구매해보세요 !</span>
				<button class="button cw-egg-promo-go" onClick={() => { this.cherishMonTab = 'egg'; this.forceUpdate(); }}>알 상점 보기</button>
				<button class="cw-egg-promo-x" aria-label="닫기" onClick={() => { try { localStorage.setItem('cw-egg-promo-off', '1'); } catch {} this.forceUpdate(); }}>&times;</button>
			</div> : null}
			{this.cherishMonTab === 'mon' ? this.renderCherishTeam() : this.cherishMonTab === 'items' ? (this as any).renderCherishItemTab?.() : this.cherishMonTab === 'shop' ? (this as any).renderCherishShopTab?.() : (this.cherishMonTab as string) === 'music' ? (this as any).renderCherishMusicTab?.() : (this.cherishMonTab as string) === 'lab' ? (this as any).renderCherishLabTab?.() : (this.cherishMonTab as string) === 'coach' ? (this as any).renderCherishCoachNotesTab?.() : this.cherishMonTab === 'avoid' ? (this as any).renderCherishAvoidTab?.() : this.renderCherishEggTab()}
			{/* cherish-nick-offer: 「내가 보낸 아이들」 + 「이름 이야기」 팝업 (patch-nickname-offer.py 가 메서드 소유).
			    **호출은 블록 주인인 여기가 들고 있는다** (2026-09-19 규약) — 저쪽 패치가 호출 한 줄을 이 블록
			    안에 끼워 넣었다가 이 패치 재적용에 조용히 지워져, 이름 이야기 팝업과 「내가 보낸 아이들」이
			    09-10 이후 통째로 안 떴다. 저쪽이 안 발린 작업본에서도 안 죽게 옵셔널 호출로 부른다. */}
			{this.cherishMonTab === 'mon' ? (this as any).renderCherishSentMons?.() : null}
			{(this as any).renderCherishNickPopup?.()}
		</div>;
	}
	/** 알 탭 v2 (2026-09-05 디테일 상향) — 품기 칸은 진행 링, 상점은 카드 행, 내 알은 카드 그리드. 외형 cherish-skin.css §23. */
	renderCherishEggTab() {
		if (this.cherishLoadedFor !== PS.user.userid) this.loadCherishData();
		const eggs = this.cherishEggs;
		const shop = this.cherishShop;
		const gate = this.cherishHatchGate;
		const busy = this.cherishEggBusy;
		const fuseOpen = !!(((this.cherishShop || {}).fuse) || {}).open;
		const fuseSel = this.cherishFuseSel;
		const slotted = (eggs || []).filter((e: any) => e.slot);
		const slots = [1, 2].map(n => slotted.find((e: any) => Number(e.slot) === n) || null);
		const pct = (e: any) => Math.min(100, Math.round((e.warmMinutes / Math.max(1, e.requiredMinutes)) * 100));

		return <div class="menugroup cw-eggs" style="max-width:none">
			<div class="cw-sec-head"><div class="cw-sec-head-text"><span class="cw-eyebrow">Eggs</span><h3 class="cw-sec-title">알 품기</h3>
				<p class="cw-sec-sub">통화방에 있는 동안 알이 데워져요. 한 번에 <strong>2개</strong>까지 품을 수 있고, 자리가 비면 기다리던 알이 알아서 들어가요. 부화는 통화방에 있을 때만 할 수 있어요.</p>
			</div></div>
			{this.cherishEggNotice && <p class="cw-note">{this.cherishEggNotice}</p>}
			<div class="cw-egg-slots">
				{slots.map((e: any, i: number) => <div class={`cw-egg-slot${e ? ' is-on' : ''}`}>
					<div class="cw-egg-ring" style={`--p:${e ? pct(e) : 0}`}><i class={`cw-egg-ic${e ? ` g${e.grade}` : ''}`} aria-hidden /></div>
					<div class="cw-egg-slot-body">
						<small>품기 칸 {i + 1}</small>
						{!e ? <b class="is-empty">비어 있어요</b> : <>
							<b>{this.cherishEggName(e.grade)}</b>
							<span>{e.warmMinutes}/{e.requiredMinutes}분, 통화방에서 {this.cherishEggRemain(e)} 더</span>
						</>}
					</div>
				</div>)}
			</div>
			{shop && shop.open && (shop.prices || []).length > 0 && <div class="cw-egg-shop">
				{/* 잔액은 두 화폐를 나란히 — 값에 🍒/🫐 를 붙여도 「내가 얼마 있는지」가 위에 없으면
				    살 수 있는지 매번 계산해야 한다 (2026-09-09 사용자 확정). */}
				<div class="cw-egg-shop-head"><b>알 상점</b><small class="cw-wallet">
					<span>🍒 <strong>{(shop.cherry || 0).toLocaleString('ko-KR')}</strong></span>
					<span>🫐 <strong>{(shop.berry || 0).toLocaleString('ko-KR')}</strong></span>
				</small></div>
				{(shop.prices || []).map((p: any) => {
					const poor = (shop.cherry || 0) < (p.price || 0);
					return <div class="cw-egg-row">
						<i class={`cw-egg-ic g${p.grade}`} aria-hidden />
						<div class="cw-egg-row-body">
							<b>{this.cherishEggName(p.grade)}</b>
							<span>🍒 <strong>{(p.price || 0).toLocaleString('ko-KR')}</strong>, 품기 {p.warmMinutes}분</span>
							<small>{p.boughtToday > 0 ? `오늘 ${p.boughtToday}개 샀어요, ` : ''}다음은 🍒 {(p.nextPrice || 0).toLocaleString('ko-KR')} (자정에 🍒 {this.cherishEggResetPrice(p).toLocaleString('ko-KR')} 로 돌아와요)</small>
						</div>
						<button class={`button cw-buy${busy || poor ? ' disabled' : ''}`} disabled={busy || poor} onClick={() => this.buyCherishEgg(p.grade, p.price)}>{poor ? '체리 부족' : '데려오기'}</button>
					</div>;
				})}
				{/* 베리 알은 2026-09-09 **상점 탭 › 🫐 베리** 로 옮겼다 (patch-item-tab.py 의 renderCherishShopTab).
				    여기 남은 건 체리로 사는 일반 알뿐 — 파는 자리(아래 내 알 목록)와 붙어 있어야 해서 알 탭에 뒀다. */}
				{(shop.berryEggs || []).length > 0 && <p class="cw-egg-shop-foot">🫐 베리로 데려오는 알은 <button class="button cw-linkish" onClick={() => { this.cherishMonTab = 'shop'; this.cherishShopSeg = 'dust'; this.forceUpdate(); }}>상점</button> 에 있어요.</p>}
			</div>}
			{gate && !gate.ok && (eggs || []).some((e: any) => e.ready) && <p class="cw-note is-warn">{gate.message}</p>}
			<div class="cw-sec-head" style="margin-top:14px"><div class="cw-sec-head-text"><span class="cw-eyebrow">My eggs</span><h3 class="cw-sec-title">내 알</h3>
				{fuseOpen && <p class="cw-sec-sub">알 여러 개를 하나로 모을 수 있어요. 많이 모을수록 숨겨진 특성을 지니고 태어날 가능성이 높아져요. 특별한 색을 가진 아이가 태어날 가능성도 함께 올라가요.</p>}
			</div>{eggs && eggs.length ? <span class="cw-sec-count">{eggs.length}개</span> : null}</div>
			{/* 알 모으기 (0397) — 확률 수치는 어디에도 쓰지 않는다. 구간 라벨만 (사용자 확정). */}
			{fuseOpen && (eggs || []).some((e: any) => this.cherishFusable(e)) && <div class="cw-egg-fuse">
				{!this.cherishFuseMode ? (
					<button class={`button cw-egg-collect${busy ? ' disabled' : ''}`} disabled={busy} onClick={() => this.toggleCherishFuseMode()}>
						<i class="cw-egg-collect-ic" aria-hidden />
						<span><b>알 모으기</b></span>
					</button>
				) : <>
					<b>{fuseSel.length}개 선택{fuseSel.length >= this.cherishFuseMin() ? ` · ${this.cherishFuseBadge(fuseSel.length)}` : ''}</b>
					<span>모을 알을 골라주세요. 포근한 알만 넣을 수 있어요.</span>
					<button class={`button cw-hatch${busy || fuseSel.length < this.cherishFuseMin() ? ' disabled' : ''}`}
						disabled={busy || fuseSel.length < this.cherishFuseMin()} onClick={() => this.doCherishFuse()}>모으기</button>
					<button class={`button${busy ? ' disabled' : ''}`} disabled={busy} onClick={() => this.toggleCherishFuseMode()}>취소</button>
				</>}
			</div>}
			{!eggs ? this.renderCherishSkeleton('grid') : !eggs.length ? (
				<div class="cw-empty">아직 알이 없어요. 위에서 알을 데려오거나, 어린이집·온실에서 만나보세요!</div>
			) : (
				<div class="cw-egg-grid">
					{eggs.map((e: any) => <div class={`cw-egg-card${e.ready ? ' is-ready' : e.warming ? ' is-warming' : ''}`}>
						<div class="cw-egg-ring sm" style={`--p:${pct(e)}`}><i class={`cw-egg-ic g${e.grade}`} aria-hidden /></div>
						<b>{this.cherishEggName(e.grade)}{e.fusedBadge ? <small class="cw-egg-fuse-badge"> {e.fusedBadge}</small> : null}</b>
						<span>{e.ready ? '부화할 수 있어요!' : e.warming ? `품는 중 ${e.warmMinutes}/${e.requiredMinutes}분` : `차례 기다리는 중 ${e.warmMinutes}/${e.requiredMinutes}분`}</span>
						{/* 모으는 중에는 부화·먼저 품기·팔기를 감춘다 — 손이 미끄러지면 재료가 사라진다. */}
						{this.cherishFuseMode ? (
							this.cherishFusable(e) ? (
								<button class={`button${fuseSel.indexOf(e.id) >= 0 ? ' cw-hatch' : ''}${busy ? ' disabled' : ''}`} disabled={busy}
									onClick={() => this.toggleCherishFusePick(e.id)}>{fuseSel.indexOf(e.id) >= 0 ? '고름' : '고르기'}</button>
							) : e.fusedCount != null ? (
								<em>이미 모은 알</em>
							) : <em>포근한 알만</em>
						) : e.ready ? (
							<button class={`button cw-hatch${busy || !(gate && gate.ok) ? ' disabled' : ''}`} disabled={busy || !(gate && gate.ok)} onClick={() => this.hatchCherishEgg(e.id)}>부화</button>
						) : e.warming ? (
							<em>품는 중</em>
						) : (
							<button class={`button${busy ? ' disabled' : ''}`} disabled={busy} title="지금 품고 있는 알 중 덜 데워진 쪽과 자리를 바꿔요" onClick={() => this.slotCherishEgg(e.id)}>먼저 품기</button>
						)}
						{/* 팔기 (2026-09-09) — 부화·품기 버튼보다 **한 단계 약하게** 둔다. 이 탭의 주된 행동은
						    까는 것이고 파는 건 남을 때 하는 선택이다.
						    **품는 중인 알에도 띄운다.** 처음엔 데운 시간이 아까워 숨겼는데, 알은 받는 즉시 빈
						    품기 칸으로 들어가고 칸이 둘뿐이라 하루 2개 받는 유저는 가진 알이 전부 품기 중이
						    된다 — 그러면 팔려면 다른 알로 자리를 밀어내야 해서 기능이 죽는다 (사용자 지적).
						    대신 확인창에서 데운 시간이 얼마나 사라지는지 알려 준다.
						**다 데운 알에도 띄운다** (2026-09-10 사용자 결정) — 다 데워졌다는 이유로 팔 길이 막히면
						품기 칸이 그 알에 잠긴 채 파는 것도 까는 것도 미룬 유저가 갇힌다. 판매·부화 동시 요청은
						DB 가 막는다(둘 다 `hatched_at IS NULL` 을 건 원자적 한 문장이라 한쪽만 이긴다).
						확인창은 이때 한 줄 더 무겁게 나간다. */}
						{!this.cherishFuseMode && !!(((shop || {}).berrySell || {})[e.grade]) && (
							<button class={`button cw-egg-sell${busy ? ' disabled' : ''}`} disabled={busy}
								title="이 알을 베리로 바꿔요. 판 알은 돌아오지 않아요"
								onClick={() => this.sellCherishEgg(e)}>팔기 🫐{((shop.berrySell || {})[e.grade] || 0).toLocaleString('ko-KR')}</button>
						)}
					</div>)}
				</div>
			)}
		</div>;
	}
	// cherish-egg:end
	// cherish-party-ui:begin (patch-party-ui.py)
	/** 파티 명령은 전부 여기로 — 인자 규약은 `/cherishparty <동작>, <상대>` (서버가 ',' 로 쪼갠다). */
	cherishPartyCmd = (action: string, who?: string) => {
		PS.send(who ? `/cherishparty ${action}, ${who}` : `/cherishparty ${action}`);
	};
	/**
	 * 접속(또는 계정 전환) 후 한 번 상태를 물어본다 — 새로고침·재접속으로 놓친 초대를
	 * 되찾는 길이다. push 는 그 순간 붙어 있던 화면에만 닿는다.
	 */
	cherishPartySyncFor: string = '';
	ensureCherishPartySync() {
		if (!PS.user.named) return;
		if (this.cherishPartySyncFor === PS.user.userid) return;
		this.cherishPartySyncFor = PS.user.userid;
		PS.send('/cherishparty status');
	}
	/**
	 * 홈 「파티」 섹션 — 상태 세 가지를 한 자리에서 보여준다.
	 * 초대 받음 > 파티 중 > 초대 보내기 순으로 가른다 (초대를 받은 상태가 제일 급하다).
	 *
	 * ⚠️ **모드 게이트는 「초대 보내기」에만 건다** (2026-09-09). 앞 둘까지 가리면 초대를
	 * 받은 사람이 「매칭하기」를 고르고 있어야만 초대장을 볼 수 있는데, 미졸업자는 그 모드를
	 * 아예 고를 수 없어 영영 못 본다 — 서버는 파티 초대를 일부러 안 막는다(09-06 결정).
	 */
	renderCherishParty() {
		if (!PS.user.named) return null;
		this.ensureCherishPartySync();
		const party = ((PS as any).cherishParty || {}) as {
			partner?: string | null, partnerName?: string | null,
			invitedTo?: string | null, invitedToName?: string | null,
			leaderName?: string | null, isLeader?: boolean,
			partnerOffline?: boolean,
		};
		let invite = (PS as any).cherishPartyInvite as
			{ from: string, fromName: string, until?: number } | null;
		// 만료된 초대는 없는 것과 같다 (서버 TTL 2분). 수락해 봐야 거절당한다.
		if (invite?.until && invite.until <= Date.now()) {
			(PS as any).cherishPartyInvite = null;
			invite = null;
		}

		if (invite && !party.partner) {
			return <div class="menugroup cw-party-got" style="max-width:none">
				<h3>파티 초대</h3>
				<p><strong>{invite.fromName}</strong> 님이 함께하자고 불렀어요!</p>
				<p class="buttonbar">
					<button class="button" onClick={() => this.cherishPartyCmd('accept')}>
						<strong>수락</strong>
					</button> {}
					<button class="button" onClick={() => this.cherishPartyCmd('decline')}>거절</button>
				</p>
			</div>;
		}

		if (party.partner) {
			return <div class="menugroup" style="max-width:none">
				<h3>파티</h3>
				<p>
					<strong>{party.partnerName}</strong> 님과 함께하고 있어요.
					{party.leaderName ? <small> (리더: {party.leaderName})</small> : null}
				</p>
				{party.partnerOffline ? <p class="cw-party-away">
					<strong>{party.partnerName}</strong> 님이 지금 배틀 화면을 벗어나 있어요.
					돌아올 때까지는 매칭이 잡히지 않아요. 돌아오면 그대로 이어져요.
				</p> : null}
				<p><small>
					난투전은 <strong>둘이 한 편</strong>이 되어 포켓몬 세 마리를 나눠 맡아요.
					2대2 는 같은 팀으로 들어가요.
					1대1 은 자리가 하나뿐이라 <strong>번갈아 한 명씩</strong> 나가고, 나머지 한 명은
					따라가서 구경해요.
				</small></p>
				<p><small>
					매칭은 <strong>리더</strong>가 돌려요. 세 모드를 켜두면 골고루 걸리도록 순서대로 잡혀요.
				</small></p>
				<p class="buttonbar">
					<button class="button" onClick={() => this.cherishPartyCmd('leave')}>파티 나가기</button>
				</p>
			</div>;
		}

		// 「초대 보내기」도 **모드와 무관하게** 그린다 (2026-09-09 사용자 결정 — v2 에서 이것만
		// 매칭하기에 남겼더니 「여전히 랜덤매칭을 골라야 파티가 보인다」였다). 섹션이 접속
		// 트레이너 **바로 위**로 내려가 있어 홈이 길어지는 문제도 함께 없어진다 — 초대 상대를
		// 고르는 <select> 와 바로 아래 트레이너 카드가 같은 목록이라 오히려 붙어 있는 게 낫다.
		// 접속자 목록 재사용 — 타일은 <button> 이라 초대 버튼을 중첩할 수 없다
		const roomsCache = PS.mainmenu.roomsCache as any;
		const users: [string, string][] = roomsCache.cherishUsers || [];
		const others = users.map(([name]) => name).filter(name => toID(name) !== PS.user.userid);
		return <div class="menugroup" style="max-width:none">
			<h3>파티</h3>
			<p>친구와 팀을 맺으면 <strong>함께 매칭</strong>돼요. 둘까지 모일 수 있어요.</p>
			{others.length ? <p class="buttonbar">
				<select class="button cherish-party-pick" style="max-width:180px">
					{others.map(name => <option value={name}>{name}</option>)}
				</select> {}
				<button
					class="button"
					onClick={ev => {
						const box = (ev.currentTarget as HTMLElement).parentElement;
						const pick = box?.querySelector('select') as HTMLSelectElement | null;
						if (pick?.value) this.cherishPartyCmd('invite', pick.value);
					}}
				><strong>초대하기</strong></button>
			</p> : <p><em>지금 접속한 다른 트레이너가 없어요.</em></p>}
			{party.invitedTo ? <p><small>
				<strong>{party.invitedToName}</strong> 님에게 초대를 보냈어요. 2분 안에 수락하지 않으면 사라져요.
				{} 상대 화면 위에 초대 띠가 떠요.
			</small></p> : null}
		</div>;
	}
	// cherish-party-ui:end
	// cherish-blossom:begin (patch-class-blossom.py)
	/** api /showdown/daycare/blossom 응답. null = 아직 안 받음, { open: false } = 학기 스위치 꺼짐. */
	cherishBlossom: any = null;
	/** 반 색을 흰색 쪽으로 섞어 막대 왼쪽 끝에 쓴다 — API 는 색 하나만 내려보낸다. */
	cherishBlossomSoft(hex: string) {
		const m = /^#([0-9a-f]{6})$/i.exec(String(hex || ''));
		if (!m) return hex;
		const n = parseInt(m[1], 16);
		const mix = (c: number) => Math.round(c + (255 - c) * 0.62);
		const r = mix((n >> 16) & 255), g = mix((n >> 8) & 255), b = mix(n & 255);
		return '#' + ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1);
	}
	/**
	 * 조각 마감까지 남은 기간. 서버가 준 마감 시각(ISO)으로 **그릴 때마다** 센다 —
	 * 홈을 켜 둔 채 날이 바뀌어도 숫자가 굳지 않는다. 경계 규칙(1·8·15·22일)은 서버에만 있다.
	 * 하루 미만은 시간으로 바꾼다. 마지막 날에 「1일 남음」이면 아직 여유가 있는 줄 안다.
	 */
	cherishBlossomLeft(iso: string) {
		const end = Date.parse(String(iso || ''));
		if (!isFinite(end)) return null;
		const ms = end - Date.now();
		if (ms <= 0) return {label: '결산 중', soon: true};
		if (ms < 3600000) return {label: '곧 마감', soon: true};
		if (ms < 86400000) return {label: `${Math.ceil(ms / 3600000)}시간 남음`, soon: true};
		return {label: `${Math.ceil(ms / 86400000)}일 남음`, soon: false};
	}
	/**
	 * 「지난 주 결과 확인」 (2026-09-22) — 덮개를 걷는다.
	 *
	 * 왕복을 기다리지 않고 **먼저 걷는다**. 실패해도 되돌리지 않는 이유 — 결과는 이미 읽었고,
	 * 여기서 카드를 다시 덮으면 「눌렀는데 안 없어진다」가 된다. 기록이 안 남았으면 다음에 한 번 더
	 * 뜰 뿐이고, 그게 반대 방향(못 본 결과가 조용히 닫힌다)보다 낫다.
	 *
	 * 좌표(term·week)를 실어 보낸다 — 조각이 바뀌는 순간에 눌러도 **화면에 떠 있던 그 주**가 걷힌다.
	 */
	cherishBlossomSeen = (lw: any) => {
		if (this.cherishBlossom?.lastWeek) this.cherishBlossom.lastWeek.seen = true;
		this.forceUpdate();
		fetch('https://cherish-world.com/api/showdown/daycare/blossom/seen', {
			method: 'POST', credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ term: lw?.term, week: lw?.week }),
		}).catch(() => {});
	};
	/**
	 * 지난 주 마감 결과 — 확인을 누를 때까지 꽃밭 카드 **자리를 대신한다** (2026-09-22).
	 *
	 * ## 왜 있나
	 *
	 * 결산·지급은 자정 틱이 조용히 끝낸다. 유저가 그걸 아는 길은 담임의 반 게시판 마감 글 하나뿐이었고,
	 * 09-21 에 LLM 이 죽어 세 반 전부 안 나갔다. 결과를 볼 자리가 화면에 아예 없었다.
	 *
	 * ## 왜 얹지 않고 갈아 끼우나
	 *
	 * 절대배치로 덮으면 카드와 높이가 어긋나 모바일에서 아래가 잘리거나 빈 자리가 남는다.
	 * 자리를 통째로 바꿔 끼우면 그 문제가 통째로 없고, 사용자가 보기엔 똑같이 「덮개가 걷힌다」이다.
	 *
	 * ⚠️ **금색·입체·발광·반짝임 금지** (체리쉬타워 카드에서 확정된 취향). 강조는 반 색 하나로만 준다.
	 * ⚠️ 받은 것은 **이미 지갑에 들어가 있다.** 여긴 통지지 수령 버튼이 아니다 — 「받기」라고 쓰면
	 *    안 누르면 못 받는 줄 안다.
	 */
	renderCherishBlossomResult(d: any) {
		const lw = d.lastWeek;
		const me = (lw.classes || []).find((c: any) => c.classIdx === lw.classIdx);
		if (!me) return null;
		const bg = `url('/cherish/ui/blossom-bg-${lw.classIdx}.webp?v=4')`;
		// 「지켰다 / 올라왔다」만 말한다. 내려온 주에는 줄이 아예 없다 — 감점을 화면에 안 보이는 규칙과 같은 결.
		const up = lw.prevRank !== null && lw.prevRank !== undefined && lw.prevRank > lw.rank ? lw.prevRank - lw.rank : 0;
		const move = up > 0
			? `지난 주보다 ${['', '한', '두', '세'][up] || up}계단 올라왔어요`
			: (lw.prevRank === lw.rank ? (lw.rank === 1 ? '맨 앞자리를 지켰어요' : '지난 주 자리를 지켰어요') : '');
		const got: string[] = [];
		if (lw.mine?.dust > 0) got.push(`🫐 베리 ${Number(lw.mine.dust).toLocaleString()}개`);
		// 무슨 알인지까지 (2026-10-01). eggName 이 없는 옛 api 응답이면 예전처럼 「알」.
		if (lw.mine?.eggs > 0) got.push(`🥚 ${lw.mine.eggName || '알'} ${lw.mine.eggs}개`);
		const rows = (lw.classes || []).slice().sort((a: any, b: any) => b.blossoms - a.blossoms || a.classIdx - b.classIdx);
		return <div class="menugroup cw-blr"
			style={`max-width:none;--cls:${me.color};--cls-soft:${this.cherishBlossomSoft(me.color)};--cls-bg:${bg}`}>
			<div class="cw-blr-petals"><i /><i /><i /><i /><i /><i /></div>
			<div class="cw-blr-in">
				<div class="cw-blr-cap">지난 주 꽃밭이 끝났어요</div>
				{/* ⚠ `d.termLabel`(이번 학기)이 아니라 **결과가 속한 학기**다 — 달이 바뀐 첫 조각에서는
				    직전 조각이 지난 달이라, 9월 결과에 「2026년 10월」이 붙는다. */}
				<div class="cw-blr-term">{lw.termLabel} · {lw.weekLabel}</div>
				<div class="cw-blr-cls">{me.emoji} {me.name}</div>
				<div class="cw-blr-rank"><b>{lw.rank}</b><i>등</i></div>
				<div class="cw-blr-pct"><b>{me.blossoms}%</b> 피웠어요</div>
				<div>
					{lw.bloomed ? <span class="cw-blr-move">꽃밭이 만개했어요</span> : null}
					{move ? <span class="cw-blr-move">{move}</span> : null}
				</div>
				<div class="cw-blr-rows">
					{rows.map((r: any) => <div
						class={`cw-bl-row${r.classIdx === lw.classIdx ? ' me' : ''}${r.blossoms > 100 ? ' over' : ''}`}
						style={`--cls:${r.color};--cls-soft:${this.cherishBlossomSoft(r.color)}`}>
						<span class="nm">{r.emoji} {r.name}</span>
						<span class="mini"><span style={`width:${Math.min(100, r.blossoms)}%`} /></span>
						<span class="pct">{r.blossoms}%</span>
					</div>)}
				</div>
				<div class="cw-blr-pct cw-blr-mine">{lw.mine && lw.mine.points > 0
					? <span>나는 지난 주에 <b>{Number(lw.mine.points).toLocaleString()}송이</b> 피웠어요</span>
					: <span>나는 지난 주에 피운 꽃이 없어요</span>}</div>
				{got.length
					? <div class="cw-blr-got">
						<div class="big">{got.join('와 ')}를 받았어요</div>
					</div>
					: <div class="cw-blr-got none">
						<div class="big">이번에는 보상을 받지 못했어요</div>
						<div class="sub">꽃밭 보상은 한 주에 랭크 배틀 {lw.minBattles}판부터 받아요.<br />
							{lw.mine && lw.mine.battles > 0 ? `지난 주에는 ${lw.mine.battles}판 뛰었어요.` : '지난 주에는 한 판도 뛰지 않았어요.'}</div>
					</div>}
				<button class="cw-blr-ok" onClick={() => this.cherishBlossomSeen(lw)}>확인</button>
			</div>
		</div>;
	}
	renderCherishBlossom() {
		if (!PS.user.named) return null;
		const d = this.cherishBlossom;
		// 학기 스위치가 꺼져 있으면 섹션 자체가 없다 — 「준비 중」도 안 띄운다(아직 알릴 것이 아니다).
		if (!d || !d.open) return null;
		// 지난 주 마감 결과를 아직 안 봤으면 **그것부터** 보여준다 (2026-09-22).
		// 확인을 누르면 `seen` 이 서고 이 카드로 돌아온다. 새 render 자리를 만들지 않는 이유 —
		// 호출 한 줄이 남의 블록에 얹히면 그 패치 재적용에 조용히 지워진다 (09-18 종료 카드 사고).
		if (d.lastWeek && !d.lastWeek.seen) return this.renderCherishBlossomResult(d);
		const bg = (key: string) => `url('/cherish/ui/blossom-bg-${key}.webp?v=4')`;

		// ── 반이 없는 사람 — 순위표 대신 배정 안내. 이 카드가 배정의 유일한 발견 경로다.
		if (d.classIdx === null || d.classIdx === undefined) {
			return <div class="menugroup cw-bl is-none" style={`max-width:none;--cls:#c9b8bb;--cls-soft:#eee2e4;--cls-bg:${bg('none')}`}>
				<div class="cw-bl-head"><span class="cw-bl-title">우리 반 꽃밭</span><span class="cw-bl-week">{d.termLabel}</span></div>
				<div class="cw-bl-none">
					<div class="cw-bl-flowers">🌸 🌻 🌼</div>
					<div class="big">아직 반을 정하지 않았어요</div>
					<div class="sub">어린이집에서 반을 정하면 우리 반 꽃밭이 생겨요.<br />정하지 않아도 괜찮아요, 지금처럼 아무 반에나 보낼 수 있어요.</div>
				</div>
			</div>;
		}

		const classes: any[] = d.classes || [];
		const me = classes.find((c: any) => c.classIdx === d.classIdx);
		if (!me) return null;
		const top: any[] = d.top || [];
		const over = me.points > me.goal;
		const bloom = Math.max(0, Math.min(1, me.goal ? me.points / me.goal : 0));
		const chairId = d.chair?.discordUserId;
		const left = this.cherishBlossomLeft(d.weekEndsAt);
		/**
		 * 안내 두 문장. 마감 날짜와 조각 번호는 api 가 준다 (경계 규칙은 서버에만 있다).
		 * **학기 첫 주는 보너스 문장을 뺀다** — 견줄 지난 주가 없어서 그 주에는 추가 보상이 아예 없다.
		 * `!== 0` 으로 보는 이유: 클라만 먼저 깔린 회차에는 week 이 없다(undefined). 그때는 그대로 둔다.
		 */
		const hint = [
			d.weekEndLabel ? `이번 주 꽃밭은 ${d.weekEndLabel}까지예요.` : '',
			d.week !== 0 ? '마감할 때 우리 반 등수가 지난 주보다 오르거나, 상위권을 유지하면 추가보상을 받아요 !' : '',
		].filter(Boolean).join(' ');

		// 달성률 내림차순 — 순위를 달성률로 가르므로 막대 길이로 바로 읽혀야 한다.
		const rows = classes.slice().sort((a: any, b: any) => b.blossoms - a.blossoms || a.classIdx - b.classIdx);

		const podium = top.length
			? <div class="cw-bl-top">
				<div class="cw-bl-cap">이번 주 우리 반 꽃을 가장 많이 피워주신 분</div>
				<div class="cw-bl-podium">
					{top.map((t: any, i: number) => <div class={`cw-bl-one${i === 0 ? ' first' : ''}`}>
						<img class="cw-bl-ava" src={t.avatarUrl} alt="" />
						<div class="cw-bl-nm">{t.name}</div>
						<div class="cw-bl-pt">{t.points}송이</div>
						{t.discordUserId === chairId ? <span class="cw-bl-chair">👑 학부모회장</span> : null}
					</div>)}
				</div>
			</div>
			: <div class="cw-bl-empty">이번 주는 아직 꽃이 피지 않았어요.<br />배틀 한 판이면 우리 반 꽃밭이 시작돼요.</div>;

		return <div class={`menugroup cw-bl${top.length ? '' : ' is-empty'}`}
			style={`max-width:none;--cls:${me.color};--cls-soft:${this.cherishBlossomSoft(me.color)};--cls-bg:${bg(String(me.classIdx))};--bl-bloom:${bloom.toFixed(2)}`}>
			<div class="cw-bl-head">
				<span class="cw-bl-title">{me.emoji} {me.name} 꽃밭</span>
				<span class="cw-bl-week">{d.termLabel} {d.weekLabel}</span>
				{left ? <span class={`cw-bl-left${left.soon ? ' soon' : ''}`}>{left.label}</span> : null}
			</div>
			<div class="cw-bl-count">
				<span class="cw-bl-now">{me.points.toLocaleString()}</span>
				<span class="cw-bl-goal">/ {me.goal.toLocaleString()}송이</span>
				{over ? <span class="cw-bl-full">꽃이 만개했어요</span> : null}
			</div>
			<div class="cw-bl-others">
				{rows.map((r: any) => <div
					class={`cw-bl-row${r.classIdx === d.classIdx ? ' me' : ''}${r.blossoms > 100 ? ' over' : ''}`}
					style={`--cls:${r.color};--cls-soft:${this.cherishBlossomSoft(r.color)}`}>
					<span class="nm">{r.emoji} {r.name}</span>
					<span class="mini"><span style={`width:${Math.min(100, r.blossoms)}%`} /></span>
					<span class="pct">{r.blossoms}%</span>
				</div>)}
			</div>
			{hint ? <div class="cw-bl-hint">{hint}</div> : null}
			{podium}
			<div class="cw-bl-mine">{d.mine && d.mine.points > 0
				? <span>나는 이번 주 <b>{d.mine.points}송이</b> 피웠어요</span>
				: <span>나는 아직 피운 꽃이 없어요</span>}</div>
			{/* 어린이집 교실로 가는 버튼 (2026-09-23). `yardOpen` 은 같은 응답에 실려 온다 —
			    스위치가 꺼진 동안은 버튼 자체가 없다(눌러 놓고 「준비 중」을 맞지 않게).
			    반이 없는 사람 카드에는 안 단다 — 거기서 열면 「우리 반」이 제 반이 아니다. */}
			{d.yardOpen ? <a class="cw-bl-go" href="https://cherish-world.com/pokemon/yard?board=1"
				target="_blank" rel="noreferrer">우리 반 교실 가기</a> : null}
		</div>;
	}
	// cherish-blossom:end
	// cherish-maze:begin (patch-maze-card.py)
	/** api /showdown/maze 응답 — { access, status }. null = 아직 안 받음. */
	cherishMaze: any = null;
	cherishMazeErr = '';
	cherishMazeBusy = false;
	cherishMazeAnon: boolean | null = null;
	/** 카드를 고르는 중 — 선택지가 있는 카드는 두 번째 단계로 들어간다. null = 아직 카드를 안 눌렀다. */
	cherishMazeCardPick: number | null = null;
	/** 도구 보상에서 고른 도구 id — 두 번째 단계(누구에게)로 넘어가는 표식. null = 아직 안 골랐다. */
	cherishMazeItemPick: string | null = null;
	cherishMazeOpenRank = false;
	/** 마지막 매칭 결과 — 서버 flash 는 한 번만 오므로 닫을 때까지 들고 있는다. */
	cherishMazeFlash: any = null;
	/** 미궁 api 호출 공통 — 성공하면 상태를 갈아끼우고, 실패 사유는 카드 안 한 줄로. */
	cherishMazePost(path: string, body: any, after?: () => void, method = 'POST') {
		if (this.cherishMazeBusy) return;
		this.cherishMazeBusy = true;
		this.cherishMazeErr = '';
		this.forceUpdate();
		fetch(`https://cherish-world.com/api/showdown/maze${path}`, {
			method, credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(body || {}),
		})
			.then(r => r.json())
			.then(json => {
				this.cherishMazeBusy = false;
				if (!json?.ok) {
					if (json?.code === 'maze_closed') PS.alert(json?.message || '구름 미궁은 준비 중이에요.');
					else this.cherishMazeErr = json?.message || '지금은 들어갈 수 없어요. 잠시 후 다시 해 주세요.';
				} else {
					this.cherishMazeErr = '';
					if (after) after();
					// 런 뷰를 돌려주는 조작(카드·선봉)은 응답으로 바로 덮는다.
					if (json.data && json.data.team && this.cherishMaze?.status) this.cherishMaze.status.run = json.data;
					// ⚠️ 대기열 조작은 런 뷰를 안 돌려준다 — 안 받아오면 「눌렀는데 아무 반응 없음」이 된다
					else this.cherishMazeRefetch();
					if (path === '/card' && (window as any).CherishMazeFX) {
						(window as any).CherishMazeFX.reveal(this.base, json.data);
					}
				}
				this.forceUpdate();
			})
			.catch(() => { this.cherishMazeBusy = false; this.cherishMazeErr = '연결이 불안정해요. 잠시 후 다시 해 주세요.'; this.forceUpdate(); });
	}
	/**
	 * 상태를 그 자리에서 다시 받아온다 (2026-09-09).
	 *
	 * 대기열 조작(줄서기·취소·수락·음성방)은 런 뷰를 안 돌려주므로 응답으로 화면을 못 덮는다.
	 * 홈 폴링을 기다리면 「취소를 눌러도 반응이 없다」가 된다 (사용자 신고).
	 */
	cherishMazeRefetch() {
		fetch('https://cherish-world.com/api/showdown/maze', { credentials: 'include' })
			.then(r => r.json())
			.then(json => { if (json?.ok) { this.cherishMaze = json.data; this.forceUpdate(); } })
			.catch(() => {});
	}
	/**
	 * 미궁에 들어간다. `party` 면 지금 맺고 있는 파티의 상대와 **둘이** 들어간다 (2026-09-08).
	 * 상대가 누구인지는 **안 보낸다** — api 가 포크에 직접 물어본다(클라 값을 믿으면 남의 하루 횟수를 태운다).
	 */
	cherishMazeStart(party = false) {
		const st = this.cherishMaze?.status;
		const anonymous = this.cherishMazeAnon ?? (st?.run?.anonymous === true);
		this.cherishMazePost('/start', { anonymous, party });
	}
	/**
	 * 끝난 미궁 방 탭을 닫는다 (2026-09-08 사용자: 「다음단계 넘어갈때마다 기존탭을 정리안해서 탭이 하나씩 늘어나네」).
	 * 다음 단계 방은 api 가 열어 주므로, 이미 끝난 미궁 방만 골라 정리한다. 진행 중인 방은 안 건드린다.
	 */
	cherishMazeCloseDoneRooms() {
		try {
			for (const id in PS.rooms) {
				if (!id.startsWith('battle-gen9cherishmaze-')) continue;
				if ((PS.rooms[id as RoomID] as any)?.battle?.ended) PS.leave(id as RoomID);
			}
		} catch {}
	}
	cherishMazeNext() { this.cherishMazePost('/next', {}, () => this.cherishMazeCloseDoneRooms()); }
	/** 선봉 바꾸기 — 전투 사이에만. 팀프리뷰가 꺼져 있어 맨 앞 아이가 그대로 나간다. */
	cherishMazeLead(index: number) { this.cherishMazePost('/lead', { index }); }
	cherishMazeAbandon() {
		const party = this.cherishMaze?.status?.run?.party;
		const ask = party
			? `여기서 미궁을 나갈까? ${party.withName} 님의 미궁도 함께 끝나고, 오늘 남은 횟수는 돌아오지 않아.`
			: '여기서 미궁을 나갈까? 지금까지 간 단계가 기록으로 남고, 오늘 남은 횟수는 돌아오지 않아.';
		if (!confirm(ask)) return;
		this.cherishMazePost('/abandon', {});
	}
	/*
	 * 「이 유저랑 앞으로 팀 되지않기」 — 미궁 회피 목록에 담는다 (상한 20).
	 * ⚠️ 상대 id 를 **안 보낸다**. 지금 내 런의 동행자는 서버가 이미 안다 — 보내면 아무나 남의 목록을 건드린다.
	 * 상대에게는 아무 알림도 안 간다(회피는 원래 조용히 걸린다). 이미 도는 판은 그대로 끝까지 간다.
	 */
	cherishMazeAvoidMate() {
		const party = this.cherishMaze?.status?.run?.party;
		if (!party) return;
		if (!confirm(`${party.withName} 님과 앞으로 동행자로 안 만나게 할까?
지금 도는 미궁은 그대로 끝까지 가고, 상대에겐 알리지 않아.`)) return;
		this.cherishMazePost('/avoid-mate', {}, () => PS.alert('앞으로 동행자 찾기에서 안 만나게 했어. 「내 포켓몬 › 거리두기」에서 풀 수 있어.'));
	}
	cherishMazeTakeCard(index: number, option: string | null) {
		this.cherishMazePost('/card', { index, option }, () => {
			this.cherishMazeCardPick = null;
			this.cherishMazeItemPick = null;
		});
	}
	/*
	 * 동행자 매치메이킹 (2026-09-09). 친한 사람이 없어도 듀오를 돌 수 있게 하는 길이다.
	 * 짝은 **서버가** 붙인다 — 클라는 줄을 서고 결과를 받아 그릴 뿐이다.
	 */
	cherishMazeQueueJoin() {
		const st = this.cherishMaze?.status;
		const anonymous = this.cherishMazeAnon ?? (st?.run?.anonymous === true);
		this.cherishMazePost('/queue', { anonymous });
	}
	cherishMazeQueueLeave() { this.cherishMazePost('/queue', {}, undefined, 'DELETE'); }
	/*
	 * 참가비 수락 응답 (2026-09-09). **둘 다 수락**해야 시작한다.
	 * 거절하면 둘 다 대기열로 돌아가고, 그 짝은 둘 다 대기를 취소했다 다시 서야 재매칭된다.
	 */
	/** 「이 유저와 거리두기」 — 답을 보낼 때 함께 실어 미궁 회피 목록에 담는다. */
	cherishMazeKeepAway = false;
	cherishMazeOffer(yes: boolean) {
		const avoid = this.cherishMazeKeepAway;
		this.cherishMazeKeepAway = false;
		this.cherishMazePost('/queue/offer', { yes, avoid });
	}
	/** 음성방 팝업 응답. 15초를 넘기면 서버가 이미 「아니오」로 굳혔다. */
	cherishMazeVoice(yes: boolean) { this.cherishMazePost('/queue/voice', { yes }); }
	/*
	 * 팝업 남은 시간을 초 단위로 보이게 하는 짧은 타이머.
	 * ⚠️ 홈 폴링은 5초라 그것만 믿으면 15초 카운트가 세 칸씩 뛴다. 팝업이 떠 있는 동안만 돌린다.
	 * ⚠️ 끄는 길이 「다시 그릴 때」뿐인데 배틀홈을 떠나면 그리기가 멈춘다 — 그러면 영원히 돈다.
	 *    팝업은 15초를 못 넘으므로 **20초 뒤 스스로 꺼지게** 상한을 걸어 둔다.
	 */
	cherishMazeTicker: any = null;
	cherishMazeTickStop: any = null;
	cherishMazeTick(on: boolean) {
		if (on && !this.cherishMazeTicker) {
			this.cherishMazeTicker = setInterval(() => this.forceUpdate(), 1000);
			this.cherishMazeTickStop = setTimeout(() => this.cherishMazeTick(false), 20000);
		} else if (!on && this.cherishMazeTicker) {
			clearInterval(this.cherishMazeTicker);
			if (this.cherishMazeTickStop) clearTimeout(this.cherishMazeTickStop);
			this.cherishMazeTicker = null;
			this.cherishMazeTickStop = null;
		}
	}
	/*
	 * 배틀 화면이 읽어 갈 **현재 단계**. 홈 패널 상태는 배틀 방에서 못 읽으므로 PS 에 얹는다
	 * (patch-party-ui.py 의 PS.cherishParty 와 같은 길).
	 * 진행 중 런의 stage 는 **클리어한 수**라, 지금 싸우는 판은 stage + 1 이다.
	 */
	cherishMazeStash() {
		const run = this.cherishMaze?.status?.run;
		(PS as any).cherishMazeStage = run && run.status === 'active' ? run.stage + 1 : null;
	}
	renderCherishMaze() {
		if (!PS.user.named) return null;
		this.cherishMazeStash();
		if ((this as any).cherishModeHides?.('maze')) return null;
		const m = this.cherishMaze;
		if (!m) return null;
		// 제목 = 워드마크 그림 한 덩어리 (체리쉬타워와 같은 방식). 그림이 없으면 .no-img 가 붙어 글자로 폴백.
		const head = <div class="cw-maze-head">
			<span class="cw-eyebrow">Cloud Maze</span>
			<h3 class="cw-maze-title">
				<img class="cw-maze-wordmark" src="/cherish/ui/maze-wordmark.png?v=1" alt="구름 미궁"
					onError={(e: any) => { const el = e.currentTarget; el.style.display = 'none'; if (el.parentNode) el.parentNode.classList.add('no-img'); }} />
				<span class="cw-maze-title-text">구름 미궁</span>
			</h3>
		</div>;
		if (m.access === 'closed' || !m.status) {
			return <div class="cw-maze" style="max-width:none"><div class="cw-maze-in">
				{head}
				<p class="cw-maze-note">준비 중이에요. 야생 포켓몬을 하나씩 물리치며 깊이 들어가는 곳이야.</p>
			</div></div>;
		}
		// 매치메이킹 결과 한 줄은 서버가 **한 번만** 준다. 다음 폴링(5초)에 사라지므로 여기서 붙잡아 둔다.
		// ⚠️ JSX 안에서 대입하면 그 값이 자식으로 렌더돼 [object Object] 가 찍힌다 — 반드시 return 전에.
		if (m.flash) this.cherishMazeFlash = m.flash;
		const st = m.status;
		if (!st.graduated) {
			return <div class="cw-maze" style="max-width:none"><div class="cw-maze-in">
				{head}
				<p class="cw-maze-note">포켓몬학교 졸업시험을 통과하면 열려. 야생 포켓몬을 하나씩 물리치며 깊이 들어가는 곳이야.</p>
			</div></div>;
		}
		const inBattle = !!PS.mainmenu.search.cherishBattle;
		const run = st.run;
		const body = run ? this.renderCherishMazeRun(run, inBattle) : this.renderCherishMazeIdle(st, inBattle);
		return <div class="cw-maze" style="max-width:none"><div class="cw-maze-in">
			{head}
			{body}
			{this.cherishMazeErr ? <p class="cw-maze-err">{this.cherishMazeErr}</p> : null}
			{this.cherishMazeFlash ? <p class="cw-maze-flash">
				{this.cherishMazeFlash.message}
				{this.cherishMazeFlash.voiceUrl ? <a href={this.cherishMazeFlash.voiceUrl} target="_blank">음성방으로 가기</a> : null}
				<button class="cw-maze-flash-x" onClick={() => { this.cherishMazeFlash = null; this.forceUpdate(); }}>✕</button>
			</p> : null}
			{this.renderCherishMazeRank(st)}
		</div></div>;
	}
	/** 대기열 화면 — 짝 찾는 중 · 음성방 팝업. 대기 중이 아니면 null. */
	renderCherishMazeQueue() {
		const q = this.cherishMaze?.queue;
		this.cherishMazeTick(!!q && (typeof q.voiceAskLeftMs === 'number' || typeof q.offerLeftMs === 'number'));
		if (!q) return null;
		// 참가비 수락 — 음성방보다 **먼저** 묻는다. 돈을 낼지부터 정하고 그다음이 음성방이다.
		if (typeof q.offerLeftMs === 'number') {
			const left = Math.max(0, Math.ceil(q.offerLeftMs / 1000));
			const fee = Number(q.offerFee || 0);
			return <div class="cw-maze-q is-ask">
				<p class="cw-maze-q-title"><b>{q.partnerName}</b> 님과 짝이 됐어!</p>
				<p class="cw-maze-q-fee">참가비 <b>{fee > 0 ? `${fee.toLocaleString('ko-KR')}체리` : '무료'}</b>{fee > 0 ? ' (각자)' : ''}</p>
				<p class="cw-maze-q-note">둘 다 수락해야 시작해. 한 명이라도 거절하면 다시 짝을 찾아.</p>
				{/* 거리두기 — 켜고 답하면 앞으로 이 사람과는 안 붙는다. 상대에겐 안 알린다. */}
				<label class="cw-maze-q-away">
					<input type="checkbox" checked={this.cherishMazeKeepAway}
						onChange={(e: any) => { this.cherishMazeKeepAway = !!e.currentTarget.checked; this.forceUpdate(); }} />
					<span>이 유저와 거리두기 (앞으로 동행자로 안 만나기)</span>
				</label>
				<div class="cw-maze-q-btns">
					<button class="cw-maze-cta" disabled={this.cherishMazeBusy} onClick={() => this.cherishMazeOffer(true)}>수락 ({left}초)</button>
					<button class="cw-maze-q-no" disabled={this.cherishMazeBusy} onClick={() => this.cherishMazeOffer(false)}>거절</button>
				</div>
			</div>;
		}
		if (q.state === 'offered') {
			// 거절했어도 **같은 화면**이다 — 그래야 상대가 「쟤가 거절했다」를 알아채지 못한다.
			// 내가 뭘 눌렀는지는 나만 안다 (아래 문구가 갈린다).
			const mine = q.offerChoice;
			return <div class="cw-maze-q">
				<p class="cw-maze-q-title"><b>{q.partnerName}</b> 님과 짝이 됐어!</p>
				<p class="cw-maze-q-note">
					{mine === 'no' ? '이번 짝은 넘겼어. 잠시 뒤 다시 찾을게…' : '상대의 대답을 기다리는 중…'}
				</p>
			</div>;
		}
		if (typeof q.voiceAskLeftMs === 'number') {
			const left = Math.max(0, Math.ceil(q.voiceAskLeftMs / 1000));
			return <div class="cw-maze-q is-ask">
				<p class="cw-maze-q-title"><b>{q.partnerName}</b> 님과 짝이 됐어!</p>
				<p class="cw-maze-q-note">같은 음성방으로 옮길까? 둘 다 좋다고 해야 방이 만들어져.</p>
				<div class="cw-maze-q-btns">
					<button class="cw-maze-cta" disabled={this.cherishMazeBusy} onClick={() => this.cherishMazeVoice(true)}>좋아 ({left}초)</button>
					<button class="cw-maze-q-no" disabled={this.cherishMazeBusy} onClick={() => this.cherishMazeVoice(false)}>괜찮아</button>
				</div>
			</div>;
		}
		if (q.state === 'matched') {
			return <div class="cw-maze-q">
				<p class="cw-maze-q-title"><b>{q.partnerName}</b> 님과 짝이 됐어!</p>
				<p class="cw-maze-q-note">곧 미궁이 열려…</p>
			</div>;
		}
		return <div class="cw-maze-q">
			<p class="cw-maze-q-title">동행자를 찾는 중… <b>{q.waitedSec}초</b></p>
			{/* 참가비는 **짝마다** 오르므로 오늘 처음 만난 사람과는 무료다. 그래서 무료 상대부터 찾고
			    기다릴수록 범위를 넓힌다 — 얼마를 내게 될지는 짝이 붙어야 알 수 있어 미리 못 적는다. */}
			<p class="cw-maze-q-note">
				지금 {q.waiting}명이 기다리고 있어. 배틀홈을 닫으면 줄에서 빠져.
				<br />오늘 함께 안 돈 사람부터 찾고 있어 (무료).
				{q.allowance > 0 ? ` 지금은 ${q.allowance}번까지 함께 돈 사람도 찾는 중이야 — 참가비가 붙을 수 있어.` : ' 30초가 지나면 범위를 넓혀.'}
			</p>
			<button class="cw-maze-q-no" disabled={this.cherishMazeBusy} onClick={() => this.cherishMazeQueueLeave()}>그만 찾기</button>
		</div>;
	}
	/** 진행 중 런이 없을 때 — 오늘 남은 횟수 · 내 최고 기록 · 들어가기. */
	renderCherishMazeIdle(st: any, inBattle: boolean) {
		// 횟수 제한은 없다 (2026-09-09). 대신 판이 거듭될수록 **참가비**가 오른다.
		// 사다리는 혼자와 **짝마다** 따로 센다 — 새 사람과 돌면 다시 무료부터다.
		const fee = Number(st.today?.nextFee ?? 0);
		const duo = st.duoToday as { runs: number, nextFee: number, withName: string } | null;
		const locked = inBattle || this.cherishMazeBusy;
		const duoLocked = locked;
		const lockNote = inBattle ? '배틀이 끝나면 들어갈 수 있어요' : '';
		const feeText = (n: number) => n > 0 ? `${n.toLocaleString('ko-KR')}체리` : '무료';
		const anonymous = this.cherishMazeAnon ?? false;
		// 파티는 배틀홈 파티 UI 가 정본 (patch-party-ui.py 가 PS.cherishParty 에 담아 둔다)
		const mate = ((PS as any).cherishParty || {}) as { partner?: string | null, partnerName?: string | null };
		const queued = this.renderCherishMazeQueue();
		return <div class="cw-maze-idle">
			<p class="cw-maze-lead">아이 셋으로 야생 포켓몬을 하나씩 물리치며 나아가. <b>체력은 다음 단계로 이어지고</b>, 한 번 지면 그걸로 끝이야.</p>
			<div class="cw-maze-facts">
				<span class="cw-maze-fact"><small>{duo ? '혼자 다음 판' : '다음 판 참가비'}</small><b>{feeText(fee)}</b></span>
				{duo ? <span class="cw-maze-fact"><small>{duo.withName} 님과 다음 판</small><b>{feeText(duo.nextFee)}</b></span> : null}
				{st.best ? <span class="cw-maze-fact"><small>내 최고 기록</small><b>{st.best.stage}단계</b></span> : null}
			</div>
			<label class="cw-maze-anon">
				<input type="checkbox" checked={anonymous} onChange={(e: any) => { this.cherishMazeAnon = !!e.currentTarget.checked; this.forceUpdate(); }} />
				<span>순위표에 익명으로 올리기</span>
			</label>
			{queued}
			{queued ? null : <button class="cw-maze-cta" disabled={locked} onClick={() => this.cherishMazeStart(false)}>
				{this.cherishMazeBusy ? '여는 중…' : `${mate.partner ? '혼자 들어가기' : '미궁에 들어가기'}${fee > 0 ? ` (${feeText(fee)})` : ''}`}
			</button>}
			{/* 동행자 찾기 — 파티를 안 맺어도 서버가 짝을 붙여 준다 (2026-09-09). 듀오의 주 입구다. */}
			{queued ? null : <button class="cw-maze-cta is-find" disabled={locked}
				onClick={() => this.cherishMazeQueueJoin()}>동행자 찾아서 둘이 들어가기</button>}
			{/* 파티를 맺어 둔 사람만 버튼이 뜬다. 안 맺은 사람에게 「파티를 맺으면…」이라고 설명하던
			    문단(+「파티 맺으러 가기」 링크)은 2026-09-09 사용자 지시로 삭제 — 듀오의 주 입구는
			    위의 「동행자 찾아서 둘이 들어가기」다. 재추가 제안 금지. */}
			{queued || !mate.partner ? null
				: <button class="cw-maze-cta is-party" disabled={duoLocked} onClick={() => this.cherishMazeStart(true)}>
					{mate.partnerName} 님과 둘이 들어가기{duo && duo.nextFee > 0 ? ` (각 ${feeText(duo.nextFee)})` : ''}
				</button>}
			{lockNote ? <p class="cw-maze-lock">{lockNote}</p> : null}
			{st.lastPayout ? <p class="cw-maze-payout">
				지난 주기 보상 <b>{Number(st.lastPayout.total).toLocaleString('ko-KR')}체리</b>를 받았어
				{st.lastPayout.rank ? ` (${st.lastPayout.rank}위)` : ''}.
			</p> : null}
		</div>;
	}
	/** 진행 중 런 — 체력 막대 · 카드 고르기 · 다음 단계 · 기권. */
	renderCherishMazeRun(run: any, inBattle: boolean) {
		const cards = run.cards as any[] | null;
		const next = run.next;
		const ongoing = !!run.ongoingRoom;
		// 2인 파티에서 선봉·다음 단계는 **방장만** 고른다 (서버도 거절한다). 나가기는 둘 다 할 수 있다.
		// ⚠️ 보상 카드만 예외 — **그 라운드 선봉이었던 사람**이 고른다 (2026-09-09 사용자 요청).
		const party = run.party;
		const canAct = !party || party.leader === true;
		const cardMine = !party || party.cardMine === true;
		const locked = inBattle || ongoing || this.cherishMazeBusy || !canAct || (cards && cards.length > 0);
		const lockNote = ongoing ? '진행 중인 배틀을 먼저 마쳐 주세요'
			: inBattle ? '배틀이 끝나면 이어갈 수 있어요'
			: (cards && cards.length > 0) ? '' : '';
		return <div class="cw-maze-run">
			<div class="cw-maze-depth">
				<b>{run.stage}단계</b>
				<span>까지 왔어{party ? `, ${party.withName} 님과 함께` : ''}</span>
				{party ? <button class="cw-maze-avoid" disabled={this.cherishMazeBusy}
					title="앞으로 이 사람과 동행자로 안 만나기" onClick={() => this.cherishMazeAvoidMate()}>팀 그만</button> : null}
			</div>
			{/* 방금 고른 카드가 한 일. 몸풀기는 무작위라 이게 없으면 다음 라운드까지 뭐가 올랐는지 모른다. */}
			{run.lastCard ? <p class="cw-maze-got">{run.lastCard}</p> : null}
			<ul class="cw-maze-team">
				{(run.team || []).map((p: any, i: number) => <li class={p.fainted ? 'is-fnt' : ''}>
					{/* 선봉 = 맨 앞 아이. 전투 사이에 눌러 바꾼다 (팀프리뷰는 꺼져 있다) */}
					{/* ⚠️ 클래스는 cw-maze-first 다 — cw-maze-lead 는 위 소개 문단이 쓴다(2026-09-09 충돌 사고) */}
					{/* 선봉은 **그 차례 주인**이 자기 아이 중에서 고른다 (2026-09-09) — 방장이 동행자 차례를 덮으면 교대가 무너진다 */}
					{i === 0 ? <span class="cw-maze-first is-on">선봉</span>
						: p.fainted || (party && (party.leadMine === false || p.mine === false)) ? <span class="cw-maze-first" />
						: <button class="cw-maze-first" disabled={inBattle || ongoing || this.cherishMazeBusy || !!(cards && cards.length)} title="선봉으로 세우기"
							onClick={() => this.cherishMazeLead(i)}>선봉으로</button>}
					{p.sprite ? <img class="cw-maze-sp" src={`/cherish/sprite/${p.sprite}.png?v=2`} width="28" height="28" alt="" /> : null}
					<span class="cw-maze-mon" data-maze-mon={p.name}>{p.name}</span>
					<span class="cw-maze-bar"><i style={`width:${p.fainted ? 0 : Math.max(3, p.hp)}%`}></i></span>
					<span class="cw-maze-hp">{p.fainted ? '쓰러짐' : `${p.hp}%`}</span>
					{p.item ? <span class="cw-maze-item" title={p.item}>◆</span> : null}
					{/* 몸풀기로 쌓인 랭크업 — 이 런의 유일한 성장 축이라 눈에 보여야 고를 이유가 생긴다 */}
					{(p.boosts || []).length ? <span class="cw-maze-boosts">
						{(p.boosts as any[]).map((b: any) => `${b.label} ${b.n > 0 ? "+" : ""}${b.n}`).join(' ')}
					</span> : null}
				</li>)}
			</ul>
			{cards && cards.length > 0 && !cardMine
				? <p class="cw-maze-lock">{party.withName} 님이 이번 보상을 고르는 중이야. 앞에 섰던 사람이 골라.</p>
				: cards && cards.length > 0 ? this.renderCherishMazeCards(cards) : <>
				{/* 다음에 뭐가 나오는지는 안 알려준다 — 단계 번호와 마릿수만 (야생 조우) */}
				<button class="cw-maze-cta" disabled={locked} onClick={() => this.cherishMazeNext()}>
					{this.cherishMazeBusy ? '여는 중…' : next ? `${next.stage}단계로 나아가기${next.foeCount > 1 ? ` (야생 ${next.foeCount}마리)` : ''}` : '나아가기'}
				</button>
				{lockNote ? <p class="cw-maze-lock">{lockNote}</p> : null}
				{!canAct ? <p class="cw-maze-lock">{party.withName} 님이 다음 단계를 열어 줄 거야.</p> : null}
				<button class="cw-maze-quit" onClick={() => this.cherishMazeAbandon()}>여기서 나가기</button>
			</>}
		</div>;
	}
	/** 강화 카드 3장. 선택지가 있는 카드는 누르면 두 번째 단계(선택지 목록)로 바뀐다. */
	renderCherishMazeCards(cards: any[]) {
		const pick = this.cherishMazeCardPick;
		if (pick !== null && cards[pick] && cards[pick].options?.length) {
			const card = cards[pick];
			const targets = card.targets as any[] | undefined;
			/*
			 * ⚠️ 「← 다시 고르기」는 뺐다 (2026-09-09 사용자). 선택지를 본 뒤 무를 수 있으면
			 *    「도구가 마음에 안 드니 회복으로」가 되어 카드를 고르는 일이 공짜 정찰이 된다.
			 *    **카드를 누르는 순간 그 안에서 골라야 한다.**
			 */
			// 도구는 두 단계 — 무엇을 줍고, 누구에게 줄지 (2026-09-09)
			if (targets && targets.length) {
				const chosen = this.cherishMazeItemPick;
				if (chosen === null) {
					return <div class="cw-maze-cards is-step2">
						<p class="cw-maze-cardq"><b>{card.title}</b> — 어느 도구를 주울까?</p>
						{card.options.map((o: any) => <button class="cw-maze-opt" disabled={this.cherishMazeBusy}
							onClick={() => { this.cherishMazeItemPick = o.key; this.forceUpdate(); }}>
							<b>{o.label}</b>
							{o.desc ? <small>{o.desc}</small> : null}
						</button>)}
					</div>;
				}
				const chosenLabel = (card.options.find((o: any) => o.key === chosen) || {}).label || '도구';
				return <div class="cw-maze-cards is-step2">
					<p class="cw-maze-cardq"><b>{chosenLabel}</b> — 누구에게 줄까?</p>
					{targets.map((t: any) => <button class="cw-maze-opt" disabled={this.cherishMazeBusy}
						onClick={() => this.cherishMazeTakeCard(pick, `${chosen}|${t.key}`)}>
						{t.sprite ? <img class="cw-maze-optsp" src={`/cherish/sprite/${t.sprite}.png?v=2`} width="32" height="32" alt="" /> : null}
						<b>{t.label}</b>
					</button>)}
				</div>;
			}
			return <div class="cw-maze-cards is-step2">
				<p class="cw-maze-cardq"><b>{card.title}</b>, 어느 아이에게?</p>
				<p class="cw-maze-cardq">{card.desc}</p>
				{card.options.map((o: any) => <button class="cw-maze-opt" disabled={this.cherishMazeBusy}
					onClick={() => this.cherishMazeTakeCard(pick, o.key)}>
					{o.sprite ? <img class="cw-maze-optsp" src={`/cherish/sprite/${o.sprite}.png?v=2`} width="32" height="32" alt="" /> : null}
					<b>{o.label}</b>
					{o.desc ? <small>{o.desc}</small> : null}
				</button>)}
			</div>;
		}
		return <div class="cw-maze-cards">
			<p class="cw-maze-cardq">하나만 가져갈 수 있어. 랜덤 효과는 선택한 뒤 정해져.</p>
			<div class="cw-maze-cardrow">
				{cards.map((c: any, i: number) => <button class="cw-maze-card" data-maze-kind={c.kind} data-maze-stat={c.stat || undefined} disabled={this.cherishMazeBusy}
					onClick={() => {
						if (c.options?.length) { this.cherishMazeCardPick = i; this.forceUpdate(); }
						else this.cherishMazeTakeCard(i, null);
					}}>
					<b>{c.title}</b>
					<small>{c.desc}</small>
				</button>)}
			</div>
		</div>;
	}
	/**
	 * 순위 — **혼자**와 **둘이**를 따로 (2026-09-09). 접혀 있고 눌러야 펼친다.
	 * 듀오는 6마리라 설계상 더 깊이 간다. 한 표에 섞으면 혼자 도는 사람이 구조적으로 밀린다.
	 * 듀오 줄의 단위는 사람이 아니라 **짝**이라 이름이 둘 나온다.
	 */
	renderCherishMazeRank(st: any) {
		const rank = st.rank || {};
		const solo = (rank.solo || []) as any[];
		const duo = (rank.duo || []) as any[];
		if (!solo.length && !duo.length) return null;
		const open = this.cherishMazeOpenRank;
		const list = (rows: any[]) => <ol class="cw-maze-ranklist">
			{rows.map((r: any) => <li class={r.mine ? 'is-me' : ''}>
				<span class="cw-maze-rk">{r.rank}</span>
				{/* ⚠️ api 가 주는 faceUrl 은 **상대경로**다. 클라는 play.cherish-world.com 에서 돌아서
				    그대로 쓰면 없는 주소를 가리켜 깨진 아이콘이 된다. 타워 카드도 같은 이유로 접두사를 붙인다. */}
				{(r.members || []).map((m: any) => <img class={`cw-maze-face${r.anonymous ? ' is-anon' : ''}`} src={m.faceUrl ? `https://cherish-world.com${m.faceUrl}` : ''} alt="" />)}
				<span class="cw-maze-rname">{(r.members || []).map((m: any) => m.name).join(', ')}</span>
				<span class="cw-maze-rstage">{r.stage}단계</span>
			</li>)}
		</ol>;
		return <div class="cw-maze-rank">
			<button class="cw-maze-ranktog" onClick={() => { this.cherishMazeOpenRank = !open; this.forceUpdate(); }}>
				기록 {open ? '▲' : '▼'}
			</button>
			{open ? <div class="cw-maze-ranksec">
				{solo.length ? <><p class="cw-maze-ranklabel">혼자</p>{list(solo)}</> : null}
				{duo.length ? <><p class="cw-maze-ranklabel">둘이</p>{list(duo)}</> : null}
				{rank.endsAt ? <p class="cw-maze-ranknote">{this.cherishMazeLeftText(rank.endsAt)} 뒤 초기화 및 보상지급</p> : null}
			</div> : null}
		</div>;
	}
	/** 남은 시간 한 줄 — 「N일 M시간」·「M시간」·「M분」. */
	cherishMazeLeftText(iso: string) {
		const ms = new Date(iso).getTime() - Date.now();
		if (!(ms > 0)) return '곧';
		const h = Math.floor(ms / 3600000);
		if (h >= 24) return `${Math.floor(h / 24)}일 ${h % 24}시간`;
		return h >= 1 ? `${h}시간` : `${Math.max(1, Math.floor(ms / 60000))}분`;
	}
	// cherish-maze:end
	// cherish-tower:begin (patch-tower-card.py)
	/** api /showdown/tower 응답 — { access: 'open'|'tester'|'closed', status }. null = 아직 안 받음. */
	cherishTower: any = null;
	cherishTowerErr = '';
	cherishTowerBusy = false;
	/**
	 * 층 목록은 **늘 전부** 그리고 상자 안에서 스크롤한다 (2026-09-15 사용자).
	 * 옛 「탑 전체 보기」는 55줄을 통째로 펼쳐 카드가 화면 몇 개 길이가 됐다.
	 * 페이지로 안 나눈 이유: `GET /showdown/tower` 가 floors 전체를 한 번에 주므로 나눠도 받는 양이
	 * 안 줄고, 층은 이어진 사다리라 끊으면 「몇 층 남았나」의 연속감이 깨진다.
	 */
	/** 마지막으로 가운데 맞춘 층. 층이 바뀔 때만 다시 맞춘다 — 매 렌더마다 맞추면 스크롤이 튄다. */
	cherishTowerCentered = -1;
	cherishTowerCenter(el: any, smooth = false) {
		if (!el) return;
		// 자리가 없으면(배치 전) 도전할 1층 줄을 가운데로 (2026-09-24). 예전엔 내 줄이 없으면 그냥 돌아가서
		// 목록이 맨 위(정상)에서 멈췄고, 도전 버튼이 있는 1층은 스크롤 맨 끝에 숨어 있었다.
		const mine = el.querySelector('.cw-tower-rung.is-mine') || el.querySelector('.cw-tower-rung.is-target');
		if (!mine) return;
		// ⚠️ offsetTop 은 **가장 가까운 positioned 조상** 기준이라 여기선 .cw-tower 가 잡혀 엉뚱한 데로 튄다
		//    (2026-09-15 시안에서 실제로 겪음). 사각형 차이로 잰다.
		const top = mine.getBoundingClientRect().top - el.getBoundingClientRect().top + el.scrollTop
			- el.clientHeight / 2 + mine.clientHeight / 2;
		if (smooth && el.scrollTo) el.scrollTo({ top, behavior: 'smooth' });
		else el.scrollTop = top;
	}
	cherishTowerScrollRef = (el: any) => {
		if (!el) return;
		const floor = this.cherishTower?.status?.floor || 0;
		// 상자가 새로 생겼으면(모드 카드를 오가면 카드가 내려갔다 다시 붙는다) 층이 같아도 다시 맞춘다 (2026-09-24)
		if (this.cherishTowerScrollEl === el && this.cherishTowerCentered === floor) return;
		this.cherishTowerScrollEl = el;
		this.cherishTowerCentered = floor;
		this.cherishTowerCenter(el);
	};
	cherishTowerScrollEl: any = null;
	/** 익명 체크 — null 이면 서버가 기억하는 내 고스트 표시 모드를 따른다. */
	cherishTowerAnon: boolean | null = null;
	cherishTowerStart() {
		if (this.cherishTowerBusy) return;
		if (this.cherishTower?.access === 'closed') {
			PS.alert('체리쉬타워은 준비 중이에요. 조금만 기다려 줘!');
			return;
		}
		const st = this.cherishTower?.status;
		const anonymous = this.cherishTowerAnon ?? (st?.display === 'anon');
		this.cherishTowerBusy = true;
		this.cherishTowerErr = '';
		this.forceUpdate();
		fetch('https://cherish-world.com/api/showdown/tower/start', {
			method: 'POST', credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ anonymous }),
		})
			.then(r => r.json())
			.then(json => {
				this.cherishTowerBusy = false;
				if (!json?.ok) {
					if (json?.code === 'tower_closed') PS.alert(json?.message || '체리쉬타워은 준비 중이에요.');
					else this.cherishTowerErr = json?.message || '도전을 열지 못했어요. 잠시 후 다시 해 주세요.';
				} else {
					// 배틀 방은 서버가 만들어 저절로 열린다 (학교·대기방과 같은 경로)
					this.cherishTowerErr = '';
				}
				this.forceUpdate();
			})
			.catch(() => { this.cherishTowerBusy = false; this.cherishTowerErr = '연결이 불안정해요. 잠시 후 다시 해 주세요.'; this.forceUpdate(); });
	}
	renderCherishTower() {
		if (!PS.user.named) return null;
		// 모드 선택 줄(patch-home-modes.py)이 있으면 그 규칙을 따른다 — 없으면 종전대로 늘 그린다
		if ((this as any).cherishModeHides?.('tower')) return null;
		const t = this.cherishTower;
		if (!t) return null;
		// 제목 = 워드마크 그림 한 덩어리. 그림이 없으면 글자로 폴백 (install-ui-assets.py 선택 목록).
		const head = <div class="cw-tower-head">
			<span class="cw-eyebrow">Cloud Tower</span>
			<h3 class="cw-tower-title">
				<img class="cw-tower-wordmark" src="/cherish/ui/tower-wordmark.png?v=1" alt="체리쉬타워"
					onError={(e: any) => { const el = e.currentTarget; el.style.display = 'none'; if (el.parentNode) el.parentNode.classList.add('no-img'); }} />
				<span class="cw-tower-title-text">체리쉬타워</span>
			</h3>
		</div>;
		if (t.access === 'closed' || !t.status) {
			return <div class="cw-tower" style="max-width:none"><div class="cw-tower-in">
				{head}
				<p class="cw-tower-note">준비 중이에요. 졸업생들의 고스트가 층을 이룬 탑이야.</p>
			</div></div>;
		}
		const st = t.status;
		if (!st.graduated) {
			return <div class="cw-tower" style="max-width:none"><div class="cw-tower-in">
				{head}
				<p class="cw-tower-note">포켓몬학교 졸업시험을 통과하면 열려. 졸업생들의 고스트가 층을 이룬 탑이야.</p>
			</div></div>;
		}
		const inBattle = !!PS.mainmenu.search.cherishBattle;
		const target = st.target;
		// 패배 상한 — 어드민 설정. 0 = 무제한 (2026-09-07 기본): 남은 기회 칸을 그리지 않는다.
		const cap = Number(st.today?.cap ?? 0);
		const blocked = cap > 0 && !!st.today?.blocked;
		// 같은 상대 재도전 쿨타임 (2026-09-09). status.rematch 는 대기 중일 때만 온다 — 상대가 바뀌면 서버에서 사라진다.
		// 카드가 30초마다 폴링하므로 남은 분은 그 주기로 줄어든다 (초 단위 카운트다운은 돌리지 않는다).
		const cool = st.rematch ? Math.max(1, Math.ceil(Number(st.rematch.seconds || 0) / 60)) : 0;
		const locked = inBattle || !!st.ongoing || blocked || cool > 0 || st.atTop || !target || this.cherishTowerBusy;
		const lockNote = inBattle ? '배틀이 끝나면 도전할 수 있어요'
			: st.ongoing ? '끝나지 않은 도전이 있어요. 그 배틀을 먼저 마쳐 주세요'
			: blocked ? `오늘은 ${cap}번 졌어. 내일 아침 7시에 다시 오를 수 있어`
			: cool > 0 ? `방금 붙은 상대야. ${cool}분 뒤에 다시 도전할 수 있어`
			: st.atTop ? '정상이야. 누가 널 밀어내면 다시 오를 수 있어'
			: !target ? '탑이 비어 있어요' : '';
		const anonymous = this.cherishTowerAnon ?? (st.display === 'anon');
		const since = st.sinceLastClimb;
		const sinceLine = since && (since.pushedDown > 0 || since.defended > 0)
			? `지난 등반 이후 ${since.pushedDown > 0 ? `${since.pushedDown}명이 네 아이들을 이겼어` : ''}${since.pushedDown > 0 && since.defended > 0 ? ', ' : ''}${since.defended > 0 ? `${since.defended}명은 막았어` : ''}.`
			: '';
		// 남은 기회 — 몬스터볼. CSS 원 그라데이션이 저렴해 보여 그린 SVG 로 (2026-09-06 사용자).
		const ball = (isUsed: boolean) => isUsed
			? <svg class="cw-tower-ball is-used" viewBox="0 0 24 24" aria-hidden>
				<circle cx="12" cy="12" r="10.6" fill="rgba(255,255,255,.45)" />
				<circle cx="12" cy="12" r="10.6" fill="none" stroke="#9c86a2" stroke-width="1.4" stroke-dasharray="2.4 2.2" />
				<path d="M1.4 12h6.6M16 12h6.6" stroke="#9c86a2" stroke-width="1.2" stroke-linecap="round" opacity=".7" />
				<circle cx="12" cy="12" r="3.7" fill="none" stroke="#9c86a2" stroke-width="1.3" />
			</svg>
			: <svg class="cw-tower-ball" viewBox="0 0 24 24" aria-hidden>
				<defs>
					<linearGradient id="cwtwBallTop" x1="0" y1="0" x2="0" y2="1">
						<stop offset="0" stop-color="#ff97ab" /><stop offset=".55" stop-color="#ef3f63" /><stop offset="1" stop-color="#d31742" />
					</linearGradient>
					<linearGradient id="cwtwBallBot" x1="0" y1="0" x2="0" y2="1">
						<stop offset="0" stop-color="#ffffff" /><stop offset="1" stop-color="#efe4ea" />
					</linearGradient>
				</defs>
				<circle cx="12" cy="12" r="10.6" fill="url(#cwtwBallBot)" />
				<path d="M1.4 12a10.6 10.6 0 0 1 21.2 0z" fill="url(#cwtwBallTop)" />
				<circle cx="12" cy="12" r="10.6" fill="none" stroke="#4a2f4e" stroke-width="1.5" />
				<path d="M1.4 12h6.6M16 12h6.6" stroke="#4a2f4e" stroke-width="1.5" stroke-linecap="round" />
				<circle cx="12" cy="12" r="3.7" fill="#fff" stroke="#4a2f4e" stroke-width="1.5" />
				<circle cx="12" cy="12" r="1.5" fill="#4a2f4e" opacity=".28" />
				<ellipse cx="8.3" cy="6.9" rx="2.9" ry="1.6" fill="#fff" opacity=".5" transform="rotate(-26 8.3 6.9)" />
			</svg>;
		const usedN = Number(st.today?.losses ?? 0);
		const balls = [];
		for (let i = 0; i < cap; i++) balls.push(ball(i < usedN));
		const faceOf = (f: any) => f && f.faceUrl ? `https://cherish-world.com${f.faceUrl}` : '';
		const rate = (stat: any) => {
			const w = Number(stat ? stat.wins : 0), l = Number(stat ? stat.losses : 0);
			return w + l > 0 ? Math.round((w / (w + l)) * 100) : 0;
		};
		const statLine = (label: string, stat: any) => <span class="cw-tower-stat"><span class="cw-tower-def-label">{label} </span>{Number(stat?.wins ?? 0)}승 {Number(stat?.losses ?? 0)}패</span>;
		const fatigueIcon = (f: any) => f.fatigue ? <button
			type="button"
			class={`cw-tower-fatigue ${f.fatigue.active ? 'is-active' : 'is-warning'}`}
			title={f.fatigue.label}
			aria-label={f.fatigue.label}
			onClick={(e: any) => { e.preventDefault(); e.stopPropagation(); PS.alert(`${f.fatigue.label}\n\n${f.fatigue.detailText}`); }}
		>
			<i class="fa fa-cloud" aria-hidden></i>
		</button> : null;
		// 층 줄 — 랭킹 4위 이하 행과 같은 격자(층·얼굴·이름·전적). 오른쪽 전적 무리가 가운데 여백을 잡아 준다.
		const rung = (f: any, kind: string, extra: any) => <div class={`cw-tower-rung ${kind}${f.anonymous ? ' is-anon' : ''}`}>
			<span class="cw-tower-rung-no">{f.floor}</span>
			<span class="cw-tower-face-wrap"><img class="cw-tower-face" src={faceOf(f)} alt="" /></span>
			<div class="cw-tower-who">
				{kind.startsWith('is-target') ? <span class="cw-tower-tag">바로 위층</span> : null}
				<div class="cw-tower-name-line"><span class="cw-tower-name">{f.name}{f.mine ? ' (나)' : ''}</span>{fatigueIcon(f)}</div>
			</div>
			{/* 하위 층은 서버가 defend 를 안 보낸다 (2026-09-09) — 갓 들어온 사람의 전적을 늘어놓지 않는다 */}
			{f.defend ? <div class="cw-tower-def">
				<span class="cw-tower-def-txt">{statLine('도전', f.climb)}{statLine('방어', f.defend)}</span>
				<span class="cw-tower-track"><span class="cw-tower-fill" style={`width:${rate(f.defend)}%`} /></span>
			</div> : <span />}
			{extra}
		</div>;
		// 늘 전부 그린다. 길이는 상자가 잡고, 열면 내 층이 가운데 와 있다 (cherishTowerScrollRef).
		const near: any[] = st.floors || [];
		// 첫 등반 상자 (2026-09-24) — 자리가 없는 졸업생에게만. 졸업생 14명이 한 번도 안 올라온 걸 보고 넣었다.
		// ⚠️ 「1층은 쉬워요」류 문구 금지: 바닥이 특별하다고 말하는 순간 사람처럼 보이는 1층의 정체가 드러난다.
		// 보상 숫자는 api(firstRankBerry)가 준다 — 옛 응답이면 그 줄만 빠진다.
		const firstClimb = !st.floor;
		const firstBerry = Number(st.firstRankBerry || 0);
		return <div class="cw-tower" style="max-width:none">
			<span class="cw-tower-motes" aria-hidden />
			<div class="cw-tower-in">
				{head}
				{sinceLine ? <p class="cw-tower-since">{sinceLine}</p> : null}
				{firstClimb ? <div class="cw-tower-first">
					<span class="cw-tower-first-ic" aria-hidden><i class="fa fa-cloud"></i></span>
					<div class="cw-tower-first-txt">
						<b>첫 등반</b>
						<p>1층에서 이기면 2층에 이름이 올라가요. 그다음부터는 바로 위층에 도전해요.</p>
						{firstBerry > 0 ? <span class="cw-tower-first-chip">처음 오르는 칸마다 {firstBerry.toLocaleString('ko-KR')} 베리</span> : null}
					</div>
				</div> : null}
				<div class="cw-tower-meta">
					<div class="cw-tower-summit">정상 <b>{st.height}층</b> {st.floor ? <span>{Math.max(0, st.height - st.floor)}층 남았어</span> : <span>첫 등반은 1층부터야</span>}</div>
					{cap > 0 ? <div class="cw-tower-chance"><span class="cw-tower-chance-label">남은 기회</span><span class="cw-tower-balls">{balls}</span></div> : null}
				</div>
				<div class="cw-tower-rungs cw-tower-scroll" ref={this.cherishTowerScrollRef}>
					{near.map((f: any) => rung(
						f,
						f.mine ? 'is-mine' : (target && f.floor === target.floor) ? (firstClimb ? 'is-target is-first' : 'is-target') : 'is-dim',
						(target && f.floor === target.floor)
							? <button class="cw-tower-go" disabled={locked} onClick={() => this.cherishTowerStart()}>
								{this.cherishTowerBusy ? '오르는 중…' : blocked ? '내일 아침 7시에' : cool > 0 ? `${cool}분 뒤에` : `${f.name}에게 도전`}
							</button>
							: <span />,
					))}
				</div>
				<div class="cw-tower-foot">
					<label class="cw-tower-anon"><input type="checkbox" checked={anonymous} onChange={(e: any) => { this.cherishTowerAnon = !!e.currentTarget.checked; this.forceUpdate(); }} /> 익명으로 도전</label>
					{st.floor ? <button class="cw-tower-list-toggle" onClick={() => this.cherishTowerCenter(this.cherishTowerScrollEl, true)}>내 층으로</button> : null}
				</div>
				{lockNote ? <p class="cw-tower-note">{lockNote}</p> : null}
				{this.cherishTowerErr ? <p class="cw-tower-err">{this.cherishTowerErr}</p> : null}
			</div>
		</div>;
	}
	// cherish-tower:end
	// cherish-school:begin (patch-school-card.py)
	/** api /showdown/school 응답 — { access: 'open'|'tester'|'closed', status }. null = 아직 안 받음. */
	cherishSchool: any = null;
	cherishSchoolErr = '';
	cherishSchoolBusy = false;
	cherishSchoolStart(kind: 'lesson' | 'exam', lesson: number | null) {
		if (this.cherishSchoolBusy) return;
		if (this.cherishSchool?.access === 'closed') {
			PS.alert('체리의 포켓몬학교는 준비 중이에요. 조금만 기다려 줘!');
			return;
		}
		// 리플레이 교시(3교시, 2026-09-06): 서버 배틀이 아니라 녹화 대본을 재생하는 방을 클라가 직접 연다 (patch-school-replay.py).
		// 순서 잠금은 타일 쪽(locked → 팝업)이 이미 막고, 통과 기록은 끝까지 보면 api replay-complete 가 찍는다.
		const lessonInfo = kind === 'lesson' ? (this.cherishSchool?.status?.lessons || []).find((l: any) => l.lesson === lesson) : null;
		if (lessonInfo?.kind === 'replay') {
			const id = `battle-cherishlesson${lesson}` as RoomID;
			if (!PS.rooms[id]) {
				const room = PS.addRoom({ id, type: 'battle', connectMode: null, args: { cherishLesson: lesson } });
				if (room) (room as any).rejoining = false;
			}
			PS.focusRoom(id);
			PS.update();
			return;
		}
		this.cherishSchoolBusy = true;
		this.cherishSchoolErr = '';
		this.forceUpdate();
		fetch('https://cherish-world.com/api/showdown/school/start', {
			method: 'POST', credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(kind === 'exam' ? { kind } : { kind, lesson }),
		})
			.then(r => r.json())
			.then(json => {
				this.cherishSchoolBusy = false;
				if (!json?.ok) {
					if (json?.code === 'school_closed') PS.alert(json?.message || '체리의 포켓몬학교는 준비 중이에요.');
					else this.cherishSchoolErr = json?.message || '수업을 열지 못했어요. 잠시 후 다시 해 주세요.';
				} else {
					// 배틀 방은 서버가 만들어 저절로 열린다 (대기방 「시작」과 같은 경로)
					this.cherishSchoolErr = '';
				}
				this.forceUpdate();
			})
			.catch(() => { this.cherishSchoolBusy = false; this.cherishSchoolErr = '연결이 불안정해요. 잠시 후 다시 해 주세요.'; this.forceUpdate(); });
	}
	/**
	 * 잠긴 타일을 눌렀을 때 이유를 말한다 (2026-09-14). 잠금은 두 가지고 할 말이 다르다:
	 *  - 포크가 인정하는 **살아 있는 배틀**(search.cherishBattle) → 그 방으로 데려다준다.
	 *    배틀 탭을 닫아 버린 사람에게는 이게 유일한 복귀 경로다.
	 *  - api 에만 남은 **미종료 판**(status.ongoing) → 방은 이미 사라졌다는 뜻이다.
	 *    배포 점검은 이제 api 가 그 자리에서 접지만(/internal/showdown/maze/void-open),
	 *    점검을 안 거친 재시작·크래시가 남아 있어 30분 시한을 같이 알려 준다.
	 */
	cherishSchoolBlocked() {
		const live = PS.mainmenu.search.cherishBattle;
		if (live) {
			PS.confirm('지금 진행 중인 배틀이 있어요.\n그 배틀을 마치면 수업을 열 수 있어요.', { okButton: '배틀로 가기', cancelButton: '닫기' })
				.then(go => { if (go) PS.join(live as any); });
			return;
		}
		PS.alert('끝나지 않은 수업이 남아 있어요.\n\n배틀이 이미 닫혔다면 잠시 뒤(최대 30분) 저절로 풀려요. 그때 다시 눌러 줘!');
	}
	renderCherishSchool() {
		if (!PS.user.named) return null;
		// 「학교」 모드에서만 그린다 (patch-home-modes.py). 모드 선택 줄이 바로 위에 있다.
		if ((this as any).cherishModeHides?.('school')) return null;
		const s = this.cherishSchool;
		if (!s) return null;
		const inBattle = !!PS.mainmenu.search.cherishBattle;
		if (s.access === 'closed' || !s.status) {
			return <div class="menugroup cw-school" style="max-width:none">
				<div class="cw-school-head">
					<div><span class="cw-eyebrow">School</span><h3 class="cw-school-title">체리의 포켓몬학교</h3></div>
					<span class="cw-school-badge is-soon">준비 중</span>
				</div>
				<button class="button cw-school-go" onClick={() => this.cherishSchoolStart('lesson', 1)}>들어가기</button>
			</div>;
		}
		const st = s.status;
		const lessons: any[] = st.lessons || [];
		const next = lessons.find((l: any) => !l.passed && l.available) || null;
		const allPassed = lessons.length > 0 && lessons.every((l: any) => l.passed);
		const ongoing = st.ongoing;
		// ⚠️ 잠겼다고 타일을 **disabled 로 두지 않는다** (2026-09-14). disabled 버튼은 클릭에 완전히
		// 침묵한다 — 배포 점검으로 졸업시험이 끊긴 유저가 「졸업시험을 눌러도 아무 반응이 없다」고
		// 신고했다. 아래 안내 줄은 그때도 있었지만 작은 회색 글씨라 아무도 안 본다.
		// 누른 자리에서 이유를 말해 준다 → cherishSchoolBlocked(). disabled 는 요청 중(busy)에만.
		const blocked = inBattle || !!ongoing;
		const lockNote = inBattle ? '배틀이 끝나면 수업을 열 수 있어요' : ongoing ? '끝나지 않은 수업이 있어요. 그 배틀을 먼저 마쳐 주세요' : '';
		// 타일 — 그림(/cherish/ui/school-*.webp, docs/showdown-school-assets.md)이 있으면 그림, 없으면 번호 타일(no-img).
		// 클리어 후는 같은 그림 위에 「통과」 도장(CSS). 다음 수업 타일만 크다. 누르면 그 수업(통과분은 재수강).
		const tile = (key: string, no: any, label: string, state: 'done' | 'now' | 'locked' | 'open', onPick: () => void, lockMsg: string) => {
			// ?v= 는 그림을 갈 때뿐 아니라 **처음 올릴 때도** 올려야 한다 — 파일이 없던 동안 받은 404 가 브라우저·CDN 에
			// 4시간 캐시로 남아 새 파일을 안 받아 간다 (2026-09-05 실측). v=1 은 404 로 오염됐다.
			const src = `/cherish/ui/school-${key}.webp?v=3`; // v=3: 2026-09-06 속도 교시 폐지로 school-lesson-3 그림이 보조기 장면으로 바뀜
			return <button
				class={`cw-st is-${state}${blocked ? ' is-wait' : ''}`} title={label} disabled={this.cherishSchoolBusy}
				onClick={() => {
					if (state === 'locked') { PS.alert(lockMsg); return; }
					if (blocked) { this.cherishSchoolBlocked(); return; }
					onPick();
				}}
			>
				<img src={src} alt="" onError={(e: any) => { e.currentTarget.parentNode.classList.add('no-img'); }} />
				<span class="cw-st-no">{no}</span>
				<span class="cw-st-label">{label}</span>
				{state === 'done' ? <span class="cw-st-stamp">통과</span> : null}
				{state === 'locked' ? <span class="cw-st-lock"><i class="fa fa-lock" aria-hidden></i></span> : null}
				{state === 'now' ? <span class="cw-st-next">다음 수업</span> : null}
			</button>;
		};
		// 테스터(학교 스위치 꺼짐 + 어드민)는 순서와 무관하게 전 교시가 열린다 — 서버도 같은 판정으로 순서를 건너뛴다
		const tester = s.access === 'tester';
		const dots = <div class="cw-school-tiles">
			{lessons.map((l: any) => tile(
				`lesson-${l.lesson}`, String(l.lesson), `${l.lesson}교시 ${l.title}`,
				l.passed ? 'done' : next && next.lesson === l.lesson ? 'now' : !l.available && !tester ? 'locked' : 'open',
				() => this.cherishSchoolStart('lesson', l.lesson),
				`${l.lesson - 1}교시를 먼저 통과해야 해요.`,
			))}
			{tile('exam', <i class="fa fa-graduation-cap" aria-hidden></i>, '졸업시험', st.examPassed ? 'done' : allPassed ? 'now' : 'open', () => this.cherishSchoolStart('exam', null), '')}
		</div>;
		// 첫 수업 띠 (2026-10-01) — 1교시를 아직 통과하지 않은 사람에게. 디스코드의 「1교시 들어가기」(`?go=school`)로 떨어진
		// 사람이 뭘 누르면 되는지 한 칸으로 보여준다 (실측: 09-14 이후 가입 87명 중 배틀 홈 로그인 22명 · 1교시 13명).
		// 배틀은 그 사람이 누를 때 시작한다 — 도착 즉시 교실을 열면 로그인 왕복 뒤 화면이 바뀌는 순간 배틀이 시작돼 당황한다.
		// 잠겼으면(진행 중 판) 띠는 두고 버튼이 cherishSchoolBlocked 로 이유를 말한다. 1교시를 통과하면 사라진다.
		const firstLesson = !st.graduated && lessons.length > 0 && !lessons[0].passed;
		const firstStrip = firstLesson ? <div class="cw-school-first">
			<img class="cw-school-first-art" src="/cherish/ui/home-mode-school.webp?v=1" alt=""
				onError={(e: any) => { e.currentTarget.style.display = 'none'; }} />
			<div class="cw-school-first-body">
				<b class="cw-school-first-title">첫 수업이 준비됐어요</b>
				<small class="cw-school-first-desc">1교시는 상성 하나만 배워요. 아이는 체리가 빌려줘요. 금방 끝나요.</small>
				<button class="button cw-school-go" disabled={this.cherishSchoolBusy}
					onClick={() => { if (blocked) { this.cherishSchoolBlocked(); return; } this.cherishSchoolStart('lesson', 1); }}>1교시 들어가기</button>
			</div>
		</div> : null;
		// 졸업 뒤에도 교시 타일은 그대로 — 원하는 교시를 골라 재수강 (2026-09-06 사용자: 「재수강」 버튼 하나는 무조건 1교시로 가서 별로).
		// ⚠️ 설명 문단(교시 소개)과 조작 안내 줄은 **2026-09-06 사용자 결정으로 삭제**했다 —
		// 「너무 자세하다, 어차피 1교시 들어가면 들을 수 있잖아」. 타일 그림·번호·「통과」 도장만으로
		// 읽히고, 자세한 설명은 수업에 들어가면 수업 카드가 한다. 다시 넣지 말 것.
		// 그래서 api 의 SchoolLesson.card / SCHOOL_EXAM.card 는 지금 **화면에 안 나온다**(데이터만 남음).
		return <div class="menugroup cw-school" style="max-width:none">
			<div class="cw-school-head">
				<div><span class="cw-eyebrow">School</span><h3 class="cw-school-title">체리의 포켓몬학교{st.graduated ? <span class="cw-school-badge is-grad">졸업생</span> : null}</h3></div>
				{s.access === 'tester' ? <span class="cw-school-badge is-soon">테스트</span> : null}
			</div>
			{firstStrip}
			{dots}
			{this.cherishSchoolBusy ? <p class="cw-school-hint">교실 여는 중…</p> : null}
			{lockNote ? <p class="cw-school-note">{lockNote}</p> : null}
			{this.cherishSchoolErr ? <p class="cw-school-err">{this.cherishSchoolErr}</p> : null}
		</div>;
	}
	// cherish-school:end
	// cherish-pwa:begin (patch-pwa.py)
	/**
	 * 「앱으로 설치」 한 줄 카드. 신호(beforeinstallprompt·앱 창 여부·iOS)는 /cherish/pwa/pwa.js 가 window.cherishPwa 에 든다.
	 * 이벤트가 이 패널보다 먼저 올 수 있어 패널은 읽기만 한다. 설치 가능할 때만 그린다: 크롬·엣지·안드로이드는 설치창 버튼,
	 * iOS Safari 는 안내 문장. 이미 앱 창이면·설치가 끝났으면·30일 안에 「나중에」를 눌렀으면 없다. 설치 뒤엔 브라우저가
	 * beforeinstallprompt 를 다시 안 주므로 저절로 사라진다.
	 */
	cherishPwaHooked = false;
	cherishPwaBusy = false;
	cherishPwaDismissed(): boolean {
		try {
			const t = Number(localStorage.getItem('cherish-pwa-dismissed') || 0);
			return t > 0 && Date.now() - t < 30 * 86400000;
		} catch { return false; }
	}
	cherishPwaDismiss = () => {
		try { localStorage.setItem('cherish-pwa-dismissed', String(Date.now())); } catch {}
		this.forceUpdate();
	};
	cherishPwaInstall = () => {
		const P = (window as any).cherishPwa;
		if (!P || this.cherishPwaBusy) return;
		this.cherishPwaBusy = true;
		this.forceUpdate();
		P.install().then(() => {
			this.cherishPwaBusy = false;
			this.forceUpdate();
		}).catch(() => {
			this.cherishPwaBusy = false;
			this.forceUpdate();
		});
	};
	renderCherishPwa() {
		if (!PS.user.named) return null;
		const P = (window as any).cherishPwa;
		if (!P) return null;
		// 스위치 pwa_open (api /showdown/home 의 pwaOpen = 스위치 켜짐 또는 관리자). 꺼진 사람에게는 manifest 조차 안 붙어
		// 브라우저 설치 아이콘도 없다 — 먼저 써 보고 문제를 잡은 뒤 연다 (2026-10-06 사용자 지시).
		const h = this.cherishHome;
		if (!h || h === 'hidden' || !h.pwaOpen) return null;
		if (!P.enabled) P.enable();
		if (!this.cherishPwaHooked) {
			this.cherishPwaHooked = true;
			P.onChange(() => this.forceUpdate());
		}
		// 방금 설치가 끝났으면 「어디에 생겼나」를 알려 준다 — 브라우저는 아무 말도 안 하고 창만 하나 더 연다.
		if (P.justInstalled) {
			const where = P.mobile ? '홈 화면' : '바탕화면과 시작 메뉴';
			return <div class="menugroup cw-pwa cw-pwa-done" style="max-width:none">
				<img class="cw-pwa-ic" src="/cherish/pwa/icon-192.png?v=5ac3b6ecf1e8" alt="" width="40" height="40" />
				<div class="cw-pwa-text">
					<b>설치됐어요.</b>
					<span>{where}에 「체리쉬 배틀」 아이콘이 생겼어요. 앞으로는 거기서 열면 돼요. 이 브라우저 탭은 닫아도 돼요.</span>
				</div>
				<div class="cw-pwa-btns">
					<button class="button cw-pwa-go" onClick={() => P.ackInstalled()}>확인</button>
				</div>
			</div>;
		}
		if (P.standalone || P.installed) return null;
		if (this.cherishPwaDismissed()) return null;
		const canPrompt = !!P.prompt;
		if (!canPrompt && !P.ios) return null;
		return <div class="menugroup cw-pwa" style="max-width:none">
			<img class="cw-pwa-ic" src="/cherish/pwa/icon-192.png?v=5ac3b6ecf1e8" alt="" width="40" height="40" />
			<div class="cw-pwa-text">
				<b>체리쉬 배틀을 앱으로 쓸 수 있어요.</b>
				<span>{canPrompt ? '바탕화면과 홈 화면에 아이콘이 생기고, 주소창 없는 창으로 열려요.' : 'Safari 아래 공유 버튼을 누르고 「홈 화면에 추가」를 고르면 돼요.'}</span>
			</div>
			<div class="cw-pwa-btns">
				{canPrompt && <button class="button cw-pwa-go" disabled={this.cherishPwaBusy} onClick={this.cherishPwaInstall}>앱으로 설치</button>}
				<button class="button cw-pwa-later" onClick={this.cherishPwaDismiss}>나중에</button>
			</div>
		</div>;
	}
	/**
	 * 고정 입구 — 홈 맨 아래 테마 칩 옆의 작은 칩. 카드를 「나중에」로 닫았거나 다른 컴퓨터·폰에서 다시 깔고 싶을 때의 길이다.
	 * 설치할 수 있을 때만(설치창을 줄 수 있거나 iOS) 보이고, 앱 창 안·이미 깔린 브라우저에서는 안 보인다.
	 */
	renderCherishPwaFoot() {
		if (!PS.user.named) return null;
		const P = (window as any).cherishPwa;
		if (!P || !P.enabled || P.standalone || P.installed || P.justInstalled) return null;
		if (P.prompt) {
			return <p class="cw-pwa-foot">
				<button class="cw-pwa-chip" disabled={this.cherishPwaBusy} onClick={this.cherishPwaInstall}>📱 앱으로 설치</button>
			</p>;
		}
		if (P.ios) {
			return <p class="cw-pwa-foot">📱 앱으로 쓰려면 Safari 공유 버튼 → 「홈 화면에 추가」</p>;
		}
		return null;
	}
	// cherish-pwa:end
	// cherish-raid-live:begin (patch-raid-live.py)
	/**
	 * 배틀홈 마왕토벌전 배너 (2026-11-02, 실시간 도전형). 재료 /showdown/home raidLive — 열린 회차가 없으면 null 이라 안 그린다.
	 * 끝 시각이 지났는데 폴링 전이면 그리지 않는다. 누르면 마왕토벌전 방(cherish-raid).
	 */
	renderCherishRaidLive() {
		const h = this.cherishHome;
		const r = h && h !== 'hidden' ? h.raidLive : null;
		if (!r) return null;
		const end = Date.parse(r.endsAt);
		if (!Number.isFinite(end) || end <= Date.now()) return null;
		const mins = Math.max(1, Math.ceil((end - Date.now()) / 60000));
		const leftText = mins >= 60 ? `${Math.floor(mins / 60)}시간${mins % 60 ? ` ${mins % 60}분` : ''}` : `${mins}분`;
		const over = r.phase === 'done';
		const sub = over ? '마왕토벌전이 끝났어요. 결과와 행운의 자리 룰렛을 보러 가요.'
			: r.phase === 'lottery' ? '첫 마왕을 뽑고 있어요. 지금 들어오면 추첨에 참여할 수 있어요.'
			: r.phase === 'empty' && !(r.bosses && r.bosses.length) ? '마왕 자리가 비어 있어요. 먼저 앉는 사람이 마왕이에요.'
			: r.bosses && r.bosses.length > 1 ? `마왕 ${r.bosses.map((b: any) => `${b.name} ${b.wins}연승`).join(' · ')}. 옥좌 둘, 대기 ${r.queue}명.`
			: r.bossName ? `마왕 ${r.bossName} ${r.wins}연승 중. 목표 ${r.targetStreak}연승, 대기 ${r.queue}명.`
			: '지금 진행 중이에요.';
		const crown = <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden><path d="M3 8l4.5 4L12 5l4.5 7L21 8l-2 11H5L3 8z" fill="currentColor" /><path d="M5 19h14v2H5z" fill="currentColor" opacity=".6" /></svg>;
		return <button type="button" class="menugroup cwrl-banner" style="max-width:none" onClick={() => cwrlOpen()} aria-label="마왕토벌전 열기">
			<style>{`
			.cwrl-banner { display:flex; align-items:center; gap:12px; width:100%; text-align:left; border:0; cursor:pointer; border-radius:14px; padding:12px 14px; color:#f5f0ff; background:radial-gradient(120% 120% at 0% 0%, #4c1d95 0%, #1e1033 70%); box-shadow:0 8px 24px rgba(60,20,110,.25); }
			.cwrl-banner .cwrl-b-ic { display:inline-flex; width:40px; height:40px; border-radius:12px; background:rgba(251,191,36,.18); color:#fbbf24; align-items:center; justify-content:center; flex:none; }
			.cwrl-banner .cwrl-b-text { display:flex; flex-direction:column; gap:2px; min-width:0; flex:1; }
			.cwrl-banner .cwrl-b-text b { font-size:15px; }
			.cwrl-banner .cwrl-b-text span { font-size:12.5px; color:#d9ccff; white-space:normal; }
			.cwrl-banner .cwrl-b-go { flex:none; text-align:right; }
			.cwrl-banner .cwrl-b-go small { display:block; font-size:11px; color:#c4b5fd; }
			.cwrl-banner .cwrl-b-go b { font-size:13px; color:#fbbf24; }
			`}</style>
			<span class="cwrl-b-ic">{crown}</span>
			<span class="cwrl-b-text"><b>{over ? '마왕토벌전 결과' : '마왕토벌전 진행 중'}</b><span>{sub}</span></span>
			<span class="cwrl-b-go"><small>{over ? '결과 보기' : r.locked ? '종료까지' : '남은 시간'}</small><b>{over ? '→' : leftText}</b><small>{over ? '' : r.locked ? '새 도전 마감' : '들어가기'}</small></span>
		</button>;
	}
	// cherish-raid-live:end
	// cherish-hot-time:begin (patch-hot-time-ui.py)
	/** 「38분」 / 「1시간 5분」 — 핫타임 띠의 남은 시간. 홈 폴링(30초)마다 다시 그려진다. */
	cherishHotLeft(ms: number): string {
		const m = Math.max(1, Math.ceil(ms / 60000));
		const h = Math.floor(m / 60);
		return h > 0 ? `${h}시간${m % 60 ? ` ${m % 60}분` : ''}` : `${m}분`;
	}
	/** 「21:00」 — 오늘이 아니면 「11월 4일 21:00」. 브라우저 시각대(한국) 기준. */
	cherishHotClock(iso: string): string {
		const d = new Date(iso);
		const hm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
		return d.toDateString() === new Date().toDateString() ? hm : `${d.getMonth() + 1}월 ${d.getDate()}일 ${hm}`;
	}
	/** 관전석 후보 목록 (null = 닫힘). 띠에서 「앉히기」를 누를 때만 받는다. */
	cherishHotPick: any[] | null = null;
	cherishHotPickBusy = false;
	/** 관전석 아이 그림 — 새 기술 카드와 같은 경로 규약 (특별폼이면 폼 그림, 없으면 기본 그림으로 물러난다). */
	renderCherishHotArt(m: any) {
		const base = `/cherish/sprite/${m.shiny ? 'shiny/' : ''}${m.spriteId}.png?v=2`;
		const art = m.customFormId ? `/cherish/form/${m.customFormId}/${m.spriteId}.png?v=1` : base;
		return <img src={art} alt="" width="40" height="40" loading="lazy" onError={(e: any) => {
			const img = e.currentTarget as HTMLImageElement;
			if (img.src.indexOf(base) < 0) img.src = base;
		}} />;
	}
	/** 빈 칸 「앉히기」 — 후보를 받아 띠 안에 펼친다. 이미 열려 있으면 닫는다. */
	cherishHotPickToggle = () => {
		if (this.cherishHotPick) { this.cherishHotPick = null; this.forceUpdate(); return; }
		if (this.cherishHotPickBusy) return;
		this.cherishHotPickBusy = true;
		fetch('https://cherish-world.com/api/showdown/hot-watch/candidates', { credentials: 'include' })
			.then(r => r.json()).then(json => {
				this.cherishHotPick = json?.ok && Array.isArray(json.data?.mons) ? json.data.mons : [];
			}).catch(() => { this.cherishHotPick = []; })
			.then(() => { this.cherishHotPickBusy = false; this.forceUpdate(); });
	};
	/** 앉히기/내리기 — 응답의 seats 로 칸을 바로 다시 그린다 (다음 홈 폴링을 기다리지 않는다). */
	cherishHotSeat = (id: string) => {
		fetch('https://cherish-world.com/api/showdown/hot-watch', {
			method: 'POST', credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ id }),
		}).then(r => r.json()).then(json => {
			if (!json?.ok) { PS.alert(json?.message || json?.error?.message || '저장하지 못했어요.'); return; }
			const h = this.cherishHome;
			if (h && h !== 'hidden' && h.hotWatch) h.hotWatch.seats = json.data.seats || [];
			// 한 자리라 고르면 끝 — 목록을 접는다
			this.cherishHotPick = null;
			this.forceUpdate();
		}).catch(() => PS.alert('저장하지 못했어요.'));
	};
	/**
	 * 관전석 줄 (2026-10-29) — 핫타임 띠 안 맨 아래, 한 칸. 앉은 칸을 누르면 목록이 열려 다른 아이로 바꾸고, × 로 내린다.
	 * 수치(얼마나 오르는지)는 안 쓴다 — 방향만 (10-28 사용자 지시).
	 */
	renderCherishHotWatch() {
		const h = this.cherishHome;
		const w = h && h !== 'hidden' ? h.hotWatch : null;
		if (!w) return null;
		const m = Array.isArray(w.seats) ? w.seats[0] : null;
		const pick = this.cherishHotPick;
		return <div class="cw-hw">
			<div class="cw-hw-head">
				<b>응원석</b>
				<span>응원석에 앉은 아이가 배틀을 응원하면서 새 기술과 추억의 힘을 키워요</span>
			</div>
			<div class="cw-hw-slots">
				{m ? <div class={`cw-hw-slot is-on${pick ? ' is-open' : ''}`}>
					<button class="cw-hw-main" title="다른 아이로 바꾸기" onClick={this.cherishHotPickToggle}>
						<span class="cw-hw-art">{this.renderCherishHotArt(m)}</span>
						<span class="cw-hw-txt"><b>{m.name}</b><small>응원하는 중이에요. 눌러서 바꾸기</small></span>
					</button>
					<button class="cw-hw-x" title="응원석에서 내리기" aria-label="응원석에서 내리기" onClick={() => this.cherishHotSeat(m.id)}>×</button>
				</div> : <button class={`cw-hw-slot is-empty${pick ? ' is-open' : ''}`} onClick={this.cherishHotPickToggle}>
					<span class="cw-hw-art cw-hw-plus" aria-hidden>+</span>
					<span class="cw-hw-txt"><b>앉히기</b><small>응원시킬 아이를 골라 주세요</small></span>
				</button>}
			</div>
			{pick ? <div class="cw-hw-pick">
				{pick.length ? pick.map((c: any) => <button class={`cw-hw-cand${c.seated ? ' is-on' : ''}`} title={c.seated ? '눌러서 내리기' : '눌러서 앉히기'} onClick={() => this.cherishHotSeat(c.id)}>
					<span class="cw-hw-art">{this.renderCherishHotArt(c)}</span>
					<span class="cw-hw-name">{c.name}</span>
				</button>) : <p class="cw-hw-none">지금 앉힐 수 있는 아이가 없어요</p>}
			</div> : null}
		</div>;
	}
	/**
	 * 배틀 홈 핫타임 띠 (2026-10-27). 열림 = 「핫타임 진행 중」 + 남은 시간, 시작 전 = 예고. 재료 /showdown/home hotTime.
	 * 끝 시각이 지났는데 폴링 전이면 그리지 않는다 (30초 사이 「0분 남음」이 남지 않게).
	 */
	renderCherishHotTime() {
		const h = this.cherishHome;
		const t = h && h !== 'hidden' ? h.hotTime : null;
		if (!t) return null;
		const now = Date.now();
		const start = Date.parse(t.startAt);
		const end = Date.parse(t.endAt);
		if (!Number.isFinite(end) || end <= now) return null;
		const live = Number.isFinite(start) && start <= now;
		// 패배 보호 하루 상한(lossCap)은 숫자로 안 쓴다 — 「일부 보호」로 상한이 있다는 것만 말한다 (10-29 사용자 문구)
		const flame = <svg class="cw-hot-flame" viewBox="0 0 24 24" width="26" height="26" aria-hidden><path d="M12.6 2.2c.4 3.1-1.3 4.8-2.9 6.4C8.1 10.2 6.5 11.9 6.5 15a5.5 5.5 0 0 0 11 0c0-2.3-1-3.9-2-5.2-.3 1.4-1 2.3-2 2.6.6-3.2-.2-6.6-.9-10.2z" fill="currentColor" /><path d="M12 21a3.2 3.2 0 0 1-3.2-3.2c0-1.7 1.1-2.6 2.2-3.7.3 1 .9 1.6 1.6 1.8-.1-1 .2-2 .8-2.8.9 1.1 1.8 2.4 1.8 4.7A3.2 3.2 0 0 1 12 21z" fill="#fff" opacity=".55" /></svg>;
		return <div class={`menugroup cw-hot${live ? ' is-live' : ' is-soon'}`} style="max-width:none" role="status">
			<span class="cw-hot-ic">{flame}</span>
			<div class="cw-hot-text">
				<b>{live ? '핫타임 진행 중' : `${this.cherishHotClock(t.startAt)} 핫타임`}</b>
				<span>{live
					? `${this.cherishHotClock(t.endAt)}까지 시작한 랭크 배틀은 패배로 인한 점수하락이 일부 보호되고 포켓몬들이 새로운 기술을 추가로 배워와요.`
					: `${this.cherishHotClock(t.startAt)}부터 ${this.cherishHotClock(t.endAt)}까지 시작한 랭크 배틀은 패배로 인한 점수하락이 일부 보호되고 포켓몬들이 새로운 기술을 추가로 배워와요.`}</span>
				<span class="cw-hot-chips">
					<i>이기면 기술 포인트가 많이 올라요</i><i>져도 조금 올라요</i><i>{(t.learnAt || 10) / 10}점 모이면 새 기술</i>
				</span>
			</div>
			<div class="cw-hot-clock">
				<small>{live ? '남은 시간' : '시작까지'}</small>
				<b>{this.cherishHotLeft(live ? end - now : start - now)}</b>
			</div>
			{this.renderCherishHotWatch()}
		</div>;
	}
	/**
	 * 배틀 홈 「새내기 혜택 받는 중」 띠 (2026-10-27, 사용자 시안 A). 뉴비에게만 — /showdown/home 의 newbieLearn {every, nextIn}.
	 * 핫타임 띠와 같은 골격, 색만 청록(자람). 졸업 기준(기술 합)은 **비공개**(사용자 검수). nextIn 이 없으면(명단 앞 세 칸에 배울 게
	 * 남은 아이가 없으면) 오른쪽 칸을 안 그린다.
	 */
	renderCherishNewbieLearn() {
		const h = this.cherishHome;
		const n = h && h !== 'hidden' ? h.newbieLearn : null;
		if (!n || !(n.every > 0)) return null;
		const spark = <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden><path d="M12 2.5l2.2 6.3 6.3 2.2-6.3 2.2L12 19.5l-2.2-6.3L3.5 11l6.3-2.2z" fill="currentColor" /></svg>;
		return <div class="menugroup cw-hot cw-nb" style="max-width:none" role="status">
			<span class="cw-hot-ic">{spark}</span>
			<div class="cw-hot-text">
				<b>새내기 혜택 받는 중</b>
				<span>배틀 {n.every}판마다 명단에 있던 아이들이 새 기술을 하나씩 배워 와요.</span>
			</div>
			{typeof n.nextIn === 'number' ? <div class="cw-hot-clock">
				<small>다음 새 기술까지</small>
				<b>{n.nextIn}판</b>
			</div> : null}
		</div>;
	}
	// cherish-hot-time:end
	// cherish-battle-points:begin (patch-battle-points.py)
	/** GET /showdown/battle-points 응답. null = 아직 안 불러옴, 'hidden' = 비공개·권한 없음. */
	cherishBp: any | 'hidden' | null = null;
	cherishBpTimer: any = null;
	/** 수령 요청이 도는 동안. 연타 방지 (중복 지급 자체는 서버 락이 막는다). */
	cherishBpBusy = false;
	/** 방금 받은 알 개수 — 6초 동안 축하 줄을 띄운다. 0 이면 안 띄운다. */
	cherishBpGot = 0;
	/** 방금 받은 체리 (2026-09-07 보상 개편). 알과 같은 줄에 뜬다. */
	cherishBpGotCherry = 0;
	/** 방금 받은 베리 (2026-09-09 개편). 셋 다 같은 줄에 목록으로 뜬다. */
	cherishBpGotDust = 0;
	cherishBpGotTimer: any = null;
	cherishBpReceipt(eggs: number, cherry: number, dust: number) {
		return [
			eggs > 0 ? '알 ' + eggs.toLocaleString() + '개를 받았어요' : '',
			cherry > 0 ? '체리 ' + cherry.toLocaleString() + '개를 받았어요' : '',
			dust > 0 ? '베리 ' + dust.toLocaleString() + '개를 받았어요' : '',
		].filter(Boolean).join(', ');
	}
	fetchCherishBp = () => {
		if (!PS.user.named) return;
		// 숨긴 탭은 안 부른다 — 탭을 여러 개 띄우면 합계가 유저 한도를 넘겨 429 가 난다.
		if (typeof document !== 'undefined' && document.hidden) return;
		fetch('https://cherish-world.com/api/showdown/battle-points', { credentials: 'include' })
			.then(r => r.json())
			.then(json => {
				if (!json?.ok || !json.data?.open) {
					this.cherishBp = 'hidden';
					this.forceUpdate();
					return;
				}
				this.cherishBp = json.data;
				this.forceUpdate();
			})
			.catch(() => {});
	};
	/**
	 * 알 수령 (2026-09-07 수령제) — 게이지를 채우면 알이 지갑에 쌓이고 여기서 꺼낸다.
	 *
	 * 예전엔 배틀이 끝나고 홈에 오면 `PS.alert` 가 「알 받았어요」를 띄웠다. 방해만 되고, 새로고침하면
	 * 「직전 단계」 기억이 없어 조용히 지나가는 자리였다(사용자 지적 「엄청 빈약하다」).
	 * **카드 자체가 가득 찬 상태로 서서** 기다리고, 직접 수령에 성공했을 때만 팝업을 띄운다.
	 * 서버가 중복 지급을 막지만(FOR UPDATE), 연타로 요청이 겹치면 「받는 중」이 깜빡이므로 busy 로 막는다.
	 */
	cherishBpClaim() {
		if (this.cherishBpBusy) return;
		this.cherishBpBusy = true;
		this.forceUpdate();
		fetch('https://cherish-world.com/api/showdown/battle-points/claim', { method: 'POST', credentials: 'include' })
			.then(r => r.json())
			.then(json => {
				this.cherishBpBusy = false;
				if (!json?.ok) { this.forceUpdate(); return; }
				const got = Number(json.data?.claimed) || 0;
				const gotCherry = Number(json.data?.cherry) || 0;
				const gotDust = Number(json.data?.dust) || 0;
				// 응답이 갱신된 status 를 함께 준다 — 폴링을 기다리지 않고 바로 다음 구간 게이지로 넘어간다.
				if (json.data?.status) this.cherishBp = json.data.status;
				if (got > 0 || gotCherry > 0 || gotDust > 0) {
					this.cherishBpGot = got;
					this.cherishBpGotCherry = gotCherry;
					this.cherishBpGotDust = gotDust;
					if (this.cherishBpGotTimer) clearTimeout(this.cherishBpGotTimer);
					this.cherishBpGotTimer = setTimeout(() => { this.cherishBpGot = 0; this.cherishBpGotCherry = 0; this.cherishBpGotDust = 0; this.forceUpdate(); }, 6000);
					PS.alert(this.cherishBpReceipt(got, gotCherry, gotDust));
				}
				this.forceUpdate();
			})
			.catch(() => { this.cherishBpBusy = false; this.forceUpdate(); });
	}
	/**
	 * 오늘의 배틀 점수. `inline` 이면 menugroup 껍데기 없이 속만 돌려준다 — 「오늘의 나」 카드가
	 * 아래 띠로 품는 자리 (2026-09-06 사용자: 「둘은 애초에 한 섹션이 맞다」). 홈 카드가 품고 있으면
	 * (`cherishHomeOwnsBp`) 최상위 호출은 아무것도 안 그린다. 그 표식이 없는 옛 조합에선 종전대로 단독 카드.
	 *
	 * 상태는 셋이다 (2026-09-07): 평소(진행 게이지) · **가득 참**(is-ready, 수령 버튼) · 받은 직후(is-got, 6초).
	 */
	renderCherishBattlePoints(inline = false) {
		if (!PS.user.named) return null;
		if (!inline && (this as any).cherishHomeOwnsBp) return null;
		if (!this.cherishBpTimer) {
			this.cherishBpTimer = setInterval(this.fetchCherishBp, 30000); // poll-ok: 화면당 하나(cherishBpTimer 가드), 30초 단일 폴링
			this.fetchCherishBp();
		}
		const bp = this.cherishBp;
		if (!bp || bp === 'hidden') return null;
		const stages: number[] = bp.stages || [];
		const stage = Number(bp.stage) || 0;
		const points = Number(bp.points) || 0;
		const pending = Number(bp.pending) || 0;
		// 보상 개편 (2026-09-07): 단계마다 알이 아니라 체리가 나올 수 있다. stageRewards 가 정본이고,
		// CDN 에 남은 옛 응답에는 이 필드가 없다 — 그때는 종전대로 「전부 알」로 읽어 화면이 안 깨지게 한다.
		const rewards: any[] = bp.stageRewards || [];
		const pendingCherry = Number(bp.pendingCherry) || 0;
		// 2026-09-09 개편으로 중간 단계 보상이 체리 → **베리**로 옮겨졌다. 이 값을 안 읽으면
		// 「채우면 체리 0」·「+0」 처럼 빈 문구가 나간다 (2026-09-10 사용자 신고).
		const pendingDust = Number(bp.pendingDust) || 0;
		const nextReward = bp.nextReward || null;
		const nextEgg = nextReward ? (Number(nextReward.egg) || 0) : 1;
		const nextCherry = nextReward ? (Number(nextReward.cherry) || 0) : 0;
		const nextDust = nextReward ? (Number(nextReward.dust) || 0) : 0;
		const done = rewards.slice(0, stage);
		const todayEggs = done.length ? done.reduce((a: number, r: any) => a + (Number(r.egg) || 0), 0) : stage;
		const todayCherry = done.reduce((a: number, r: any) => a + (Number(r.cherry) || 0), 0);
		const todayDust = done.reduce((a: number, r: any) => a + (Number(r.dust) || 0), 0);
		// 「알 2개 · 베리 25」 — 조사를 피하려고 목록으로 쓴다 (숫자마다 이/가가 갈린다).
		const sumUp = (e: number, c: number, d: number) => [e > 0 ? '알 ' + e + '개' : '', c > 0 ? '체리 ' + c.toLocaleString() : '', d > 0 ? '베리 ' + d.toLocaleString() : ''].filter(Boolean).join(', ');
		// 다음 단계 한 칸의 문구. 셋 다 0 인 빈 단계면 그냥 「보상」으로 둔다 (숫자 0 을 노출하지 않는다).
		const nextLabel = sumUp(nextEgg, nextCherry, nextDust) || '보상';
		const next: number | null = bp.nextAt == null ? null : Number(bp.nextAt);
		// 진행 바는 지금 단계 구간 안에서만 그린다 (0 기준이 아니라 직전 임계값 기준).
		const floor = stage > 0 ? (Number(stages[stage - 1]) || 0) : 0;
		const raw = next !== null && next > floor ? ((points - floor) / (next - floor)) * 100 : 100;
		// 받을 알이 있으면 게이지는 가득 찬 채로 선다 — 이 상태의 할 일은 「눌러서 받기」 하나뿐이다.
		// 그 사이 쌓인 초과분은 숨어 있다가 수령 직후 status 가 오면 다음 구간에 그대로 반영된다.
		const ready = pending > 0 || pendingCherry > 0 || pendingDust > 0;
		const pct = ready ? 100 : Math.max(0, Math.min(100, raw));
		const remain = next === null ? 0 : Math.max(0, next - points);
		const got = this.cherishBpGot || 0;
		const gotCherry = this.cherishBpGotCherry || 0;
		const gotDust = this.cherishBpGotDust || 0;
		const gotAny = got > 0 || gotCherry > 0 || gotDust > 0;
		const cls = `${inline ? 'cw-bp is-inline' : 'menugroup cw-bp'}`
			+ (next === null && !ready ? ' is-done' : '')
			+ (ready ? ' is-ready' : '')
			+ (gotAny ? ' is-got' : '');
		return <div class={cls} style={inline ? '' : 'max-width:none'}>
			<p class="cw-bp-head">
				<span class="cw-bp-title"><i class="cw-egg-ic" aria-hidden /> 오늘의 배틀 점수</span>
				<span class="cw-bp-score"><span ref={(el: any) => this.cherishCountUp(el, points)}>{points.toLocaleString()}</span><small>점</small></span>
			</p>
			{/* cherish-bp-goal: 게이지 끝의 목표 배지 — 채우면 뭐가 나오는지를 글자가 아니라 그림으로 (2026-09-05 사용자 요청).
				알 단계면 알 실루엣, 체리 단계면 CSS 체리, 베리 단계면 CSS 반짝임 (2026-09-07 개편 → 2026-09-09 베리).
				80% 넘으면 is-near 로 살짝 맥동, 받을 게 있으면 개수를 띄운다. 외형 patch-skin.py §11·§33. */}
			<div class="cw-bp-row">
				<div class="cw-bp-track"><div class="cw-bp-fill" style={`width:${pct}%`} /></div>
				<span class={`cw-bp-goal${ready ? ' is-ready' : next !== null && pct >= 80 ? ' is-near' : ''}`}
					title={ready ? '받을 보상이 있어요' : next === null ? '오늘 몫을 다 받았어요' : '게이지를 다 채우면 ' + nextLabel}>
					{(ready ? pending > 0 : nextEgg > 0)
						? <i class="cw-bp-goal-egg" aria-hidden />
						: (ready ? pendingCherry > 0 : nextCherry > 0)
							? <i class="cw-bp-goal-cherry" aria-hidden />
							: <i class="cw-bp-goal-dust" aria-hidden />}
					<b>{ready
						? String(pending > 0 ? pending : pendingCherry > 0 ? pendingCherry : pendingDust)
						: next === null ? '✓'
							: nextEgg > 0 ? '+' + nextEgg
								: nextCherry > 0 ? '+' + nextCherry.toLocaleString()
									: '+' + nextDust.toLocaleString()}</b>
				</span>
			</div>
			{ready
				? <div class="cw-bp-claim">
					<span class={`cw-bp-claim-egg${pending > 0 ? '' : pendingCherry > 0 ? ' is-cherry' : ' is-dust'}`} aria-hidden />
					<span class="cw-bp-claim-text">
						<b>{sumUp(pending, pendingCherry, pendingDust)} 받아 가세요</b>
						<small>점수는 그동안에도 계속 쌓여요</small>
					</span>
					<button class="button cw-bp-claim-go" disabled={this.cherishBpBusy} onClick={() => this.cherishBpClaim()}>
						{this.cherishBpBusy ? '받는 중…' : '받기'}
					</button>
				</div>
				: gotAny
					? <div class="cw-bp-got">
						<span class={`cw-bp-got-egg${got > 0 ? '' : gotCherry > 0 ? ' is-cherry' : ' is-dust'}`} aria-hidden />
						<span class="cw-bp-got-text"><b>{this.cherishBpReceipt(got, gotCherry, gotDust)}</b><small>{got > 0 ? '내 포켓몬 탭에서 품어 주세요'
							: gotCherry > 0 && gotDust > 0 ? '체리와 베리는 바로 지갑에 들어갔어요'
								: gotCherry > 0 ? '체리는 바로 지갑에 들어갔어요' : '베리는 바로 지갑에 들어갔어요'}</small></span>
					</div>
					: <p class="cw-bp-foot">
						<span class="cw-bp-left">
							{todayEggs > 0 && <span class="cw-bp-eggs"><i class="cw-egg-ic" aria-hidden /> 오늘 알 {todayEggs}개</span>}
							{todayCherry > 0 && <span class="cw-bp-cherries">오늘 체리 {todayCherry.toLocaleString()}</span>}
							{todayDust > 0 && <span class="cw-bp-dusts">오늘 베리 {todayDust.toLocaleString()}</span>}
							{/* 문구가 길면 한 줄에 안 들어가 발이 두 줄로 감긴다 — 짧게 유지할 것 */}
							{next === null
								? <span>오늘 몫을 다 받았어요</span>
								: <span>{stage > 0 ? '다음 보상까지' : '첫 보상까지'} <b>{remain.toLocaleString()}점</b><span class="cw-bp-hint">, 채우면 {nextLabel}</span></span>}
						</span>
						<span class="cw-bp-reset">오전 7시에 초기화</span>
					</p>}
		</div>;
	}
	// cherish-battle-points:end
	// cherish-motion:begin (patch-motion.py)
	/** 로딩 스켈레톤 — 「불러오는 중...」 글자 대신 자리 카드. kind 로 모양만 고른다 (외형 cherish-skin.css §26). */
	renderCherishSkeleton(kind: 'list' | 'grid' | 'rank' = 'list') {
		if (kind === 'grid') {
			return <div class="cw-skel cw-skel-grid" aria-hidden>{[0, 1, 2, 3, 4, 5].map(() => <i class="cw-skel-tile" />)}</div>;
		}
		if (kind === 'rank') {
			return <div class="cw-skel" aria-hidden><i class="cw-skel-card" /><i class="cw-skel-card is-tall" />{[0, 1, 2].map(() => <i class="cw-skel-row" />)}</div>;
		}
		return <div class="cw-skel" aria-hidden><i class="cw-skel-line" style="width:40%" /><i class="cw-skel-card" /><i class="cw-skel-card" /><i class="cw-skel-line" style="width:60%" /></div>;
	}
	/** 숫자 카운트업 — ref 콜백에서 부른다. 같은 값이면 다시 안 돈다. 대상 span 은 자식이 없어야 한다. */
	cherishCountUp(el: HTMLElement | null, target: number, suffix = '') {
		if (!el) return;
		const key = String(target);
		if (el.dataset.cwv === key) return;
		const from = Number(el.dataset.cwv || 0);
		el.dataset.cwv = key;
		if (!Number.isFinite(target) || (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)) {
			el.textContent = target.toLocaleString() + suffix;
			return;
		}
		const t0 = performance.now();
		const dur = 700;
		const step = (t: number) => {
			const k = Math.min(1, (t - t0) / dur);
			const e = 1 - Math.pow(1 - k, 3);
			el.textContent = Math.round(from + (target - from) * e).toLocaleString() + suffix;
			if (k < 1) requestAnimationFrame(step);
		};
		requestAnimationFrame(step);
	}
	// cherish-motion:end
	renderCherishBoard() {
		const roomsCache = PS.mainmenu.roomsCache as any;
		const users: [string, string][] = roomsCache.cherishUsers || [];
		const cherishLoading = roomsCache.cherishUsers === undefined;
		if (!this.cherishRoomsTimer) {
			const refresh = () => {
				if (PS.connection?.connected) { PS.send('/cmd rooms'); PS.send('/cmd roomlist ,'); }
				else setTimeout(refresh, 500);
			};
			this.cherishRoomsTimer = setInterval(refresh, 25000);
			refresh();
		}
		return <div class="menugroup" style="max-width:none">
			<h3>접속 중인 트레이너 {}
				<small style="font-weight:normal">
					{roomsCache.userCount || '-'}명 접속
				</small>
			</h3>
			<p>아바타를 누르면 도전장을 보낼 수 있어요!</p>
			<div style="display:flex;flex-wrap:wrap;gap:8px">
				{users.map(([name, avatar]) => (
					<button
						data-href={`user-${toID(name)}`} class="cw-tile cw-tile-trainer"
					>
						<img
							src={avatar.startsWith('#cherish-') ?
								`/sprites/trainers-custom/cherish${avatar.slice(9)}-full.png` :
								Dex.resolveAvatar(avatar)}
							style="width:110px;height:150px;object-fit:contain"
						/>
						<span style="font-size:12px;margin-top:5px;max-width:120px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">
							{name}
						</span>
					</button>
				))}
			</div>
			{!users.length && <p><em>{cherishLoading ? '접속 정보를 불러오는 중...' : '지금 접속한 트레이너가 없어요.'}</em></p>}
		</div>;
	}
	renderCherishTeam() {
		if (!PS.user.named) return null;
		if (this.cherishLoadedFor !== PS.user.userid) this.loadCherishData();
		const mons = this.cherishPokemon;
		return <div class="menugroup cw-monpage" style="max-width:none">
			{/* cherish-album-links: 도감 링크 */}
			<div style="display:flex;align-items:baseline;justify-content:space-between;gap:8px;flex-wrap:wrap">
				<h3 style="margin:0">내 포켓몬·배틀 팀</h3>
				<a href="https://cherish-world.com/pokemon/dex" target="_blank" rel="noreferrer" class="button" style="font-size:12px">&#x1F4D5; 포켓몬 도감</a>
			</div>
			<p>
				포켓몬을 누르면 자세히 보여요. 배틀 팀(출전 6, 후보 3)과 이름, 파트너, 일지, 기술을 거기서 정해요. 3대3 은 앞에서 세 마리, 6대6 은 앞에서 여섯 마리가 나가고, 어린이집이나 온실에 간 아이가 있으면 뒤 칸이 대신 나가요. 알 부화는 디스코드에서 /포켓몬
			</p>
			{this.renderCherishRosterBar(mons || [])}
			{!mons ? this.renderCherishSkeleton('grid') : !mons.length ? (
				<p><em>아직 포켓몬이 없어요. 디스코드에서 <strong>/포켓몬</strong> 으로 첫 파트너를 만나보세요!</em></p>
			) : (
				<div class="cw-monlist">
					{/* cherish-mon-detail:grid:begin */}
					{this.cherishSortedMons(mons).filter((p: any) => (!this.cherishSpecOnlyIds || this.cherishSpecOnlyIds.includes(p.id))).map((p: any) => {
					const slot = this.cherishTeamIds.indexOf(p.id);
					const picked = this.cherishSelectedMonId === p.id;
					const glow = this.cherishTypeColor((p.types || [])[0]);
					return <div
						onClick={() => this.selectCherishMon(p)}
						class={`cw-tile cw-tile-mon${picked ? ' cw-tile-on' : ''}${p.shiny ? ' is-shiny' : ''}${p.inDaycare || p.rentedOut ? ' is-away' : ''}`} role="button" tabIndex={0}
						style={`--glow:${glow}`}
					>
						{slot >= 0 && <span class={`cw-mon-slot${slot < 3 ? ' is-out' : slot < 6 ? ' is-out2' : ' is-sub'}`} title={slot < 3 ? '출전 (3대3·6대6 모두 나가요)' : slot < 6 ? '출전 (6대6 에서 나가요)' : '후보 (앞 자리가 비면 대신 나가요)'}>{slot < 6 ? `출전 ${slot + 1}` : `후보 ${slot - 5}`}</span>}
						{p.isPartner && <span class="cw-mon-partner" title="파트너"><svg viewBox="0 0 24 24" width="13" height="13" aria-hidden><path d="M12 21s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 11c0 5.6-7 10-7 10z" fill="currentColor" /></svg></span>}
						{/* cherish-pin: 오른쪽 위는 파트너 자리라, 둘 다면 한 칸 아래로 (0317) */}
						{p.pinned && <span title="곁에 두는 아이" style={`position:absolute;top:${p.isPartner ? 34 : 8}px;right:8px;z-index:1;width:22px;height:22px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:12px;line-height:1;background:#fff;box-shadow:0 2px 6px -2px rgba(15,23,42,.45)`}>🧡</span>}
						{/* 특별폼 그림은 custom-form 의 모듈 함수를 블록 주인인 여기가 직접 부른다 (2026-10-17, typeof 가드) */}
						<span class="cw-mon-art"><img src={typeof cherishMonArt === 'function' ? cherishMonArt(p) : `/cherish/sprite/${p.shiny ? 'shiny/' : ''}${p.spriteId}.png?v=2`} data-cwfb={typeof cherishMonArtBase === 'function' ? cherishMonArtBase(p) : ''} onError={typeof cherishMonArtFb === 'function' ? cherishMonArtFb : undefined} width="120" height="120" />{p.inDaycare && <span class="cw-mon-away" title="어린이집에 가 있어요">어린이집</span>}{p.rentedOut && !p.inDaycare && <span class="cw-mon-away" title={this.cherishLentNote(p)}>빌려주는 중</span>}</span>
						{p.epithet ? <span class="cw-mon-epithet">&#x300C;{p.epithet}&#x300D;</span> : null}
						<span class="cw-mon-name">{p.shiny ? <svg class="cw-shiny-ic" viewBox="0 0 24 24" aria-hidden><path d="M12 2l2.4 6.6L21 11l-6.6 2.4L12 20l-2.4-6.6L3 11l6.6-2.4z" /></svg> : null}<span class="cw-mon-name-tx">{p.nickname || p.koName || p.speciesName}</span>{p.abilityHidden ? <span title="숨은 특성" style="flex:0 0 auto;font-size:11px;line-height:1">🧬</span> : null}</span>
						{/* 빌린 아이 띠 (0477) — 명단에 직접 넣는다. 스킨을 안 건드리려 인라인 */}
						{p.borrowed ? <span title="빌린 아이예요. 배틀 명단에 넣으면 나가요" style="display:inline-block;margin:2px 0 0;padding:1px 8px;border-radius:999px;font-size:10.5px;line-height:16px;background:var(--cw-tint, #fff3f5);border:1px solid var(--cw-line, #f1c7d0);color:var(--cw-ink-soft, #7a4a58)">🤝 {p.borrowedFromName || '빌린'} 님의 아이</span> : null}
						{/* 내가 빌려준 아이 띠 (2026-10-18) — 누구에게 갔는지. 빌린 아이 띠와 같은 모양, 색만 하늘색 */}
						{p.rentedOut ? <span title={this.cherishLentNote(p)} style="display:inline-block;margin:2px 0 0;padding:1px 8px;border-radius:999px;font-size:10.5px;line-height:16px;max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;background:#eef6fc;border:1px solid #b9d9ef;color:#2f6f99">🤝 {p.rentedOutToName ? `${p.rentedOutToName} 님에게` : '빌려주는 중'}</span> : null}
						<span class="cw-mon-types">{(p.types || []).map((t: string) => <span dangerouslySetInnerHTML={{ __html: Dex.getTypeIcon(t) }} />)}</span>
						{/* 특기 뱃지 (2026-10-16) — 스킨을 안 건드리려 인라인 */}
						{this.renderCherishSpecBadges(p)}
						{/* 새 기술 알약 (2026-10-27) — 상세를 열면 꺼진다. 외형 cherish-learn.css */}
						{this.renderCherishNewMovePill(p)}
						{/* 뉴비 진행 점 (2026-10-27) — 뉴비가 아니면 안 그린다 */}
						{this.renderCherishLearnDots(p)}
						{/* 핫타임 포인트 막대 (2026-10-27) — 핫타임 중에만 */}
						{this.renderCherishHotBar(p)}
					</div>;
				})}
				</div>
			)}
			{this.renderCherishMonDock()}
			{/* cherish-mon-detail:grid:end */}
		</div>;
	}
}

class MainMenuPanel extends CherishPanelBase<MainMenuRoom> {
	static readonly id = 'mainmenu';
	static readonly routes = [''];
	static readonly Model = MainMenuRoom;
	static readonly icon = <i class="fa fa-home" aria-hidden></i>;
	override cherishNeeds = ['quests', 'avoids', 'me', 'school', 'tower', 'maze', 'auction', 'blossom', 'itemAuction'];
	override componentDidMount() {
		super.componentDidMount();
		this.subscribeTo(PSBackground);
	}
	override focus(options?: PSRoomFocusOptions) {
		this.cherishHomeRefresh();
		(this as any).fetchCherishRooms?.();
		if (!options?.preventScroll) PSView.scrollToRoom();
		if (PSView.hasTapped) return;

		PSView.politeFocus(this.base?.querySelector<HTMLButtonElement>('.formatselect'));
	}
	submitSearch = (ev: Event, format: string, team?: Team) => {
		if (!PS.user.named) {
			// cherish-login-flow: 포맷 하나로 검색 — 이름 입력창 대신 디스코드 로그인 (patch-login-flow.py)
			cherishLoginStart(true);
			return;
		}
		PS.mainmenu.startSearch(format, team, ev.target as HTMLElement);
	};
	handleDragStart = (e: DragEvent) => {
		const room = PS.getRoom(e.currentTarget);
		if (!room) return;
		const foreground = (PS.leftPanel.id === room.id || PS.rightPanel?.id === room.id);
		PS.dragging = { type: 'room', roomid: room.id, foreground };
	};
	handleDragEnter = (e: DragEvent) => {
		// console.log('dragenter ' + e.dataTransfer!.dropEffect);
		e.preventDefault();
		if (PS.dragging?.type !== 'room') return;
		const draggingRoom = PS.dragging.roomid;
		if (draggingRoom === null) return;

		const draggedOverRoom = PS.getRoom(e.target);
		if (draggingRoom === draggedOverRoom?.id) return;

		const index = PS.miniRoomList.indexOf(draggedOverRoom?.id as any);
		if (index >= 0) {
			PS.dragOnto(PS.rooms[draggingRoom]!, 'mini-window', index);
		} else if (PS.rooms[draggingRoom]?.location !== 'mini-window') {
			PS.dragOnto(PS.rooms[draggingRoom]!, 'mini-window', 0);
		}

		// dropEffect !== 'none' prevents bounce-back animation in
		// Chrome/Safari/Opera
		// if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
	};
	renderMiniRoom(room: PSRoom) {
		return <PSPanelErrorBoundary key={room.id} room={room} />;
	}
	handleClickMinimize = (e: MouseEvent) => {
		if ((e.target as Element)?.getAttribute('data-cmd')) {
			return;
		}
		if (((e.target as Element)?.parentNode as Element)?.getAttribute('data-cmd')) {
			return;
		}
		const room = PS.getRoom(e.currentTarget);
		if (room) {
			room.minimized = !room.minimized;
			this.forceUpdate();
		}
	};
	renderMiniRooms() {
		return PS.miniRoomList.map(roomid => {
			const room = PS.rooms[roomid]!;
			const notifying = room.notifications.length ? ' notifying' : room.isSubtleNotifying ? ' subtle-notifying' : '';
			return <div
				class={`mini-window${room.minimized ? ' collapsed' : ''}${room === PS.room ? ' focused' : ''}`}
				key={roomid} data-roomid={roomid}
			>
				<h3
					class={`mini-window-header${notifying}`} draggable onDragStart={this.handleDragStart} onClick={this.handleClickMinimize}
				>
					<button class="closebutton" data-cmd="/close" aria-label="Close" tabIndex={-1}>
						<i class="fa fa-times-circle" aria-hidden></i>
					</button>
					<button class="maximizebutton" data-cmd="/maximize" tabIndex={-1} aria-label="Maximize">
						<i class="fa fa-stop-circle" aria-hidden></i>
					</button>
					<button class="minimizebutton" tabIndex={-1} aria-label="Expand/Collapse">
						<i class="fa fa-minus-circle" aria-hidden></i>
					</button>
					{room.title}
				</h3>
				{this.renderMiniRoom(room)}
			</div>;
		});
	}
	renderGames() {
		if (!PS.mainmenu.search.games) return null;

		// This does not use the word "game" because it includes things like help tickets
		return <div class="menugroup">
			<p class="label">참여 중:</p>
			{Object.entries(PS.mainmenu.search.games).map(([roomid, gameName]) => <div>
				<a class="blocklink" href={`${roomid}`}>{gameName}</a>
			</div>)}
		</div>;
	}
	// cherish-ffa:begin (patch-ffa-client.py)
	/** 홈 상단 CTA — 「자동매칭 시작」 한 버튼 + 모드 토글 3칸 (2026-09-04 사용자 확정).
	 *
	 *  이전에는 난투전/1대1/2대2 큰 버튼 셋이었다. 셋 다 동시 대기가 전제인데 기본 상태가
	 *  「0개 선택」이라, 아무것도 모르는 유저는 하나만 누르고 3등분된 풀에서 혼자 기다렸다.
	 *  게다가 업스트림 startSearch 는 카운트다운 중이면 다른 포맷 검색을 거절해서
	 *  (「Wait for this countdown to finish first...」) 실제로 셋을 함께 걸기가 번거로웠다.
	 *
	 *  그래서 **의도된 동작(셋 다 대기)이 무조작 경로**가 되게 뒤집었다. 토글은 기본 전부 켜짐,
	 *  시작 버튼 한 번이면 켜둔 모드 전부에 검색이 나간다. 카운트다운은 걷어냈다 — 팀이
	 *  서버 권위라 되돌릴 것이 없고, 취소 버튼이 바로 그 자리에 있다.
	 *
	 *  ⚠️ 토글을 전부 끄면 시작이 죽으므로 마지막 하나는 못 끄게 막는다.
	 *  ⚠️ 파티면 **리더만** 이 UI 를 본다. 비리더는 리더의 대기 상태만 읽는다 (서버가 강제하고
	 *     UI 는 거기 맞춘다 — patch-ffa-server.py searchBattle 게이트).
	 *
	 * 제목은 **화면에 뜨는 이름**이다. 2026-09-22 사용자 확정으로 **세 칸 평면**이 됐다 —
	 * 그전에는 큰 칸이 「1대1」·「2대2」 둘이고 3대3·6대6 이 그 아래 하위 체크였는데,
	 * 6대6 이 한 단 아래로 묻혀 보기 어려웠다(사용자 지적).
	 *
	 * 이름 체계도 그때 통일했다: 앞 둘은 **마릿수**(3대3·6대6), 사람이 둘인 모드만 **팀배틀**이다.
	 * 예전엔 1대1·2대2(사람 수)와 6대6(마릿수)이 섞여 있어서 6대6 만 결이 달랐다.
	 * ⚠️ **라벨만 바꾼 것이다** — 포맷 id·`mode=solo|multi`·Elo 풀은 그대로다(옛 기록이 고아가 된다).
	 */
	cherishFormats = [
		{ id: 'gen9cherishbattle', title: '3대3', sub: '한 명과 세 마리씩' },
		{ id: 'gen9cherishsixbattle', title: '6대6', sub: '한 명과 여섯 마리씩' },
		{ id: 'gen9cherishmultibattle', title: '팀배틀', sub: '둘이 한 편' },
	];
	/** 켜둔 모드 — 브라우저에만 남는다 (서버는 모른다). 저장값이 없는 포맷은 cherishModeOn 의 기본값. */
	cherishModes: { [format: string]: boolean } = (() => {
		try { return JSON.parse(localStorage.getItem('cherish-battle-modes') || '{}') || {}; } catch { return {}; }
	})();
	cherishPartyPoll: any = null;
	/**
	 * 기본값: **팀배틀만 켜짐**. 3대3·6대6 은 꺼진 채 시작한다.
	 *
	 * 09-05 「1대1 꺼짐」 → 09-06 「난투전도 꺼짐」 → **2026-09-21 「6대6 도 꺼짐」** (사용자 확정).
	 * 셋 다 같은 이유다: 신규가 첫 시작에서 맞붙게 될 판을 **팀배틀 하나로** 좁힌다. 3대3 은 초보와
	 * 고수가 그대로 붙고, 6대6 은 명단 여섯 칸을 갖춘 사람의 모드라 신규에겐 더 그렇다.
	 *
	 * 저장값이 있으면 그게 우선이라 **이미 즐기던 사람은 소급되지 않는다** (3대3 을 직접 켠 사람은
	 * 계속 켜져 있다). 새로 생긴 6대6 은 저장값이 없으므로 전원이 꺼진 채로 시작한다.
	 * 2분 동안 안 잡히면 「3대3도 켜볼까?」 팝업이 회수한다 (renderCherishNudge).
	 */
	cherishModeOn(format: string) {
		// 6대6 은 여섯 마리를 못 갖추면 **켜져 있어도 안 켜진 것**이다 (2026-09-21).
		// 저장값보다 자격이 우선이다 — 한때 갖췄다가 어린이집에 맡겨 모자라진 사람이
		// 저장값 때문에 계속 6대6 검색을 걸면 서버가 매번 거절 팝업을 띄운다.
		if (format === 'gen9cherishsixbattle' && !this.cherishSixReady()) return false;
		const saved = this.cherishModes[format];
		if (saved === undefined) return format === 'gen9cherishmultibattle';
		return saved !== false;
	}
	/**
	 * 6대6 자격 (2026-09-21) — 홈 데이터(GET /showdown/home)의 sixReady.
	 *
	 * 아직 안 받았으면(첫 페인트·patch-home-dash.py 없는 조합) **막지 않는다.** 여기서 막으면
	 * 자격이 있는 사람도 새로고침 직후 잠깐 잠긴 칸을 본다. 진짜 관문은 서버다
	 * (`/internal/showdown/team` 이 정원을 못 채우면 팀을 안 내준다).
	 */
	cherishSixReady() {
		const home = (this as any).cherishHome;
		if (!home || home === 'hidden' || home.sixReady === undefined) return true;
		return home.sixReady === true;
	}
	/** 6대6 칸에 붙일 안내 — 자격이 있으면 빈 문자열. */
	cherishSixNote() {
		if (this.cherishSixReady()) return '';
		const home = (this as any).cherishHome;
		const have = Number(home?.sixReadyCount);
		const need = Number(home?.sixTeamSize) || 6;
		return Number.isFinite(have)
			? `지금 내보낼 수 있는 아이가 ${have}마리예요. 서로 다른 ${need}마리가 모이면 열려요`
			: `서로 다른 ${need}마리가 모이면 열려요`;
	}
	/**
	 * 싱글 포맷 — 3대3 과 6대6 은 **구조가 같다**: 같은 gameType(싱글)·같은 매칭 경로
	 * (업스트림 matchmakingOK)·같은 승패 구조. 구조가 다른 건 팀배틀 하나뿐이다.
	 *
	 * 2026-09-22 에 **화면 묶음은 걷었다**(세 칸 평면). 이 배열은 화면이 아니라 판정용으로만 남는다 —
	 * 실력 차 상한 완화 팝업(cherishWide)과 2분 회수 팝업(cherishNudge)이 「싱글을 돌고 있나」를 묻는다.
	 */
	cherishSingleFormats = ['gen9cherishbattle', 'gen9cherishsixbattle'];
	/**
	 * 싱글(3대3·6대6) 중 하나라도 켜져 있나. **저장하지 않고 그때그때 끌어낸다** —
	 * 따로 저장하면 `cherish-battle-modes` 형식이 바뀌어 이주가 필요해진다.
	 */
	cherishSingleOn() {
		return this.cherishSingleFormats.some(id => this.cherishModeOn(id));
	}
	cherishOnFormats() {
		const on = this.cherishFormats.filter(f => this.cherishModeOn(f.id));
		// 6대6 만 켜 둔 사람이 여섯 마리를 잃으면 켜진 모드가 **0개**가 된다 (2026-09-21).
		// 토글은 마지막 하나를 못 끄게 막지만 자격 상실은 그 방어를 지나쳐 온다.
		// 그대로 두면 시작 버튼이 아무것도 안 보내고 조용히 죽는다 — 3대3 으로 떨어뜨린다.
		if (on.length) return on;
		return this.cherishFormats.filter(f => f.id === 'gen9cherishbattle');
	}
	cherishToggleMode = (format: string) => {
		// 자격이 없는 6대6 은 눌러도 안 켜진다 (칸은 disabled 지만 안내 팝업의 카드로도 눌린다).
		if (format === 'gen9cherishsixbattle' && !this.cherishSixReady()) return;
		// 마지막 하나까지 끄면 시작 버튼이 죽는다 — 최소 하나는 켜 둔다.
		if (this.cherishModeOn(format) && this.cherishOnFormats().length <= 1) return;
		this.cherishModes[format] = !this.cherishModeOn(format);
		try { localStorage.setItem('cherish-battle-modes', JSON.stringify(this.cherishModes)); } catch {}
		this.forceUpdate();
	};
	cherishQueueOf(format: string) {
		return PS.mainmenu.search.cherishQueue?.[format] || null;
	}
	cherishParty() {
		return ((PS as any).cherishParty || {}) as {
			partner?: string | null, partnerName?: string | null,
			leaderName?: string | null, isLeader?: boolean, partySearching?: string[],
		};
	}
	/**
	 * 지금 대기 중인 모드.
	 *
	 * ⚠️ 파티면 내 |updatesearch| 만으로는 부족하다. 싱글(3대3·6대6)은 **출전자 명의로** 큐에 서기
	 * 때문에(교대), 내 차례가 아니면 내 searching 에 그 포맷이 안 들어온다. 그래서 파티일 때는 서버가
	 * 내려주는 두 사람 합집합(partySearching)을 함께 본다.
	 */
	cherishWaitingFormats() {
		const mine = PS.mainmenu.search.searching || [];
		const party = this.cherishParty();
		const ours = party.partner ? (party.partySearching || []) : [];
		return this.cherishFormats.filter(f => mine.includes(f.id) || ours.includes(f.id));
	}
	cherishStartAll = (elem: HTMLElement | null) => {
		if (!PS.user.named) {
			// cherish-login-flow: 「배틀 시작」 — 이름 입력창 대신 디스코드 로그인 (patch-login-flow.py)
			cherishLoginStart(true);
			return;
		}
		// 첫 시작은 바로 돌리지 않고 모드 안내를 먼저 띄운다 (2026-09-05). 거기서 「이대로 시작」이 검색을 건다.
		if (this.cherishIntroEnabled() && !this.cherishIntroSeen()) {
			this.cherishOpenIntro();
			return;
		}
		this.cherishSearchNow();
	};
	/** 켜둔 모드 전부에 /search — 시작 버튼과 안내 팝업의 「이대로 시작」이 함께 쓴다. */
	cherishSearchNow() {
		const mm = PS.mainmenu;
		PS.send('/utm ');
		for (const f of this.cherishOnFormats()) {
			if (mm.search.searching?.includes(f.id)) continue;
			PS.send(`${mm.adjustPrivacy()}/search ${f.id}`);
		}
		mm.update(null);
	}
	/**
	 * 자동매칭 첫 안내 (2026-09-05, 사용자 확정).
	 *
	 * 매칭 풀이 좁아 초보와 고수가 1대1 로 계속 붙었다. 풀을 강제로 가르는 대신, **첫 「자동매칭 시작」에서**
	 * 세 모드를 설명하고 원하는 것만 골라 돌릴 수 있음을 알린 뒤 **팀배틀 하나로** 유도한다
	 * (2대2 는 실력 밸런스로 편을 짜 준다 — 서버 patch-match-skill.py `cherishTryMatchMulti`).
	 *
	 * ⚠️ 2026-09-21 이전에는 **난투전도 함께 추천**했다 (등수제라 지는 부담이 옅다는 이유).
	 *    난투전을 홈에서 내리면서 그 카드가 6대6 으로 바뀌었고, 6대6 은 추천이 아니다 —
	 *    명단 여섯 칸을 갖춘 사람의 모드라 신규에게 권할 자리가 아니다. **추천은 2대2 하나뿐**이다.
	 * 한 번 본 뒤에는 안 뜬다 (localStorage cherish-mode-intro-seen-3). 모드 칸 오른쪽 「?」 로 다시 연다.
	 * 1대1 은 기본 꺼짐(cherishModeOn). 문구는 2026-09-05 사용자가 직접 준 것 — 임의로 다듬지 말 것.
	 *
	 * 2026-09-06 사용자 확정으로 전원 노출 — 기존 유저도 배포 후 첫 시작에서 한 번 본다 (1대1 이 꺼진 걸 알리는 유일한
	 * 능동 안내). 검수 때는 `?cwintro` 플래그로만 열었다. 다시 잠그려면 여기서 플래그 검사로 되돌리면 된다.
	 */
	cherishIntroEnabled() {
		return true;
	}
	cherishIntroOpen = false;
	/**
	 * 「이미 본 사람」 표식. **내용이 바뀌면 키를 올린다** (2026-09-21).
	 *
	 * 2026-09-22 에 **-3 으로 한 번 더 올렸다** — 홈 칸이 세 칸 평면이 되고 「2대2」가 「팀배틀」로
	 * 바뀌었다. 그전(09-21)에는 안내 카드가 난투전에서 6대6 으로 갈려 -2 로 올렸다.
	 * 기존 유저는 이미 한 번 봐서 옛 키가
	 * 남아 있다 — 그대로 두면 **바뀐 안내를 아무도 다시 보지 못한다**. 09-06 에 「전원 노출」을
	 * 정한 이유(1대1 이 꺼진 걸 알리는 유일한 능동 안내)가 여기서도 그대로 성립한다.
	 *
	 * 옛 키는 지우지 않는다 — 되돌릴 때 이 문자열만 `cherish-mode-intro-seen` 으로 내리면
	 * 그때 본 사람들이 다시 안 보게 된다.
	 */
	cherishIntroSeen() {
		try { return !!localStorage.getItem('cherish-mode-intro-seen-3'); } catch { return true; }
	}
	cherishOpenIntro = () => {
		this.cherishIntroOpen = true;
		this.forceUpdate();
	};
	cherishCloseIntro = (start: boolean) => {
		try { localStorage.setItem('cherish-mode-intro-seen-3', '1'); } catch {}
		this.cherishIntroOpen = false;
		if (start) this.cherishSearchNow();
		else this.forceUpdate();
	};
	/** 안내 카드 순서 = **유도 순서**라 추천(팀배틀)이 먼저다 — 홈 칸의 배열(3대3·6대6·팀배틀)과
	 *  목적이 다르니 맞추지 말 것. 문구는 사용자 원문, 줄표(—)는 쓰지 않는다. */
	cherishIntroCards = [
		{
			id: 'gen9cherishmultibattle', title: '팀배틀', sub: '둘이 한 편', rec: true,
			desc: '무작위 파트너와 같은 편이 돼요. 상대팀과 비슷한 전력이 되게끔 판을 짜 주니까, 처음이라면 여기서 배우기 좋아요.',
		},
		{
			id: 'gen9cherishbattle', title: '3대3', sub: '한 명과 세 마리씩', rec: false,
			desc: '슬슬 자신감이 붙어 누구와 겨뤄도 혼자 힘으로 이길 수 있을 것 같을 때 켜주세요 !',
		},
		{
			id: 'gen9cherishsixbattle', title: '6대6', sub: '한 명과 여섯 마리씩', rec: false,
			desc: '배틀 명단 앞 여섯 칸이 그대로 나가요. 한 판이 오래 걸리는 대신 점수를 두 배로 받아요. 서로 다른 여섯 마리가 모이면 열려요 !',
		},
	];
	/**
	 * 2분 무매칭 회수 팝업 (2026-09-05 사용자 확정). 싱글이 꺼진 채 팀배틀만 기다리다 2분이 지나면
	 * 「3대3도 켜볼까?」 를 한 번 띄운다. 「켜기」는 모드를 켜고 그 자리에서 /search 를 보낸다 (대기 중엔 토글이
	 * 잠겨 있어서 이 경로가 유일하다). 대기가 끝나면(잡힘·취소) 타이머와 표식이 리셋된다.
	 * ⚠️ 대기 시작 시각은 렌더에서 잡는다 — 새로고침으로 대기 중에 들어와도 그 시점부터 2분이다.
	 */
	cherishWaitSince: number | null = null;
	cherishNudgeTimer: any = null;
	cherishNudgeOpen = false;
	cherishNudgeShown = false;
	/**
	 * 3분 무매칭 「실력 차 상관없이 찾기」 팝업 (2026-09-10). **1대1 을 켜고 기다리는 동안만** 뜬다.
	 * 서버가 실력 차 상한(300)을 두기 때문에 상대가 있어도 안 잡힐 수 있고, 그 상한은 **양쪽 다**
	 * 이 버튼을 눌러야만 풀린다 (`/cherishparty widen`). 자동으로는 절대 안 풀린다.
	 * 2분 팝업(1대1 켜기)과는 배타적이다 — 그건 1대1 이 꺼져 있을 때만 뜬다.
	 */
	cherishWideTimer: any = null;
	cherishWideOpen = false;
	cherishWideShown = false;
	cherishTrackWaiting(waiting: boolean) {
		if (waiting && this.cherishWaitSince === null) {
			this.cherishWaitSince = Date.now();
			this.cherishNudgeShown = false;
			this.cherishWideShown = false;
			this.cherishWideTimer = setTimeout(() => {
				this.cherishWideTimer = null;
				// 실력 차 상한은 **2인 포맷 전부**에 걸린다 (서버 matchmakingOK) — 6대6 도 같다.
				if (this.cherishWideShown || !this.cherishSingleOn()) return;
				if (!this.cherishWaitingFormats().length) return;
				this.cherishWideShown = true;
				this.cherishWideOpen = true;
				this.forceUpdate();
			}, 180_000);
			this.cherishNudgeTimer = setTimeout(() => {
				this.cherishNudgeTimer = null;
				// 싱글 전체로 본다 — 6대6 만 켠 사람도 **이미 싱글을 도는 중**이라 권할 것이 없다.
				if (this.cherishNudgeShown || this.cherishSingleOn()) return;
				if (!this.cherishWaitingFormats().length) return;
				this.cherishNudgeShown = true;
				this.cherishNudgeOpen = true;
				this.forceUpdate();
			}, 120_000);
		} else if (!waiting && this.cherishWaitSince !== null) {
			this.cherishWaitSince = null;
			if (this.cherishNudgeTimer) clearTimeout(this.cherishNudgeTimer);
			this.cherishNudgeTimer = null;
			this.cherishNudgeOpen = false;
			if (this.cherishWideTimer) clearTimeout(this.cherishWideTimer);
			this.cherishWideTimer = null;
			this.cherishWideOpen = false;
		}
	}
	cherishNudgeSolo = () => {
		this.cherishNudgeOpen = false;
		this.cherishModes['gen9cherishbattle'] = true;
		try { localStorage.setItem('cherish-battle-modes', JSON.stringify(this.cherishModes)); } catch {}
		const mm = PS.mainmenu;
		if (!mm.search.searching?.includes('gen9cherishbattle')) PS.send(`${mm.adjustPrivacy()}/search gen9cherishbattle`);
		mm.update(null);
	};
	cherishNudgeKeep = () => {
		this.cherishNudgeOpen = false;
		this.forceUpdate();
	};
	cherishWideGo = () => {
		this.cherishWideOpen = false;
		// 표식만 켠다 — 대기는 이미 서 있다. 검색을 다시 걸면 대기 시각이 초기화돼 오히려 손해다.
		PS.send('/cherishparty widen');
		this.forceUpdate();
	};
	cherishWideKeep = () => {
		this.cherishWideOpen = false;
		this.forceUpdate();
	};
	renderCherishWide() {
		if (!this.cherishWideOpen) return null;
		// 문구는 짧게 세 줄 (2분 팝업과 같은 결). 점수·상한 같은 내부 규칙은 말하지 않는다.
		return <div class="cw-intro-backdrop" onClick={this.cherishWideKeep}>
			<div class="cw-intro" role="dialog" aria-label="상대 범위 안내" onClick={ev => ev.stopPropagation()}>
				<h3 class="cw-intro-title">아직 상대를 못 찾았어요</h3>
				<p class="cw-intro-lead">
					비슷한 실력의 트레이너를 찾고 있어요.<br />
					지금은 기다리는 사람이 적은 것 같아요.<br />
					실력 차가 나는 상대와도 붙어 볼까요?
				</p>
				<div class="cw-intro-foot">
					<p class="cw-intro-note">계속 기다려도 괜찮아요. 잡히면 바로 들어가요.</p>
					<button class="cw-intro-close" onClick={this.cherishWideKeep}>계속 기다릴게</button>
					<button class="cw-intro-go" onClick={this.cherishWideGo}>누구든 찾기</button>
				</div>
			</div>
		</div>;
	}
	renderCherishNudge() {
		if (!this.cherishNudgeOpen) return null;
		// 문구는 2026-09-05 사용자 확정 — 짧게 세 줄. 임의로 늘리지 말 것.
		return <div class="cw-intro-backdrop" onClick={this.cherishNudgeKeep}>
			<div class="cw-intro" role="dialog" aria-label="3대3 켜기 안내" onClick={ev => ev.stopPropagation()}>
				<h3 class="cw-intro-title">아직 상대를 못 찾았어요</h3>
				<p class="cw-intro-lead">3대3도 같이 찾아볼까요?</p>
				<div class="cw-intro-foot">
					<p class="cw-intro-note">계속 기다려도 괜찮아요. 잡히면 바로 들어가요.</p>
					<button class="cw-intro-close" onClick={this.cherishNudgeKeep}>계속 기다릴게</button>
					<button class="cw-intro-go" onClick={this.cherishNudgeSolo}>3대3도 켜기</button>
				</div>
			</div>
		</div>;
	}
	/**
	 * 팝업 두 개는 **.menugroup 밖**(.pad 층)에서 그린다 (2026-09-06 실측 수정).
	 * 소프트 글래스(§15)가 .menugroup 에 backdrop-filter 를 주는데, 그게 position:fixed 자손의 기준 상자가 되어
	 * 팝업이 그 박스 안에 갇히고, 뒤에 오는 「오늘의 배틀 점수」 박스(역시 backdrop-filter = 자기 스태킹 컨텍스트)가
	 * 위에 덮였다. .ps-room/.pad 에는 filter·transform 이 없어 여기서는 뷰포트 기준으로 뜬다.
	 * 호출 자리는 render() 의 renderSearchButton() 바로 뒤 (아래 pair 가 끼워 넣는다).
	 */
	renderCherishOverlays() {
		return [this.renderCherishIntro(), this.renderCherishNudge(), this.renderCherishWide()];
	}
	renderCherishIntro() {
		if (!this.cherishIntroOpen) return null;
		const onCount = this.cherishOnFormats().length;
		return <div class="cw-intro-backdrop" onClick={() => this.cherishCloseIntro(false)}>
			<div class="cw-intro" role="dialog" aria-label="자동매칭 안내" onClick={ev => ev.stopPropagation()}>
				<h3 class="cw-intro-title">어떤 배틀을 돌릴까요?</h3>
				<p class="cw-intro-lead">
					세 모드를 한꺼번에 돌릴 수 있어요.<br />
					켜 둔 모드 중 먼저 잡히는 판으로 들어가요.<br />
					처음이시라면 팀배틀로 시작해보시는걸 추천해요 !
				</p>
				{this.cherishIntroCards.map(card => {
					const on = this.cherishModeOn(card.id);
					// 6대6 은 여섯 마리를 갖춰야 켜진다 (2026-09-21). 카드를 숨기지 않고 **잠근 채 보여 준다** —
					// 목록에서 빼 버리면 여섯 마리를 모을 이유도 안 생긴다.
					const locked = card.id === 'gen9cherishsixbattle' && !this.cherishSixReady();
					return <button
						class={`cw-intro-card${on ? ' on' : ''}${locked ? ' is-locked' : ''}`}
						aria-pressed={on}
						disabled={locked}
						onClick={() => this.cherishToggleMode(card.id)}
					>
						<span class="cw-intro-check" aria-hidden>✓</span>
						<span class="cw-intro-body">
							<span class="cw-intro-head">
								<span class={`cw-modechip-mark is-${this.cherishModeKind(card.id)}`} aria-hidden />
								{card.title} <span class="cw-intro-sub">{card.sub}</span>
								{card.rec && <span class="cw-intro-rec">추천</span>}
							</span>
							<span class="cw-intro-desc">{locked ? this.cherishSixNote() : card.desc}</span>
						</span>
					</button>;
				})}
				<div class="cw-intro-foot">
					<p class="cw-intro-note">모드는 홈의 칸에서 언제든 바꿀 수 있어요.</p>
					<button class="cw-intro-close" onClick={() => this.cherishCloseIntro(false)}>닫기</button>
					<button class="cw-intro-go" onClick={() => this.cherishCloseIntro(true)}>
						{onCount >= 3 ? '전부 켜고 시작' : '이대로 시작'}
					</button>
				</div>
			</div>
		</div>;
	}
	cherishCancelAll = () => {
		const mm = PS.mainmenu;
		if (mm.searchCountdown) {
			clearTimeout(mm.searchCountdown.timer);
			mm.searchCountdown = null;
		}
		mm.teamSent = null;
		// 파티면 파트너 명의로 걸린 대기까지 함께 끊어야 한다 (리더 전용 매칭).
		// 혼자면 자기 대기만 지운다 — 서버가 알아서 갈라준다.
		PS.send('/cherishparty cancel');
		mm.update(null);
	};
	/** 파티 대기 상태는 파티가 바뀔 때만 push 된다. 화면에 띄우는 동안은 짧게 폴링한다. */
	cherishEnsurePartyPoll(on: boolean) {
		if (on && !this.cherishPartyPoll) {
			this.cherishPartyPoll = setInterval(() => PS.send('/cherishparty status'), 5000); // poll-ok: 파티를 맺은 동안만, 화면당 하나(cherishPartyPoll 가드)
		} else if (!on && this.cherishPartyPoll) {
			clearInterval(this.cherishPartyPoll);
			this.cherishPartyPoll = null;
		}
	}
	/** 포맷 id → 엠블럼 키. 파일명(mode-ffa/solo/multi.png)이 이 값을 따른다. */
	cherishModeKind(format: string) {
		if (format.includes('ffa')) return 'ffa';
		if (format.includes('multi')) return 'multi';
		// 6대6 은 전용 엠블럼이 없다 — 1대1 과 같은 표식을 쓴다 (구조가 같은 모드라 어색하지 않다).
		// 전용 그림(mode-six.png)을 만들면 여기서 'six' 를 돌려주고 install-ui-assets.py 목록에 넣을 것.
		return 'solo';
	}
	renderCherishModeChip(format: string, title: string) {
		const on = this.cherishModeOn(format);
		// 6대6 은 여섯 마리를 못 갖추면 **잠긴 채 남는다** — 숨기면 모을 이유가 안 생긴다.
		// (2026-09-21 하위 칸의 규칙을 09-22 평면화 때 그대로 옮겨 왔다. 사유 줄은 renderCherishSubNote.)
		const locked = format === 'gen9cherishsixbattle' && !this.cherishSixReady();
		const queue = this.cherishQueueOf(format);
		// need > 2 인 포맷(팀배틀)만 인원을 보여준다. 싱글은 셀 것이 없다.
		const crowd = queue && queue.need > 2 && queue.n ? `${queue.n}/${queue.need}` : '';
		const busy = this.cherishWaitingFormats().length > 0;
		// 켜짐/꺼짐은 투명도가 아니라 형태로 가른다 (외형은 cherish-skin.css .cw-modechip).
		// 체크 자리는 꺼져 있어도 남겨 둔다 — 토글할 때 글자가 좌우로 밀리면 안 된다.
		return <button
			class={`cw-modechip${on ? ' on' : ''}${locked ? ' is-locked' : ''}`}
			disabled={busy || locked}
			aria-pressed={on}
			title={locked ? this.cherishSixNote() : busy ? '대기 중에는 바꿀 수 없어요' : ''}
			onClick={() => this.cherishToggleMode(format)}
		>
			<span class={`cw-modechip-mark is-${this.cherishModeKind(format)}`} aria-hidden />
			<span class="cw-modechip-label">{title}</span>
			{crowd && <span class="cw-modechip-crowd">{crowd}</span>}
		</button>;
	}
	/** 6대6 이 잠긴 이유 — 여섯 마리가 모이면 줄째로 사라진다 (상시 문구가 아니다). */
	renderCherishSubNote() {
		const note = this.cherishSixNote();
		return note ? <p class="cw-submodes-note">{note}</p> : null;
	}
	renderSearchButton() {
		// 홈 모드 선택 (patch-home-modes.py) — 「매칭하기」 모드에서만 그린다. 그 패치가 없으면 항상 그린다.
		if ((this as any).cherishModeHides?.('match')) return null;
		if (PS.down) {
			return <div class="menugroup" style="background: rgba(10,10,10,.6)">
				{PS.down === 'ddos' ? (
					<p class="error"><strong>지금 서버에 문제가 생겼어요 (DDoS)</strong></p>
				) : (
					<p class="error"><strong>지금 서버에 문제가 생겼어요</strong></p>
				)}
				<p>잠시 뒤에 다시 들러주세요.</p>
			</div>;
		}

		if (!PS.user.userid || PS.isOffline) {
			this.cherishEnsurePartyPoll(false);
			return <div class="menugroup">
				<button class="mainmenu1 mainmenu big button disabled" style="width:100%" disabled>
					<em>{PS.isOffline ? [<span class="fa-stack fa-lg">
						<i class="fa fa-plug fa-flip-horizontal fa-stack-1x" aria-hidden></i>
						<i class="fa fa-ban fa-stack-2x text-danger" aria-hidden></i>
					</span>, " 연결 끊김"] : "접속 중..."}</em>
				</button>
				{PS.isOffline && <p class="buttonbar">
					<button class="button" data-cmd="/reconnect">
						<i class="fa fa-plug" aria-hidden></i> <strong>다시 연결</strong>
					</button> {}
					<ReconnectTimer />
				</p>}
			</div>;
		}

		const party = this.cherishParty();
		const inParty = !!party.partner;
		this.cherishEnsurePartyPoll(inParty);
		const waiting = this.cherishWaitingFormats();
		// 2분 무매칭 팝업용 대기 추적 — 비리더·배틀 중은 이 CTA 가 아니므로 리셋한다
		this.cherishTrackWaiting(waiting.length > 0 && !(inParty && party.isLeader === false) && !PS.mainmenu.search.cherishBattle);

		// 비리더 — 읽기 전용. 서버가 검색을 거절하므로 버튼을 주면 팝업만 뜬다.
		if (inParty && party.isLeader === false) {
			return <div class="menugroup" style="max-width:none">
				<div class="cw-cta-passive">
					<span class="cw-cta-icon"><i class="fa fa-users" aria-hidden></i></span>
					<span class="cw-cta-body">
						<span class="cw-cta-title">{party.leaderName} 님이 매칭을 돌려요</span>
						<span class="cw-cta-sub">
							{waiting.length
								? `지금 ${waiting.map(f => f.title).join(', ')} 를 함께 기다리고 있어요.`
								: '아직 대기를 걸지 않았어요. 리더가 시작하면 함께 들어가요.'}
						</span>
					</span>
				</div>
			</div>;
		}

		/*
		 * 배틀 중 — 서버가 새 매칭을 거절한다 (patch-one-battle.py). 누르면 팝업이 뜨는
		 * 것보다 **누르기 전에 아는** 게 낫다. 판정은 서버가 내려주는 값을 그대로 쓴다 —
		 * 클라가 PS.rooms 로 따로 세면, 유저가 배틀 탭을 닫았을 때 게이트와 어긋난다.
		 * 카드를 누르면 그 배틀로 돌아간다 (탭을 닫아 버렸을 때의 유일한 복귀 경로다).
		 */
		const cherishBattleRoom = PS.mainmenu.search.cherishBattle;
		if (cherishBattleRoom) {
			return <div class="menugroup" style="max-width:none">
				<button class="cw-cta-passive" data-href={cherishBattleRoom}>
					<span class="cw-cta-icon"><i class="fa fa-gamepad" aria-hidden></i></span>
					<span class="cw-cta-body">
						<span class="cw-cta-title">배틀 진행 중</span>
						<span class="cw-cta-sub">지금 배틀을 끝내면 다시 매칭할 수 있어요. 눌러서 돌아가기</span>
					</span>
					<span class="cw-cta-go"><i class="fa fa-chevron-right" aria-hidden></i></span>
				</button>
			</div>;
		}

		// 외형은 cherish-skin.css .cw-cta 가 소유한다. 업스트림 .mainmenu1(광택 버튼)은
		// 쓰지 않는다 — 두 줄짜리 <strong>/<small> 한 장이라 위계도 여백도 만들 수 없었다.
		// 대기 중에는 색을 빼고(is-waiting) 바닥 진행 줄만 남긴다. 계속 띄워 두는 상태다.
		const cherishCta = <div class="menugroup" style="max-width:none">
			{waiting.length ? <button
				class="cw-cta is-waiting"
				onClick={this.cherishCancelAll}
			>
				<span class="cw-cta-icon"><i class="fa fa-refresh fa-spin" aria-hidden></i></span>
				<span class="cw-cta-body">
					<span class="cw-cta-title">상대를 찾고 있어요</span>
					<span class="cw-cta-sub">{waiting.map(f => f.title).join(', ')}</span>
				</span>
				<span class="cw-cta-go" title="대기 취소"><i class="fa fa-times" aria-hidden></i> 취소</span>
				<span class="cw-cta-wait-bar" />
			</button> : <button
				class="cw-cta"
				onClick={ev => this.cherishStartAll(ev.currentTarget as HTMLElement)}
			>
				<span class="cw-cta-icon"><svg class="cw-pokeball" viewBox="0 0 24 24" width="21" height="21" aria-hidden><circle cx="12" cy="12" r="9.2" fill="none" stroke="currentColor" stroke-width="2.1" /><path d="M2.8 12a9.2 9.2 0 0 1 18.4 0z" fill="currentColor" opacity=".32" /><path d="M2.8 12h6.2M15 12h6.2" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" /><circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" stroke-width="2.1" /></svg></span>
				<span class="cw-cta-body">
					<span class="cw-cta-title">자동매칭 시작</span>
					<span class="cw-cta-sub">{this.cherishOnFormats().map(f => f.title).join(', ')}</span>
				</span>
				{/* 오른쪽 셰브론(cw-cta-go)은 2026-09-05 사용자 요청으로 제거 — 대기 중 「취소」 알약은 그대로 */}
			</button>}
			{/* 세 칸 평면 (2026-09-22 사용자 결정). 그전에는 큰 칸이 「1대1」·「2대2」 둘이고
				3대3·6대6 이 그 아래 하위 체크였는데, 6대6 이 한 단 아래로 묻혀 보기 어려웠다.
				앞 둘은 마릿수, 사람이 둘인 모드만 「팀배틀」이라 축이 안 섞인다. */}
			<p class="cw-modes">
				{this.renderCherishModeChip('gen9cherishbattle', '3대3')}
				{this.renderCherishModeChip('gen9cherishsixbattle', '6대6')}
				{this.renderCherishModeChip('gen9cherishmultibattle', '팀배틀')}
				{this.cherishIntroEnabled() && <button class="cw-modes-help" title="모드 설명 보기" aria-label="모드 설명 보기" onClick={this.cherishOpenIntro}>?</button>}
			</p>
			{this.renderCherishSubNote()}
			{/* cw-cta-hint 문단은 2026-09-05 사용자 요청으로 제거 */}
			{/* 팝업(첫 안내·2분 무매칭)은 여기가 아니라 renderCherishOverlays 가 .pad 층에서 그린다 — 아래 주석 참고 */}
		</div>;
		// 대회 중 잠금 (2026-10-27, cherish-tournament.tsx) — 대회가 전 유저 자동매칭을 잠그면 이 자리에 대회 카드가 선다.
		// 서버가 거절할 걸 누르고 나서 알게 하지 않는다. 이미 대기 중인 사람은 「취소」가 있어야 해서 감싸지 않는다.
		// 대회 패치가 없는 트리에서도 깨지지 않게 typeof 로 본다 (배틀 셸의 CherishTourStrip 과 같은 규약).
		if (waiting.length || typeof CherishTourMatchGate !== 'function') return cherishCta;
		return <CherishTourMatchGate>{cherishCta}</CherishTourMatchGate>;
	}
	// cherish-ffa:end
	renderBackgroundCredit() {
		const attrib = PSBackground.attrib;
		if (!attrib) return null;
		return (
			<small>
				<a href={attrib.url} target="_blank" class="subtle">"{attrib.title}" <small>background by {attrib.artist}</small></a>
			</small>
		);
	}
	// cherish-itemauction:begin (patch-item-auction.py)
	/** 펼쳐 둔 출품 id. null = 전부 접힘. */
	cherishItemAucOpen: number | null = null;
	cherishItemAucDetail: any = null;
	cherishItemAucBid = '';
	cherishItemAucBusy = false;
	cherishItemAucMsg = '';
	cherishItemAucMsgOk = false;
	cherishItemAucPoll: any = null;
	cherishItemAucTick: any = null;
	cherishItemAucNow(): number { return Date.now() + this.cherishItemAucSkew; }
	cherishItemAucLeft(a: any): number {
		const end = a?.scheduled_end_at ? new Date(a.scheduled_end_at).getTime() : 0;
		return Math.max(0, end - this.cherishItemAucNow());
	}
	cherishItemAucLeftText(a: any): string {
		const ms = this.cherishItemAucLeft(a);
		if (ms <= 0) return '마감';
		const s = Math.floor(ms / 1000);
		const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), mi = Math.floor((s % 3600) / 60), se = s % 60;
		if (d > 0) return `${d}일 ${h}시간 남음`;
		if (h > 0) return `${h}시간 ${mi}분 남음`;
		if (mi > 0) return `${mi}분 ${se}초 남음`;
		return `${se}초 남음`;
	}
	cherishItemAucName(e: any): string {
		return e?.display_name || e?.name || '누군가';
	}
	cherishItemAucHasWinner(a: any): boolean {
		return Number(a?.uniform_price || 0) > 0 || !!(a?.winners && a.winners.length);
	}
	cherishItemAucResultText(a: any): string {
		if (a.show_winner && a.winners && a.winners.length) {
			return `${this.cherishItemAucName(a.winners[0])}에게 낙찰, ${Number(a.uniform_price || 0).toLocaleString('ko-KR')}체리`;
		}
		if (this.cherishItemAucHasWinner(a)) return a.show_winner ? '낙찰 결과를 확인하는 중이에요' : '낙찰 완료 · 낙찰자 비공개';
		if (Number(a?.bid_count || 0) > 0 || Number(a?.participant_count || 0) > 0 || Number(a?.my_locked || 0) > 0) return '낙찰 결과를 확인하는 중이에요';
		return '아무도 입찰하지 않았어요';
	}
	cherishItemAucToggle(id: number) {
		if (this.cherishItemAucOpen === id) { this.cherishItemAucClose(); return; }
		this.cherishItemAucOpen = id;
		this.cherishItemAucDetail = null;
		this.cherishItemAucBid = '';
		this.cherishItemAucMsg = '';
		this.cherishItemAucFetchDetail();
		if (!this.cherishItemAucPoll) this.cherishItemAucPoll = setInterval(() => {
			if (typeof document !== 'undefined' && document.hidden) return;
			this.cherishItemAucFetchDetail();
		}, 10000);
		if (!this.cherishItemAucTick) this.cherishItemAucTick = setInterval(() => this.forceUpdate(), 1000);
		this.forceUpdate();
	}
	cherishItemAucClose() {
		this.cherishItemAucOpen = null;
		this.cherishItemAucDetail = null;
		if (this.cherishItemAucPoll) { clearInterval(this.cherishItemAucPoll); this.cherishItemAucPoll = null; }
		if (this.cherishItemAucTick) { clearInterval(this.cherishItemAucTick); this.cherishItemAucTick = null; }
		this.forceUpdate();
	}
	cherishItemAucFetchDetail() {
		const id = this.cherishItemAucOpen;
		if (id === null) return;
		fetch(`https://cherish-world.com/api/showdown/auction/${id}`, { credentials: 'include' })
			.then(r => r.json())
			.then(json => {
				if (this.cherishItemAucOpen !== id) return;
				if (json?.ok) {
					this.cherishItemAucDetail = json.data;
					if (json.data.now) this.cherishItemAucSkew = new Date(json.data.now).getTime() - Date.now();
					const list = this.cherishItemAuc || [];
					for (let i = 0; i < list.length; i++) if (list[i].id === id) list[i] = json.data;
				}
				this.forceUpdate();
			})
			.catch(() => {});
	}
	cherishItemAucMinValid(a: any): number {
		const inc = Number(a?.min_increment || 1);
		const top = a?.highest_bid;
		if (top !== null && top !== undefined) return Number(top) + inc;
		return Number(a?.starting_price || 0);
	}
	cherishItemAucSetBid(v: string) {
		this.cherishItemAucBid = v.replace(/[^0-9]/g, '');
		this.forceUpdate();
	}
	cherishItemAucPlaceBid() {
		const id = this.cherishItemAucOpen;
		const amount = parseInt(this.cherishItemAucBid, 10);
		if (id === null || this.cherishItemAucBusy) return;
		if (!Number.isFinite(amount) || amount <= 0) { this.cherishItemAucMsg = '입찰할 체리를 적어 주세요.'; this.cherishItemAucMsgOk = false; this.forceUpdate(); return; }
		this.cherishItemAucBusy = true;
		this.cherishItemAucMsg = '';
		this.forceUpdate();
		fetch(`https://cherish-world.com/api/showdown/auction/${id}/bid`, {
			method: 'POST', credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ bidAmount: amount }),
		})
			.then(r => r.json())
			.then(json => {
				this.cherishItemAucBusy = false;
				if (json?.ok) {
					const d = json.data || {};
					this.cherishItemAucMsgOk = true;
					this.cherishItemAucMsg = `${Number(d.bid_amount || amount).toLocaleString('ko-KR')}체리로 입찰했어요.` +
						(d.deadline_extended ? ' 마감이 연장됐어요.' : '');
					this.cherishItemAucBid = '';
					this.cherishItemAucFetchDetail();
				} else {
					this.cherishItemAucMsgOk = false;
					this.cherishItemAucMsg = json?.message || '입찰하지 못했어요. 잠시 후 다시 해 주세요.';
					this.cherishItemAucFetchDetail();
				}
				this.forceUpdate();
			})
			.catch(() => { this.cherishItemAucBusy = false; this.cherishItemAucMsgOk = false; this.cherishItemAucMsg = '연결이 불안정해요. 잠시 후 다시 해 주세요.'; this.forceUpdate(); });
	}
	renderCherishItemAucBadges(a: any) {
		const badges: string[] = (a?.special_item?.badges || []);
		if (!badges.length) return null;
		return <span class="cw-auc-badges">{badges.map(b => <span class="cw-auc-badge">{b}</span>)}</span>;
	}
	renderCherishItemAucDetail(a: any) {
		const active = a.status === 'active' && this.cherishItemAucLeft(a) > 0;
		const inc = Number(a.min_increment || 1);
		const minValid = this.cherishItemAucMinValid(a);
		const myLocked = Number(a.my_locked || 0);
		const myTop = a.highest_bidder_id && a.my_highest_bid && Number(a.my_highest_bid) === Number(a.highest_bid);
		const bidders: any[] = a.top_bidders || [];
		const winners: any[] = a.winners || [];
		const it = a.special_item;
		return <div class="cw-auc-detail">
			{a.description ? <p class="cw-auc-desc">{a.description}</p> : null}
			{it ? <p class="cw-auc-spec"><b>{it.ko}</b> <small>{it.desc}</small></p> : null}
			<div class="cw-auc-prices">
				<div><small>{a.status === 'ended' ? '낙찰가' : '현재 입찰가'}</small><b>{a.status === 'ended' && a.uniform_price ? Number(a.uniform_price).toLocaleString('ko-KR') : a.highest_bid !== null && a.highest_bid !== undefined ? Number(a.highest_bid).toLocaleString('ko-KR') : '-'}</b></div>
				<div><small>시작가</small><b>{Number(a.starting_price).toLocaleString('ko-KR')}</b></div>
				<div><small>지금 낼 금액</small><b>{active ? minValid.toLocaleString('ko-KR') : '-'}</b></div>
				<div><small>내 입찰액</small><b>{myLocked.toLocaleString('ko-KR')}</b></div>
			</div>
			{active ? <div class="cw-auc-time">{this.cherishItemAucLeftText(a)} <small>· 마감 {Math.round(Number(a.extension_seconds || 1800) / 60)}분 전에 입찰이 들어오면 그만큼 연장돼요</small></div>
				: <div class="cw-auc-time is-end">{a.status === 'ended' ? '경매가 끝났어요' : '낙찰자를 정하는 중이에요'}</div>}
			{active && PS.user.named ? <div class="cw-auc-bidbox">
				{myTop ? <p class="cw-auc-mine">지금 가장 높은 값을 부른 사람이 바로 당신이에요.</p> : null}
				<div class="cw-auc-bidrow">
					<input class="textbox cw-auc-input" type="text" inputMode="numeric" placeholder={`${minValid.toLocaleString('ko-KR')} 이상`}
						value={this.cherishItemAucBid} onInput={(e: any) => this.cherishItemAucSetBid(e.currentTarget.value)}
						onKeyDown={(e: any) => { if (e.key === 'Enter') this.cherishItemAucPlaceBid(); }} disabled={this.cherishItemAucBusy} />
					<button class="button cw-auc-quick" onClick={() => this.cherishItemAucSetBid(String(minValid))} disabled={this.cherishItemAucBusy}>최소</button>
					<button class="button cw-auc-quick" onClick={() => this.cherishItemAucSetBid(String(Math.max(minValid, (parseInt(this.cherishItemAucBid, 10) || minValid) + inc)))} disabled={this.cherishItemAucBusy}>+{inc.toLocaleString('ko-KR')}</button>
					<button class="button cw-auc-go" onClick={() => this.cherishItemAucPlaceBid()} disabled={this.cherishItemAucBusy || !this.cherishItemAucBid}>{this.cherishItemAucBusy ? '입찰하는 중…' : '입찰하기'}</button>
				</div>
				<p class="cw-auc-note">입찰하면 체리가 바로 묶여요. 더 높은 값이 들어오면 자동으로 돌려받아요. 내 체리 {a.my_cherry !== undefined ? Number(a.my_cherry).toLocaleString('ko-KR') : '-'}개.</p>
				{this.cherishItemAucMsg ? <p class={`cw-auc-msg${this.cherishItemAucMsgOk ? ' is-ok' : ' is-err'}`}>{this.cherishItemAucMsg}</p> : null}
			</div> : null}
			{a.status === 'ended' && (a.show_winner || this.cherishItemAucHasWinner(a)) ? <div class="cw-auc-list">
				<h4>낙찰</h4>
				{a.show_winner && winners.length ? winners.map(w => <div class="cw-auc-row"><span class="cw-auc-rank">🏆</span><span class="cw-auc-name">{this.cherishItemAucName(w)}</span><span class="cw-auc-amt">{Number(w.charged || 0).toLocaleString('ko-KR')}체리</span></div>)
					: <p class="cw-auc-empty">{this.cherishItemAucResultText(a)}</p>}
			</div> : null}
			{active && a.show_bidders ? <div class="cw-auc-list">
				<h4>입찰한 사람들 <small>{Number(a.participant_count || 0)}명</small></h4>
				{bidders.length ? bidders.map(b => <div class="cw-auc-row"><span class="cw-auc-rank">{b.rank}</span><span class="cw-auc-name">{this.cherishItemAucName(b)}</span><span class="cw-auc-amt">{b.bid_amount !== null && b.bid_amount !== undefined ? `${Number(b.bid_amount).toLocaleString('ko-KR')}체리` : ''}</span></div>)
					: <p class="cw-auc-empty">아직 아무도 입찰하지 않았어요. 첫 입찰자가 돼 보세요!</p>}
			</div> : null}
			<p class="cw-auc-foot">낙찰한 물건은 「내 포켓몬 → 🎒 인벤토리」 에 들어가요. 남에게 넘길 수 없어요.</p>
		</div>;
	}
	renderCherishItemAuction() {
		if (!PS.user.named) return null;
		const list = this.cherishItemAuc;
		if (!list || !list.length) return null;
		const openId = this.cherishItemAucOpen;
		return <div class="menugroup cw-auction cw-itemauc" style="max-width:none">
			<div class="cw-auc-head">
				<div><span class="cw-eyebrow">Market</span><h3 class="cw-auc-title">구름 장터</h3></div>
				<span class="cw-auc-sub">귀한 물건이 하나씩 나와요</span>
			</div>
			{list.map((row: any) => {
				const isOpen = openId === row.id;
				const a = isOpen && this.cherishItemAucDetail ? this.cherishItemAucDetail : row;
				const active = a.status === 'active' && this.cherishItemAucLeft(a) > 0;
				const img = a.special_item?.imageUrl || a.image_url;
				const line = active
					? `${a.highest_bid !== null && a.highest_bid !== undefined ? '현재 입찰가 ' + Number(a.highest_bid).toLocaleString('ko-KR') : '시작가 ' + Number(a.starting_price).toLocaleString('ko-KR')}체리, ${Number(a.participant_count || 0)}명, ${this.cherishItemAucLeftText(a)}`
					: a.status === 'ended'
						? this.cherishItemAucResultText(a)
						: '낙찰자를 정하는 중';
				return <div class={`cw-auc-item${isOpen ? ' is-open' : ''}${active ? '' : ' is-end'}`}>
					<button class="cw-auc-summary" onClick={() => this.cherishItemAucToggle(row.id)} aria-expanded={isOpen}>
						<span class="cw-auc-art">{img ? <img src={String(img).startsWith('http') ? img : 'https://cherish-world.com' + img} alt="" onError={(e: any) => { e.currentTarget.style.visibility = 'hidden'; }} /> : null}</span>
						<span class="cw-auc-body">
							<span class="cw-auc-name-line"><b>{a.title}</b>{this.renderCherishItemAucBadges(a)}</span>
							<span class="cw-auc-line">{line}{Number(a.my_locked || 0) > 0 ? `, 내 입찰액 ${Number(a.my_highest_bid || a.my_locked).toLocaleString('ko-KR')}` : ''}</span>
						</span>
						<span class="cw-auc-caret">{isOpen ? '접기' : active ? '입찰' : '결과'}</span>
					</button>
					{isOpen ? this.renderCherishItemAucDetail(a) : null}
				</div>;
			})}
		</div>;
	}
	// cherish-itemauction:end
	// cherish-home:begin (patch-home-dash.py)
	/**
	 * 「오늘의 배틀 점수」를 이 카드가 품는다는 표식 (2026-09-06 사용자: 「둘은 애초에 한 섹션」).
	 * patch-battle-points.py 의 최상위 호출이 이 표식을 보고 스스로 물러난다. 두 패치의 적용
	 * 순서와 무관하게 동작한다 — 같은 클래스의 필드라 런타임엔 둘 다 있거나 둘 다 없다.
	 */
	cherishHomeOwnsBp = true;
	/** GET /showdown/home 응답. null = 아직, 'hidden' = 권한 없음·옛 api (카드를 아예 안 그린다). */
	cherishHome: any | 'hidden' | null = null;
	cherishHomeFor = '';
	cherishHomeTimer: any = null;
	/** 첫 방문인가 — 이 세션 동안 고정 (렌더마다 다시 읽으면 30초 뒤 제목이 바뀐다). */
	cherishWelcome: boolean = (() => {
		try {
			if (localStorage.getItem('cw-welcomed')) return false;
			localStorage.setItem('cw-welcomed', '1');
			return true;
		} catch { return false; }
	})();
	/** 마지막으로 홈 데이터를 부른 시각 — cherishHomeRefresh 의 스로틀 기준. */
	cherishHomeLastFetch = 0;
	/**
	 * 홈으로 **돌아올 때** 즉시 다시 받는다 (2026-09-06 사용자: 「1교시 클리어 후 메인화면으로 돌아가면
	 * 2교시가 안 열려있고 새로고침을 해야 열려」). 30초 폴링만으로는 수업을 막 통과하고 돌아온 화면이
	 * 옛 진도를 보여준다. 학교 진도·모드 잠금이 같은 응답에 실려 오므로 졸업 직후 「매칭하기」가 열리는
	 * 것도 이 경로가 맡는다. 방을 옮길 때마다 불려도 되도록 2초 스로틀.
	 */
	cherishHomeRefresh = () => {
		if (Date.now() - this.cherishHomeLastFetch < 2000) return;
		this.fetchCherishHome();
	};
	fetchCherishHome = () => {
		if (!PS.user.named) return;
		if (typeof document !== 'undefined' && document.hidden) return;
		this.cherishHomeLastFetch = Date.now();
		this.cherishHomeFor = PS.user.userid;
		fetch('https://cherish-world.com/api/showdown/home', { credentials: 'include' })
			.then(r => r.json())
			.then(json => {
				this.cherishHome = json?.ok ? json.data : 'hidden';
				// 흐르는 공지 띠 (2026-10-30, patch-notice-ticker.py) — 같은 응답에 실려 온다(새 요청 없음). 옵셔널 호출이라 그 패치가 없어도 무해.
				if (json?.ok) (window as any).cherishNoticeFeed?.(json.data?.notices);
				this.forceUpdate();
			})
			.catch(() => {});
	};
	ensureCherishHome() {
		if (!PS.user.named) return;
		if (!this.cherishHomeTimer) this.cherishHomeTimer = setInterval(this.fetchCherishHome, 30000); // poll-ok: 홈 하나뿐(cherishHomeTimer 가드), 탭이 숨으면 fetch 가 스스로 빠진다
		if (this.cherishHomeFor !== PS.user.userid) this.fetchCherishHome();
	}
	cherishHomeGames(me: any) { return me ? (me.wins || 0) + (me.losses || 0) + (me.ties || 0) : 0; }
	cherishHomeWinRate(me: any) { const g = this.cherishHomeGames(me); return g ? Math.round((me.wins || 0) / g * 100) : 0; }
	/** 홈 맨 위 「오늘의 나」 — 얼굴·티어·순위·전적·Elo + 다섯 칸. */
	renderCherishHome() {
		if (!PS.user.named) return null;
		this.ensureCherishHome();
		const h = this.cherishHome;
		if (h === 'hidden') return null;
		if (!h) return <div class="menugroup cw-home is-loading" style="max-width:none"><div class="cw-skel" aria-hidden><i class="cw-skel-card" /></div></div>;
		const me = h.me;
		const games = this.cherishHomeGames(me);
		const placing = me && games < (h.placementGames || 5);
		const face = me?.faceUrl ? `https://cherish-world.com${me.faceUrl}` : `/sprites/trainers-custom/cherish${PS.user.avatar && String(PS.user.avatar).startsWith('#cherish-') ? String(PS.user.avatar).slice(9) : '0'}.png`;
		const rec = me ? `${me.wins}승 ${me.losses}패${me.ties ? ` ${me.ties}무` : ''}` : '';
		return <div class="menugroup cw-home" style="max-width:none">
			<div class="cw-home-top">
				<span class="cw-home-face-wrap"><img class="cw-home-face" src={face} alt="" />{me ? this.renderCherishTierIcon(me.tier, 'cw-tier-xs') : null}</span>
				<div class="cw-home-who">
					<span class="cw-eyebrow">오늘의 나</span>
					<div class="cw-home-name-line">
						<span class="cw-home-name">{h.name || PS.user.name}</span>
						{/* 익명 토글 (2026-09-15, **2026-09-22 양방향**) — 옛날엔 배틀 시작 버튼 아래 menugroup 한 칸을
						    통째로 썼다. 익명은 「이번 매칭 설정」이 아니라 **접속 이름 자체**를 바꾸는 일이라 내 이름 옆이 맞다.
						    09-15 에는 켜기만 옮기고 끄기는 아래 띠에 남겨 뒀는데, 그게 회수한 자리를 도로 쓰는 꼴이라
						    사용자가 지적했다 — 이제 **칩 하나가 양방향**이고 띠는 없앴다(patch-anon-toggle.py).
						    지금 가명이라는 신호는 `is-on` 칩과 title 이 맡는다. 옵셔널 호출이라 anon-toggle 이
						    없는 조합에서도 무해하다. */}
						{PS.user.name.startsWith('익명트레이너')
							? <button class="cw-anon-chip is-on" title={`지금 ${PS.user.name} 으로 접속 중이에요`}
								onClick={() => (this as any).toggleCherishAnon?.(false)}>실명으로 돌아가기</button>
							: <button class="cw-anon-chip" onClick={() => (this as any).toggleCherishAnon?.(true)}>익명으로 배틀</button>}
					</div>
					<div class="cw-home-sub">{me
						? <span>{me.tier?.name}{placing ? <span>, {games}/{h.placementGames}판</span> : me.idleHidden ? <span>, {h.idleHideDays || 10}일 동안 배틀이 없어 랭킹판에서 빠져있어요</span> : <span>, <b>{me.rank}위</b></span>}, {rec}</span>
						: <span>아직 이번 시즌 기록이 없어요. 첫 배틀을 해보세요</span>}</div>
				</div>
				{me ? <div class="cw-home-elo"><span ref={(el: any) => this.cherishCountUp(el, me.elo)}>{Number(me.elo).toLocaleString()}</span><small>점</small></div> : null}
			</div>
			<div class="cw-home-stats">
				<a class="cw-home-stat" href="/cherish-ranking"><b>{me ? `${this.cherishHomeWinRate(me)}%` : '-'}</b><small>승률</small></a>
				<span class="cw-home-stat"><b>{Number(h.todayBattles) || 0}</b><small>오늘 배틀</small></span>
				<a class="cw-home-stat" href="/cherish-team"><b>{h.teamOut || 0}<em>/3</em></b><small>출전</small></a>
				<a class="cw-home-stat" href="/cherish-team"><b>{h.pokemonCount || 0}</b><small>포켓몬</small></a>
				<a class="cw-home-stat" href="/cherish-team"><b>{h.eggCount || 0}</b><small>알</small></a>
			</div>
			{this.renderCherishBattlePoints(true)}
		</div>;
	}
	/** 첫걸음 안내 — 모든 단계가 끝나면 사라진다(단계 수는 학교·체리쉬타워 스위치에 따라 4~6). */
	renderCherishGuide() {
		if (!PS.user.named) return null;
		const h = this.cherishHome;
		if (!h || h === 'hidden') return null;
		const games = this.cherishHomeGames(h.me);
		const pg = h.placementGames || 5;
		const steps: { done: boolean, title: string, desc: string, href: string | null }[] = [
			{ done: (h.pokemonCount || 0) > 0, title: '첫 포켓몬 만나기', desc: '디스코드에서 /포켓몬 으로 알을 받고 부화시켜요', href: null },
			{ done: (h.teamOut || 0) >= 3, title: '출전 3자리 채우기', desc: '내 포켓몬 탭에서 자리표를 눌러 배틀 팀을 짜요', href: '/cherish-team' },
		];
		// 학교 졸업이 체리쉬타워·자동매칭의 전제다 (2026-09-06). 안 열렸으면(access!=='open') 그 단계를 아예 넣지 않는다.
		const sc = this.cherishSchool;
		if (sc?.access === 'open') {
			steps.push({
				done: !!sc.status?.graduated,
				title: '포켓몬학교 졸업하기',
				desc: '체리가 빌려주는 아이들로 상성과 교체를 배우고 졸업시험을 봐요',
				href: null,
			});
		}
		// 졸업 다음은 체리쉬타워 (2026-09-07 사용자 결정): 상대를 골라 주는 한 판이라 자동매칭보다 앞이 자연스럽다.
		// 완료 판정 = 내 고스트가 층에 자리를 잡았나(floor). 무승부·기권은 자리를 안 만드므로 아직 미완료다.
		const tw = (this as any).cherishTower;  // patch-tower-card.py 가 아직 안 깔렸으면 undefined = 단계 없음
		if (tw?.access === 'open') {
			steps.push({
				done: tw.status?.floor != null,
				title: '체리쉬타워 참여해보기',
				desc: '졸업생들의 고스트가 층을 이룬 탑이야. 한 판 오르면 내 자리가 생겨요',
				href: null,
			});
		}
		steps.push(
			{ done: games >= 1, title: '첫 배틀 해보기', desc: '학교를 졸업하면 자동매칭으로 상대를 찾아줘요', href: null },
			{ done: games >= pg, title: `배치 ${pg}판 끝내기`, desc: '배치가 끝나면 티어가 정해지고 랭킹에 올라가요', href: '/cherish-ranking' },
		);
		const doneN = steps.filter(s => s.done).length;
		if (doneN === steps.length) return null;
		let now = -1;
		for (let i = 0; i < steps.length; i++) { if (!steps[i].done) { now = i; break; } }
		return <div class="menugroup cw-guide" style="max-width:none">
			<div class="cw-guide-head">
				<div><span class="cw-eyebrow">Getting started</span><h3 class="cw-guide-title">{this.cherishWelcome ? '체리쉬 배틀에 온 걸 환영해요' : '첫걸음'}</h3></div>
				<span class="cw-guide-count"><b>{doneN}</b>/{steps.length}</span>
			</div>
			<ol class="cw-guide-steps">
				{steps.map((s, i) => <li class={`cw-guide-step${s.done ? ' is-done' : ''}${i === now ? ' is-now' : ''}`}>
					<span class="cw-guide-no">{s.done ? <svg viewBox="0 0 24 24" aria-hidden><path d="M5 12.5l4.5 4.5L19 7.5" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" /></svg> : i + 1}</span>
					<span class="cw-guide-body"><b>{s.title}</b><small>{s.desc}</small></span>
					{!s.done && s.href ? <a class="button cw-guide-go" href={s.href}>가기</a> : null}
				</li>)}
			</ol>
		</div>;
	}
	override renderCherishBoard() {
		const roomsCache = PS.mainmenu.roomsCache as any;
		const users: any[][] = roomsCache.cherishUsers || [];
		const cherishLoading = roomsCache.cherishUsers === undefined;
		if (!this.cherishRoomsTimer) {
			const refresh = () => {
				if (PS.connection?.connected) { PS.send('/cmd rooms'); PS.send('/cmd roomlist ,'); }
				else setTimeout(refresh, 500);
			};
			this.cherishRoomsTimer = setInterval(refresh, 25000); // poll-ok: 홈 방 목록 하나뿐(cherishRoomsTimer 가드)
			refresh();
		}
		const battling = users.filter(u => u[2]).length;
		return <div class="menugroup cw-board" style="max-width:none">
			<div class="cw-board-head">
				<div><h3 class="cw-board-title">접속 중인 트레이너</h3><p class="cw-board-sub">아바타를 누르면 파티에 부르거나 미니홈에 갈 수 있어요</p></div>
				<span class="cw-board-count"><span><b>{roomsCache.userCount || users.length || '-'}</b>명 접속</span>{battling ? <span class="cw-board-battling"><i aria-hidden />{battling}명 배틀 중</span> : null}</span>
			</div>
			<div class="cw-trainers">
				{users.map(u => {
					const name = String(u[0]);
					const avatar = String(u[1] || '');
					const inBattle = !!u[2];
					const id = toID(name);
					const isMe = id === PS.user.userid;
					const src = avatar.startsWith('#cherish-') ? `/sprites/trainers-custom/cherish${avatar.slice(9)}-full.png` : Dex.resolveAvatar(avatar);
					return <button data-href={`user-${id}`} class={`cw-trainer${inBattle ? ' is-battling' : ''}${isMe ? ' is-me' : ''}`} title={isMe ? '나' : name}>
						<span class="cw-trainer-ava"><img src={src} alt="" /></span>
						<span class="cw-trainer-status">{inBattle ? '배틀 중' : '대기 중'}</span>
						<span class="cw-trainer-name">{name}</span>
					</button>;
				})}
			</div>
			{!users.length && <p class="cw-board-empty"><em>{cherishLoading ? '접속 정보를 불러오는 중...' : '지금 접속한 트레이너가 없어요.'}</em></p>}
		</div>;
	}
	// cherish-home:end
	// cherish-home-modes:begin (patch-home-modes.py)
	/**
	 * 마지막으로 고른 모드. 잠겨 있으면 이 값과 무관하게 'school' 이다.
	 * 값이 'tower' 로 남아 있어도 (준비 중이라) 'match' 로 떨어진다.
	 */
	cherishModePick: string = (() => {
		// 딥링크 `?go=school|match|tower|maze` (2026-10-01) — 디스코드 봇의 「1교시 들어가기」·「학교 가기」가 이걸로 온다.
		// 주소에서 지우지 않는다: 로그인 왕복(patch-login-flow.py 의 next=location.href)이 이 주소를 그대로 들고 가야 한다.
		// 잠겨 있으면(미졸업) 어차피 school 이고, 졸업자는 이 값이 「고른 모드」가 된다. 닫힌 탑·미궁은 cherishHomeMode 가 match 로 돌린다.
		try {
			// ⚠️ location.search 가 아니라 **client-main.ts 가 부팅 때 적어 둔 값**을 읽는다 (cherish-go-entry 블록).
			// PSRouter 가 / 로 replaceState 한 뒤에 이 패널이 만들어져서, 여기서 location.search 를 보면 늘 비어 있다
			// (2026-10-08 실측: 6일간 /?go=school 요청은 있었는데 도착 표 0건·모드도 안 바뀜). 도착 신호도 그 블록이 보낸다.
			const qs = new URLSearchParams((window as any).cherishGoQuery ?? location.search);
			const go = qs.get('go');
			if (go === 'school' || go === 'match' || go === 'tower' || go === 'maze') {
				try { localStorage.setItem('cherish-home-mode', go); } catch {}
				return go;
			}
		} catch {}
		try { return localStorage.getItem('cherish-home-mode') || ''; } catch { return ''; }
	})();
	/**
	 * 잠금 상태 캐시 — 학교 데이터(/showdown/school)는 홈 폴링에 동승해서 첫 페인트엔 없다.
	 * 캐시가 없으면 미졸업자 화면에 자동매칭 CTA 가 한 번 번쩍였다 사라진다 (버그로 보인다).
	 */
	cherishModeLockCache: boolean = (() => {
		try { return localStorage.getItem('cherish-school-locked') === '1'; } catch { return false; }
	})();
	/** 자동매칭이 잠겼나 — 학교가 **열린 뒤** 미졸업이면 잠긴다. 오픈 전에는 아무도 안 잠긴다. */
	cherishMatchLocked(): boolean {
		const s = this.cherishSchool;
		if (!s) return this.cherishModeLockCache;
		const locked = s.access === 'open' && !s.status?.graduated;
		if (locked !== this.cherishModeLockCache) {
			this.cherishModeLockCache = locked;
			try { localStorage.setItem('cherish-school-locked', locked ? '1' : '0'); } catch {}
		}
		return locked;
	}
	/**
	 * 졸업했는데 탑에 자리가 아직 없나 (2026-09-24). 서버 값(floor == null)이라 기기를 바꿔도 같다.
	 * 실측: 졸업생 15명이 한 번도 타워에 안 왔다. 홈 기본 모드가 「매칭하기」라 타워 카드를 본 적이 없었다.
	 */
	cherishTowerFirstClimb(): boolean {
		const st = (this as any).cherishTower?.status;
		return this.cherishTowerMode() === 'open' && !!st && st.floor == null;
	}
	/**
	 * 졸업한 뒤(타워가 열린 뒤) 모드를 직접 고른 적이 있나. 있으면 그 선택을 따르고, 없으면 첫 등반 전까지 타워로 연다.
	 * 옛 `cherish-home-mode` 는 졸업 전에 누른 값도 남아 있어서 따로 센다. 배틀방의 「체리쉬타워로 가기」가 이 표시를 지운다.
	 * 렌더마다 읽는다 — 다른 방(배틀방)에서 바꾼 값이 홈 컴포넌트 필드에는 안 들어오기 때문이다.
	 */
	cherishModePickedAfterGrad(): boolean {
		try { return localStorage.getItem('cherish-home-mode-picked') === '1'; } catch { return false; }
	}
	cherishHomeMode(): string {
		if (this.cherishMatchLocked()) return 'school';
		if (this.cherishTowerFirstClimb() && !this.cherishModePickedAfterGrad()) {
			// 고른 값도 타워로 맞춰 둔다 — 첫 판을 이기고 돌아왔을 때 홈이 「매칭하기」로 튀면 방금 선 자리를 못 본다
			if (this.cherishModePick !== 'tower') {
				this.cherishModePick = 'tower';
				try { localStorage.setItem('cherish-home-mode', 'tower'); } catch {}
			}
			return 'tower';
		}
		if (this.cherishModePick === 'school' || this.cherishModePick === 'match') return this.cherishModePick;
		// 체리쉬타워는 열려 있을 때만 고른 값으로 인정한다 — 닫힌 뒤에도 'tower' 가 남아 빈 화면이 되면 안 된다
		if (this.cherishModePick === 'tower' && this.cherishTowerMode() === 'open') return 'tower';
		// 구름 미궁도 같은 규칙 (2026-09-08). 2026-09-09 부터 네 번째 **메인 카드**다.
		if (this.cherishModePick === 'maze' && this.cherishMazeMode() === 'open') return 'maze';
		return 'match';
	}
	/**
	 * 체리쉬타워 카드 상태 — 하드코딩 대신 실제 데이터(patch-tower-card.py 가 받아 두는 cherishTower)를 본다.
	 *   soon   스위치가 꺼져 있다(어드민 🗼 토글 전)
	 *   locked 학교를 아직 졸업 안 했다
	 *   open   오를 수 있다
	 * 데이터가 아직 없으면 soon (첫 페인트에 눌리는 카드가 번쩍이면 안 된다).
	 */
	cherishTowerMode(): string {
		const t = (this as any).cherishTower;
		if (!t || t.access === 'closed' || !t.status) return 'soon';
		return t.status.graduated ? 'open' : 'locked';
	}
	/** 구름 미궁 상태 — 탑과 같은 규칙. 데이터는 patch-maze-card.py 가 받아 두는 cherishMaze. */
	cherishMazeMode(): string {
		const m = (this as any).cherishMaze;
		if (!m || m.access === 'closed' || !m.status) return 'soon';
		return m.status.graduated ? 'open' : 'locked';
	}
	/** 각 섹션이 첫 줄에서 물어본다 — 지금 모드가 아니면 스스로 물러난다. */
	cherishModeHides(mode: string): boolean {
		return this.cherishHomeMode() !== mode;
	}
	cherishModePickSet(mode: string) {
		this.cherishModePick = mode;
		try { localStorage.setItem('cherish-home-mode', mode); } catch {}
		// 타워가 열린 뒤 직접 고른 것만 센다 — 졸업 전에 학교 카드를 누른 건 「고른 것」이 아니다
		if (this.cherishTowerMode() === 'open') { try { localStorage.setItem('cherish-home-mode-picked', '1'); } catch {} }
		this.forceUpdate();
	}
	/** 「오늘의 나」 아래 모드 선택 줄. 고른 카드의 섹션만 아래에 뜬다. */
	renderCherishModes() {
		if (!PS.user.named) return null;
		const locked = this.cherishMatchLocked();
		const mode = this.cherishHomeMode();
		// 카드마다 그림 한 장(/cherish/ui/home-mode-<key>.webp). 없으면 <img onError> 로 그림만 빠지고
		// 카드는 흰 바탕에 글자만 남는다 — 설치 전에도 화면이 깨지지 않는다.
		const card = (key: string, title: string, desc: string, state: string, lockMsg: string) => {
			return <button
				class={`cw-mode is-${state}${mode === key ? ' is-on' : ''}`}
				onClick={() => {
					if (state === 'locked' || state === 'soon') { PS.alert(lockMsg); return; }
					this.cherishModePickSet(key);
				}}
			>
				<img class="cw-mode-art" src={`/cherish/ui/home-mode-${key}.webp?v=1`} alt=""
					onError={(e: any) => { e.currentTarget.style.display = 'none'; }} />
				<span class="cw-mode-body"><b>{title}</b><small>{desc}</small></span>
				{state === 'locked' || state === 'soon' ? <span class="cw-mode-lock"><i class="fa fa-lock" aria-hidden></i></span> : null}
				{key === 'tower' && state === 'open' && this.cherishTowerFirstClimb() ? <span class="cw-mode-pill">첫 등반</span> : null}
			</button>;
		};
		return <div class="menugroup cw-modes-row" style="max-width:none">
			<div class="cw-modes-grid">
				{card('school', '학교',
					locked ? '상성과 교체를 배우고 졸업시험까지' : '들었던 수업 다시 듣기', 'open', '')}
				{card('match', '매칭하기',
					locked ? '학교를 졸업하면 열려요' : '3대3 · 6대6 · 팀배틀', locked ? 'locked' : 'open',
					'체리의 포켓몬학교를 졸업하면 열려요. 왼쪽 학교 카드에서 수업을 들어보세요!')}
				{(() => {
					const ts = this.cherishTowerMode();
					return card('tower', '체리쉬타워',
						ts === 'soon' ? '준비 중이에요' : ts === 'locked' ? '학교를 졸업하면 열려요'
							: this.cherishTowerFirstClimb() ? '탑에 내 이름 올리기' : '한 층씩 올라가 보자',
						ts,
						ts === 'soon' ? '체리쉬타워는 준비 중이에요. 조금만 기다려 줘!'
							: '체리의 포켓몬학교를 졸업하면 열려요. 학교 카드에서 수업을 들어보세요!');
				})()}
				{/* 구름 미궁 — 2026-09-09 부터 네 번째 칸. 옛 서브 띠(🌀 이모지)는 걷어냈다. */}
				{(() => {
					const ms = this.cherishMazeMode();
					return card('maze', '구름 미궁',
						ms === 'soon' ? '준비 중이에요' : ms === 'locked' ? '학교를 졸업하면 열려요' : '야생을 헤치고 깊이 들어가 보자',
						ms,
						ms === 'soon' ? '구름 미궁은 준비 중이에요. 조금만 기다려 줘!'
							: '체리의 포켓몬학교를 졸업하면 열려요. 학교 카드에서 수업을 들어보세요!');
				})()}
			</div>
			{locked ? <p class="cw-modes-hint">학교를 졸업하면 매칭하기와 체리쉬타워, 구름 미궁이 열려요.</p> : null}
		</div>;
	}
	// cherish-home-modes:end
	override render() {
		const tinyLayout = this.props.room.width < 620 ? ' tiny-layout' : '';
		this.ensureCherishData();
		return <PSPanelWrapper room={this.props.room} onDragEnter={this.handleDragEnter} scrollable>
			<div class={`mainmenu-mini-windows${tinyLayout}`}>
				{this.renderMiniRooms()}
			</div>
			<div class="pad" style="max-width:1100px;margin:0 auto">
				{this.renderGames()}
				{this.renderCherishHome()}
				{/* cherish-tour */}<CherishTourBanner />
				{this.renderCherishHotTime()}
				{this.renderCherishRaidLive()}
				{this.renderCherishNewbieLearn()}
				{this.renderCherishModes()}
				{/* cherish-extraction-lobby-entry:begin */}
                {/* 정식 오픈 전: 소풍은 주소 직접 입력으로만 진입. */}
				{/* cherish-extraction-lobby-entry:end */}
				{this.renderSearchButton()}
				{this.renderCherishOverlays()}
				{this.renderCherishBattlePoints()}
				{this.renderCherishSchool()}
				{this.renderCherishTower()}
				{this.renderCherishMaze()}
				{this.renderCherishBlossom()}
				{this.renderCherishPwa()}
				{this.renderCherishGuide()}
				{this.renderCherishItemAuction()}
				{this.renderCherishQuests()}
				{this.renderCherishRooms()}
				{this.renderCherishParty()}
				{this.renderCherishBoard()}
				{this.renderCherishThemePicker()}
				{this.renderCherishPwaFoot()}
			</div>
		</PSPanelWrapper>;
	}
}

export class CCPAIntercept extends preact.Component {
	intercepted = false;
	override shouldComponentUpdate() {
		this.intercept();
		return false;
	}
	override componentDidMount() {
		this.intercept();
		setTimeout(() => this.intercept(), 500);
		setTimeout(() => this.intercept(), 1000);
		setTimeout(() => this.intercept(), 2000);
		setTimeout(() => this.intercept(), 3000);
		setTimeout(() => this.intercept(), 5000);
		setTimeout(() => this.intercept(), 10000);
	}
	intercept() {
		if (this.intercepted || !window.$) return;
		const $ccpa = $('.fc-dns-dialog');
		if (!$ccpa.length) return;
		$ccpa.appendTo(this.base!);
		// $ccpa.css({ position: 'relative', zIndex: 2 });
		this.intercepted = true;
	}
	override render() {
		return <div></div>;
	}
}

export class FormatDropdown extends preact.Component<{
	selectType?: SelectType, format?: string, defaultFormat?: string, placeholder?: string,
	onChange?: JSX.EventHandler<Event>,
}> {
	declare base?: HTMLButtonElement;
	format = '';
	change = (e: Event) => {
		if (!this.base) return;
		this.format = this.base.value;
		this.forceUpdate();
		if (this.props.onChange) this.props.onChange(e);
	};
	render() {
		this.format = this.props.format || this.format || this.props.defaultFormat || '';
		let [formatName, customRules] = this.format.split('@@@');
		customRules = customRules?.replace(/,/g, ', ');
		if (window.BattleLog) formatName = BattleLog.formatName(formatName);
		else setTimeout(() => this.forceUpdate(), 500); // cherish-world: BattleLog 로드 전 렌더 시 재시도 (raw id 잔상 방지)
		if (this.props.format && !this.props.onChange) {
			// There's intentionally no `disabled` prop. If this is out of sync
			// with the `format` and `onChange` props, that's a bug.
			return <button
				name="format" value={this.format} class="select formatselect preselected" disabled
			>
				{formatName}
				{!!customRules && [<br />, <small>커스텀 룰: {customRules}</small>]}
			</button>;
		}
		return <button
			name="format" value={this.format} data-selecttype={this.props.selectType}
			class="select formatselect" data-href="/formatdropdown" onChange={this.change}
		>
			{formatName || (!!this.props.placeholder && <em>{this.props.placeholder}</em>) || null}
			{!!customRules && [<br />, <small>커스텀 룰: {customRules}</small>]}
		</button>;
	}
}

class TeamDropdown extends preact.Component<{ format: string }> {
	teamFormat = '';
	teamKey = '';
	change = () => {
		if (!this.base) return;
		this.teamKey = (this.base as HTMLButtonElement).value;
		this.forceUpdate();
	};
	getDefaultTeam(teambuilderFormat: string) {
		for (const team of PS.teams.list) {
			if (!team.isBox && team.format === teambuilderFormat) return team.key;
		}
		return '';
	}
	render() {
		const teamFormat = PS.teams.teambuilderFormat(this.props.format);
		const formatData = window.BattleFormats?.[teamFormat];
		if (formatData?.team) {
			return <button class="select teamselect preselected" name="team" value="random" disabled>
				<div class="team">
					<strong>Random team</strong>
					<small>
						<PSIcon pokemon={null} />
						<PSIcon pokemon={null} />
						<PSIcon pokemon={null} />
						<PSIcon pokemon={null} />
						<PSIcon pokemon={null} />
						<PSIcon pokemon={null} />
					</small>
				</div>
			</button>;
		}
		if (teamFormat !== this.teamFormat) {
			this.teamFormat = teamFormat;
			this.teamKey = this.getDefaultTeam(teamFormat);
		}
		const team = PS.teams.byKey[this.teamKey] || null;
		return <button
			name="team" value={this.teamKey}
			class="select teamselect" data-href="/teamdropdown" data-format={teamFormat} onChange={this.change}
			disabled={!!PS.mainmenu.searchingFormat()}
		>
			{PS.roomTypes['teamdropdown'] && <TeamBox team={team} noLink />}
		</button>;
	}
}

export class TeamForm extends preact.Component<{
	children: preact.ComponentChildren,
	class?: string, format?: string, teamFormat?: string, hideFormat?: boolean, selectType?: SelectType,
	defaultFormat?: string,
	onSubmit: ((e: Event, format: string, team?: Team) => void) | null,
	onValidate?: ((e: Event, format: string, team?: Team) => void) | null,
}> {
	format = '';
	teraPreview = false;
	bestOf = false;
	bestOfValue = '3';
	customRules = false;
	customRuleText = '';
	itemClause = false;
	changeFormat = (ev: Event) => {
		this.setFormat((ev.target as HTMLButtonElement).value);
	};
	setFormat(format: string) {
		const [baseFormat, customRules] = format.split('@@@');
		this.format = baseFormat;
		this.loadCustomRules(customRules);
	};
	loadCustomRules(customRules: string) {
		this.bestOf = false;
		this.bestOfValue = '3';
		this.teraPreview = false;
		this.itemClause = false;
		if (!customRules) {
			this.customRules = false;
			this.customRuleText = '';
			return;
		}

		this.customRules = true;
		const unknownRules: string[] = [];
		for (const rule of customRules.split(',')) {
			const trimmedRule = rule.trim();
			if (!trimmedRule) continue;
			const bestOfMatch = /^best[-\s]*of\s*=\s*(\d+)$/i.exec(trimmedRule);
			if (bestOfMatch) {
				this.bestOf = true;
				this.bestOfValue = bestOfMatch[1];
			} else if (/^tera\s+type\s+preview$/i.test(trimmedRule)) {
				this.teraPreview = true;
			} else if (/^item\s+clause\s*=\s*1$/i.test(trimmedRule)) {
				this.itemClause = true;
			} else {
				unknownRules.push(trimmedRule);
			}
		}
		this.customRuleText = unknownRules.join('\n');
	};
	changeBestOfValue = (ev: Event) => {
		this.bestOfValue = (ev.target as HTMLInputElement).value;
	};
	changeCustomRules = (ev: Event) => {
		this.customRuleText = (ev.target as HTMLTextAreaElement).value;
	};
	addCustomRules(format: string, rules: string[]) {
		if (!rules.length) return format;
		const hasCustomRules = format.includes('@@@');
		return `${format}${hasCustomRules ? ', ' : '@@@ '}${rules.join(', ')}`;
	}
	submit = (ev: Event, validate?: 'validate') => {
		ev.preventDefault();
		let format = this.format;
		// in tournaments, format is the custom name & teamFormat is the original format.
		const teambuilderFormat = PS.teams.teambuilderFormat(this.props.teamFormat || format);
		const teamElement = this.base!.querySelector<HTMLButtonElement>('button[name=team]');
		const teamKey = teamElement?.value || '';
		const team = teamKey ? PS.teams.byKey[teamKey] : undefined;
		// cherish-world: 팀은 서버가 소유 포켓몬으로 자동 구성 — 팀빌더 요구 게이트 제거
		const customRules: string[] = [];
		if (this.customRules) {
			if (this.bestOf) {
				customRules.push(`Best of = ${this.bestOfValue || '3'}`);
			}
			if (this.teraPreview) customRules.push('Tera Type Preview');
			customRules.push(...this.customRuleText.split('\n').map(rule => rule.trim()).filter(Boolean));
		}
		if (this.itemClause) {
			customRules.push('Item Clause = 1');
		}
		format = this.addCustomRules(format, customRules);
		PS.teams.loadTeam(team).then(() => {
			(validate === 'validate' ? this.props.onValidate : this.props.onSubmit)?.(ev, format, team);
		});
	};
	toggleCustomRule = (ev: Event) => {
		const checked = (ev.target as HTMLInputElement)?.checked;
		const rule = (ev.target as HTMLInputElement)?.name;
		if (rule === 'terapreview') this.teraPreview = checked;
		if (rule === 'bestof') this.bestOf = checked;
		if (rule === 'customrules') {
			this.customRules = checked;
			if (!checked) {
				this.bestOf = false;
				this.teraPreview = false;
			}
			this.forceUpdate();
		}
		if (rule === 'itemclause') this.itemClause = checked;
	};
	handleClick = (ev: Event) => {
		let target = ev.target as HTMLButtonElement | null;
		while (target && target !== this.base) {
			if (target.tagName === 'BUTTON' && target.name === 'validate') {
				this.submit(ev, 'validate');
				return;
			}
			target = target.parentNode as HTMLButtonElement | null;
		}
	};
	render() {
		if (window.BattleFormats) {
			this.format ||= this.props.defaultFormat || '';
			// cherish-world: 봉인된 포맷(랜덤배틀 등)이 기본값으로 남아있으면 리셋
			if (this.format && !window.BattleFormats[toID(this.format.split('@@@')[0])]) this.format = '';
			if (!this.format) {
				const cherishFirst = Object.values(window.BattleFormats).find(f => f.searchShow !== false && !!f.id);
				this.format = cherishFirst?.id || `gen${Dex.gen}randombattle`;

				const starredPrefs = PS.prefs.starredformats || {};
				// .reverse() because the newest starred format should be the default one
				const starred = Object.keys(starredPrefs).filter(id => starredPrefs[id] === true).reverse();
				for (let id of starred) {
					let format = window.BattleFormats[id];
					if (!format) continue;
					if (this.props.selectType === 'challenge' && format?.challengeShow === false) continue;
					if (this.props.selectType === 'search' && format?.searchShow === false) continue;
					if (this.props.selectType === 'teambuilder' && format?.team) continue;
					this.format = id;
					break;
				}
			}
		}
		if (this.props.defaultFormat?.startsWith('!!')) {
			// The !! means that it overrides any current format, and will only be
			// sent as a prop once
			this.setFormat(this.props.defaultFormat.slice(2));
		}
		if (this.props.format) this.format = this.props.format;
		if (!this.props.format && this.format.includes('@@@')) this.setFormat(this.format);
		const formatId = toID(this.format.split('@@@')[0]);
		const format = window.BattleFormats[formatId];
		const showCustomRules = false; // cherish-lockdown-ui: 커스텀 룰 입력 제거 (포맷은 서버 봉인 목록만)
		return <form class={this.props.class} onSubmit={this.submit} onClick={this.handleClick}>
			{!this.props.hideFormat && <p>
				<label class="label">
					포맷:<br />
					<FormatDropdown
						selectType={this.props.selectType} format={this.format}
						onChange={this.props.format ? undefined : this.changeFormat}
					/>
				</label>
			</p>}
			{showCustomRules && (!this.customRules ? <p>
				<label class="checkbox"><input
					type="checkbox" name="customrules" checked={this.customRules} onChange={this.toggleCustomRule}
				/> 커스텀 룰</label>
			</p> : <fieldset class="fieldset">
				<legend><label class="checkbox"><input
					type="checkbox" name="customrules" checked={this.customRules} onChange={this.toggleCustomRule}
				/> 커스텀 룰</label></legend>
				{(format?.bestOfDefault || this.bestOf) && <p>
					<label class="checkbox">
						<input
							type="checkbox" name="bestof" checked={this.bestOf} onChange={this.toggleCustomRule}
						/>
						<abbr title="Start a team-locked best-of-n series">Best-of-<input
							name="bestofvalue" type="number" min="3" max="9" step="2" value={this.bestOfValue}
							onInput={this.changeBestOfValue}
							style="width: 28px; vertical-align: initial;"
						/></abbr></label>
				</p>}
				{(format?.teraPreviewDefault || this.teraPreview) && <p>
					<label class="checkbox"><input
						type="checkbox" name="terapreview" checked={this.teraPreview} onChange={this.toggleCustomRule}
					/> Tera Type Preview</label>
				</p>}
				{(format?.itemClauseDefault || this.itemClause) && <p>
					<label class="checkbox"><input
						type="checkbox" name="itemclause" checked={this.itemClause} onChange={this.toggleCustomRule}
					/> Item Clause</label>
				</p>}
				<textarea
					name="customrules" class="textbox" rows={3} placeholder="Rules separated by commas or lines"
					value={this.customRuleText} onInput={this.changeCustomRules}
					style="min-height:3em"
				/>
				<small><a
					href="https://github.com/smogon/pokemon-showdown/blob/master/config/CUSTOM-RULES.md" target="_blank"
				>Custom rules guide</a></small>
			</fieldset>)}
			<p>{this.props.children}</p>
		</form>;
	}
}


/** cherish-world fork: 닫을 수 없는 기본 탭 3종 (patch-cherish-tabs.py).
 *  홈이 너무 길어져서 도전과제·랭킹·내 포켓몬을 각자 탭으로 분리했다. */
class CherishTabRoom extends PSRoom {
	override readonly classType: string = 'cherishtab';
}
/** 세 탭이 공유하는 껍데기 — 로그인 전에는 안내만 띄운다. */
class CherishTabPanel extends CherishPanelBase<CherishTabRoom> {
	/** 하위 탭이 각자 자기 섹션 하나를 돌려준다. */
	renderCherishTab(): any { return null; }
	override render() {
		this.ensureCherishData();
		return <PSPanelWrapper room={this.props.room} scrollable>
			<div class="pad" style="max-width:1100px;margin:0 auto">
				{PS.user.named && <button class="button" style="float:right;font-size:12px;padding:3px 10px;margin-left:8px" title="다시 불러오기" onClick={() => this.loadCherishData()}><i class="fa fa-refresh" aria-hidden></i> 새로고침</button>}
				{!PS.user.named ? <p><em>로그인하면 볼 수 있어요.</em></p> :
					this.renderCherishTab() || <p><em>불러오는 중...</em></p>}
			</div>
		</PSPanelWrapper>;
	}
}
class CherishAchievementsPanel extends CherishTabPanel {
	static readonly id = 'cherishachievements';
	static readonly routes = ['cherish-achievements'];
	static readonly Model = CherishTabRoom;
	static readonly title = '도전과제';
	static readonly icon = <i class="fa fa-certificate" aria-hidden></i>;
	override cherishNeeds = ['achievements', 'quests'];
	override renderCherishTab() {
		return <>{this.renderCherishQuestList()}{this.renderCherishAchievements()}</>;
	}
}
class CherishRankingPanel extends CherishTabPanel {
	static readonly id = 'cherishranking';
	static readonly routes = ['cherish-ranking'];
	static readonly Model = CherishTabRoom;
	static readonly title = '랭킹';
	static readonly icon = <i class="fa fa-trophy" aria-hidden></i>;
	// cherish-rank-sub:begin (patch-nest-tab.py)
	/**
	 * 도전과제가 상단 탭에서 내려와 여기 하위탭이 됐다 (2026-09-21). 둘 다 「내 성취」라
	 * 결이 맞고, 상단 4칸 중 한 칸을 보금자리에 내주려면 자리가 필요했다.
	 * `cherish-achievements` 방은 그대로 살아 있다 — 퀘스트바 버튼이 그리로 간다.
	 */
	override cherishNeeds = ['ranking', 'achievements', 'quests'];
	/** 'ranking' | 'achievements' */
	cherishRankTab = 'ranking';
	override renderCherishTab() {
		const tab = this.cherishRankTab;
		return <>
			<div class="cw-subtab cw-rank-subtab">
				<button
					class={'button' + (tab === 'ranking' ? ' cur' : '')}
					onClick={() => { this.cherishRankTab = 'ranking'; this.forceUpdate(); }}
				>랭킹</button>
				<button
					class={'button' + (tab === 'achievements' ? ' cur' : '')}
					onClick={() => { this.cherishRankTab = 'achievements'; this.forceUpdate(); }}
				>도전과제</button>
			</div>
			{tab === 'ranking'
				? this.renderCherishRanking()
				: <>{this.renderCherishQuestList()}{this.renderCherishAchievements()}</>}
		</>;
	}
	// cherish-rank-sub:end
}
class CherishTeamPanel extends CherishTabPanel {
	static readonly id = 'cherishteam';
	static readonly routes = ['cherish-team'];
	static readonly Model = CherishTabRoom;
	static readonly title = '내 포켓몬';
	static readonly icon = <i class="fa fa-paw" aria-hidden></i>;
	override cherishNeeds = ['me', 'shop', 'nickoffers', 'avoids']; // cherish-nick-offer
	override renderCherishTab() { return this.renderCherishTeamTabs(); }
}

// cherish-nest:begin (patch-nest-tab.py)
/**
 * 「보금자리」 탭 — 아이가 새 집을 찾는 자리를 한 지붕에 모은다 (2026-09-21).
 *
 *   인연의 보금자리 = 유저끼리 (교환 게시판, cherish-market.tsx)
 *   체리의 온실     = 아는 트레이너와 같이 짝 찾기 (cherish-love.tsx, 2026-09-30)
 *   빌려주기        = 다 자란 아이를 누구에게나 1·3·7일 (cherish-lending.tsx, 2026-10-15)
 *   특별한 보금자리 = 운영이 내는 아이 (경매, patch-auction-panel.py)
 *
 * 「상점」이 아니라 「보금자리」인 이유: 서버 어휘가 어린이집·온실·마당이라 상점만 건조하고,
 * 무엇보다 게시판을 상점 아래 두면 **「체리로 사는 곳」이라는 틀**이 씌워진다. 게시판은 돈이
 * 아니라 아이를 주고받는 자리다 (「체리는 남에게 선물할 수 없다」가 서버 대원칙이다).
 *
 * ⚠️ 인연의 보금자리·체리의 온실 하위탭은 **각자 스위치가 켜져 있을 때만 존재한다**. 꺼져 있으면
 * 탭 자체가 없다 — 들어가서 「준비 중」을 보는 건 노출이다. 하위탭이 하나뿐이면 탭 바도 안 그린다.
 * 온실 탭의 점은 **내가 답할 차례인 요청이 있을 때**만 — 탭이 뜰 때 한 번 받은 값이다(주기 없음).
 */
class CherishNestPanel extends CherishTabPanel {
	static readonly id = 'cherishnest';
	static readonly routes = ['cherish-nest'];
	static readonly Model = CherishTabRoom;
	static readonly title = '보금자리';
	static readonly icon = <i class="fa fa-home" aria-hidden></i>;
	override cherishNeeds = ['auction'];
	/** 'market' | 'love' | 'auction'. 닫힌 탭이면 열린 탭 중 첫 번째로. */
	cherishNestTab = 'market';
	override componentDidMount() {
		super.componentDidMount?.();
		// NEW 점을 내린다 — 기준은 「봤나」다 (patch-nest-tab.py 7-b). 주소로 바로 들어오든
		// 하단 내비로 들어오든 여기를 지나므로, 탭 클릭이 아니라 방이 뜨는 자리에 둔다.
		cwmNestSeen();
		cwmAskGate(() => this.forceUpdate());
		cwlAskGate(() => this.forceUpdate());
		cwbAskGate(() => this.forceUpdate());
	}
	override renderCherishTab() {
		// 온실 신청 카드 링크·받은 신청 팝업으로 왔다 (0460, cherish-market.tsx cwmLoveBoot) — 온실 하위탭을 연다.
		// 요청 화면은 CherishLoveView 가 그 값을 소비하며 연다. 게이트가 아직이면 탭이 생길 때까지 이 값을 들고 있다.
		if (cwlWantsLove()) this.cherishNestTab = 'love';
		const tabs: string[] = [];
		if (cwmMarketOpen() === true) tabs.push('market');
		if (cwlLoveOpen() === true) tabs.push('love');
		if (cwbLendingOpen() === true) tabs.push('lending');
		tabs.push('auction');
		const tab = tabs.includes(this.cherishNestTab) ? this.cherishNestTab : tabs[0];
		const narrow = this.props.room.width > 0 && this.props.room.width < 720;
		const label: { [k: string]: string } = { market: '인연의 보금자리', love: '체리의 온실', lending: '빌려주기', auction: '특별한 보금자리' };
		return <>
			{tabs.length > 1 ? <div class="cw-subtab cw-nest-subtab">
				{tabs.map(t => <button
					key={t}
					class={'button' + (tab === t ? ' cur' : '') + (t === 'love' && cwlLoveTodo() > 0 ? ' cw-new-dot' : '') + (t === 'love' && cwlLoveIsNew() ? ' cw-new-chip' : '')}
					onClick={() => { this.cherishNestTab = t; if (t === 'love') cwlLoveOpened(); this.forceUpdate(); }}
				>{label[t]}</button>)}
			</div> : null}
			{tab === 'market'
				? <CherishMarketView narrow={narrow} />
				: tab === 'love'
					? (cwlLoveOpened(), <CherishLoveView narrow={narrow} onTodo={() => this.forceUpdate()} />)
					: tab === 'lending'
						? <CherishLendingView narrow={narrow} />
						: this.renderCherishAuction()}
		</>;
	}
}
// cherish-nest:end
// cherish-gym:begin (patch-gym-tab.py)
/**
 * 「체육관」 탭 (2026-10-21) — 타입별 관장의 고스트에 도전하고, 관장이 연 실시간 창에 줄을 선다.
 * 본체는 cherish-gym.tsx (CherishGymView). 데이터는 그 컴포넌트가 스스로 받는다 — cherishNeeds 는 비운다.
 * 스위치가 꺼져 있으면 탭 알약이 안 그려진다 (panel-topbar.tsx cherish-gym-hide). 방은 있어도 들어올 길이 없다.
 */
class CherishGymPanel extends CherishTabPanel {
	static readonly id = 'cherishgym';
	static readonly routes = ['cherish-gym'];
	static readonly Model = CherishTabRoom;
	static readonly title = '체육관';
	static readonly icon = <i class="fa fa-shield" aria-hidden></i>;
	override cherishNeeds = [];
	override focus(options?: any) {
		super.focus(options);
		window.dispatchEvent(new Event('cherish-gym-focus'));
	}
	override componentDidMount() {
		super.componentDidMount?.();
		cwgAskGate(() => this.forceUpdate());
	}
	override renderCherishTab() {
		if (cwgGymOpen() === false) return <p class="cw-muted" style="padding:16px">지금은 문이 닫혀 있어요.</p>;
		const narrow = this.props.room.width > 0 && this.props.room.width < 720;
		return <CherishGymView narrow={narrow} />;
	}
}
// cherish-gym:end
PS.addRoomType(NewsPanel, MainMenuPanel, CherishAchievementsPanel, CherishRankingPanel, CherishTeamPanel, CherishNestPanel, CherishGymPanel);

// 고정 탭은 접속 직후 홈 다음 순서로 미리 만들어 둔다 (addRoomType 뒤여야 제 타입으로 생성됨).
for (const id of ['cherish-team', 'cherish-nest', 'cherish-gym', 'cherish-ranking'] as RoomID[]) {
	if (!PS.rooms[id]) PS.addRoom({ id, autofocus: false });
}
